import { costMatchesEn, costMatchesPtBr } from '@/lib/i18n/cost-matches';
import { taxLiabilityReviewEn, taxLiabilityReviewPtBr } from '@/lib/i18n/tax-liability-review';

export const billsPtBr = {
  needsReview: {
    title: 'Pagamentos em revisão',
    subtitle:
      'Pagamentos com um resultado do banco que o Alchemy ERP não aplica sozinho. Confira no banco, aja no pagamento e registre a decisão.',
    views: 'Motivos',
    viewAll: 'Todos',
    reason: {
      REVERSED_AFTER_PAYMENT: 'Estornado após o pagamento',
      REVERSED_AFTER_RECONCILIATION: 'Estornado após a conciliação',
      NO_PROVIDER_RESULT_BY_DUE_TIME: 'Sem retorno no prazo',
      PROVIDER_RESULT_CODE_UNKNOWN: 'Retorno não reconhecido',
      PROVIDER_RESULT_AMOUNT_MISMATCH: 'Valor pago diferente',
      PROVIDER_RESULT_UNMATCHED: 'Retorno sem envio correspondente',
      PROVIDER_PAYMENT_HELD: 'Retido pelo banco',
      UNKNOWN: 'Motivo não reconhecido',
    },
    reasonHint: {
      REVERSED_AFTER_PAYMENT:
        'O banco estornou um pagamento que já estava pago. O pagamento ficou como estornado, mas o lançamento contábil e a situação da conta continuam como pagos: estorne o lançamento e reabra a conta, se for o caso.',
      REVERSED_AFTER_RECONCILIATION:
        'O banco estornou um pagamento já conciliado. A situação do pagamento não foi alterada: confira o extrato e desfaça a conciliação, se for o caso.',
      NO_PROVIDER_RESULT_BY_DUE_TIME:
        'O gateway de pagamento aceitou o envio e não informou o resultado no prazo. Confira no banco se o pagamento saiu antes de reenviar.',
      PROVIDER_RESULT_CODE_UNKNOWN:
        'O gateway de pagamento informou um retorno do banco que o Alchemy ERP não aplica sozinho. Confira no banco o que aconteceu com o pagamento antes de agir.',
      PROVIDER_RESULT_AMOUNT_MISMATCH:
        'O banco informou o pagamento como efetuado, mas com valor ou data que não conferem com o envio. O pagamento não foi baixado: confira no banco e baixe manualmente, se for o caso.',
      PROVIDER_RESULT_UNMATCHED:
        'O gateway de pagamento devolveu um retorno com a referência deste pagamento que não corresponde ao envio registrado (outra conta de origem, envio não confirmado ou pagamento já encerrado). Nada foi aplicado: confira no banco.',
      PROVIDER_PAYMENT_HELD:
        'O banco reteve o pagamento na análise de segurança ou o invalidou. Ele não foi pago nem recusado: confira no app do banco o que falta e, se for o caso, cancele o envio antes de reenviar.',
      UNKNOWN:
        'O Alchemy ERP não reconhece este motivo de revisão. Nada foi aplicado ao pagamento: confira no banco o que aconteceu antes de agir e, se persistir, fale com o suporte.',
    },
    columns: {
      vendor: 'Fornecedor',
      reason: 'Motivo',
      amount: 'Valor',
      scheduledDate: 'Pagar em',
      status: 'Situação',
      raisedAt: 'Em revisão desde',
      origin: 'Conta de origem',
    },
    unknownVendor: 'Sem fornecedor',
    resolve: 'Resolver',
    resolveRow: 'Resolver a revisão do pagamento de {{name}}',
    emptyTitle: 'Nenhum pagamento em revisão',
    emptyDescription:
      'Quando o banco estornar um pagamento ou não informar o resultado de um envio no prazo, ele aparece aqui.',
    dialog: {
      title: 'Resolver revisão',
      description:
        'Registre o que você conferiu e o que foi feito. A situação do pagamento não muda aqui: pague, cancele o envio ou estorne o lançamento pelos fluxos do pagamento.',
      noteLabel: 'Decisão',
      notePlaceholder: 'Ex.: conferido no banco, estorno creditado; lançamento estornado.',
      noteHint: 'Mínimo de {{min}} caracteres. Fica no histórico do pagamento.',
      submit: 'Registrar decisão',
      cancel: 'Cancelar',
      success: 'Revisão resolvida.',
      refusal: {
        PAYMENT_NOT_IN_NEEDS_REVIEW:
          'Este pagamento não aguarda mais revisão: alguém já a resolveu ou o banco informou o resultado. Atualize a lista.',
      },
    },
  },
  // The shared post-approval offers dialog (D47/D52): chrome only. Each registered step
  // (e.g. ALC-4565's approval-rule) owns its own copy.
  postApprovalOffers: {
    stepBar: '{{current}} de {{total}}',
    stepError: {
      title: 'Não foi possível mostrar esta sugestão',
      next: 'Próxima',
      close: 'Fechar',
    },
  },
  // ALC-4565: the approval-rule step of the post-approval dialog, its hint and its lines.
  approvalOffer: {
    hint: 'Criar uma regra para as próximas contas da {{vendorShort}} como esta',
    hintButton: 'Criar regra',
    billRef: 'NF {{number}}',
    title: 'Automatizar as próximas contas da {{vendorShort}}?',
    titleApproveOnly: 'Aprovar sozinhas as próximas contas da {{vendorShort}}?',
    basedOn: 'Baseado na {{billRef}}, que você aprovou · {{entity}}',
    basedOnNoRef: 'Baseado nesta conta, que você aprovou · {{entity}}',
    basedOnByRule: 'Baseado na {{billRef}}, que uma regra aprovou · {{entity}}',
    basedOnByRuleNoRef: 'Baseado nesta conta, que uma regra aprovou · {{entity}}',
    whenTitle: 'Quando chegar uma conta da {{vendorShort}} com',
    rows: {
      glAccounts: 'Conta contábil',
      costCenters: 'Centro de custo',
      departments: 'Departamento',
      managerialAccounts: 'Conta gerencial',
      segment: 'Segmento',
      paymentMethod: 'Forma de pagamento',
      withholding: 'Retenções',
      clientReimbursable: 'Reembolsável',
      amount: 'Total até',
    },
    editAria: 'Editar {{label}}',
    removeFromRule: 'Tirar da regra',
    outOfRule: 'Fora da regra:',
    backToRule: 'Voltar {{label}} para a regra',
    variesRecently: 'varia nas últimas contas',
    done: 'Pronto',
    yes: 'Sim',
    no: 'Não',
    joinAnd: ' e ',
    joinOr: ' ou ',
    modeGroup: 'Como comparar {{name}}',
    mode: {
      all: 'Rateio entre estes',
      exact: 'Exatamente estes %',
      any: 'Pelo menos um destes',
    },
    modeHelp: {
      all: 'Usa todos, com qualquer %. Nenhum outro.',
      exact: 'Usa todos, com estes %. Nenhum outro.',
      any: 'Usa pelo menos um. Outros podem entrar.',
    },
    modeRule: {
      all: 'Passa se usar só estes {{count}}, com qualquer %.',
      exact: 'Passa só com {{percents}}. Nenhum outro.',
      any: 'Passa se usar pelo menos um destes. Outros podem entrar.',
    },
    onBill: 'Na {{billRef}}',
    onBillNoRef: 'Nesta conta',
    removeTarget: 'Tirar {{name}}',
    add: '+ Adicionar',
    addAria: 'Adicionar {{name}}',
    percentAria: 'Percentual de {{name}}',
    percentInvalid: 'Digite um percentual de 0 a 100, como 60 ou 33,33.',
    percentSum: 'A soma precisa dar 100%. Hoje dá {{sum}}.',
    examplesTitle: 'Outras contas:',
    examplePasses: 'Passa',
    exampleStops: 'Para',
    paymentGroup: 'Formas de pagamento aceitas',
    paymentRule: 'A conta passa com qualquer uma das marcadas.',
    withholdingNone: 'sem retenção',
    withholdingRule: 'A conta passa só se tiver as mesmas retenções: {{profile}}.',
    reimbursableRule: 'A conta passa só se for reembolsável pelo cliente.',
    amountOnBill: 'Na {{billRef}}: {{amount}}',
    amountOnBillNoRef: 'Nesta conta: {{amount}}',
    amountAria: 'Total até, em reais',
    amountHint: 'Sugerido: {{ceiling}} (esta conta + 10%).',
    amountError: 'Digite um valor maior que zero, como 2.200,00.',
    sourceFails: 'A {{billRef}} não passaria nesta regra ({{rows}}). Você ainda pode salvar.',
    sourceFailsNoRef: 'Esta conta não passaria nesta regra ({{rows}}). Você ainda pode salvar.',
    thenTitle: 'Faça isto',
    send: {
      title: 'Enviar para aprovação',
      body: 'Pula a revisão. Uma pessoa aprova.',
      active: 'Já ativa',
    },
    approve: {
      title: 'Aprovar',
      body: 'Aprova no seu nome. A regra confere a sua alçada em cada conta.',
      bodyLimit: 'Aprova no seu nome, dentro da sua alçada de {{limit}}.',
      useLimit: 'Usar teto de {{limit}}',
    },
    pickOne: 'Marque pelo menos uma opção para salvar.',
    reasons: {
      NO_AUTHORITY: 'Você não aprova contas da {{vendorShort}}.',
      APPROVED_BY_RULE: 'A {{billRef}} foi aprovada por uma regra, não por você.',
      APPROVED_BY_RULE_NO_REF: 'Esta conta foi aprovada por uma regra, não por você.',
      MULTI_APPROVER_CHAIN: 'Esta conta tem mais de um aprovador. A aprovação continua com eles.',
      CURRENCY_NOT_BRL: 'A aprovação sozinha só vale para entidades em reais.',
      PROFILE_UNAVAILABLE:
        'Sem as retenções desta conta na regra, a aprovação continua com uma pessoa.',
      NO_CEILING: 'Sem "Total até", a regra não tem teto. A sua alçada é {{limit}}.',
      NO_CEILING_NO_LIMIT: 'Sem "Total até", a regra não tem teto.',
      CEILING_ABOVE_AUTHORITY: 'O teto é {{ceiling}}. A sua alçada é {{limit}}.',
      NO_COST_CENTER: 'Sem centro de custo, a sua alçada não cobre a conta.',
      ANY_MODE_COST_CENTER:
        'Com "Pelo menos um destes", a conta pode ter outros centros de custo. A sua alçada só cobre {{ccs}}.',
      COST_CENTER_NOT_COVERED: 'A sua alçada não cobre {{ccs}}.',
      DEPARTMENT_NOT_COVERED: 'A sua alçada não cobre o departamento {{depts}}.',
      VENDOR_NOT_COVERED: 'A sua alçada não cobre a {{vendorShort}}.',
    },
    dismissNote:
      '"Não oferecer de novo" vale para todos nesta entidade. Você pode trazer de volta na página do fornecedor.',
    dismiss: 'Não oferecer de novo para este fornecedor',
    notNow: 'Agora não',
    saveOne: 'Salvar regra',
    saveTwo: 'Salvar 2 regras',
    saving: 'Salvando…',
    dismissing: 'Dispensando…',
    saved: {
      titleOne: 'Regra salva',
      titleTwo: '2 regras salvas',
      bodySend: 'Salva: enviar para aprovação.',
      bodyApprove: 'Salva: aprovar no seu nome.',
      bodyTwo: 'Salvas: enviar para aprovação e aprovar no seu nome.',
      link: 'Ver na aba Conhecimento da {{vendor}}',
      close: 'Fechar',
      next: 'Próxima',
    },
    savedLineOne: 'Regra salva. Veja na aba Conhecimento da {{vendor}}.',
    savedLineTwo: '2 regras salvas. Veja na aba Conhecimento da {{vendor}}.',
    dismissedLine:
      'Oferta de regra dispensada na entidade {{entity}}. Reative na página do fornecedor.',
    ruleName: {
      send: '{{vendor}} — enviar para aprovação',
      approve: '{{vendor}} — aprovar',
    },
    ruleDescription: 'Oferecida na conta {{billRef}}',
    ruleDescriptionNoRef: 'Oferecida em uma conta aprovada',
    saveError: 'Não foi possível salvar a regra. Tente de novo.',
    dismissError: 'Não foi possível dispensar a oferta. Tente de novo.',
  },
  lineItemsEditor: {
    title: 'Itens da conta',
    description: 'Registre os itens reais do documento antes de classificá-los.',
    add: 'Adicionar item',
    empty: 'Nenhum item registrado.',
    unnamed: 'Item sem descrição',
    edit: 'Editar item',
    delete: 'Excluir item',
    createTitle: 'Adicionar item da conta',
    editTitle: 'Editar item da conta',
    dialogDescription:
      'Quantidade, valor e NCM ajudam a identificar itens que devem seguir para a revisão de ativos.',
    cancel: 'Cancelar',
    close: 'Fechar',
    save: 'Salvar item',
    saving: 'Salvando…',
    saved: 'Item salvo.',
    saveError: 'Não foi possível salvar o item.',
    sourceReviewImmutable:
      'A decisão fiscal confirmada do ativo congelou os itens, os dados fiscais e os valores de PIS/Cofins desta conta. Campos apenas informativos continuam editáveis. Este fluxo ainda não permite corrigir esses fatos congelados; mantenha a conta sem aprovação e encaminhe-a para revisão contábil e fiscal.',
    deleted: 'Item excluído.',
    deleteError: 'Não foi possível excluir o item.',
    deleteTitle: 'Excluir item?',
    deleteDescription: 'O item “{{description}}” será removido desta conta.',
    deleteConfirm: 'Excluir item',
    fields: {
      description: 'Descrição',
      type: 'Tipo',
      unit: 'Unidade',
      quantity: 'Quantidade',
      unitPrice: 'Valor unitário',
      amount: 'Valor total',
      ncm: 'NCM',
      cfop: 'CFOP',
      cst: 'CST',
      notes: 'Observações',
    },
    types: {
      SERVICE: 'Serviço',
      INTEREST: 'Juros',
      FINE: 'Multa',
      DISCOUNT: 'Desconto',
      OTHER: 'Outro',
    },
  },
  taxLiabilityAssistance: {
    analysisExpired: 'A análise não terminou após a segunda tentativa. Tente novamente.',
    analysisInconclusive:
      'A análise terminou sem uma decisão que libere a aprovação. Analise novamente ou peça a quem gerencia o tratamento de guias para classificá-la manualmente.',
    analysisFailed: 'A análise da guia falhou. Analise novamente antes de continuar.',
    thirdPartyPayerConfirmation:
      'Guia de terceiro ({{payer}}): será lançada como despesa desta conta, sem baixar imposto provisionado. Confira se o contribuinte não é uma entidade da empresa.',
    thirdPartyPayerUnnamedConfirmation:
      'Guia de terceiro: será lançada como despesa desta conta, sem baixar imposto provisionado. Confira se o contribuinte não é uma entidade da empresa.',
    derivedClassifyConfirmation:
      'O tratamento desta guia foi definido automaticamente: despesa desta conta, sem baixar imposto provisionado. Confirme antes de aprovar.',
    issues: {
      permissionRequired: 'Uma pessoa autorizada precisa aprovar esta etapa.',
      consentRequired: 'Confirme os avisos antes de continuar.',
      notCompleted: 'A ação não foi concluída. Confira a etapa de aprovação.',
      planChanged: 'A guia mudou. Confira as decisões atualizadas.',
      explicitLineAllocationRequired:
        'Revise a atribuição dos pagamentos anteriores antes de liquidar parcialmente esta guia.',
      generic:
        'Não foi possível concluir a ação. Tente novamente; se o problema continuar, fale com o suporte.',
      validationGeneric: 'Confira os dados obrigatórios da conta antes de continuar.',
      genericWarning: 'Há um aviso sobre esta conta. Confira os dados antes de continuar.',
    },
    analysisStale:
      'A conta mudou após a análise. Atualize a análise para conferir as sugestões atuais. Sua classificação será preservada.',
    analysisStalePermission:
      'A conta mudou após a análise. Peça a alguém com permissão para enviar contas para aprovação que atualize a análise.',
    configurationSaved: 'Contas salvas. Aprovação não concluída.',
    actionRefreshFailed: 'A ação foi concluída. Atualize a conta para ver o resultado.',
    wholeBillClassification: 'Esta escolha se aplica aos {{count}} componentes da guia inteira.',
    acknowledgeWarnings: 'Li os avisos e desejo continuar.',
    selectTemporaryAccounting: 'Usar conta temporária com revisão obrigatória',
    accountProposalTitle: 'Confirmar contas sugeridas',
    approvingBill: 'Guia {{number}}',
    accountDecisionTitle: 'Antes de aprovar, defina onde lançar {{slot}}',
    accountSteps: 'Contas para confirmar',
    accountStepPosition: 'Conta {{current}} de {{total}}',
    previousAccount: 'Conta anterior',
    nextAccount: 'Próxima conta',
    liabilityAccountLabel: 'Conta contábil do passivo',
    expenseAccountLabel: 'Conta contábil da despesa',
    accountAppliesTo: 'Vale para todo o plano {{version}}',
    dueDate: 'vence {{date}}',
    aiSuggestedBadge: 'Sugerida pela IA',
    userSelectedBadge: 'Escolhida por você',
    newAccountBadge: 'Nova conta',
    createSpecificAccount: 'Criar conta específica',
    createSpecificAccountHint:
      'Separe este tributo dos demais. O código e o escopo serão mostrados antes da confirmação.',
    chooseOtherAccount: 'Escolher outra conta',
    chooseOtherAccountPlaceholder: 'Selecione uma conta de passivo',
    chooseOtherAccountRequired: 'Selecione uma conta para continuar.',
    confirmAccount: 'Confirmar conta',
    proposalAccount: 'Conta',
    proposalPurpose: 'Finalidade',
    taxSuggestionTitle: 'Sugestão para o tratamento da guia',
    taxSuggestionDescription:
      'A escolha atual continua valendo até você aceitar a sugestão. Confira a evidência do documento.',
    classifySuggestion: 'Tratar como despesa desta conta a pagar',
    slotSuggestion: 'Alterar o tributo para {{slot}}',
    currentTaxChoice: 'Escolha atual: {{slot}}',
    acceptTaxSuggestion: 'Aceitar sugestão',
    keepCurrentTaxChoice: 'Manter escolha atual',
    applyingTaxSuggestion: 'Registrando sua decisão e preparando a conta…',
    suggestionError:
      'Não foi possível registrar esta decisão. Atualize a análise e tente novamente.',
    confirmingAccounts: 'Aplicando a alteração confirmada e validando a conta…',
    accountProposalRefreshed:
      'A configuração contábil mudou. Confira a proposta atualizada e confirme novamente.',
    railAccountConflict:
      '{{slot}} já está configurado na conta {{account}}. Escolha a conta {{legado}}, que já faz parte desta estrutura.',
    railAccountConflictRefresh:
      '{{slot}} já está configurado na conta {{account}}. Atualize a análise para continuar com essa conta.',
    continueWithReview: 'Continuar com revisão contábil',
    continuingWithReview: 'Preparando a revisão contábil e retomando o fluxo…',
    resuming: 'Retomando o fluxo solicitado…',
    retryRequestedAction: 'Tentar novamente',
    fallbackPending: 'Revisão contábil pendente',
    fallbackAccountingPending:
      'A conta temporária será usada na aprovação. A revisão contábil permanece pendente até a reclassificação.',
    resolvedLabel: 'Pendências tributárias resolvidas',
    followProgress: 'Acompanhar análise',
    viewResult: 'Ver resultado',
    viewAccountSuggestion: 'Ver sugestão',
    accountChoiceAtApproval:
      'Um tributo ainda não tem mapeamento ativo, o seu alquimista já analisou e criou uma sugestão.',
    accountChoicesAtApproval:
      'Alguns tributos ainda não têm mapeamento ativo, o seu alquimista já analisou e criou sugestões.',
    taxSuggestionsPending: 'Confira a sugestão de tratamento do tributo antes de continuar.',
    pendingIdentity: 'Falta identificar o tributo de {{count}} itens da guia.',
    pendingCodes: 'Códigos pendentes: {{codes}}.',
    activeDebtOrigin:
      'Esses códigos de dívida ativa podem representar contribuições da folha ou de contribuintes individuais. Confira a sugestão e confirme o tributo. Encontrar o lançamento contábil de origem não é necessário para aprovar a guia.',
    identityHelp:
      'Confira o código de receita completo e a descrição no documento para identificar a obrigação correta.',
    checkPending: 'Conferir tributos pendentes',

    mappingSummary: 'Mapeamentos resolvidos',
    mappingReason: 'Motivo do mapeamento de {{code}}',
    mappingAccountPending: 'Conta contábil ainda pendente',
    title: 'Preparar conta para aprovação',
    question: 'Vamos conferir as contas e o tratamento desta guia antes de continuar.',
    manual: 'Configurar manualmente',
    ai: 'Pedir para IA resolver',
    reanalyze: 'Analisar novamente',
    auditLabel: 'Com revisão contábil posterior',
    auditHint:
      'A IA aplica decisões apoiadas em evidências e registra os dados enviados, as respostas e as alterações. O que não puder resolver fica pendente.',
    starting: 'Enviando para análise…',
    queued: 'Análise na fila',
    running: 'A IA está preparando a conta…',
    ready: 'Pendências tributárias resolvidas. A conta pode seguir para aprovação.',
    needs_review: 'A análise terminou. Confira o que ainda falta nesta guia.',
    failed:
      'Não foi possível concluir a preparação. Tente novamente ou continue com revisão contábil.',
    background: 'Você pode fechar esta janela. O processamento continua em segundo plano.',
    actionWaiting: 'Aguarde enquanto a conta é preparada para concluir a ação solicitada.',
    reviewPending: 'Uma revisão contábil foi aberta para conferir as decisões da IA.',
    deferProposal:
      'Esta guia pode seguir para aprovação, mantendo as pendências históricas em revisão contábil.',
    accept: 'Aceitar e seguir para aprovação',
    acceptAndReview: 'Aceitar e revisar depois',
    accepting: 'Registrando sua decisão e retomando a aprovação…',
    approved: 'Conta aprovada. A revisão contábil permanece registrada.',
    approvalPending: 'Decisão registrada. A conta segue no fluxo de aprovação.',
    prepared: 'Decisão registrada. A conta está preparada para seguir no fluxo de aprovação.',
    acceptError: 'Não foi possível retomar a aprovação. Tente novamente.',

    viewAudit: 'Ver histórico da IA',
    markForAccountingReview: 'Marcar para revisão contábil',
    confirmAndApprove: 'Confirmar e aprovar',
    auditTrail: {
      navigation: 'Navegação do histórico da IA',
      accountChoice: 'Escolha da conta',
      back: 'Voltar para a escolha da conta',
      backToTimeline: 'Voltar para o histórico',
      timeline: 'Histórico da decisão da conta',
      fullTrailPage: 'Trilha completa',
      candidates: 'Candidatas',
      evidence: 'Evidência',
      fullTrail: 'Abrir trilha completa',
      record: 'Registro permanente desta conta a pagar',
      loading: 'Carregando histórico da IA…',
      agent: 'Alquimista de contas a pagar',
      requested: 'Análise solicitada',
      started: 'O alquimista iniciou a análise',
      readGuide: 'Leu os dados da guia',
      checkedAccounts: 'Conferiu o plano contábil',
      suggestedAccount: 'Sugeriu {{account}}',
      suggestedAccounts: 'Sugeriu contas para confirmação',
      reason: 'Motivo',
      accountingReview: 'Sinalizou divergência histórica para revisão contábil',
      awaitingChoice: 'Aguardando sua escolha',
      finished: 'Análise concluída',
    },
    error: 'Não foi possível carregar ou iniciar a análise. Tente novamente.',
    close: 'Fechar',
  },
  statutoryLiabilityReview: taxLiabilityReviewPtBr,
  costMatches: costMatchesPtBr,
  preferredBank: {
    title: 'Banco preferido',
    placeholder: 'Selecione o banco de origem',
    none: 'Nenhum banco preferido',
    unavailable: 'Indisponível para novos pagamentos',
    unavailableOption: '{{name}} · indisponível',
    updated: 'Banco preferido atualizado.',
    cardDisabled: 'Faturas de cartão usam a conta configurada no cartão.',
    statusDisabled: 'O banco preferido não pode ser alterado em contas pagas ou anuladas.',
    source: { user: 'Usuário', rule: 'Regra {{name}}', system: 'Sistema' },
  },
  workflowRules: {
    entity: {
      label: 'Entidade',
      placeholder: 'Selecione a entidade',
      tenantWide: 'Todas as entidades',
      optionalHint: 'Opcional: sem entidade, a regra vale para todo o tenant.',
      derivedHint: 'Obrigatória, derivada de: {{fields}}.',
      changeTitle: 'Alterar entidade da regra?',
      changeConfirm:
        'Alterar a entidade removerá as seleções vinculadas à entidade atual. Deseja continuar?',
      changeAction: 'Alterar entidade',
      requiredError: 'Selecione uma entidade para esta regra.',
      sources: {
        cost_center: 'Centro de custo',
        department: 'Departamento',
        gl_account: 'Conta contábil',
        managerial_account: 'Conta gerencial',
        set_gl_account: 'Conta contábil',
        set_cost_center: 'Centro de custo',
        set_custom_segment: 'Segmento customizado',
        set_department: 'Departamento',
        set_managerial_account: 'Conta gerencial',
        set_origin_bank_account: 'Banco de origem',
        segment: 'Segmento customizado',
      },
    },
    list: {
      createRule: 'Nova Regra',
      allEntities: 'Todas as entidades',
      tenantWide: 'Todas',
      filterByEntity: 'Filtrar por entidade',
      entityColumn: 'Entidade',
    },
    // Describe a rule in words (ALC-4564): a dialog that reads a sentence into a draft rule, then
    // opens the rule builder on that draft. Nothing is saved until the person saves the rule.
    describe: {
      openButton: 'Criar a partir de uma descrição',
      title: 'Criar uma regra a partir de uma descrição',
      description: 'Escreva o que a regra deve fazer. Você confere no editor antes de salvar.',
      textLabel: 'Descreva a regra',
      textPlaceholder: 'Ex.: Mandar as contas da ACME até R$ 5.000,00 direto para aprovação',
      textHelp:
        'Só regras que enviam a conta para aprovação podem ser criadas assim. Até {{max}} caracteres.',
      entityLabel: 'Entidade',
      entityPlaceholder: 'Selecione a entidade',
      entityRequired: 'Selecione uma entidade.',
      textRequired: 'Escreva o que a regra deve fazer.',
      cancel: 'Cancelar',
      preview: 'Pré-visualizar regra',
      retry: 'Tentar de novo',
      loading: 'Lendo a sua descrição…',
      partsLabel: 'Palavras que não consigo usar:',
      candidatesTitle: '"{{text}}" corresponde a {{count}} opções. Escolha uma e tente de novo.',
      candidatesGroup: 'Opções para "{{text}}"',
      pickRequired: 'Escolha uma opção para tentar de novo.',
      codeLine: 'Código: {{code}}',
      genericError: 'Não foi possível ler a regra agora. Tente de novo em um minuto.',
      back: 'Descrever de novo',
      builderTitle: 'Revisar a regra',
      builderDescription:
        'Confira as condições antes de salvar. Nada é salvo até você salvar a regra.',
    },
    // The read-only line that says where a rule came from, and the review notes of a proposal.
    origin: {
      description: 'Criada a partir da sua descrição: «{{text}}»',
      descriptionSaved: 'Criada a partir de uma descrição: «{{text}}»',
      approvalOffer: 'Criada a partir de uma oferta de aprovação na conta.',
      openBill: 'Abrir a conta',
      createdBy: 'por {{name}}',
      createdAt: 'em {{date}}',
      kindDescription: 'Descrição',
      kindApprovalOffer: 'Oferta de aprovação',
    },
    review: {
      sourcesTitle: 'Como lemos a sua descrição',
      sourcesHint: 'Confira se cada trecho virou a condição que você queria.',
      replayNone:
        'Como foi descrita, esta regra não teria pegado nenhuma das últimas {{total}} contas desta entidade.',
      replayEmpty: 'Esta entidade ainda não tem contas para testar a regra.',
      replayMatched:
        'Como foi descrita, esta regra teria pegado {{matched}} das últimas {{total}} contas desta entidade: {{numbers}}{{more}}.',
      replayMore: ' e mais {{count}}',
      replayNoNumber: 'sem número',
      replayEdited:
        'Você mudou as condições. A contagem de contas vale só para a regra como foi descrita.',
      // The amount a rule compares is the net amount to pay (total minus withholdings).
      netAmountField: 'Valor líquido a pagar',
      netAmountNote:
        'O valor da regra é o valor líquido a pagar da conta (o total menos os impostos retidos), não o valor total.',
    },
    actions: {
      delete: 'Excluir ação',
      deleteGroup: 'Excluir grupo de ações',
      types: {
        set_gl_account: 'Definir conta contábil (GL)',
        set_department: 'Definir departamento',
        set_cost_center: 'Definir centro de custo',
        set_managerial_account: 'Definir conta gerencial',
        set_custom_segment: 'Definir segmento customizado',
        set_project: 'Definir projeto',
        require_approval: 'Requer aprovação',
        require_project: 'Requer projeto',
        require_split: 'Requer rateio',
        block_transaction: 'Bloquear transação',
      },
    },
    condition: {
      fieldLabel: 'Campo da condição',
      textValuePlaceholder: 'Digite {{field}}',
      noTextValues: 'Nenhum valor adicionado',
      textValuesPlaceholder: 'Digite e pressione Enter para adicionar',
      operatorLabel: 'Operador da condição',
      joinLabel: 'Ligação com a condição anterior',
      delete: 'Excluir condição',
      save: 'Salvar',
      cancel: 'Cancelar',
      fieldSearchPlaceholder: 'Buscar campo...',
      fieldSearchEmpty: 'Nenhum campo encontrado.',
      fieldGroups: {
        properties: 'Propriedades',
        classifications: 'Classificações',
        custom: 'Campos customizados',
        facts: 'Estado atual da conta',
      },
    },
    // ANY/ALL choice of a line-item condition (ALC-4629): how the lines of the bill combine.
    lineQuantifier: {
      label: 'Deve valer em',
      options: {
        ALL: 'Todas as linhas',
        ANY: 'Pelo menos uma linha',
      },
      hint: {
        ALL: 'Toda linha da conta precisa atender a condição. Uma conta sem linhas não atende.',
        ANY: 'Pelo menos uma linha precisa atender. Condições de item ligadas por E ou OU com esta mesma opção são avaliadas juntas, na mesma linha. Uma conta sem linhas não atende.',
      },
      summary: {
        ALL: 'em todas as linhas',
        ANY: 'em pelo menos uma linha',
      },
    },
    // Labels of the bill fields of the conditions editor, keyed by the camelCase field name
    // (`workflowRules.fields.<field>`, see fieldI18nKey). `segment` labels a custom segment field
    // whose segment is not loaded.
    fields: {
      entity: 'Entidade',
      vendor: 'Fornecedor',
      description: 'Descrição',
      amount: 'Valor',
      cfop: 'CFOP',
      billIssueDate: 'Data de emissão',
      billType: 'Tipo da conta',
      paymentMethod: 'Forma de pagamento',
      costCenter: 'Centro de custo',
      project: 'Projeto',
      department: 'Departamento',
      category: 'Categoria',
      glAccount: 'Conta contábil',
      managerialAccount: 'Conta gerencial',
      lineItemId: 'Item da conta',
      lineDescription: 'Descrição do item',
      lineAmount: 'Valor do item',
      cfopCode: 'CFOP do item',
      purchaseType: 'Tipo de compra',
      needByDate: 'Data necessária',
      segment: 'Segmento',
    },
    // Operator wording of the conditions editor, keyed by the operator code of the shared workflow
    // contract (BillWorkflowConditionOperator): `workflowRules.operators.<operator>`.
    operators: {
      equals: 'é igual a',
      not_equals: 'não é igual a',
      contains: 'contém',
      not_contains: 'não contém',
      starts_with: 'começa com',
      ends_with: 'termina com',
      in: 'está em',
      not_in: 'não está em',
      greater_than: 'maior que',
      less_than: 'menor que',
      greater_or_equal: 'maior ou igual a',
      less_or_equal: 'menor ou igual a',
      between: 'entre',
      is_null: 'está vazio',
      is_not_null: 'não está vazio',
      has_any_of: 'tem algum de',
      has_none_of: 'não tem nenhum de',
      same_profile_as: 'tem o mesmo perfil que',
      all_within: 'estão todos dentro de',
      same_targets: 'Rateio entre estes',
      exact_split: 'Exatamente estes %',
      any_within: 'Pelo menos um destes',
    },
    // Labels of the bill_updated fact fields (BILL_UPDATED_FACT_FIELD_DEFINITIONS), keyed by the
    // camelCase field name: `workflowRules.facts.<field>`, one per registered fact (ALC-4625). A
    // fact ticket adds its label here and in `en`, and its enum value labels under
    // `workflowRules.enum.<field>.<VALUE>`; `findFactTranslationGaps` (facts/fact-fields.test.ts)
    // fails the web test while either is missing in pt-BR or en.
    facts: {
      reconciliationState: 'Situação da reconciliação',
      openAlertTypes: 'Alertas em aberto',
      barcodePresence: 'Pagamentos com código de barras',
      barcodeValid: 'Código de barras válido',
      barcodeAmountMatches: 'Valor do código de barras igual ao do pagamento',
      isClientReimbursable: 'Reembolsável pelo cliente',
      fiscalServiceType: 'Tipo de serviço fiscal',
      withholdingProfile: 'Perfil de retenções',
      creditProfile: 'Perfil de crédito de PIS/COFINS',
      allocationsWithinTargets: 'Destinos da alocação',
      serviceVendorRule: 'Regra do fornecedor para o serviço',
    },
    boolean: {
      placeholder: 'Selecione',
      true: 'Sim',
      false: 'Não',
    },
    enum: {
      valueLabel: 'Valor da condição',
      placeholder: 'Selecione um valor',
      billType: {
        EXPENSE: 'Despesa',
        PAYROLL: 'Folha de pagamento',
        TAXES: 'Impostos',
        BANK_FEE: 'Tarifa bancária',
        VENDOR_ADVANCEMENT: 'Adiantamento a fornecedor',
        CREDIT_CARD_INVOICE: 'Fatura de cartão',
      },
      paymentMethod: {
        BOLETO: 'Boleto',
        PIX: 'PIX',
        TED: 'Transferência (TED)',
        DOC: 'DOC',
        CREDIT_CARD: 'Cartão de crédito',
        DEBIT_CARD: 'Cartão de débito',
        GUIA: 'Guia',
        CHECK: 'Cheque',
        OTHER: 'Outro',
        ADVANCE: 'Adiantamento',
        ACH: 'Transferência ACH (EUA)',
      },
      reconciliationState: {
        NONE: 'Não reconciliada',
        PARTIAL: 'Parcialmente reconciliada',
        FULL: 'Totalmente reconciliada',
      },
      barcodePresence: {
        ALL: 'Todos',
        SOME: 'Alguns',
        NONE: 'Nenhum',
      },
      // D41: "sem tipo de serviço" is not "sem regra"; the three answers are kept apart.
      serviceVendorRule: {
        has_rule: 'Tem regra',
        no_rule: 'Não tem regra',
        no_service_type: 'Conta sem tipo de serviço',
      },
      // Open alert types read their label from the top-level `alertTypes.<TYPE>` copy (the bill
      // detail alert rows), see enumOptionI18nKeys. Only the types `alertTypes` cannot carry live
      // here: the bill detail shows the stored alert message for them, and an `alertTypes` key would
      // replace it.
      openAlertTypes: {
        EXTERNAL_FORM_DOCUMENT_MISMATCH: 'Documento diferente do formulário externo',
        INTAKE_DOCUMENTS_MISSING: 'Documentos pendentes na entrada',
        INTAKE_METADATA_MISMATCH: 'Dados da entrada diferentes do documento',
        ONFLY_CLASSIFICATION_INCOMPLETE: 'Classificação automática incompleta',
        ONFLY_DEFAULT_COST_CENTER_USED: 'Centro de custo padrão aplicado automaticamente',
        ONFLY_AUTO_APPROVAL_BLOCKED: 'Aprovação automática bloqueada',
        ONFLY_POSTING_FAILED: 'Falha na contabilização automática',
      },
    },
    // The remove button of a chip in a multi-select value (the chip's name is the label)
    removeValueAria: 'Remover {{name}}',
    serviceType: {
      valueLabel: 'Tipo de serviço',
      placeholder: 'Selecione o tipo de serviço',
      addPlaceholder: 'Adicionar tipo de serviço',
      // F-07: without settings:read the catalog is not read; the saved ids still show
      noCatalogAccess: 'Você não tem acesso ao catálogo de tipos de serviço.',
    },
    // ALC-4563: editors of the facts whose value is a profile or a set of targets.
    profile: {
      withholding: {
        groupLabel: 'Perfil de retenções',
        intro:
          'Marque os impostos que a conta retém e informe a alíquota de cada um. A condição compara o que foi decidido, nunca o valor retido.',
        applies: 'Retém {{tax}}',
        rateLabel: 'Alíquota de {{tax}} (%)',
        ratePlaceholder: 'Ex.: 1,50',
        rateInvalid: 'Use uma alíquota maior que 0 e até 100, com até 4 casas decimais.',
        rateRequired: 'Informe a alíquota: a condição compara a alíquota decidida de cada imposto.',
        noneSelected: 'Nenhum imposto marcado: a condição vale para contas sem retenção.',
        none: 'Nenhum imposto retido',
        rateMissing: 'sem alíquota',
      },
      credit: {
        groupLabel: 'Perfil de crédito de PIS/COFINS',
        intro:
          'Informe o que a conta decidiu sobre o crédito de PIS/COFINS. A condição compara a decisão, nunca valores.',
        generatesPis: 'Gera crédito de PIS',
        generatesCofins: 'Gera crédito de COFINS',
        notStated: 'Não informado',
        creditType: 'Natureza do crédito',
        revenueUse: 'Destinação do crédito',
        onlyWhenGenerates: 'Natureza e destinação só contam quando a conta gera crédito.',
        type: {
          BASIC: 'Compra comum',
          FIXED_ASSET: 'Ativo imobilizado',
          PRESUMED: 'Crédito presumido',
          IMPORT: 'Importação',
        },
        use: {
          DIRECT_CUMULATIVE: 'Uso direto em receita cumulativa',
          DIRECT_NON_CUMULATIVE: 'Uso direto em receita não cumulativa',
          COMMON: 'Uso comum entre regimes',
        },
        summary: {
          both: 'Gera crédito de PIS e de COFINS',
          none: 'Não gera crédito de PIS nem de COFINS',
          pisYes: 'Gera crédito de PIS',
          pisNo: 'Não gera crédito de PIS',
          cofinsYes: 'Gera crédito de COFINS',
          cofinsNo: 'Não gera crédito de COFINS',
        },
      },
    },
    allocationTargets: {
      groupLabel: 'Destinos da alocação',
      intro:
        'Escolha os destinos permitidos em cada dimensão. A condição vale quando a conta tem destino na dimensão e todas as linhas e partes da alocação estão na lista; conta sem classificação, ou classificada só em parte, não vale. Dimensão sem seleção não é testada; percentuais e valores não são comparados.',
      entityRequired:
        'Selecione a entidade da regra para listar contas contábeis, centros de custo, departamentos e contas gerenciais.',
      none: 'A conta não pode ter destino nesta dimensão',
      introSameTargets:
        'Escolha os destinos de cada dimensão. A condição vale quando a conta usa exatamente estes destinos, em qualquer percentual: nenhum a mais, nenhum a menos. Conta sem classificação, ou classificada só em parte, não vale. Dimensão sem seleção não é testada.',
      introExactSplit:
        'Escolha os destinos e o percentual de cada um; os percentuais de cada dimensão somam 100,00%. A condição vale quando o rateio da conta tem exatamente estes destinos com estes percentuais. Rateio por item, rateio personalizado ou conta com mais de uma linha de rateio não vale. Dimensão sem seleção não é testada.',
      introAnyWithin:
        'Escolha os destinos de cada dimensão. A condição vale quando a conta usa pelo menos um destes destinos; outros destinos também são aceitos. Conta sem destino na dimensão não vale. Dimensão sem seleção não é testada.',
      percentLabel: 'Percentual de {{target}}',
      percentPlaceholder: '0,00',
      percentRequired: 'Informe o percentual',
      percentInvalid: 'Use um percentual maior que 0 e até 100, com até 2 casas decimais',
      splitSum: 'Soma: {{sum}}',
      splitSumNot100: 'Os percentuais desta dimensão precisam somar 100,00%',
      dimensions: {
        glAccounts: 'Contas contábeis',
        costCenters: 'Centros de custo',
        departments: 'Departamentos',
        managerialAccounts: 'Contas gerenciais',
      },
      segmentsSummary: 'Segmentos',
      summaryNone: 'nenhum',
    },
    bankAction: {
      itemLabel: 'banco de origem',
      label: 'Definir banco de origem',
      placeholder: 'Selecione uma conta bancária ativa',
      entityRequired: 'Selecione a entidade da regra antes do banco.',
      empty: 'Nenhuma conta bancária ativa para esta entidade.',
      save: 'Salvar',
      cancel: 'Cancelar edição',
    },
    submitAction: {
      label: 'Enviar para aprovação',
    },
    waitingBillCheck: {
      title: 'Você quer aplicar essa regra às contas que já existem em rascunho ou revisão?',
      hint: 'A verificação roda uma vez. Contas em aprovação não são alteradas.',
      yes: 'Aplicar às contas existentes',
      no: 'Salvar sem aplicar',
      cancel: 'Cancelar',
    },
  },
  paymentMethods: {
    boleto: 'Boleto',
    pix: 'PIX',
    ted: 'Transferência (TED)',
    doc: 'DOC',
    creditCard: 'Cartão de Crédito',
    debitCard: 'Cartão de Débito',
    guia: 'Guia',
    check: 'Cheque',
    advance: 'Adiantamento',
    manual: 'Pagamento manual',
    ach: 'Transferência ACH (EUA)',
    other: 'Outro',
  },
  paymentsPage: {
    entityFilter: 'Filtrar por entidade',
    allEntities: 'Todas as entidades',
    queueTotal: 'Total a pagar',
    summaryMeta: '{{payments}} · {{entities}}',
    entityCount_one: '{{count}} entidade',
    entityCount_other: '{{count}} entidades',
    insufficientBalance: 'Saldo insuficiente em {{account}} — faltam {{amount}}',
    overdue: 'Atrasados',
    today: 'Hoje',
    nextSevenDays: 'Próximos 7 dias',
    later: 'Depois',
    none: 'nenhum',
    amountToPay: 'A pagar',
    available: 'Disponível',
    balanceUnavailable: 'Saldo indisponível',
    balanceUpdatedUnderHour: 'Saldo atualizado há menos de 1 hora',
    balanceUpdatedHours_one: 'Saldo atualizado há {{count}} hora',
    balanceUpdatedHours_other: 'Saldo atualizado há {{count}} horas',
    balanceStale_one: 'Saldo há mais de {{count}} dia sem atualização',
    balanceStale_other: 'Saldo há mais de {{count}} dias sem atualização',
    refreshBalance: 'Atualizar saldo',
    refreshBalanceFor: 'Atualizar saldo de {{account}}',
    balanceRefreshSuccess: 'Saldo atualizado',
    balanceRefreshError: 'Não foi possível atualizar o saldo',
    noOriginAccount: 'Sem conta de origem',
    queueEmpty: 'Nenhum pagamento pendente',
    noVisibleCheckingAccounts: 'Nenhuma conta corrente visível com pagamentos pendentes',
    paymentCount_one: '{{count}} pagamento',
    paymentCount_other: '{{count}} pagamentos',
    loadError: 'Não foi possível carregar o resumo',
    sectionLabel: 'Resumo dos pagamentos',
    expandSummary: 'Mostrar saldos',
  },
  paymentSection: {
    transfer: 'Transferência',
    registerDestination: 'Cadastrar destino',
    amountDefinedByCode: 'Definido pelo código',
    amountDiffersFromCode:
      'O Pix Copia e Cola deste pagamento é de {{amount}}, diferente do valor do pagamento. Ajuste o valor ou cole outro código.',
    useCodeAmount: 'Usar o valor do código',
    transferDestinationRequired: 'Escolha para onde enviar a transferência.',
    transferDestination: {
      label: 'Destino',
      registeredKey: 'Chave Pix',
      registeredBank: 'Dados bancários',
      railLabel: 'Como enviar',
      railPixBankData: 'PIX por dados bancários (padrão)',
      railTed: 'TED',
      pixBankDataUnavailable: 'A conta de origem não envia PIX por dados bancários. Use TED.',
      tedUnavailable:
        'A conta de origem não envia TED. Use PIX por dados bancários ou troque a conta de origem.',
      savedAccountMissing: 'A conta do favorecido não existe mais; escolha outro destino.',
      noRailAvailable: 'A conta de origem não envia transferências para dados bancários.',
      noRegisteredTitle: 'Nenhum destino cadastrado',
      noRegisteredDescription:
        'Cadastre uma chave Pix ou dados bancários do favorecido. Você também pode pagar uma chave avulsa ou um Pix Copia e Cola.',
      adHocKey: 'Chave Pix avulsa',
      adHocKeyDescription: 'Paga uma chave que não está cadastrada no favorecido.',
      adHocBadge: 'Destino avulso',
      adHocKeyType: 'Tipo da chave',
      adHocKeyTypePlaceholder: 'Tipo',
      adHocKeyInput: 'Chave Pix',
      adHocKeyPlaceholder: 'CPF, CNPJ, e-mail, celular ou chave aleatória',
      adHocKeyAmbiguous: 'Este valor pode ser mais de um tipo de chave. Qual é?',
      adHocKeyNormalized: 'Será enviada como',
      adHocKeyInvalid: 'Chave inválida para o tipo {{type}}.',
      saveToVendor: 'Salvar no fornecedor',
      copiaECola: 'Pix Copia e Cola',
      copiaEColaDescription: 'Cole o código completo do QR Code.',
      copiaEColaPlaceholder: 'Cole o código Pix Copia e Cola',
      copiaEColaInvalid:
        'Pix Copia e Cola inválido ou incompleto. Copie o código completo novamente.',
      previewReceiver: 'Recebedor',
      previewCity: 'Cidade',
      previewAmount: 'Valor',
      previewAmountOpen: 'Livre',
      previewTxid: 'Identificador (txid)',
      dynamicNote: 'QR Code dinâmico: o valor e o identificador ficam no PSP do recebedor.',
      dynamicUnsupported:
        'A conta de origem não paga QR Code dinâmico. Troque a conta de origem ou peça um QR Code estático ao fornecedor.',
      keyTypes: {
        CPF: 'CPF',
        CNPJ: 'CNPJ',
        EMAIL: 'E-mail',
        PHONE: 'Celular',
        RANDOM: 'Chave aleatória',
      },
    },
    transferErrors: {
      PIX_COPIA_E_COLA_INVALID:
        'Pix Copia e Cola inválido ou incompleto. Copie o código completo novamente.',
      PIX_COPIA_E_COLA_AMOUNT_MISMATCH:
        'O valor do pagamento é diferente do valor definido no Pix Copia e Cola.',
      PIX_KEY_INVALID: 'A chave Pix não é válida para o tipo informado.',
      PIX_KEY_TYPE_REQUIRED: 'Informe o tipo da chave Pix.',
      TED_REGISTERED_ACCOUNT_REQUIRED: 'TED exige uma conta bancária cadastrada do favorecido.',
      PIX_BANK_DATA_ACCOUNT_REQUIRED:
        'PIX por dados bancários exige uma conta bancária cadastrada do favorecido.',
      PIX_BANK_DATA_WITH_KEY: 'PIX por dados bancários não leva chave Pix nem Copia e Cola.',
      PIX_BANK_DATA_ACCOUNT_INCOMPLETE:
        'A conta do favorecido não tem banco, agência e conta completos para PIX por dados bancários.',
      PAYMENT_VENDOR_BANK_ACCOUNT_INVALID:
        'A conta bancária informada não é uma conta ativa do fornecedor desta conta a pagar. Escolha uma conta do fornecedor.',
    },
    transferWarnings: {
      PIX_DYNAMIC_QR_ORIGIN_UNSUPPORTED:
        'Pagamento salvo, mas a conta de origem não paga QR Code dinâmico. Troque a conta de origem antes de enviar.',
      PIX_BANK_DATA_ORIGIN_UNSUPPORTED:
        'Pagamento salvo, mas a conta de origem não envia PIX por dados bancários. Troque a conta de origem ou use TED antes de enviar.',
      TED_ORIGIN_UNSUPPORTED:
        'Pagamento salvo, mas a conta de origem não envia TED. Troque a conta de origem ou use PIX antes de enviar.',
    },
    pixInstructionLabel: 'Chave Pix ou Copia e Cola',
    pixInstructionValid: 'Dados Pix válidos',
    pixInstructionPlaceholder: 'Cole uma chave Pix ou o código Copia e Cola completo',
    pixInstructionInvalid:
      'Pix Copia e Cola inválido ou incompleto. Copie o código completo novamente.',
    title: 'Pagamento',
    reimbursement: 'Reembolso',
    transferMethod: 'Método de transferência',
    usAccountInfo: 'Routing ••••{{routing}} · Conta ••••{{account}} · EUA',
    usAccountTitle: 'Conta nos EUA',
    routing: 'Routing',
    achBadge: 'ACH',
    usAccountsOnly: 'Só aparecem contas dos EUA, que recebem por ACH.',
    methodAch: 'Transferência ACH (EUA)',
    achDestination: 'Conta do favorecido nos EUA',
    achAddAccount: 'Adicionar conta nos EUA',
    achAccountRequired: 'Escolha a conta do favorecido nos EUA para enviar por ACH.',
    noUsAccounts: 'Este fornecedor ainda não tem conta nos EUA. Adicione uma para pagar por ACH.',
    pixKey: 'Chave PIX',
    bank: 'Banco',
    agency: 'Agência',
    account: 'Conta',
    favorecido: 'Favorecido',
    totalValue: 'Valor total',
    dueDate: 'Vencimento',
    documentDueDateLabel: 'Data de vencimento',
    scheduledFor: 'Programado para',
    automaticBankPayment: 'Débito automático',
    editDueDate: 'Editar data de vencimento',
    editScheduledDate: 'Editar data programada',
    originAccount: 'Conta de origem',
    default: 'Padrão',
    confirmPayment: 'Confirmar pagamento',
    parcelOrSplit: 'Parcelar ou dividir pagamento',
    payViaPixOrTed: 'Pagar via PIX ou TED',
    supplierAccount: 'Conta do fornecedor',
    noBankAccount: 'Preencha os dados para pagamento',
    noBankAccountDescription:
      'Cadastre a conta do fornecedor para transferência (PIX ou TED) ou avance para boleto, guia, cartão e outras opções no fluxo abaixo.',
    useBoleto: 'Usar boleto',
    addBankAccount: 'Adicionar conta bancária',
    createBankAccount: 'Criar conta bancária',
    createBoleto: 'Criar boleto',
    createNewAccount: 'Criar nova conta',
    addBankAccountModalTitle: 'Adicionar dados bancários',
    addBankAccountModalSubtitle: 'Registre como pagar este fornecedor',
    addBankAccountSuccess: 'Conta bancária criada com sucesso',
    addBankAccountError: 'Não foi possível criar a conta bancária',
    paymentMethod: 'Método de pagamento',
    bankSearchPlaceholder: 'Buscar por nome ou código...',
    accountType: 'Tipo de conta',
    accountNickname: 'Apelido da conta',
    accountNicknamePlaceholder: 'Ex: PIX Principal, Conta Pagamentos',
    accountNicknameHint: 'Opcional — ajuda a diferenciar quando o fornecedor tem múltiplos métodos',
    pixKeyTypeAutoDetected: 'Tipo detectado automaticamente pelo formato da chave',
    beneficiaryBank: 'Banco recebedor',
    beneficiaryBankHint:
      'Em qual banco esta chave está registrada. Agência e conta não são necessárias.',
    beneficiaryBankRequiredBadge: '{{bank}} exige',
    beneficiaryBankRequiredTooltip:
      'Sua conta de origem {{origin}} precisa saber em qual banco a chave está registrada para ' +
      'concluir o PIX. Sem isso o pagamento é recusado no envio. Agência e conta não são ' +
      'necessárias para PIX por chave.',
    alsoHasPixKey: 'Esta conta também tem chave PIX?',
    alsoHasPixKeyHint: 'Informe para pagar a mesma conta por PIX, sem cadastrar um segundo método.',
    optionalPixKeyPlaceholder: 'Opcional',
    defaultMethod: 'Método padrão',
    defaultMethodHint: 'Usado automaticamente ao pagar este fornecedor',
    pixKeyDuplicate: 'Já existe um método PIX com esta chave.',
    tedDuplicate: 'Já existe uma conta com este banco, agência e conta.',
    prefilledFromVendor: 'Pré-populado do cadastro do fornecedor',
    saving: 'Salvando...',
    saveBankAccount: 'Salvar',
    methodOther: 'Outro',
    methodUndefined: 'Método não definido',
    selectPaymentMethod: 'Selecionar método de pagamento',
    loadingInformation: 'Carregando informações de pagamentos...',
    updatingInformation: 'Atualizando informações de pagamentos...',
    statusScheduled: 'Agendado',
    statusPending: 'Pendente',
    statusCompleted: 'Concluído',
    statusReconciled: 'Conciliado',
    reconciliationLoading: 'Concluindo conciliação…',
    createdPaymentLoading: 'Carregando pagamento…',
    edit: 'Editar',
    delete: 'Excluir',
    deletePayment: 'Excluir pagamento',
    typePrincipal: 'Principal',
    dueDateShort: 'Venc.',
    paidOn: 'Pago em: {{date}}',
    debitCardLabel: 'Débito',
    creditCardLabel: 'Crédito',
    adjustmentPrincipal: 'Principal',
    scheduledAmountContext: 'Valor originalmente agendado',
    adjustmentInterest: 'Juros',
    adjustmentPenalty: 'Multa',
    adjustmentDiscount: 'Desconto',
    adjustmentsTitle: 'Ajustes da conciliação',
    adjustmentAddedBy: 'por {{name}}',
    adjustmentDescriptionFallback: 'Sem descrição',
    adjustmentAdvanceExplanation:
      'A transação total foi de {{total}}, e {{advance}} foi lançado como um adiantamento a fornecedor',
    adjustmentUnknownUser: 'Usuário não identificado',
    viewTransactionDetails: 'Ver detalhes da transação',
    refreshBankStatus: 'Consultar status no banco',
    bankStatusUpdated: 'Status no banco atualizado: {{status}}',
    bankStatusUnchanged: 'Sem mudança no banco: {{status}}',
    bankStatusError: 'Não foi possível consultar o status no gateway de pagamento',
    gatewayPaymentStatus: {
      CREATED: 'Criado (aguardando retorno)',
      SCHEDULED: 'Agendado',
      PAID: 'Pago no banco',
      CANCELLED: 'Cancelado',
      REJECTED: 'Rejeitado',
      REFUNDED: 'Estornado',
    },
    downloadReceipt: 'Baixar comprovante',
    receiptUnavailable:
      'A TecnoSpeed ainda está gerando o comprovante. Tente de novo em instantes.',
    receiptNotPaid: 'A TecnoSpeed só emite comprovante de pagamento confirmado pelo banco.',
    receiptError: 'Não foi possível baixar o comprovante.',
    autoExtracted: 'Auto-extraído',
    digitalLine: 'Linha digitável',
    digitalLineEmpty: 'Não preenchida',
    fillDigitalLine: 'Preencher',
    editDigitalLine: 'Editar linha digitável',
    boletoValidationValid: 'Boleto válido',
    boletoValidationInvalid: 'Boleto inválido',
    boletoUnavailable: 'Dados do boleto ainda não disponíveis.',
    boletoLinhaDigitavelUnavailable:
      'Linha digitável do boleto não disponível. Use o PIX abaixo ou pague pelo débito automático.',
    pixCopiaECola: 'PIX copia e cola',
    clickToCopy: 'Clique para copiar',
    copiedToClipboard: 'Copiado para a área de transferência',
    readonlyNotice: 'Conta aprovada — pagamento não pode ser alterado',
    pageTitle: 'Pagamentos — Redesign',
    pageDescription: 'Seção de pagamentos dentro do Bill Detail. Selecione um cenário:',
    selectScenario: 'Selecionar cenário',
    scenarios: {
      smartDefault: 'Padrão inteligente',
      multiBank: 'Múltiplas contas',
      confirmed: 'Confirmado',
      noBank: 'Sem conta bancária',
      readonly: 'Somente leitura',
      boleto: 'Boleto (NFS-e)',
    },
    annotations: {
      smartDefault:
        '80% case: Fornecedor tem 1 conta bancária com chave PIX. Sistema monta o pagamento inline — sem dialog, sem clique em "Adicionar". Só precisa confirmar.',
      multiBank:
        'Múltiplas contas: Fornecedor tem 2+ contas bancárias cadastradas. Usuário seleciona qual usar. A primária vem pré-selecionada.',
      confirmed:
        'Pagamento confirmado. Card compacto com detalhes expansíveis. Botão de editar para ajustes antes da aprovação.',
      noBank:
        'Sem dados bancários: Fornecedor não tem conta cadastrada. Oferece caminho para adicionar ou usar outro método (boleto).',
      readonly:
        'Somente leitura: Conta aprovada ou em estágio posterior. Pagamento visível mas não editável (exceto master approver).',
      boleto:
        'Boleto (auto-extraído): NFS-e veio com boleto. Sistema extrai linha digitável automaticamente e monta o pagamento. Mesmo padrão inline.',
    },
    subtitleScheduled: '{{amount}} agendado',
    subtitlePending: '{{amount}} pendente',
    subtitlePartial: '{{paid}} de {{total}}',
    emptyState: 'Nenhum pagamento registrado',
    emptyStateDescription:
      'Use Criar pagamento e escolha o meio: transferência (PIX ou TED), boleto, guia, cartão ou outro disponível.',
    createPayment: 'Criar pagamento',
    editingPayment: 'Editando pagamento',
    creatingPayment: 'Novo pagamento',
    amount: 'Valor',
    amountRequiredLabel: 'Valor *',
    dueDateRequiredLabel: 'Vencimento *',
    paymentStatusRequiredLabel: 'Status do pagamento *',
    originAccountRequiredLabel: 'Conta de origem *',
    requiredFieldHint: 'Campos com * são obrigatórios.',
    transferAccountOptionalWithPixCode:
      'Opcional ao usar PIX copia e cola — selecione uma conta se quiser vincular ao pagamento.',
    pixCopiaEColaPlaceholder: 'Cole o código PIX aqui',
    transferMethodMismatchHint:
      'Nenhuma conta do fornecedor compatível com o método selecionado. Cadastre uma conta adequada ou alterne entre PIX e TED.',
    transferPayeeOrPixCodeRequired:
      'Informe o PIX copia e cola ou selecione uma conta do favorecido para salvar.',
    transferPayeeRequired: 'Selecione uma conta do favorecido para salvar o pagamento TED.',
    method: 'Método',
    notes: 'Observações',
    notesPlaceholder: 'Notas internas sobre este pagamento',
    save: 'Salvar',
    cancel: 'Cancelar',
    deleteConfirmTitle: 'Excluir pagamento',
    deleteConfirmMessage: 'Tem certeza que deseja excluir este pagamento?',
    paymentCreated: 'Pagamento criado com sucesso',
    paymentUpdated: 'Pagamento atualizado com sucesso',
    paymentUpdateError: 'Não foi possível atualizar o pagamento.',
    paymentRefreshError:
      'Pagamento criado, mas não foi possível atualizar a lista. Recarregue a página.',
    paymentDeleted: 'Pagamento excluído com sucesso',
    newPaymentSeparator: 'Novo pagamento',
    bulkPaymentsCreated: '{{count}} pagamentos criados com sucesso',
    confirmAmount: 'Confirmar {{amount}}',
    confirmInstallments: 'Confirmar {{count}} parcelas',
    remainingAfterPartial: 'Você poderá agendar o restante ({{amount}}) depois',
    backToSinglePayment: 'Voltar para pagamento único',
    generatedInstallments: 'PARCELAS GERADAS',
    installmentsSummary: 'Pagamentos conferem',
    firstDueDate: 'Primeiro vencimento',
    paymentValueLabel: 'Valor deste pagamento',
    installmentsHeaderSummary: '{{amount}} em {{count}} parcelas',
    installmentsBadge: '{{count}} parcelas',
    intervalAuto: 'Intervalo automático',
    customDates: 'Datas personalizadas',
    totalLabel: 'Total',
    methodBoleto: 'Boleto',
    methodGuia: 'Guia',
    slipOrTaxPaymentType: 'Tipo de pagamento (boleto ou guia)',
    methodCard: 'Cartão',
    otherMethods: 'Outros métodos',
    methodDoc: 'DOC',
    methodCheck: 'Cheque',
    creditCard: 'Cartão de crédito',
    debitCard: 'Cartão de débito',
    selectCard: 'Selecione o cartão',
    noCreditCards: 'Nenhum cartão cadastrado. Cadastre um cartão em Configurações.',
    guiaVsBoletoHint:
      'Guia: tributos e convênios (DARF, GPS, arrecadação). Boleto: cobrança bancária. Ambos aceitam linha digitável ou código de barras.',
    noBankNextStepHint:
      'Sem conta do fornecedor: escolha a conta de origem acima e use Continuar para boleto ou guia, ou cadastre a conta do fornecedor.',
    continueToSlipOrTax: 'Continuar para boleto ou guia',
    invalidBarcodeBoletoGuia: 'Código de barras inválido',
    boletoTypeBankSlip: 'Boleto bancário',
    boletoTypeUtilityCollection: 'Arrecadação - concessionária',
    boletoTypeTaxCollection: 'Arrecadação - tributo',
    boletoTypeCollection: 'Arrecadação',
    barcode: 'Código de barras',
    barcodeOrDigitalLine: 'Código de barras / linha digitável',
    barcodeOrDigitalLinePlaceholder: 'Cole o código de barras ou a linha digitável',
    barcodeHint: 'Aceita 44, 47 ou 48 dígitos. Você pode colar com máscara.',
    nossoNumero: 'Nosso número',
    quickSuggestionTitle: 'Sugestão da Alchemy',
    quickSuggestionPrimary: 'Método principal configurado',
    quickSuggestionHistory: 'Baseado no histórico • Usado {{count}}x',
    quickSuggestionCta: 'Criar pagamento rápido - {{method}}',
    quickSuggestionSubmitting: 'Criando...',
    quickSuggestionSubmit: 'Criar Pagamento',
    quickPaymentDueDateDialogTitle: 'Informe a data de vencimento',
    quickPaymentDueDateLabel: 'Data de vencimento *',
    quickPaymentValueLabel: 'Valor',
    quickPaymentMethodLabel: 'Método',
    quickPaymentPixKeyLabel: 'Chave PIX',
    quickPaymentBankLabel: 'Banco',
    quickPaymentAccountLabel: 'Conta',
    quickPaymentCancel: 'Cancelar',
    quickPaymentCreated: 'Pagamento criado com sucesso',
    quickPaymentError: 'Erro ao criar pagamento',
    quickPaymentDueDateRequired: 'Informe a data de vencimento',
    sourceToggleBankAccount: 'Conta bancária',
    sourceToggleCreditCard: 'Cartão de crédito',
    paymentStatus: 'Status do pagamento',
    statusToPay: 'A pagar',
    statusPaid: 'Pago',
    paidStatusWarning:
      'Ao selecionar "Pago", esta conta não será enviada para Pagamentos após a aprovação. Ainda será necessário conciliar o pagamento com a transação bancária ou do cartão de crédito.',
    ccInstallments: 'Parcelas',
    ccInstallmentsSuffix: 'x',
    payWithCreditCard: 'Pagar com cartão de crédito',
    selectCreditCard: 'Selecione o cartão',
    noCreditCardsRegistered: 'Nenhum cartão cadastrado para esta entidade',
    paymentDate: 'Data do pagamento',
    saveCreditCardPayment: 'Salvar pagamento',
  },
  billStatuses: {
    draft: 'Rascunho',
    submitted: 'Em revisão',
    pendingApproval: 'Pendente aprovação',
    processingApproval: 'Processando aprovação',
    approved: 'Aprovado',
    rejected: 'Rejeitado',
    partiallyPaid: 'Parcialmente pago',
    paid: 'Pago',
    completed: 'Concluído',
    void: 'Cancelado',
    migrated: 'Migrado',
  },
  paymentValidation: {
    pixKeyRequired: 'Chave PIX é obrigatória',
    pixKeyInvalid: 'Chave PIX inválida. Informe CPF, CNPJ, email, telefone ou chave aleatória.',
    barcodeRequired: 'Código de barras é obrigatório',
    barcodeLength: 'Código de barras deve ter 44, 47 ou 48 dígitos.',
    barcodeChecksum: 'Código de barras inválido: dígito verificador incorreto.',
    digitalLineChecksum: 'Linha digitável inválida: verificação de dígitos falhou.',
    bankAccountRequired: 'Conta bancária é obrigatória para TED/DOC',
    creditCardRequired: 'Cartão de crédito é obrigatório',
    fieldRequired: 'Campo obrigatório',
    barcodeInvalid: 'Código de barras inválido',
  },
  common: {
    select: 'Selecionar',
    define: 'Definir',
    gotIt: 'Entendi',
    cancel: 'Cancelar',
    back: 'Voltar',
    loading: 'Carregando...',
  },
  actions: {
    discard: 'Voltar',
    saveDraft: 'Salvar rascunho',
    save: 'Salvar',
    cancel: 'Cancelar',
    sendForApproval: 'Enviar para aprovação',
    void: 'Invalidar conta',
    voidShort: 'Invalidar',
    moreActions: 'Mais ações',
    returnToReview: 'Voltar para revisão',
    returnToReviewConfirmTitle: 'Voltar a conta para revisão?',
    returnToReviewConfirmDescription:
      'O fluxo de aprovação atual será cancelado. A conta voltará para revisão e poderá ser ajustada antes de um novo envio.',
    returnToReviewConfirm: 'Voltar para revisão',
    returnToReviewSuccess: 'Aprovação cancelada. A conta voltou para revisão.',
    deferral: 'Diferimento/Despesa antecipada',
    approve: 'Aprovar',
    saveAndApprove: 'Salvar e aprovar',
  },
  sendForApproval: {
    success: 'Enviado para aprovação',
    budgetHardBlockRejected: 'Orçamento excedido',
    budgetHardBlockRejectedDescription:
      'Esta conta foi rejeitada automaticamente porque excede um limite orçamentário de bloqueio rígido. Ajuste a conta ou a linha de orçamento e envie novamente.',
  },
  listPage: {
    overview: {
      sectionLabel: 'Resumo de contas a pagar',
      sampleNotice:
        'Dados de exemplo — visualização do novo resumo; valores ainda não conectados às contas.',
      sampleActionNotice:
        'O resumo usa dados de exemplo. A lista abaixo mostra contas reais e pode não corresponder ao valor exibido.',
      sampleMonth: 'outubro',
      overdue: 'Vencidas',
      nextSevenDays: 'Próximos 7 dias',
      remainingMonth: 'Restante de {{month}}',
      stillOpenMonth: 'Ainda em aberto em {{month}}',
      historicalOverlap: 'Pode incluir contas também em Vencidas.',
      reconciled: 'Pagamento conciliado',
      timelineLabel: 'Valores por dia do mês',
      timelineDayFilter: 'Dia do gráfico',
      indicatorFilter: 'Indicador',
      timelineEmpty: 'Nenhuma conta neste período',
      timelineEmptyDescription: 'Os valores por dia aparecerão quando houver contas neste mês.',
      timelineConversionUnavailable: 'Valores indisponíveis para este período',
      timelineConversionUnavailableDescription:
        'Há contas neste mês, mas falta uma taxa de câmbio para exibir os valores.',
      expand: 'Expandir resumo',
      previousMonth: 'Mês anterior',
      nextMonth: 'Próximo mês',
      chartCurrency: 'Moeda do gráfico',
      reportingCurrency: 'Moeda de apresentação',
      entityFilter: 'Filtrar resumo por entidade',
      allEntities: 'Todas as entidades',
      incompleteConversion:
        'Não foi possível converter algumas contas. Os valores exibidos estão incompletos.',
      fxUnavailable: 'Indisponível',
      fxRateFallbackLabel: 'PTAX',
      openAmount: 'Em aberto',
      reconciledAmount: 'Conciliado',
      dayPaymentCount_one: '{{count}} pagamento',
      dayPaymentCount_zero: '{{count}} pagamentos',
      dayPaymentCount_other: '{{count}} pagamentos',
      dayBillCount_one: '{{count}} conta',
      dayBillCount_other: '{{count}} contas',
      dayAriaLabel: '{{date}}: {{count}}, {{amount}}',
      today: 'hoje',
      automaticCapture: 'Captura automática',
      newVendors: 'Novos fornecedores este mês',
      missingDetails: 'Contas com dados pendentes agora',
      missingPaymentDetails: 'Dados de pagamento pendentes agora',
      drilldownVendor: 'Fornecedor',
      drilldownDescription: 'Descrição',
      drilldownContributingAmount: 'Valor neste indicador',
      drilldownSource: 'Origem',
      drilldownAutomatic: 'Automática',
      drilldownManual: 'Manual',
      drilldownTotal: 'Total do indicador',
      drilldownCaptureCount: '{{automatic}} de {{eligible}} contas capturadas automaticamente',
      drilldownBillCount_one: '{{count}} conta',
      drilldownBillCount_other: '{{count}} contas',
      drilldownEmpty: 'Nenhuma conta neste indicador.',
      loading: 'Carregando resumo de contas',
      empty: 'Nenhum dado disponível para este período.',
      noResults: 'Nenhuma conta corresponde aos filtros.',
      error: 'Não foi possível carregar o resumo de contas.',
      retry: 'Tentar novamente',
      returnToCurrentMonth: 'Voltar ao mês atual',
    },
    stats: {
      total: 'Total',
      paid: 'Pago',
      pending: 'Pendente',
      overdue: 'Em atraso',
      bill: 'conta',
      bills: 'contas',
    },
    periodHeader: {
      prevAria: 'Meses anteriores',
      nextAria: 'Próximos meses',
      complete: 'Quitado',
      loadError: 'Não foi possível carregar as estatísticas por período.',
      retry: 'Tentar novamente',
      periodPlaceholder: 'Período',
      allPeriods: 'Todos os períodos',
    },
    summaryStrip: {
      total: 'Total',
      paid: 'Pago',
      pending: 'Pendente',
      overdue: 'Em atraso',
      withCount: '{{amount}} ({{count}})',
    },
    bankFileDialog: {
      title: 'Exportar arquivo bancário',
      description:
        'O preflight valida os pagamentos sem alterar o status ou confirmar o envio ao banco.',
      loading: 'Validando seleção…',
      selected: 'Selecionados',
      eligible: 'Elegíveis',
      cnabEligible: 'Elegíveis para CNAB',
      blocked: 'Bloqueados',
      blockingReasons: 'Pagamentos que ficam de fora',
      blockedAreLeftOut_one:
        'Fica de fora {{count}} pagamento: ele não entra neste arquivo. Resolva o motivo abaixo e gere outro arquivo depois, se quiser.',
      blockedAreLeftOut_other:
        'Ficam de fora {{count}} pagamentos: eles não entram neste arquivo. Resolva os motivos abaixo e gere outro arquivo depois, se quiser.',
      generateEligible_one: 'Gerar XLSX com 1 pagamento',
      generateEligible_other: 'Gerar XLSX com {{count}} pagamentos',
      ineligible: 'Pagamento não elegível.',
      cancel: 'Cancelar',
      generate: 'Gerar XLSX',
      processing: 'Gerando arquivo bancário…',
      successTitle: 'Arquivo bancário gerado',
      successDescription:
        'O arquivo foi salvo para auditoria. O envio ao banco ainda precisa ser confirmado.',
      errorTitle: 'Não foi possível gerar o arquivo bancário',
      errorFallback: 'Tente novamente ou feche esta janela.',
      preflightErrorTitle: 'Não foi possível validar a seleção',
      preflightErrorDescription: 'Tente novamente para carregar o preflight.',
      retry: 'Tentar novamente',
      close: 'Fechar',
    },
    vendorBills: {
      requesterLabel: 'Solicitante',
      emptyState: 'Nenhuma conta encontrada para este fornecedor',
      summary: {
        totalBills: 'Total de Contas',
        totalPaid: 'Total Pago',
        openBills: 'Em Aberto',
      },
      statusFilter: {
        placeholder: 'Todos os status',
        all: 'Todos os status',
        DRAFT: 'Rascunho',
        SUBMITTED: 'Em revisão',
        PENDING_APPROVAL: 'Pendente de Aprovação',
        APPROVED: 'Aprovado',
        REJECTED: 'Rejeitado',
        PARTIALLY_PAID: 'Parcialmente Pago',
        PAID: 'Pago',
        COMPLETED: 'Concluído',
        VOID: 'Cancelado',
      },
      columns: {
        number: 'Número',
        description: 'Descrição',
        issueDate: 'Emissão',
        dueDate: 'Vencimento',
        amount: 'Valor',
        status: 'Status',
        actions: 'Ações',
      },
      actions: {
        viewDetails: 'Ver detalhes',
        openInNewTab: 'Ver em nova aba',
        replicateClassifications: 'Replicar classificações',
      },
      installmentLabel: 'Parcela {{paymentNumber}}',
      replication: {
        trigger: 'Replicar classificações',
        title: 'Replicar classificações',
        description:
          'Copie as classificações aplicadas desta conta para outras contas do mesmo fornecedor que ainda estão em pré-aprovação.',
        loading: 'Carregando contas elegíveis...',
        empty: 'Nenhuma conta elegível encontrada para receber as classificações.',
        targetLabel: 'Selecione as contas de destino',
        sourceBill: 'Conta de origem',
        selectionCount: '{{count}} selecionadas',
        confirm: 'Replicar classificações',
        submitting: 'Replicando...',
        cancel: 'Cancelar',
        success: 'Classificações replicadas em {{count}} conta(s).',
        partialSuccess:
          'Classificações replicadas em {{successCount}} conta(s). {{failedCount}} falharam.',
        genericError: 'Não foi possível replicar as classificações.',
        candidateMeta: '{{status}} · emissão {{issueDate}} · vencimento {{dueDate}}',
      },
    },
    horizon: {
      overdue: 'Atrasadas',
      dueToday: 'Vence hoje',
      next7Days: 'Próx. 7 dias',
      thisMonth: 'Este mês',
      nextMonth: 'Próximo mês',
      sublabel: {
        overdue: 'Há até 45 dias',
        until: 'Até',
      },
    },
    recurringAlerts: {
      overdueTitle: 'Contas recorrentes atrasadas',
      overdueBody: 'Na competência {{period}}, estas obrigações estão atrasadas ({{count}}):',
      overdueDueSuffix: ' — venc. {{date}}',
      moreCount: '+{{count}} outras…',
      variationTitle: 'Variação de valor detectada',
      variationLead: 'Desvios acima do limite de 15% nesta competência ({{count}}):',
      variationLine: 'esperado {{expected}} vs. real {{actual}} ({{pct}}%).',
      contractsTitle: 'Contratos / vigência terminando',
      contractsBody: '{{count}} template(s) com término de vigência nos próximos {{days}} dias.',
      ctaDetails: 'Ver detalhes',
      ctaInvestigate: 'Investigar',
      ctaReview: 'Revisar',
    },
    simple: {
      moreFiltersAria: 'Mais filtros',
      moreFiltersTitle: 'Mais filtros',
      columnsAria: 'Colunas da tabela',
      columnsTitle: 'Colunas',
      retryCustomColumns: 'Falha ao carregar campos personalizados. Tentar novamente',
      columnsGroupReorderHint: 'Arraste pela alça para ordenar as colunas dentro de cada grupo.',
      customColumnsEmpty: 'Nenhum campo personalizado ativo disponível para adicionar como coluna.',
      columnsDefaultsHint:
        'Todas as colunas podem ser exibidas ou ocultas. A tabela inicia com um conjunto padrão.',
      columnsReorderHint: 'Arraste pela alça para alterar a ordem das colunas na tabela.',
      columnDragHandleAria: 'Arrastar para reordenar coluna',
      resetColumnOrder: 'Restaurar ordem padrão',
      includeNfeAiLabel: 'Incluir criadas por IA',
      includeNfeAiFilterExcluded: 'Ocultas',
      columnDataType: {
        date: 'Data',
        text: 'Texto',
        status: 'Status',
        currency: 'Valor monetário',
        classification: 'Classificação',
        boolean: 'Sim/Não',
        payment: 'Pagamento',
        actions: 'Ações',
      },
      applyDescription: 'Aplicar',
      descriptionPlaceholder: 'Trecho da descrição…',
      rowCompetencyDate: 'Competência',
      rowPaymentMethod: 'Método de pagamento',
      rowRecurring: 'Recorrente',
      rowDescription: 'Descrição',
      rowCostCenter: 'Centro de custo',
      rowDepartment: 'Departamento',
      rowCategoria: 'Categoria',
      customSegments: 'Campos customizados',
      customSegmentsEmpty: 'Nenhum campo customizado para esta entidade.',
      customSegmentsEmptyAll: 'Nenhum campo personalizado disponível para filtrar.',
      customSegmentTextPlaceholder: 'Valor do campo…',
      customSegmentsLoading: 'Carregando...',
      customSegmentsLoadError: 'Não foi possível carregar os campos personalizados.',
      customSegmentsRetry: 'Tentar novamente',
      customSegmentsPermissionRequired:
        'Você não tem permissão para consultar campos personalizados.',
      rowSource: 'Fonte',
      rowTaxRetention: 'Retenção',
      yes: 'Sim',
      no: 'Não',
      noEntityForSelectors: 'Nenhuma entidade disponível para o seletor.',
      categoriaSegmentMissing: 'Segmento Categoria não configurado para esta entidade.',
      categoriaNoPredefinedValues: 'Categoria não tem valores pré-definidos para filtrar.',
      searchPlaceholder: 'Buscar ou adicionar filtro…',
      mainTabsMenuAria: 'Escolher visão da lista',
      workflowTabsAria: 'Etapas do fluxo',
      mainTabsMore: 'Mais {{count}}…',
      mainTabsOverflowSearchPlaceholder: 'Buscar aba…',
      mainTabsOverflowNoResults: 'Nenhuma aba encontrada',
      savedViewsOverflowSearchPlaceholder: 'Buscar visão…',
      savedViewsOverflowNoResults: 'Nenhuma visão encontrada',
      savedViewsOverflowMenuAria: 'Outras visualizações salvas',
      savedViewsOverflowTriggerFallback: 'Visualizações salvas',
      unifiedViewsOverflowMenuAria: 'Abas e visualizações salvas',
      unifiedStripWorkflowHeading: 'Abas da lista',
      unifiedStripSavedHeading: 'Visualizações salvas',
      unifiedViewsOverflowNoResults: 'Nada encontrado',
      statusFilterLabel: 'Status',
      statusFilterPickerTitle: 'Filtrar por status',
      removeStatusAria: 'Remover {{status}}',
      tabContext: {
        overview: 'Todas as contas no período',
        drafts: 'Rascunho ou enviadas para revisão',
        'for-approval': 'Aguardando aprovação',
        approved: 'Aprovadas, parcialmente pagas, pagas e concluídas',
        'for-payment': 'Prontas para pagamento',
        'automatic-debit': 'Débito automático pendente de conciliação',
        'accepted-waiting': 'Aguardando retorno do banco',
      },
      savedViews: {
        saveViewCircleLabel: 'Salvar visualização',
        defaultViewShortLabel: 'Padrão',
        defaultViewSaved: 'Esta visualização será aberta ao entrar na lista (sem aba na URL).',
        defaultViewCleared: 'Visualização padrão removida das preferências.',
        visibilityToggleTooltip:
          'Privada: só você vê e usa esta visualização. Compartilhada: qualquer pessoa da organização pode vê-la e usá-la.',
        visibilityToggleAria: 'Visibilidade da visualização salva',
        saveAction: 'Salvar visão…',
        saveDisabledHint: 'Altere filtros ou colunas para habilitar',
        saveSuccess: 'Visão salva',
        saveError: 'Não foi possível salvar a visão',
        loadSuccess: 'Visão aplicada',
        loadError: 'Não foi possível carregar a visão',
        loadInvalid: 'Estado da visão inválido',
        sharedBadge: 'compartilhada',
        dialogTitle: 'Salvar visão atual',
        nameLabel: 'Nome',
        namePlaceholder: 'Ex.: Minha lista — aprovação',
        visibilityLabel: 'Visibilidade',
        privateOption: 'Privada (só você)',
        sharedOption: 'Compartilhada (toda a organização)',
        privateHint: 'Somente você verá esta visão na lista.',
        sharedHint: 'Qualquer pessoa do tenant pode ver e usar esta visão.',
        cancel: 'Cancelar',
        confirmSave: 'Salvar',
        privateShort: 'Privada',
        sharedShort: 'Compartilhada',
        privateScopeTooltip: 'Somente você vê e usa esta visualização.',
        sharedScopeTooltip: 'Toda a organização pode ver e usar esta visualização.',
        deleteViewTooltip: 'Excluir esta visualização',
        defaultViewSetTooltip: 'Definir como visualização padrão ao abrir a lista',
        defaultViewClearTooltip: 'Remover como visualização padrão',
        deleteView: 'Excluir visualização',
        deleteConfirmTitle: 'Excluir esta visualização?',
        deleteConfirmDescription: 'Esta ação não pode ser desfeita.',
        deleteConfirmAction: 'Excluir',
        deleteSuccess: 'Visualização excluída',
        deleteError: 'Não foi possível excluir a visualização',
        scopeUpdated: 'Visibilidade atualizada',
        scopeUpdateError: 'Não foi possível atualizar a visibilidade',
      },
    },
    retorno: {
      button: 'Retorno',
      import: 'Importar Retorno',
    },
    retornoUpload: {
      title: 'Importar Retorno CNAB',
      dropHere: 'Solte os arquivos aqui',
      dragOrClick: 'Arraste arquivos de retorno ou clique para selecionar',
      hint: 'Arquivos CNAB 240 retorno (.RET, .TXT) — até {{max}} arquivos',
      selectFiles: 'Selecionar Arquivos',
      filesSelected: '{{count}} arquivo(s) selecionado(s)',
      clear: 'Limpar',
      uploading: 'Enviando...',
      send: 'Enviar {{count}} arquivo(s)',
      invalidFiles: 'Selecione arquivos .RET ou .TXT',
      maxFiles: 'Máximo de {{max}} arquivos por envio',
      sentSingle: 'Retorno "{{name}}" enviado para processamento.',
      sentMultiple: '{{count}} arquivos de retorno enviados para processamento.',
      error: 'Erro ao enviar arquivo(s) de retorno.',
    },
    retornoProcessing: {
      title: 'Processamento de retorno',
      pending: '{{count}} processando',
      completed: '{{count}} concluído(s)',
      failed: '{{count}} falha(s)',
      empty: 'Nenhum retorno em processamento.',
      totalMatched: 'Total: {{count}} encontrado(s)',
      status: {
        waiting: 'Na fila',
        active: 'Processando',
        completed: 'Concluído',
        failed: 'Falhou',
        unknown: 'Desconhecido',
      },
      summary: {
        records: '{{count}} registro(s)',
        matched: '{{count}} encontrado(s)',
        changed: '{{count}} atualizado(s)',
        unmatched: '{{count}} sem correspondência',
        awaitingProvider: '{{count}} aguardando conciliação da TecnoSpeed',
        errors: '{{count}} erro(s)',
      },
    },
    cnabHistory: {
      title: 'Histórico de arquivos de pagamento',
      button: 'Histórico de arquivos',
      empty: 'Nenhum arquivo de pagamento encontrado.',
      downloadError: 'Erro ao baixar arquivo de pagamento.',
      unverified: 'layout não homologado',
      fetchError: 'Erro ao carregar histórico de arquivos.',
      markedSent: 'Arquivo marcado como enviado.',
      markSentError: 'Erro ao marcar arquivo como enviado.',
      markSent: 'Enviado',
      download: 'Baixar',
      colFile: 'Arquivo',
      colType: 'Tipo',
      colSeq: 'Seq.',
      colPayments: 'Pgtos',
      colLotes: 'Lotes',
      lotesCount: '{{count}} lote(s)',
      colDate: 'Data',
      colImportedAt: 'Importado em',
      colStatus: 'Status',
      colActions: 'Ações',
      showingOf: 'Exibindo {{shown}} de {{total}}',
      fileType: {
        REMESSA: 'Remessa',
        RETORNO: 'Retorno',
      },
      status: {
        GENERATED: 'Gerado',
        SENT: 'Enviado',
        PROCESSED: 'Processado',
        REJECTED: 'Rejeitado',
        CANCELLED: 'Cancelado',
      },
      retornoRejectedNoMatch: 'Nenhuma linha do arquivo casou com um pagamento',
      payments: {
        back: 'Voltar aos arquivos',
        description: 'Resposta do banco sobre o pagamento e o status atual.',
        untitled: 'Retorno {{sequence}}',
        empty: 'Nenhum pagamento vinculado a este arquivo.',
        colPayee: 'Beneficiário',
        colAmount: 'Valor',
        colInstruction: 'Resposta do banco',
        colCurrentStatus: 'Status atual',
        instructionMissing: 'O banco não informou a ocorrência',
        bankReply: {
          AWAITING_RETURN: 'Aguardando retorno do banco',
          SCHEDULED: 'Agendado no banco',
          PAID: 'Pago no banco',
          REJECTED: 'Rejeitado pelo banco',
          REFUNDED: 'Devolvido pelo banco',
          CANCELLED: 'Cancelado no banco',
        },
      },
    },
    timeline: {
      title: 'Histórico do pagamento',
      empty: 'Nenhum evento encontrado para este pagamento.',
      remessaFile: 'Arquivo de remessa',
      retornoFile: 'Arquivo de retorno',
      viewTimeline: 'Histórico',
      loading: 'Carregando o histórico',
      loadError: 'Não foi possível carregar o histórico',
      loadErrorDescription: 'Tente de novo em instantes.',
      attemptTitle: 'Envio ao gateway de pagamento · tentativa {{number}}',
      remittanceTitle: 'Remessa ao gateway de pagamento',
      protocol: 'Protocolo {{id}}',
      remittanceError: 'O gateway de pagamento informou um erro nesta remessa.',
      reversedHint:
        'O banco estornou este pagamento. Se ele já estava pago, o lançamento e a situação da conta não são desfeitos sozinhos: veja Pagamentos em revisão.',
      outcome: {
        IN_FLIGHT: 'Enviando',
        ACCEPTED: 'Aceito',
        REJECTED: 'Recusado',
        TRANSPORT_ERROR: 'Sem resposta',
        ABANDONED: 'Encerrado',
      },
      remittanceStatus: {
        CREATING: 'Criando',
        PENDING_SEND: 'Aguardando envio',
        PROCESSING: 'Em processamento',
        PROCESSED: 'Processada',
        SENT: 'Enviada',
        FAILED: 'Com erro',
        CANCELLED: 'Cancelada',
        UNKNOWN: 'Situação desconhecida',
      },
      actions: {
        NEEDS_REVIEW: 'Enviado para revisão',
        NEEDS_REVIEW_RESOLVED: 'Revisão resolvida',
        PROVIDER_RESULT: 'Retorno do banco',
        PROVIDER_RESULT_RECORDED: 'Retorno do banco registrado',
        PROVIDER_DISPATCH_REJECTED: 'Envio recusado',
        PROVIDER_DISPATCH_ABANDONED: 'Envio encerrado sem resposta',
        PROVIDER_DISPATCH_ERROR: 'Erro no envio',
        PROVIDER_PAYEE_REGISTERED: 'Favorecido cadastrado no gateway de pagamento',
        PROVIDER_PAYEE_ACCOUNT_REGISTERED: 'Conta do favorecido cadastrada no gateway de pagamento',
        FINNET_PRIOR_SEND_REFUSED: 'Reenvio recusado: já existe um envio em andamento',
        PRIOR_SEND_WITHDRAWN: 'Envio anterior retirado',
        PRIOR_SEND_STILL_ACTIVE: 'Envio anterior ainda ativo',
        CANCEL_REQUESTED: 'Cancelamento solicitado',
        SEND_CANCEL_INTENT: 'Cancelamento do envio pedido',
        SEND_CANCELLED: 'Envio cancelado',
        SENT_TO_PROVIDER: 'Enviado ao gateway de pagamento',
        CREATED: 'Criado',
        UPDATE: 'Atualizado',
        UPDATED: 'Atualizado',
        STATUS_CHANGE: 'Status alterado',
        CANCELLED: 'Cancelado',
        RETRY: 'Nova tentativa',
        CNAB_REMESSA_EXPORT: 'Remessa gerada',
        BANK_FILE_REMESSA_EXPORT: 'Arquivo de pagamento gerado',
        BANK_FILE_XLSX_EXPORT: 'Planilha do banco',
        CNAB_RETORNO: 'Retorno do banco',
        CNAB_RETORNO_AUDIT: 'Retorno do banco',
        CNAB_REMESSA_FILE: 'Arquivo de remessa',
        CNAB_RETORNO_FILE: 'Arquivo de retorno',
        PROVIDER_ATTEMPT_IN_FLIGHT: 'Envio em andamento',
        PROVIDER_ATTEMPT_ACCEPTED: 'Envio aceito',
        PROVIDER_ATTEMPT_REJECTED: 'Envio rejeitado',
        PROVIDER_ATTEMPT_TRANSPORT_ERROR: 'Falha no envio',
        PROVIDER_ATTEMPT_ABANDONED: 'Envio abandonado',
        PAYMENT_REMITTANCE_CREATING: 'Arquivo em criação',
        PAYMENT_REMITTANCE_PENDING_SEND: 'Arquivo aguardando envio',
        PAYMENT_REMITTANCE_PROCESSING: 'Arquivo em processamento',
        PAYMENT_REMITTANCE_PROCESSED: 'Arquivo processado',
        PAYMENT_REMITTANCE_SENT: 'Arquivo enviado',
        PAYMENT_REMITTANCE_FAILED: 'Arquivo com falha',
        PAYMENT_REMITTANCE_CANCELLED: 'Arquivo cancelado',
        PAYMENT_REMITTANCE_UNKNOWN: 'Arquivo de pagamento',
      },
      actionUnknown: 'Evento do pagamento',
      statusUnknown: 'Status não identificado',
      remittanceUnknown: 'Arquivo de pagamento',
      statuses: {
        ACCEPTED: 'Aceito',
        REJECTED: 'Rejeitado',
        TRANSPORT_ERROR: 'Falha de comunicação',
        IN_FLIGHT: 'Em andamento',
        PENDING_SEND: 'Aguardando envio',
        SENT: 'Enviado',
        PROCESSED: 'Processado',
        GENERATED: 'Gerado',
        CREATING: 'Em criação',
        UNKNOWN: 'Não identificado',
        ABANDONED: 'Abandonado',
      },
      remittanceTypes: {
        CNAB240: 'CNAB 240',
        CNAB400: 'CNAB 400',
        BTG_XLSX: 'Planilha do banco',
        REMESSA: 'Remessa',
      },
      reasons: {
        FAILED_PAYMENT_SUPERSEDED_BY_SETTLEMENT:
          'Pagamento com falha cancelado: a conta foi quitada pelo pagamento {{paymentId}}.',
        PAYMENT_UPDATED_RECALCULATE_BILL_STATUS:
          'Status da conta recalculado após a edição de um pagamento.',
      },
    },
    table: {
      vendor: 'Fornecedor',
      reimbursementBadge: 'Reembolso',
      billNumber: 'Número da NF',
      copyBillNumber: 'Copiar número da NF',
      status: 'Status',
      billTypeColumn: 'Tipo',
      amount: 'Valor',
      contabil: 'Contábil',
      gerencial: 'Gerencial',
      gerencialExpand: 'Expandir',
      gerencialCollapse: 'Recolher',
      paymentMethod: 'Método de pagamento',
      competencyDateColumn: 'Competência',
      descriptionColumn: 'Descrição',
      sourceColumn: 'Fonte',
      recurringColumn: 'Recorrente',
      costCenterColumn: 'Centro de custo',
      departmentColumn: 'Departamento',
      entityColumn: 'Entidade',
      categoriaColumn: 'Categoria',
      taxCreditsColumn: 'Créditos PIS/COFINS',
      retainedAmountColumn: 'Retenções',
      taxRetentionColumn: 'Retenção',
      dueDate: 'Vencimento',
      scheduledDate: 'Programado para',
      postedDate: 'Lançamento',
      actions: 'Ações',
      selectAll: 'Selecionar todas',
      loading: 'Carregando...',
      empty: 'Nenhuma conta encontrada.',
      page: 'Página',
      of: 'de',
      results: 'resultados',
      previous: 'Anterior',
      next: 'Próxima',
      perPage: '/ página',
      openMenu: 'Abrir menu',
      selectBill: 'Selecionar {{vendor}}',
      cnabExportDisabledTooltip:
        'Conta sem pagamento elegível para CNAB. Defina forma de pagamento (PIX, TED, DOC) nos pagamentos agendados.',
      bulkActionSelectionDisabledTooltip:
        'Apenas contas em rascunho, enviadas ou aguardando aprovação podem ser selecionadas para ações em massa.',
      subheaderBill: 'Conta',
      subheaderPayment: 'Pagamento',
      total: 'Valor Total',
      method: 'Método',
      installmentValue: 'Valor da Parcela',
      sortAsc: 'crescente',
      sortDesc: 'decrescente',
      sortBy: 'Ordenar por {{field}}{{direction}}',
      filterVendor: 'Filtrar por fornecedor',
      sortTooltip: 'Clique para ordenar por essa coluna',
      filterVendorTooltip: 'Clique para filtrar fornecedores',
      filterEntityTooltip: 'Clique para filtrar por entidade',
      filterColumnTooltip: 'Clique para filtrar por essa coluna',
      filterDescriptionMinChars: 'Mínimo de 2 caracteres para filtrar.',
      filterRecurringYesOnly: 'Somente recorrentes',
      filterPaymentMethodNone: 'Sem método de pagamento',
      filterRetentionYesOnly: 'Somente com retenção',
      openBill: 'Abrir conta a pagar',
      openVendorInNewTab: 'Abrir fornecedor em nova aba',
      openPayrollRun: 'Abrir folha em nova aba',
      payrollRunLink: 'Ver folha',
      openBillRow: 'Abrir conta: {{vendor}}',
      reimbursementPayeePending: 'A definir beneficiário',
      agentCapBadge: 'Criada pelo agent CAP',
      agentCapTooltip: 'Criada pelo alquimista de contas a pagar',
      recentlyCreatedSubtitle: 'Criada há menos de 30 minutos',
      approvalFailureBadgeAria: 'Falha na última tentativa de aprovação',
      approvalFailureTooltipTitle: 'Falha na aprovação',
      approvalFailureTooltipHint: 'Abra a conta para tentar aprovar novamente.',
    },
    forPayment: {
      paymentNumber: 'Pagamento {{n}}',
      pastDueBanner: 'Pagamentos em atraso serão gerados com data de hoje no arquivo CNAB.',
      cnabDownloadInstructions:
        'Para atualizar o status dos pagamentos, envie o arquivo pelo Internet Banking (ou canal do banco). Após o processamento, use "Importar retorno" nesta página.',
      cnabDownloadAlertTitle: 'Próximos passos',
      statusHeader: 'Status',
      payOnHeader: 'Pagar em',
      originBankHeader: 'Conta Origem',
      bankMessageHeader: 'Observações',
      payDate: {
        tooltipNext:
          'Você escolheu {{requested}} ({{reason}}). O banco paga no próximo dia útil: {{paidOn}}.',
        tooltipPrevious:
          'Você escolheu {{requested}} ({{reason}}). Guia que vence em dia não útil é paga no dia útil anterior: {{paidOn}}.',
        tooltipLate: 'Pago após o vencimento ({{due}}); pode ter juros.',
        reasonSaturday: 'sábado',
        reasonSunday: 'domingo',
        reasonHoliday: 'feriado nacional: {{holiday}}',
        holidays: {
          NEW_YEAR: 'Confraternização Universal',
          CARNIVAL_MONDAY: 'Carnaval',
          CARNIVAL_TUESDAY: 'Carnaval',
          GOOD_FRIDAY: 'Sexta-feira Santa',
          TIRADENTES: 'Tiradentes',
          LABOUR_DAY: 'Dia do Trabalho',
          CORPUS_CHRISTI: 'Corpus Christi',
          INDEPENDENCE: 'Independência do Brasil',
          OUR_LADY_APARECIDA: 'Nossa Senhora Aparecida',
          ALL_SOULS: 'Finados',
          REPUBLIC: 'Proclamação da República',
          BLACK_CONSCIOUSNESS: 'Dia Nacional de Zumbi e da Consciência Negra',
          CHRISTMAS: 'Natal',
        },
      },
      paymentStatus: {
        SCHEDULED: 'Agendado',
        PENDING: 'Pendente',
        PROCESSING: 'Em processamento',
        PAID: 'Pago',
        RECONCILED: 'Conciliado',
        FAILED: 'Erro / rejeitado pelo banco',
        CANCELLED: 'Cancelado',
        REVERSED: 'Estornado',
      },
      onlyExportable: 'Só pendentes de envio',
      beneficiaryLabel: 'Beneficiário',
      inActiveCnabFile:
        'Pagamento já incluído em arquivo CNAB ativo; aguarde retorno ou cancele o envio para reexportar.',
    },
    acceptedWaiting: {
      workflowBanner:
        'Pagamentos aceitos pelo banco. Importe o arquivo retorno (Transmissão de Arquivo > Retorno) para atualizar os status.',
      resetToPending: 'Voltar para pendente de envio',
      resetDialogTitle: 'Voltar para pendente de envio',
      resetDialogDescription:
        'Isso desvincula os pagamentos do arquivo enviado e os coloca novamente como pendentes de envio. Use apenas se o banco rejeitou o arquivo ou você cancelou o envio com o banco. Caso contrário, o banco pode tratar um novo envio como duplicado.',
      resetDialogConfirm: 'Voltar para pendente',
      resetDialogCancel: 'Cancelar',
      resetSuccess: '{{count}} pagamento(s) voltaram para pendente de envio.',
      resetError: 'Erro ao voltar pagamentos para pendente.',
      cannotResetAlreadyScheduled:
        'Não pode ser revertido: pagamento já agendado no banco (BD/BE).',
      phases: {
        agendado: 'Agendado no banco',
        data_alterada: 'Data alterada',
        aguardando_efetivacao: 'Ag. retorno',
      },
    },
    tabs: {
      all: 'Todas',
      inReview: 'Em revisão',
      inApproval: 'Em aprovação',
      approved: 'Aprovadas',
      // Legacy keys kept for any saved view labels still referencing them; new code should use
      // `all` / `inReview` / `inApproval` / `approved`.
      overview: 'Todas',
      drafts: 'Em revisão',
      forReview: 'Em revisão',
      forApproval: 'Em aprovação',
      forPayment: 'Para pagamento',
      automaticDebit: 'Débito automático',
      history: 'Aprovadas',
      acceptedWaiting: 'Aceitos / aguardando confirmação',
    },
    filters: {
      vendorLoading: 'Carregando…',
      title: 'Filtros',
      active: 'Filtros ativos:',
      clearAll: 'Limpar todos',
      clearAllDates: 'Limpar todas',
      statusLabel: 'Status',
      vendorLabel: 'Fornecedor',
      entityLabel: 'Entidade',
      entityPlaceholder: 'Selecione a entidade',
      entityAll: 'Todas as entidades',
      vendorPlaceholder: 'Selecionar fornecedor',
      vendorsCount: 'Fornecedores ({{count}})',
      billNumberLabel: 'Número da NF',
      billNumberPlaceholder: 'Número da NF',
      originBankLabel: 'Conta de Origem',
      originBankPlaceholder: 'Filtrar por conta de origem',
      originBankAll: 'Todas as contas',
      amountLabel: 'Valor',
      totalAmountLabel: 'Valor total',
      amountPlaceholder: 'Valor',
      issueDateLabel: 'Emissão',
      issueDatePlaceholder: 'Período de emissão',
      dueDateLabel: 'Vencimento',
      dueDatePlaceholder: 'Período de vencimento',
      scheduledDateLabel: 'Programado para',
      scheduledDatePlaceholder: 'Período programado',
      postedDatePlaceholder: 'Período de lançamento',
      competencyDateLabel: 'Competência',
      competencyDatePlaceholder: 'Período de competência',
      datePlaceholder: '...',
      billTypeLabel: 'Tipo de transação',
      billTypeColumn: 'Tipo',
      billType: {
        all: 'Todos',
        payable: 'Despesa',
        payroll: 'Folha (salário)',
        tax: 'Impostos',
        fee: 'Tarifa Bancária',
      },
      billTypeValues: {
        EXPENSE: 'Despesa',
        PAYROLL: 'Folha (salário)',
        TAXES: 'Impostos',
        BANK_FEE: 'Tarifa Bancária',
        VENDOR_ADVANCEMENT: 'Adiantamento a fornecedor',
        CREDIT_CARD_INVOICE: 'Fatura de cartão',
      },
      payrollSubTypeValues: {
        SALARY: 'Folha (salário)',
        ADVANCE: 'Folha (adiantamento)',
        THIRTEENTH: 'Folha (13º)',
        VACATION: 'Folha (férias)',
        PRO_LABORE: 'Folha (pró-labore)',
        RPA: 'Folha (RPA)',
        TERMINATION_NET: 'Folha (rescisão)',
        ALIMONY: 'Folha (pensão)',
        CONSIGNED: 'Folha (consignado)',
        UNION: 'Folha (sindicato)',
        TAX: 'Folha (tributo)',
      },
      reimbursementsOnlyLabel: 'Apenas reembolsos',
      recurringOnlyLabel: 'Apenas recorrentes',
      includeSalariesLabel: 'Incluir salários',
      withholdingsOnlyLabel: 'Com retenção',
      withTaxCreditsOnlyLabel: 'Com créditos PIS/COFINS',
      withRetainedAmountOnlyLabel: 'Com valor efetivamente retido',
      descriptionContainsLabel: 'Descrição',
      classificationCostCenterLabel: 'Centro de custo',
      classificationGlAccountLabel: 'Conta contábil',
      classificationManagerialAccountLabel: 'Conta gerencial',
      classificationDepartmentLabel: 'Departamento',
      classificationCategoriaLabel: 'Categoria',
      classificationSelectedCount: '{{count}} selecionado(s)',
      paymentMethodFilterLabel: 'Método de pagamento',
      billSource: {
        MANUAL: 'Lançamento manual',
        EMAIL: 'E-mail',
        OCR: 'OCR',
        API: 'Integração API',
        EXTERNAL_FORM: 'Formulário externo',
        NFE_AUTO_IMPORT: 'Integração Focus NFe',
        BANK_FEE_AUTO: 'Tarifa (automático)',
        VENDOR_ADVANCEMENT_RECONCILIATION: 'Adiantamento (reconciliação)',
        PAYROLL_PROCESSING: 'Folha de pagamento',
        CSV_IMPORT: 'Importação CSV',
        CONTA_SIMPLES_CARD_ATTACHMENT: 'Anexo Conta Simples',
        CARD_FAST_TRACK: 'Fast track de cartão',
        REIMBURSEMENT_REQUEST: 'Solicitação de reembolso',
      },
    },
    status: {
      all: 'Todos',
    },
    amountOperators: {
      eq: 'Igual',
      gte: 'Maior ou igual',
      lte: 'Menor ou igual',
      eqSymbol: '=',
      gteSymbol: '≥',
      lteSymbol: '≤',
    },
    upload: {
      duplicatesWithNames: '{{count}} documento(s) duplicado(s): {{names}}',
      duplicates: '{{count}} documento(s) duplicado(s)',
      duplicateSingleFallback: 'Documento duplicado',
      reimbursementRouted:
        'Documento roteado para pedido de reembolso. Acompanhe em Pendências — a conta só aparece na listagem após aprovação.',
      filesFallback: 'Arquivos',
      failed: '{{count}} documento(s) falharam: {{errors}}',
      globalDrop: {
        title: 'Solte os arquivos para iniciar o upload',
        subtitle: 'O upload de documentos será aberto automaticamente',
      },
    },
    processing: {
      pending: 'Pendências',
    },
    newBillsProcessed: 'Atualizar tabela — {{count}} nova(s) conta(s) processada(s)',
    sendBills: 'Enviar contas',
    newBillButton: 'Nova conta',
    newBillMenuTitle: 'Lançar conta a pagar',
    newBillMenu: {
      upload: {
        title: 'Enviar documentos',
        badge: 'Mais rápido',
        description: 'Faça upload de boletos, NFS-e ou notas — a IA preenche automaticamente',
      },
      chat: {
        title: 'Descrever para a IA',
        description: 'Dite a conta em linguagem natural — a IA cria, classifica e agenda',
      },
      form: {
        title: 'Preencher formulário',
        description: 'Lançamento manual com todos os campos',
      },
      email: {
        title: 'Enviar por e-mail',
        description: 'Encaminhe documentos para a inbox da sua empresa',
      },
      csvImport: {
        title: 'Importar CSV',
        description: 'Importe contas em lote a partir de uma planilha CSV',
      },
      paymentsSection: 'Pagamentos',
      importRetorno: {
        title: 'Importar retorno',
        description: 'Atualize o status dos pagamentos com o arquivo retorno do banco',
      },
    },
    emailInboxesButtonAria: 'Ver caixas de entrada de e-mail',
    sendByEmailLabel: 'Envie contas por e-mail:',
    emailInboxesLoading: 'Carregando caixas de entrada...',
    emailInboxesEmpty: 'Nenhuma caixa de entrada disponível.',
    emailInboxesError: 'Falha ao carregar caixas de entrada.',
    emailInboxesCopied: 'E-mail copiado.',
    emailInboxesCopyError: 'Não foi possível copiar o e-mail.',
    emailInboxesEditLink: 'Editar e-mails',
    cnabPreflight: {
      title: 'Conferir antes de gerar',
      allReady_one: '{{count}} pagamento pronto para a remessa.',
      allReady_other: '{{count}} pagamentos prontos para a remessa.',
      someBlocked_one:
        '{{ready}} de {{total}} pagamentos entram na remessa. {{count}} fica de fora.',
      someBlocked_other:
        '{{ready}} de {{total}} pagamentos entram na remessa. {{count}} ficam de fora.',
      blockedHeading: 'Não entram nesta remessa',
      blockedHint: 'Corrija o cadastro do favorecido e gere de novo, ou siga com os demais.',
      // With nothing ready there are no "demais" to go on with, and the button does not offer
      // an action it refuses: "Gerar com 0 pagamento" read as a dare, and in the singular.
      blockedHintNothingReady: 'Corrija o cadastro do favorecido e gere de novo.',
      generateNothing: 'Nada a gerar',
      back: 'Voltar',
      generate_one: 'Gerar com {{count}} pagamento',
      generate_other: 'Gerar com {{count}} pagamentos',
      nothingToSend: 'Nenhum pagamento pode ser enviado. Corrija os favorecidos acima.',
      // The check not answering is not the same as it answering "all clear": generating anyway
      // is how a partial remessa reaches a bank with nobody having seen what was left out.
      checkFailed:
        'Não foi possível conferir a remessa antes de gerar. Tente novamente em instantes.',
    },
    cnabOriginDialog: {
      title: 'Selecionar conta de origem',
      titleMultiOrigin: 'Gerar arquivos — várias contas de origem',
      description:
        'Os pagamentos selecionados possuem contas de origem diferentes. Selecione a conta usada para gerar o arquivo bancário no formato adequado. Todos serão atualizados para esta conta.',
      descriptionMultiOrigin:
        'Os pagamentos selecionados usam mais de uma conta de origem. Recomendamos exportar por conta de origem (um arquivo por conta, dentro de um ZIP).',
      descriptionAllMissing:
        'Nenhum pagamento possui conta de origem. Selecione a conta usada no arquivo bancário. Todos serão atualizados para esta conta.',
      paymentCount_one: '{{count}} pagamento',
      paymentCount_other: '{{count}} pagamentos',
      paymentCount: '{{count}} pagamento(s)',
      noOriginWarning_one: '{{count}} pagamento não possui conta de origem e será ignorado.',
      noOriginWarning_other: '{{count}} pagamentos não possuem conta de origem e serão ignorados.',
      noOriginWarning: '{{count}} pagamento(s) sem conta de origem (serão ignorados).',
      noOriginUsesSelection_one:
        '{{count}} pagamento não possui conta de origem e usará a conta selecionada.',
      noOriginUsesSelection_other:
        '{{count}} pagamentos não possuem conta de origem e usarão a conta selecionada.',
      useForAll: 'Será usada para todos os pagamentos selecionados.',
      cancel: 'Cancelar',
      confirm: 'Confirmar e Exportar',
      missingConvenioWarning:
        'Conta Santander (033) sem convênio CNAB. Configure o convênio na conta bancária antes de exportar.',
      cnabIncomplete: 'Convênio CNAB não configurado',
      supportedBanksHint:
        'Arquivos suportados: Itaú, Santander e BanBajío (piloto não homologado).',
      bankCodeLabel: 'Banco {{code}}',
      exportByBank: 'Exportar por conta de origem (ZIP)',
      exportByBankTitle: 'Um ZIP com uma pasta por conta de origem',
      exportByBankHint:
        'Cada conta de origem gera sua remessa no layout correto (Itaú pode separar PIX em outro arquivo dentro da mesma pasta).',
      useSingleAccount: 'Usar uma única conta para todos os pagamentos',
    },
    automaticDispatchDialog: {
      title: 'Revisar envio de pagamentos',
      description:
        '{{count}} pagamento(s) serão encaminhados pelo canal configurado na conta de origem.',
      multipleOriginsDescription:
        '{{count}} pagamento(s) usam {{origins}} contas de origem. Escolha como deseja enviá-los.',
      mixedCurrenciesWarning:
        'A seleção contém moedas diferentes; revise as contas antes de continuar.',
      keepCurrent: 'Manter as contas de origem atuais',
      keepCurrentDetail:
        'Cada pagamento será enviado pelo canal configurado em sua própria conta de origem.',
      useOneAccount: 'Usar uma única conta de origem',
      useOneAccountDetail:
        'Todos os pagamentos serão atualizados para a conta escolhida antes do envio.',
      differentEntities:
        'Não é possível usar uma única conta para pagamentos de entidades diferentes.',
      noEligibleAccounts: 'Não há conta habilitada e pronta para receber todos os pagamentos.',
      assignmentFailed:
        'Não foi possível alterar a conta de todos os pagamentos ({{updated}} atualizados, {{failed}} falharam). Nada foi enviado.',
      cancel: 'Cancelar',
      confirm: 'Confirmar envio',
      sending: 'Enviando...',
    },
    sendToProviderOriginDialog: {
      title: 'Conta de origem para {{integration}}',
      description:
        'Alguns pagamentos têm contas de origem diferentes ou não têm conta. Escolha como enviar via {{integration}}.',
      noEligibleAccounts:
        'Nenhuma conta de origem elegível para {{integration}}. Configure a integração em Configurações → Integrações ou associe uma conta de origem aos pagamentos.',
      option1:
        'Manter conta atual dos que já estão vinculados a {{integration}} e escolher uma conta para o restante',
      option1Detail: '{{matching}} já vinculado(s), {{rest}} para atualizar',
      option2: 'Usar uma única conta para todos os pagamentos selecionados',
      option2Detail: '{{count}} pagamento(s)',
      option3:
        'Enviar apenas os que já têm conta vinculada a {{integration}} (cada um na sua conta)',
      option3Detail: '{{count}} enviado(s), {{skipped}} ignorado(s)',
      pickAccountAll: 'Conta para todos',
      pickAccountRest: 'Conta para o restante',
      cancel: 'Cancelar',
      confirm: 'Confirmar e Enviar',
    },
    selection: {
      changeOrigin: {
        button: 'Alterar conta de origem',
        title: 'Conta de origem para {{count}} pagamento(s)',
        description:
          'Todos os pagamentos selecionados passam a sair da conta escolhida. Pagamentos já enviados ao banco não são alterados.',
        placeholder: 'Conta de origem',
        back: 'Voltar',
        apply: 'Aplicar',
      },
      reschedule: {
        button: 'Alterar data de pagamento',
        title: 'Nova data de pagamento para {{count}} pagamento(s)',
        description:
          'Muda só a data em que o pagamento sai, não o vencimento da conta. Pagamentos já enviados ao banco não são alterados.',
        dateLabel: 'Nova data de pagamento',
        back: 'Voltar',
        confirm: 'Alterar data',
      },
      clearAria: 'Limpar seleção',
      single: 'conta selecionada',
      multiple: 'contas selecionadas',
      totalLabel: 'Total:',
      exportSelected: 'Exportar selecionadas',
      bulkActions: 'Ações em lote',
      exportCnab: 'Gerar arquivo bancário',
      exportInternalCnab: 'Gerar CNAB interno',
      exportBankFile: 'Gerar arquivo bancário',
      sendPayment: 'Enviar pagamentos',
      sendViaGateway: 'Enviar pelo gateway',
      sendViaGatewayHint: 'Contingência pela VAN reservada, enquanto a API é validada.',
      moreActions: 'Mais ações',
      sendViaGatewayConfirm: {
        title: 'Enviar {{count}} selecionado(s) pelo gateway · {{total}}',
        description:
          'Toda a seleção vai pela VAN bancária reservada de cada conta de origem, em vez da API do banco, inclusive o que a API recusou. O gateway tem as próprias regras: o banco aceita só o que a conta já tem liberado nele, e o que não puder sair é recusado e continua na tabela.',
        confirm: 'Enviar pelo gateway',
      },
      sispag: 'Itaú SISPAG',
      cnab240: 'CNAB 240 (Remessa)',
      kaminoDisabled: 'Kamino (em breve)',
      forPayment: {
        single: 'pagamento selecionado',
        multiple: 'pagamentos selecionados',
      },
      send: {
        count: 'Enviar {{count}}',
        checking: 'Conferindo…',
        nothing: 'Nada a enviar',
        ready: '{{count}} pronto(s)',
        fixable: '{{count}} precisa(m) de ajuste',
        blocked: '{{count}} não pode(m) sair',
        checkingBadge: 'conferindo…',
      },
      sendConfirm: {
        title: 'Enviar {{count}} pagamento(s) · {{total}}',
        description:
          'Pelo gateway de pagamento. Cada pagamento sai da sua conta de origem e a remessa baixa sozinha quando o banco aceitar todos.',
        origins: 'São {{count}} contas de origem.',
        leftBehind: 'Os {{count}} que não estão prontos continuam selecionados na tabela.',
        partialTotal: 'Valor parcial: há pagamentos prontos fora desta página.',
        originLine: '{{count}} pagamento(s) · {{total}}',
        back: 'Voltar',
        confirm: 'Confirmar',
      },
      cnabReview: {
        title: 'Gerar remessa para {{count}} pagamento(s)',
        blocked: '{{count}} fica(m) de fora. O motivo de cada um está em Observações.',
        none: 'Nenhum pagamento pode entrar na remessa. O motivo de cada um está em Observações.',
        back: 'Voltar',
        confirm: 'Gerar',
      },
    },
    cancelSend: {
      action: 'cancelar envio',
      title: 'Cancelar envio do pagamento {{label}}',
      description:
        'Retira o pagamento do gateway de pagamento. Se o banco ainda não recebeu, ele volta para "Para pagamento" e pode ser enviado de novo; se já está agendado no banco, o banco é avisado e confirma o cancelamento.',
      warning:
        'Se o arquivo já foi entregue ao banco, o pagamento pode aparecer para aprovação mesmo assim — confira e cancele também no banco.',
      reasonLabel: 'Motivo do cancelamento',
      reasonPlaceholder: 'Ex.: conta de origem errada',
      reasonHint: 'Mínimo de {{min}} caracteres. Fica registrado no histórico do pagamento.',
      back: 'Voltar',
      confirm: 'Cancelar envio',
      confirming: 'Cancelando…',
      unknownStatus: 'desconhecido',
      refusal: {
        SEND_NOT_CANCELLABLE:
          'Este envio não pode mais ser cancelado por aqui: o gateway informa o status {{status}}.',
        PAYMENT_NOT_PROCESSING:
          'O pagamento não está mais em processamento (status {{status}}). Atualize a lista.',
        NO_ACTIVE_PROVIDER_ATTEMPT:
          'Este pagamento não tem envio ativo no gateway (pode estar em um arquivo CNAB).',
        CANCEL_ALREADY_REQUESTED:
          'O cancelamento já foi solicitado ao banco. Aguarde a confirmação dele.',
        PAYMENT_CHANGED_DURING_CANCEL:
          'O pagamento mudou durante o cancelamento. Atualize a lista e confira o resultado.',
        PROVIDER_CALL_FAILED:
          'O gateway de pagamento não respondeu. Nada foi alterado; tente de novo em instantes.',
        FILE_MAY_BE_AT_BANK:
          'A remessa deste pagamento pode já estar no banco. Confira no banco antes de cancelar o envio.',
        SEND_CANCEL_AT_BANK_ONLY:
          'O gateway de pagamento já aceitou este envio e não permite cancelá-lo. Cancele o pagamento diretamente no banco; o retorno do banco atualiza o pagamento aqui.',
        SEND_PAST_CANCELLATION:
          'O banco já confirmou ou processou este pagamento (status {{status}}). Um pagamento confirmado só pode ser estornado pelo banco: entre em contato com ele.',
        SEND_NOT_YET_LISTED:
          'O banco ainda não mostra este pagamento. Aguarde a próxima consulta de retorno e tente cancelar de novo.',
        SEND_CANCEL_NOT_CONFIRMED:
          'O banco não confirmou o cancelamento. Nada foi alterado aqui; a próxima consulta de retorno mostra a situação real do pagamento.',
        SEND_CANCEL_BUSY:
          'A conexão com o banco está em uso por um envio ou uma renovação de acesso. Tente de novo em alguns minutos.',
        SEND_CANCEL_UNAVAILABLE:
          'O cancelamento por este gateway de pagamento não está disponível neste ambiente. Fale com o suporte.',
        PROVIDER_CONNECTION_LAPSED:
          'A conexão da empresa com o banco expirou. Um administrador precisa reconectá-la em Integrações; depois, tente cancelar de novo.',
      },
      fileAtBank: {
        title: 'A remessa pode já estar no banco',
        body: 'Excluir o envio no gateway não recolhe um arquivo já entregue ao banco: se ele foi recebido, reenviar o pagamento duplicaria a ordem. Confira no banco antes de continuar.',
        remittances: 'Remessa(s): {{ids}}',
        noRemittanceIds: 'O gateway não informou o id da remessa.',
        confirm: 'Confirmei no banco que a remessa não foi recebida',
      },
      toast: {
        cancelled: 'Envio cancelado. O pagamento pode ser enviado de novo.',
        requested: 'Cancelamento solicitado ao banco (protocolo {{protocol}}).',
        requestedNoProtocol: 'Cancelamento solicitado ao banco.',
      },
    },
    readiness: {
      checking: 'Conferindo…',
      ready: 'Pronto',
      fix: {
        correct: 'corrigir',
        correctBankAccount: 'corrigir dados bancários',
        register: 'cadastrar',
      },
      payeeBankAccount: {
        summary: 'A agência ou a conta do favorecido estão fora do formato que o banco aceita.',
        hint: 'Corrija a agência e a conta no cadastro do favorecido; o pagamento não sai enquanto isso.',
        issues: {
          AGENCY_MISSING: 'Falta a agência do favorecido.',
          AGENCY_HAS_CHECK_DIGIT:
            'A agência parece trazer o dígito verificador. Informe a agência com até 4 dígitos e o dígito no campo próprio.',
          ACCOUNT_MISSING: 'Falta o número da conta do favorecido.',
          ACCOUNT_TOO_LONG:
            'A conta tem dígitos demais (máximo de 12). Informe a conta sem o dígito verificador.',
          ACCOUNT_DIGIT_MISSING: 'Falta o dígito verificador da conta.',
          ACCOUNT_DIGIT_TOO_LONG: 'O dígito da conta tem caracteres demais (máximo de 2).',
        },
      },
      short: {
        default: 'Não pode sair',
        PAYEE_BANK_ACCOUNT_INVALID: 'Dados bancários inválidos',
        PAYEE_PIX_KEY_REQUIRED: 'Sem chave PIX',
        PAYEE_IDENTITY_REQUIRED: 'Sem CPF/CNPJ',
        PAYEE_BANK_DETAILS_REQUIRED: 'Faltam dados bancários',
        PAYEE_BANK_DETAILS_REQUIRED_bankCode: 'Falta banco',
        PAYEE_ACCOUNT_UNAVAILABLE: 'Sem conta',
        BARCODE_REQUIRED: 'Sem código de barras',
        BARCODE_INVALID: 'Código inválido',
        AMOUNT_BARCODE_MISMATCH: 'Valor difere do código',
        AMOUNT_INVALID: 'Valor inválido',
        SCHEDULED_DATE_REQUIRED: 'Sem data',
        DARF_FIELD_REQUIRED: 'DARF incompleto',
        DARF_AMOUNT_INVALID: 'DARF: valor inválido',
        DARF_MULTIPLE_REVENUE_CODES: 'DARF: vários códigos',
        PAYMENT_METHOD_UNSUPPORTED: 'Método não aceito',
        PAYMENT_CURRENCY_UNSUPPORTED: 'Moeda não aceita',
        PAYEE_ACCOUNT_COUNTRY_UNSUPPORTED: 'País da conta não atendido',
        PIX_QR_UNSUPPORTED: 'QR não aceito',
        PAYMENT_REFERENCE_MISSING: 'Sem número de referência',
        PAYMENT_REFERENCE_INVALID_FOR_PROVIDER: 'Referência não aceita',
        ORIGIN_ACCOUNT_REQUIRED: 'Sem conta de origem',
        ORIGIN_ACCOUNT_CHANGED: 'Origem mudou',
        ORIGIN_ACCOUNT_BANK_MISMATCH: 'Origem incompatível',
        ORIGIN_ACCOUNT_INCOMPLETE: 'Origem incompleta',
        ORIGIN_ACCOUNT_NOT_READY: 'Origem não habilitada',
        ORIGIN_ACCOUNT_ENTITY_MISMATCH: 'Origem de outra entidade',
        CAPABILITY_ROUTE_MISMATCH: 'Não habilitado',
        CAPABILITY_NOT_CONFIGURED: 'Não habilitado',
        CAPABILITY_BLOCKED: 'Bloqueado',
        AMOUNT_EXCEEDS_LIMIT: 'Acima do teto',
        TAX_SEGMENT_NOT_ENABLED: 'Tributo não habilitado',
        FINNET_ACCOUNT_NOT_MAPPED: 'Conta não ligada',
        ORIGIN_BANK_FORM_UNSUPPORTED: 'Banco não oferece',
        PAYMENT_NOT_FOUND: 'Não encontrado',
        ALREADY_IN_FLIGHT: 'Em envio',
        ALREADY_IN_BANK_FILE: 'Em arquivo',
        BARCODE_DUE_DATE_MISMATCH: 'Vencimento difere do código',
        TAX_FIELD_REQUIRED: 'Guia incompleta',
        TAX_AMOUNT_INVALID: 'Guia: valores não fecham',
        PAYMENT_DATE_NOT_BUSINESS_DAY: 'Data não é dia útil',
        PAYMENT_DATE_AFTER_DUE_DATE: 'Depois do vencimento',
      },
    },
    configIssues: {
      affected: '{{count}} pagamento(s) selecionado(s) dependem disso.',
      support: 'Se precisar de ajuda, fale com o suporte informando o horário.',
      dismiss: 'Dispensar',
    },
    inlineBankFix: {
      title: 'Banco recebedor · {{payee}}',
      appliesToMany: 'Vale para os {{count}} pagamentos deste favorecido na seleção.',
      appliesToOne: 'Vale para este pagamento.',
      placeholder: 'Buscar por nome ou código…',
      saveNote: 'Salvo no cadastro do favorecido. A linha confere de novo ao aplicar.',
      failed: 'Não foi possível salvar o banco. Tente de novo.',
      cancel: 'Cancelar',
      apply: 'Aplicar',
    },
    bulkActions: {
      changeEntity: {
        button: 'Alterar entidade',
        title: 'Alterar entidade das contas selecionadas',
        description:
          'Escolha a entidade de destino para {{count}} conta(s). Contas que já pertencem ao destino não serão alteradas.',
        entityLabel: 'Entidade de destino',
        entityPlaceholder: 'Selecione a entidade de destino',
        effects:
          'As classificações anteriores serão removidas e uma nova classificação será solicitada em segundo plano; ela pode exigir ação manual. Contas sem documento vinculado precisarão de classificação manual. Contas em aprovação voltarão para revisão. A conta bancária preferencial, os rateios de cartão e os vínculos com recorrências de outra entidade serão removidos. Nos pagamentos ainda não enviados, a conta de origem de outra entidade será substituída pela conta padrão ou pela única conta compatível da entidade de destino. Se não houver uma conta compatível definida, a origem será removida e deverá ser selecionada antes do envio. Classificações por item passarão a ser por conta.',
        consent:
          'Confirmo a troca de entidade e autorizo a remoção das classificações anteriores e a solicitação de uma nova classificação.',
        confirm: 'Alterar {{count}} conta(s)',
        processing: 'Alterando e solicitando classificação…',
        cancel: 'Cancelar',
        close: 'Fechar',
        retry: 'Tentar novamente {{count}} pendente(s)',
        recover: 'Recuperar classificação de {{count}} conta(s)',
        requestError:
          'Não foi possível confirmar o resultado da alteração. Use a recuperação para verificar as contas.',
        recoveryNotice:
          'A resposta não foi recebida e algumas contas podem ter sido alteradas. A recuperação solicita classificação apenas para contas que já estão no destino. Para transferir as demais, feche e selecione novamente na listagem.',
        summary: '{{changed}} alterada(s), {{unchanged}} sem alteração e {{failed}} com falha.',
        asyncNotice:
          'A classificação é executada em segundo plano. A entidade alterada não significa que a classificação foi concluída.',
        refreshError:
          'O resultado foi salvo, mas a listagem não pôde ser atualizada. Atualize a página.',
        eligibility: 'Selecione até 50 contas em revisão ou em aprovação.',
        outcome: {
          CHANGED: 'Entidade alterada',
          UNCHANGED: 'Já pertence à entidade de destino',
          FAILED: 'Entidade não alterada',
        },
        classification: {
          QUEUED: 'Classificação solicitada para a entidade de destino.',
          NO_DOCUMENT: 'Sem documento vinculado. Refaça a classificação manualmente.',
          PENDING: 'Reclassificação pendente. Tente novamente.',
        },
        errors: {
          BULK_STATUS_NOT_ELIGIBLE: 'A conta não está mais em revisão ou em aprovação.',
          BULK_ENTITY_CHANGE_RETRY_ENTITY_MISMATCH:
            'A conta não está na entidade de destino. Feche e atualize a listagem antes de solicitar uma nova transferência.',
          BILL_NOT_FOUND: 'Conta indisponível. Atualize a listagem.',
          BILL_ENTITY_CHANGE_SUBMIT_PERMISSION_REQUIRED:
            'Você precisa de permissão para enviar contas à revisão antes de alterar a entidade de uma conta em aprovação.',
          BULK_ENTITY_CHANGE_CONFLICT:
            'A conta foi alterada durante a operação. Atualize e tente novamente.',
          BILL_ENTITY_CHANGE_BLOCKED_BY_GL_POSTING:
            'A entidade não pode ser alterada após a contabilização.',
          AP_BILL_ENTITY_TRANSFER_STALE_STATE:
            'A conta foi alterada durante a troca de entidade. Atualize a página e tente novamente.',
          AP_BILL_ENTITY_TRANSFER_PAYMENT_ORIGIN_LOCKED:
            'A entidade não pode ser alterada enquanto houver pagamento enviado ou liquidado com conta de origem de outra entidade.',
          AP_BILL_FOREIGN_CURRENCY_ENTITY_TRANSFER_UNSUPPORTED:
            'A moeda da conta impede a transferência direta para esta entidade.',
          BULK_ENTITY_CHANGE_FAILED:
            'Não foi possível concluir a operação. Tente novamente o item pendente.',
        },
      },
      sendPayments: 'Integração bancária',
      gatewayLabel: 'gateway de pagamento',
      markPaid: 'Marcar como Pago',
      markPosted: 'Marcar como Contabilizado',
      sendToBank: 'Enviar para Banco',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      sendToApproval: 'Enviar para aprovação',
      void: 'Cancelar',
      classify: {
        button: 'Classificar',
        title: 'Classificar contas selecionadas',
        selectedBadge: '{{count}} selecionada(s)',
        vendorLabel: 'Fornecedor',
        vendorPlaceholder: 'Selecione um fornecedor',
        glLabel: 'Conta contábil',
        glPlaceholder: 'Selecione uma conta contábil',
        managerialLabel: 'Conta gerencial',
        managerialPlaceholder: 'Selecione uma conta gerencial',
        costCenterLabel: 'Centro de custo',
        costCenterPlaceholder: 'Selecione um centro de custo',
        departmentLabel: 'Departamento',
        departmentPlaceholder: 'Selecione um departamento',
        notesLabel: 'Observações',
        notesPlaceholder: 'Adicionar observação para as classificações em lote',
        cancel: 'Cancelar',
        confirm: 'Classificar {{count}} conta(s)',
        confirmLoading: 'Classificando...',
        statusBlocked:
          'Classificar em lote está disponível apenas para contas em rascunho, revisão ou aprovação pendente.',
        entityMismatch: 'Selecione contas da mesma entidade para classificar em lote.',
        detailLoadError: 'não foi possível validar o modo de classificação',
        saveError: 'erro ao salvar classificação',
        failedBillWithReason: '{{bill}} ({{reason}})',
        moreItems: '{{items}} +{{count}} mais',
        toast: {
          success: '{{count}} conta(s) classificada(s) com sucesso.',
          partial:
            '{{success}} classificada(s), {{skipped}} ignorada(s) (modo por item), {{failed}} com falha.',
          noneEligible: 'Nenhuma conta classificada: todas estão em modo por item.',
          noneSuccess: 'Nenhuma conta foi classificada.',
          skippedDetails: 'Ignoradas (modo por item): {{bills}}',
          failedDetails: 'Falharam: {{bills}}',
        },
      },
      limitExceeded: 'Selecione no máximo {{max}} contas por ação em lote.',
      limitSelectFirst: 'Selecionamos as primeiras {{max}} contas para evitar lentidão.',
      voidStatusBlocked:
        'Cancelar em lote está disponível apenas para contas em rascunho, revisão ou aprovação pendente.',
      sendApprovalStatusBlocked:
        'Enviar para aprovação está disponível apenas para contas em rascunho ou revisão.',
      approveStatusBlocked:
        'Aprovar e rejeitar estão disponíveis apenas para contas em Aprovação pendente.',
      toast: {
        rescheduleSuccess: 'Data de pagamento alterada em {{count}} pagamento(s).',
        reschedulePartial:
          '{{updated}} pagamento(s) com nova data, {{failed}} não alterado(s) (já enviados ao banco, pagos ou cancelados).',
        rescheduleError: 'Não foi possível alterar a data de pagamento.',
        scheduleError: 'Não foi possível atualizar a data de pagamento.',
        schedulePartialBlocked:
          '{{updated}} pagamento(s) atualizado(s), {{failed}} falha(s). Envio/exportação cancelado — corrija as datas antes de continuar.',
        transitionSuccess: '{{count}} conta(s) alterada(s) para "{{status}}" com sucesso',
        transitionNone: 'Nenhuma conta pôde ser alterada para "{{status}}"',
        transitionPartial: '{{succeeded}} conta(s) alterada(s), {{failed}} falhou(aram)',
        budgetHardBlockRejected:
          '{{count}} conta(s) rejeitada(s) automaticamente por exceder o limite orçamentário',
        bulkError: 'Erro ao processar ação em lote',
        bulkPartialUnknown:
          'A ação em lote falhou. Algumas contas podem já ter sido processadas — a lista foi atualizada; confira antes de tentar de novo.',
        approveFullySucceeded: '{{count}} conta(s) aprovada(s) por completo',
        approveAdvanced:
          '{{count}} conta(s) avançaram para o próximo aprovador e seguem em Aprovação pendente',
        approveAdvancedStillSelected:
          'Elas continuam selecionadas. Você já aprovou sua etapa; a próxima ação é de outro aprovador.',
        failedStillSelected:
          'As contas que não passaram continuam selecionadas para você corrigir e tentar de novo.',
        approveSkipped: '{{count}} conta(s) não puderam ser aprovadas',
        rejectSucceeded: '{{count}} conta(s) rejeitada(s)',
        rejectSkipped: '{{count}} conta(s) não puderam ser rejeitadas',
        validationError: 'Não foi possível validar as contas selecionadas.',
        resultFailures: '{{count}} conta(s) falharam. Ex.: {{example}}',
        unknownFailure: 'Falha desconhecida',
        noVendor: 'Sem fornecedor',
        kaminoPartial: '{{valid}} conta(s) prontas para envio, {{invalid}} com problemas:',
        kaminoSuccess: '{{count}} conta(s) prontas para envio ao Kamino (modo preview)',
        kaminoError: 'Erro ao verificar envio para Kamino',
        kaminoDispatchQueued:
          'Envio ao Kamino enfileirado. A lista será atualizada enquanto o processamento ocorre.',
        providerDispatchQueued:
          'Envio via {{provider}} enfileirado. A lista será atualizada enquanto o processamento ocorre.',
        providerDispatchQueuedPartial:
          '{{provider}}: {{queued}} pagamento(s) enfileirado(s); {{skipped}} ignorado(s) (não elegíveis ao Itaú).',
        providerDispatchTracking:
          '{{provider}}: {{count}} pagamento(s) em envio. Acompanhe o andamento acima da tabela; o arquivo de remessa baixa sozinho quando o banco aceitar todos.',
        exportInProgress: 'Exportação em desenvolvimento',
        cnabError: 'Erro ao gerar arquivo CNAB',
        cnabPaymentExcluded: '1 pagamento não incluído no CNAB: {{message}}',
        cnabPaymentsExcluded: '{{count}} pagamentos não incluídos no CNAB (ex.: {{example}}…)',
        cnabSomeExcluded: 'Alguns pagamentos não foram incluídos no arquivo CNAB.',
        cnabSuccess:
          'Arquivo CNAB gerado. Envie pelo Internet Banking em Transmissão de Arquivo > Remessa.',
        cnabSuccessZip:
          'Arquivos CNAB gerados (um por tipo de pagamento quando exigido pelo banco). Envie pelo Internet Banking em Transmissão de Arquivo > Remessa.',
        bankFileExportError: 'Não foi possível gerar o arquivo bancário.',
        sendSuccess: '{{count}} pagamento(s) enviado(s) ao banco',
        sendFailed: 'Falha ao enviar pagamentos. {{error}}',
        sendPartial: '{{success}} enviado(s), {{failed}} falhou(aram)',
        sendError: 'Erro ao enviar pagamentos ao banco',
        itauNoEligiblePix:
          'Nenhum pagamento elegível para envio PIX ao Itaú. Verifique método, PIX copia e cola e conta de origem Itaú.',
        retornoSuccess: '{{count}} retorno(s) processado(s){{details}}',
        retornoStatusChanged: '{{count}} pagamento(s) tiveram o status atualizado',
        retornoNoMatch:
          'Nenhuma linha de "{{fileName}}" casou com um pagamento. O arquivo foi registrado como rejeitado no histórico de arquivos; confira se ele é desta empresa e se os pagamentos foram enviados pelo ERP.',
        retornoNoMatchUnrecorded:
          'Nenhuma linha de "{{fileName}}" casou com um pagamento e o arquivo não foi registrado: a conta de origem do cabeçalho (banco, agência e conta) não corresponde a nenhuma conta bancária cadastrada. Cadastre essa conta ou confira se o arquivo é desta empresa, e importe de novo.',
        retornoFailed: 'Não foi possível processar {{count}} arquivo(s) de retorno.',
        retornoFailedFile: 'Não foi possível processar {{fileName}}',
        retornoFailure: {
          missingAccountConfig:
            'Os pagamentos deste arquivo saíram pelo gateway e a conta de origem não está configurada nele. Sem isso não dá para saber se a resposta do banco deve ser aplicada aqui. Configure a conta no gateway e envie o arquivo de novo.',
          multiplePayers:
            'O arquivo mistura pagamentos de mais de uma empresa no gateway. Separe o retorno por empresa e envie de novo.',
          multipleAccounts:
            'O arquivo mistura pagamentos de mais de uma conta de origem. Separe o retorno por conta e envie de novo.',
          missingPayerTaxId:
            'A empresa pagadora não tem CNPJ cadastrado. Complete o cadastro e envie o arquivo de novo.',
          reconciliationUnavailable:
            'O retorno chegou, mas o registro da conciliação com o gateway não está disponível. Envie o arquivo de novo daqui a pouco. Se continuar, fale com o suporte e informe o horário do envio.',
          reconciliationBlocked:
            'Este arquivo já tinha sido enviado ao gateway e a conciliação não terminou. Não foi reenviado para evitar uma resposta duplicada. Aguarde a conciliação ou fale com o suporte informando o horário do envio.',
          reconciliationUnconfirmed:
            'O gateway recebeu o arquivo, mas a conciliação não foi confirmada aqui. Não envie de novo. Fale com o suporte e informe o horário do envio.',
          settlementEvidenceIncomplete:
            'O banco marcou um pagamento como pago, mas a data ou o valor efetivo não veio no arquivo. Corrija o retorno com o banco e envie de novo.',
          settlementFailed:
            'Um pagamento pago não pôde ser baixado. O arquivo não foi concluído. Envie de novo. Se continuar, fale com o suporte e informe o horário do envio.',
          unknown:
            'O arquivo não foi processado. Envie de novo. Se continuar, fale com o suporte e informe o horário do envio.',
        },
      },
      dispatchProgress: {
        savedBanks: '{{count}} banco(s) salvo(s) nos favorecidos.',
        counter: '{{done}} de {{total}}',
        dismiss: 'Fechar',
        skippedNote:
          '{{skipped}} pagamento(s) não foram enviados: veja o motivo em cada linha da tabela.',
        partialEnqueue: {
          title: '{{provider}}: envio interrompido com {{pending}} pagamento(s) em andamento',
          description:
            'Os pagamentos que saíram estão sendo acompanhados na faixa acima e viram remessa quando o banco confirmar. Os demais não foram enviados: reenvie apenas eles.',
          sentWithoutFollowUp:
            '{{sent}} pagamento(s) saíram por {{provider}} e {{failed}} não foram enviados. Só os que não saíram continuam selecionados: reenvie apenas eles.',
        },
        nothingSent: {
          title: '{{provider}}: nenhum dos {{skipped}} pagamento(s) pôde ser enviado',
          description:
            'Cada linha da tabela mostra o que impediu o envio e como resolver. Corrija e reenvie.',
        },
        running: {
          title: 'Enviando {{total}} pagamento(s) via {{provider}}',
          description:
            'Cada pagamento é confirmado individualmente pelo banco. Você pode continuar trabalhando; a lista atualiza sozinha.',
        },
        success: {
          title: '{{provider}}: {{total}} pagamento(s) aceito(s) para envio',
          description:
            'Se um arquivo de remessa foi baixado, envie-o pelo Internet Banking quando o banco exigir.',
        },
        partial: {
          title: '{{provider}}: {{accepted}} aceito(s), {{rejected}} recusado(s)',
          description:
            'Os pagamentos recusados estão marcados na tabela com o motivo informado pelo banco. Corrija o cadastro e reenvie apenas eles.',
        },
        failure: {
          title: '{{provider}}: nenhum dos {{total}} pagamento(s) foi aceito',
          description:
            'Veja o motivo em cada linha da tabela. Se todos apontarem para a conta de origem ou para a configuração do provedor, fale com o suporte informando o horário do envio.',
        },
        slowNote:
          'Está demorando mais que o normal. O envio continua; a lista atualiza quando o banco responder.',
        started: 'Iniciado {{when}}.',
        startedBy: 'Iniciado {{when}} por {{name}}.',
        fromAccount: 'Conta de origem: {{account}}.',
        ago: {
          justNow: 'agora há pouco',
          minutes: 'há {{count}} min',
          hours: 'há {{count}} h',
          at: 'em {{date}}',
        },
        stalled: {
          title: 'Envio de {{when}} não concluiu',
          counter: '{{pending}} de {{total}} sem resposta',
          select: 'Selecionar os {{count}}',
          selectOne: 'Selecionar o pagamento',
          conclude: 'Encerrar envio',
          concludeHint:
            'Enquanto não encerrado, os pagamentos deste envio não trocam de conta de origem. Confira no Internet Banking e encerre para liberá-los.',
          concludeQuestion: 'O banco confirmou que os {{count}} sem resposta não foram executados?',
          concludeExplain:
            'Os pagamentos que o gateway já aceitou ou recusou, e os já baixados na conciliação, ficam como estão. Se o banco executou algum, dê baixa nele (conciliação ou baixa manual) antes de encerrar: ele entra como aceito. Sua resposta vale só para os demais e fica registrada no envio.',
          concludeNotExecuted: 'Não foram executados — liberar para reenvio',
          concluded:
            'Envio encerrado. {{accepted}} aceito(s) registrados; os demais ficaram como não enviados e podem ser reenviados.',
          concludedPartial:
            'Encerrado em {{concluded}} de {{total}} contas de origem. O restante continua aberto: {{reason}}',
          reason: {
            PAYMENTS_NEVER_TERMINAL:
              '{{pending}} pagamento(s) nunca chegaram a um estado final no gateway de pagamento. Os demais {{settled}} foram concluídos e a remessa dos aceitos foi gerada.',
            PROVIDER_STATUS_UNAVAILABLE:
              'O gateway de pagamento não respondeu à consulta de situação durante todo o prazo. Confira no Internet Banking antes de reenviar.',
            MANIFEST_REJECTED:
              'Este envio não pode mais ser concluído automaticamente: os pagamentos saíram do escopo em que foram enviados. Fale com o suporte informando o horário.',
            SCOPE_UNRESOLVED:
              'Não foi possível atribuir este envio a uma conta de origem. A remessa precisa ser gerada manualmente; fale com o suporte informando o horário.',
            unknown: 'O envio ficou parado sem concluir. Fale com o suporte informando o horário.',
          },
        },
        paymentState: {
          QUEUED: 'Na fila',
          SENDING: 'Enviando…',
          RETRYING: 'Tentando novamente…',
          ACCEPTED: 'Aceito pelo banco',
          AWAITING_BANK_APPROVAL: 'Aguardando aprovação no banco',
          REJECTED: 'Recusado',
          ABANDONED: 'Falhou',
          SKIPPED: 'Não enviado',
          queuedHint: 'Aguardando a fila de envio.',
          inFlightHint: 'Aguardando resposta de {{provider}}.',
          awaitingReturn: 'Aguardando retorno…',
          retryingQueueHint:
            'Não foi possível falar com o {{provider}} agora. A fila tenta de novo sozinha; não é preciso fazer nada.',
          awaitingReturnHint:
            'O {{provider}} não confirmou este envio, e ele não é repetido sozinho, para não pagar em dobro. Aguardamos o retorno do banco; se não chegar no prazo, o pagamento aparece em Pagamentos em revisão.',
          unconfirmedHint:
            'O envio ainda não foi confirmado pelo {{provider}}. Acompanhe pela lista: ela atualiza quando houver resposta.',
          externalId: 'Protocolo {{id}}',
          awaitingBankApprovalHint:
            'Aceito pelo {{provider}}. Um usuário com poderes de assinatura na conta precisa aprovar o envio no app do banco; nada mais é preciso no Alchemy ERP.',
          noReason: 'O banco não informou o motivo.',
          approvalExpired:
            'Não aprovado a tempo: ninguém com poderes de assinatura aprovou o envio no app do banco dentro do prazo. Envie o pagamento de novo e aprove-o no banco.',
          bankRefusedGeneric:
            'O banco ou o gateway de pagamento recusou o pagamento. Confira os dados do favorecido e da conta de origem antes de reenviar; se persistir, informe o horário do envio ao suporte.',
          bankRefusedGenericRetryable:
            'O banco ou o gateway de pagamento recusou o pagamento por enquanto. Pode ser reenviado mais tarde.',
          notApprovedInTime:
            'O pagamento não foi aprovado no app do banco a tempo e foi encerrado. Nada foi pago: reenvie o pagamento e peça a aprovação dentro do prazo.',
          bankRefused:
            'O banco recusou o pagamento (ocorrência {{code}}). Confira os dados do favorecido antes de reenviar; se persistir, informe o código ao suporte.',
          bankRefusedRetryable:
            'O banco recusou o pagamento temporariamente (ocorrência {{code}}). Pode ser reenviado mais tarde.',
          priorSendNotWithdrawable:
            'Não reenviado: o envio anterior deste pagamento ainda está ativo no gateway de pagamento (agendado, pago, em outro envio em andamento ou numa remessa que pode já estar no banco) e não pôde ser retirado. Nenhum pedido novo foi criado; confira o envio anterior e, para substituí-lo, use "Cancelar envio", que pede a confirmação no banco quando a remessa pode já ter sido enviada.',
          priorSendOtherChannel:
            'Não reenviado: este pagamento foi enviado por outro canal (arquivo CNAB, PIX direto ou Kamino). Nada foi retirado nem criado; cancele o envio por esse canal antes de reenviar. O histórico do pagamento indica qual canal.',
        },
        remittanceFiles: {
          label: 'Arquivos de remessa',
          download: 'Baixar {{fileName}}',
          downloadAll: 'Baixar todos (.zip)',
        },
        toast: {
          settledAll: '{{count}} pagamento(s) aceito(s) pelo banco. {{files}}',
          filesDownloaded_one:
            'Arquivo de remessa disponível. Confira se o download foi concluído; se o navegador bloqueou, baixe-o pela faixa do envio.',
          filesDownloaded_other:
            '{{count}} arquivos de remessa disponíveis; todos precisam chegar ao banco. Confira se todos foram baixados; se o navegador bloqueou algum, baixe-o pela faixa do envio.',
          settledAllNoFile: '{{count}} pagamento(s) aceito(s) pelo banco.',
          settledPartial:
            '{{accepted}} aceito(s), {{failed}} não saíram. Veja os motivos na tabela.',
        },
      },
      sendApprovalDialog: {
        title: 'Enviar contas para aprovação',
        description:
          '{{readyCount}} de {{total}} conta(s) estão prontas. {{blockedCount}} precisa(m) de ajustes antes do envio.',
        readyTitle: 'Prontas ({{count}})',
        blockedTitle: 'Com pendências ({{count}})',
        noReady: 'Nenhuma conta pronta para envio.',
        noBlocked: 'Nenhuma pendência encontrada.',
        cancel: 'Cancelar',
        proceed: 'Enviar {{count}} pronta(s)',
      },
      voidDialog: {
        title: 'Cancelar contas selecionadas?',
        description:
          '{{eligibleCount}} de {{selectedCount}} conta(s) serão canceladas. Esta ação cancela o fluxo de aprovação e remove vínculos de documentos quando aplicável.',
        blockedNotice:
          '{{blockedCount}} conta(s) não estão em um status permitido e não serão processadas.',
        reasonLabel: 'Justificativa (obrigatória)',
        reasonPlaceholder: 'Informe o motivo do cancelamento...',
        cancel: 'Cancelar',
        confirm: 'Cancelar contas',
        confirming: 'Cancelando...',
      },
      approveDialog: {
        title: 'Aprovar contas selecionadas?',
        description:
          'Sua aprovação será registrada em {{actionableCount}} de {{selectedCount}} conta(s) selecionada(s).',
        nothingToDo:
          'Nenhuma das contas selecionadas está aguardando a sua aprovação neste momento.',
        commentLabel: 'Comentário (opcional)',
        commentPlaceholder: 'Adicione um comentário para esta aprovação...',
        cancel: 'Cancelar',
        confirm: 'Aprovar {{count}} conta(s)',
        confirming: 'Aprovando...',
      },
      rejectDialog: {
        title: 'Rejeitar contas selecionadas?',
        description:
          'Sua rejeição será registrada em {{actionableCount}} de {{selectedCount}} conta(s) selecionada(s).',
        nothingToDo: 'Nenhuma das contas selecionadas está aguardando a sua análise neste momento.',
        reasonLabel: 'Motivo da rejeição (obrigatório)',
        reasonPlaceholder: 'Informe o motivo da rejeição...',
        cancel: 'Cancelar',
        confirm: 'Rejeitar {{count}} conta(s)',
        confirming: 'Rejeitando...',
      },
      approvalPreview: {
        consentWarningsTitle: 'Confirmação necessária antes da aprovação',
        loading: 'Verificando o que acontecerá com cada conta...',
        fullyApprovesTitle: '{{count}} conta(s) serão aprovadas por completo',
        fullyApprovesDetail:
          'Sua aprovação encerra o fluxo: a conta passa para Aprovada e é contabilizada.',
        advancesTitle: '{{count}} conta(s) avançarão para o próximo aprovador',
        advancesDetail:
          'Sua etapa será aprovada, mas a conta continua em Aprovação pendente até que os demais aprovadores decidam.',
        stepPosition: 'Etapa {{current}} de {{total}}',
        thenWaitsFor: 'depois aguarda {{approvers}}',
        rejectsTitle: '{{count}} conta(s) serão rejeitadas',
        rejectsDetail: 'A rejeição encerra todo o fluxo de aprovação e a conta passa a Rejeitada.',
        retriesTitle: '{{count}} conta(s) passarão por nova tentativa de aprovação',
        retriesDetail:
          'Essas contas travaram numa tentativa anterior. A ação refaz o fluxo de aprovação e a contabilização, como o botão "Tentar novamente" na conta.',
        retryCommentNotice:
          'As contas em nova tentativa não registram comentário — o fluxo de retentativa não aceita um.',
        skippedTitle: '{{count}} conta(s) serão ignoradas',
        andMore: '+ {{count}} outra(s)',
        blockReasons: {
          billNotFound: 'Conta não encontrada',
          statusNotEligible: 'Não está em Aprovação pendente',
          noApprovalChain: 'Sem fluxo de aprovação ativo',
          alreadyApprovedByYou: 'Você já aprovou sua etapa — aguardando os demais aprovadores',
          awaitingOtherApprover: 'Ainda não é a sua vez — um aprovador anterior precisa agir',
          noActionableStep: 'Nenhuma etapa disponível para você neste momento',
          stepEscalated: 'Etapa escalada — não pode mais ser aprovada diretamente',
          previewUnavailable: 'Não foi possível verificar esta conta — tente novamente',
          notAnApprover: 'Você não é aprovador da etapa atual',
        },
      },
      merge: 'Unificar',
      mergeStatusBlocked:
        'Unificar está disponível apenas para contas em rascunho, revisão ou aprovação pendente.',
      mergeDialog: {
        title: 'Unificar contas',
        confirmationTitle: 'Confirmar unificação',
        confirmationSummary: '{{count}} contas serão reunidas em uma.',
        continue: 'Continuar',
        back: 'Voltar',
        sumSuccessDescription: 'Revise o rateio e o pagamento antes de aprovar.',
        errorTitle: 'Unificação não concluída',
        errorCode: 'Código: {{code}}',
        openMergedBill: 'Abrir conta unificada',
        close: 'Fechar',
        reviewSelection: 'Revisar seleção',
        retry: 'Tentar novamente',
        amountChoice: 'Valores da conta',
        loadingPreview: 'Atualizando prévia',
        retentions: 'Retenções',
        credits: 'Créditos',
        recurrence: 'Recorrência',
        noRecurrence: 'Sem recorrência',
        chooseRecurrence: 'Escolha uma conta',
        recurrenceHelp:
          'Mantém a recorrência existente. Se forem diferentes, escolha qual vínculo preservar. Os modelos de recorrência não serão alterados.',
        sourceRateHelp: 'Aplica a taxa efetiva de {{rate}} desta conta à base unificada.',
        sourceAmount: 'Valor original',
        combinedBase: 'Base unificada',
        sumFiscalHelp:
          'Soma os valores já registrados nas contas, sem estender a taxa de uma à outra.',
        applySourceToAll: 'Aplicar {{bill}} a todas',
        keepBill: 'Manter conta {{number}}',
        sum: 'Somar',
        grossAmount: 'Valor bruto somado',
        netAmount: 'Valor líquido somado',
        retainedAmount: 'Retenções efetivas',
        creditAmount: 'Créditos de PIS/COFINS',
        sumConsequences:
          'Notas e rateios serão preservados em um rascunho. As contas de origem serão invalidadas.',
        duplicateConsequences:
          'Os documentos serão reunidos. As contas de origem serão invalidadas e marcadas como duplicadas.',
        sumHelp: 'Como funciona a soma',
        sumDetails:
          'Soma os valores das notas. Escolha créditos, retenções e recorrência por linha. Preserva documentos e rateios.',
        sumPaymentReview: 'Pagamentos anteriores serão cancelados. Revise o pagamento único.',
        paymentHelp: 'Dados do pagamento único',
        sumPaymentDetails:
          'Boletos e códigos Pix dos valores individuais não serão reaproveitados. Informe os dados do pagamento único antes de aprovar.',
        vendorLinkHelp: 'Agrupamento dos fornecedores',
        sumUnavailable:
          'Estas contas não podem ser somadas. Revise o contexto fiscal e financeiro antes de continuar.',
        sumBlockers: {
          STATUS: 'Somar exige contas ainda não aprovadas.',
          ENTITY: 'Somar exige a mesma entidade.',
          CURRENCY: 'As contas devem usar a moeda funcional da entidade, sem conversão cambial.',
          FUNCTIONAL_CURRENCY_UNRESOLVED:
            'Revise a moeda funcional dos livros contábeis da entidade.',
          VENDOR: 'Somar exige o mesmo fornecedor identificado em todas as notas.',
          SPECIAL_BILL:
            'Somar está disponível para despesas comuns, sem vínculo com folha, reembolso ou pedido de compra.',
          COMPETENCY: 'As notas devem pertencer à mesma competência.',
          RECURRENCE_SELECTION_REQUIRED:
            'As recorrências são diferentes. Escolha a conta que fornecerá o vínculo.',
          RECURRENCE_UNAVAILABLE:
            'A recorrência selecionada não está disponível para estas contas.',
          CREDIT_TYPE:
            'Os tipos de crédito são diferentes. Escolha uma conta para aplicar seu crédito.',
          ISS_PERIOD:
            'As competências de apuração de ISS são diferentes. Escolha uma conta na linha de retenções.',
          FINANCIAL_ACTIVITY:
            'Há conciliação, envio bancário, lançamento ou vínculo financeiro. Regularize esses vínculos antes de somar.',
          BUDGET_EXCEPTION:
            'Há uma exceção de orçamento pendente ou com saldo comprometido. Regularize a exceção antes de somar.',
          CHART_VERSION: 'Os rateios devem usar a mesma versão do plano de contas.',
          CLASSIFICATION_SCOPE:
            'Revise os rateios: há classificações sobrepostas ou valores superiores ao bruto da nota.',
          AMOUNTS: 'Revise os valores bruto e líquido das notas antes de somar.',
          DUPLICATE_INVOICE:
            'Há notas com a mesma identificação fiscal. Use a opção de manter uma conta para tratar duplicadas.',
          RETENTION_COMPONENTS:
            'O total retido não confere com os tributos discriminados. Revise as retenções antes de somar.',
        },
        description: '{{count}} contas selecionadas.',
        fieldColumnHeader: 'Campo',
        noConflicts: 'Nenhum conflito encontrado. A conta com mais itens será a conta principal.',
        conflictsTitle: 'Campos com valores diferentes',
        fieldLabel_vendorId: 'Fornecedor',
        fieldLabel_dueDate: 'Vencimento',
        fieldLabel_totalAmount: 'Valor total',
        fieldLabel_subtotalAmount: 'Valor subtotal',
        fieldLabel_taxTotalAmount: 'Impostos',
        fieldLabel_description: 'Descrição',
        fieldLabel_vendorBillNumber: 'Número da NF',
        fieldLabel_paymentMethod: 'Forma de pagamento',
        fieldLabel_billCompetencyDate: 'Competência',
        fieldLabel_billManagerialDate: 'Data gerencial',
        fieldLabel_nfeChaveAcesso: 'Chave NF-e',
        fieldLabel_nfeNumero: 'Número NF-e',
        fieldLabel_nfeSerie: 'Série NF-e',
        fieldLabel_nfeDataEmissao: 'Emissão NF-e',
        billLabel: 'Conta {{number}}',
        billBadgeRecommended: 'Recomendada',
        rememberLinkLabel: 'Lembrar fornecedores',
        rememberLinkHint:
          'Futuros lançamentos desses fornecedores serão agrupados automaticamente em uma única conta.',
        reasonLabel: 'Motivo (opcional)',
        reasonPlaceholder: 'Motivo da unificação',
        cancel: 'Cancelar',
        confirm: 'Unificar contas',
        confirming: 'Unificando...',
        loadError: 'Não foi possível carregar a pré-visualização.',
        success: 'Contas unificadas com sucesso',
        error: 'Não foi possível unificar as contas.',
      },
    },
    delete: {
      loading: 'Excluindo conta...',
      success: 'Conta excluída com sucesso',
      error: 'Não foi possível excluir a conta. Tente novamente.',
    },
    billAction: {
      notFound: 'Conta não encontrada',
      edit: 'Editar',
      delete: 'Excluir',
      review: 'Revisar',
      viewBill: 'Ver conta',
      view: 'Ver',
      openMenu: 'Abrir menu',
      menuFor: 'Ações de {{vendor}}',
      deleteOnlyDraft:
        'Apenas contas em rascunho, enviadas, aguardando aprovação ou rejeitadas podem ser excluídas',
      deleteDisabledTooltip: 'Contas só podem ser excluídas antes de aprovadas',
    },
    deleteDialog: {
      title: 'Excluir conta?',
      description:
        'Esta ação irá excluir virtualmente a conta e os documentos vinculados. Você poderá reenviar os documentos depois se necessário.',
      justificationLabel: 'Justificativa (obrigatória)',
      justificationPlaceholder: 'Informe o motivo da exclusão...',
      cancel: 'Cancelar',
      confirm: 'Excluir',
      confirming: 'Excluindo...',
      reconciled: {
        title_one: 'Esta conta tem 1 transação conciliada',
        title_other: 'Esta conta tem {{count}} transações conciliadas',
        description:
          'Ao excluir, a conciliação será desfeita e a transação voltará para pendente, livre para ser conciliada com outro documento.',
        confirm: 'Desconciliar e excluir',
        forbidden:
          'Desfazer uma conciliação exige permissão de gestão de conciliação. Peça a quem tem essa permissão para desfazer a conciliação e depois exclua esta conta.',
      },
    },
  },
  billDetails: {
    intakeProvenance: {
      strip: {
        fromEmail_one: '{{count}} campo veio do e-mail',
        fromEmail_other: '{{count}} campos vieram do e-mail',
        divergence_one: '{{count}} divergência',
        divergence_other: '{{count}} divergências',
        checked: 'conferido',
        open: 'Ver de onde vieram os dados',
      },
      tags: {
        document: 'da nota',
        email: 'do e-mail',
        review: 'conferir',
      },
      panel: {
        title: 'De onde vieram os dados',
        description: 'O que o e-mail preencheu ou contestou nesta conta, e o que a nota diz.',
        emailHeading: 'E-mail recebido',
        fieldsHeading: 'Campos',
        emailLoading: 'Carregando o e-mail…',
        emailLoadError: 'Não foi possível carregar o e-mail. Os campos continuam valendo.',
        emailEmpty: 'Este e-mail não tem texto.',
        emailUnavailable: 'O e-mail de origem não está disponível. Os campos continuam valendo.',
        emailHtmlOnly:
          'Este e-mail só tem a versão formatada. Os trechos usados aparecem em cada campo.',
        from: 'De',
        received: 'Recebido em',
        noSubject: '(sem assunto)',
        readOnly: 'Esta conta já não pode ser alterada. Reabra-a para conferir os campos.',
      },
      field: {
        billValue: 'Na conta',
        documentValue: 'Na nota',
        emailValue: 'No e-mail',
        empty: 'sem valor',
        snippet: 'Trecho do e-mail',
        paymentDateNote:
          'Mudar o vencimento aqui não muda a data do pagamento já agendado. Ajuste a data na aba Pagamentos.',
        paymentScheduled_one:
          'Há um pagamento de {{amount}} nesta conta. Ajuste o pagamento antes de trocar o valor.',
        paymentScheduled_other:
          'Há {{count}} pagamentos nesta conta, somando {{amount}}. Ajuste os pagamentos antes de trocar o valor.',
        paymentsUnknown:
          'Não foi possível conferir os pagamentos desta conta agora. Atualize a página e tente de novo antes de trocar o valor.',
      },
      actions: {
        useEmail: 'Usar o do e-mail',
        keepDocument: 'Manter o da nota',
        confirm: 'Está certo',
        alreadyChecked: 'Já conferi',
      },
      resolved: {
        USE_EMAIL: 'A conta agora usa o valor do e-mail.',
        KEEP_DOCUMENT: 'Mantido o valor da nota.',
        KEEP_NO_DOCUMENT_VALUE: 'Conferido. A conta segue sem esse valor.',
        CONFIRM: 'Valor conferido.',
        allDone: 'Tudo conferido. O alerta de conferência foi encerrado.',
      },
      amountConfirm: {
        title: 'Trocar o valor da conta pelo do e-mail?',
        description:
          'O valor da conta passa de {{documentValue}} para {{emailValue}}. A mudança fica registrada no histórico da conta.',
        confirm: 'Trocar o valor',
        cancel: 'Cancelar',
        success: 'Valor trocado. A conta agora usa {{emailValue}}.',
      },
      approvalConsent: {
        title: 'Conferir os dados do e-mail antes de aprovar',
        description:
          'O e-mail preencheu ou contestou campos desta conta que ninguém conferiu ainda. Veja o que a nota diz e o que o e-mail diz.',
        documentValue: 'Na nota',
        emailValue: 'No e-mail',
        empty: 'sem valor',
        checkbox: 'Conferi estes dados',
        note: 'Marque a caixa para aprovar. Os campos ficam registrados como conferidos por você.',
        confirm: 'Aprovar',
        cancel: 'Cancelar',
      },
    },
    validation: {
      attachmentRequired:
        'Comprovantes são obrigatórios, você pode enviar pelo botão "Enviar documentos" no cabeçalho',
    },
    deletedBill: {
      title: 'Conta excluída',
      description: 'Esta conta foi excluída em {{deletedAt}} por {{deletedBy}}.',
      justificationLabel: 'Justificativa da exclusão',
      unknownDeleter: 'Usuário desconhecido',
      backToBills: 'Voltar para contas',
    },
    cardInvoice: {
      title: 'Fatura de cartão',
      formLabel: 'Forma',
      providerLabel: 'Provedor',
      providerHelp:
        'A baixa contábil será feita no passivo do emissor quando o pagamento for conciliado.',
      providerPlaceholder: 'Selecione o provedor da fatura',
      institutionOption: 'Instituição financeira',
      institutionLabel: 'Banco emissor',
      providers: {
        CLARA: 'Clara',
        CONTA_SIMPLES: 'Conta Simples',
        BREX: 'Brex',
      },
    },
    unsavedChanges: {
      title: 'Alterações não salvas',
      description:
        'Você tem alterações não salvas. Deseja sair sem salvar? As alterações serão perdidas.',
      proceed: 'Sair sem salvar',
      stay: 'Continuar editando',
    },
    approvalWarnings: {
      title: 'Enviar mesmo assim para aprovação?',
      description:
        'Esta conta tem pendências que não impedem o envio. Ao confirmar, seu aceite fica registrado no histórico da conta.',
      confirm: 'Enviar mesmo assim',
      approveTitle: 'Confirmar entidade e aprovar?',
      approveDescription:
        'Esta conta tem uma sugestão de entidade pendente. Ao confirmar, a entidade atual será mantida e seu aceite ficará registrado.',
      approveConfirm: 'Confirmar e aprovar',
    },
    clientReimbursable: {
      title: 'Cobrar esta despesa de um cliente',
      description: 'Contabiliza como adiantamento e cria um valor a recuperar.',
      clearCreditsTitle: 'Limpar créditos de PIS/COFINS?',
      clearCreditsDescriptionFlag:
        'Despesas reembolsáveis pelo cliente não geram crédito de PIS/COFINS — o valor integral será cobrado do cliente via nota de débito. Ao confirmar, os créditos desta conta serão removidos. Se cancelar, a conta não será marcada como reembolsável.',
      clearCreditsDescriptionSend:
        'Esta conta está marcada como reembolsável pelo cliente, mas ainda possui créditos de PIS/COFINS. Despesas reembolsáveis não geram crédito — ao confirmar, os créditos serão removidos e a conta seguirá para aprovação. Se cancelar, o envio será interrompido.',
      clearCreditsConfirm: 'Limpar créditos e continuar',
    },
    statutoryTaxCompetency: {
      title: 'Aplicar competência aos tributos?',
      description:
        'A apuração de todos os tributos desta guia será alterada para {{period}}. Esta alteração só será aplicada quando você salvar a conta.',
      keepCurrent: 'Manter apurações atuais',
      applyOnSave: 'Aplicar ao salvar',
    },
    statutoryRailMigration: {
      title: 'Configurar passivo tributário',
      description: 'Confira a configuração e seus efeitos contábeis antes de continuar.',
      chartContext: 'Plano de contas · {{code}} · {{name}}',
      sharedTitle: 'Configurar estrutura compartilhada de {{taxName}}',
      sharedDescription:
        '{{members}} continuam compartilhando a mesma estrutura. A configuração transforma a conta atual em Legado e mantém os lançamentos existentes nela.',
      today: 'Hoje',
      afterShared: 'Depois · três estágios na mesma estrutura',
      currentSharedAccount: 'Estrutura única, sem estágios · {{count}} lançamentos',
      legacyAccountDetail: '{{code}} · lançamentos atuais',
      assessedAccountDetail: 'reconhecido, ainda sem guia',
      guidesPayableAccountDetail: 'já incluído em uma guia',
      sharedStagesExplanation:
        'Os estágios organizam o ciclo do tributo. Eles não separam {{members}}: a titularidade de cada valor é resolvida na revisão contábil.',
      ledgerBalance: 'Saldo do livro',
      debitBalance: 'saldo devedor',
      journals: 'Lançamentos',
      evidenceCountsCompact:
        '{{origins}} origens · {{allocations}} alocações · {{snapshots}} guias',
      unpaidRetentionsLabel: 'Retenções ainda não pagas',
      ownershipReviewExplanation:
        'Sem origem, alocação ou guia vinculada, o sistema não consegue dizer quanto desse saldo pertence a {{members}}.',
      ownershipReviewRequiredExplanation:
        'Há valores históricos cuja titularidade entre {{members}} ainda precisa de revisão contábil.',
      debitWarningTitle: 'Saldo devedor de {{amount}} em conta de passivo',
      debitWarningDescription:
        'Atribuir os lançamentos identifica a qual tributo cada valor pertence, mas não corrige o saldo. Depois da atribuição, faça o lançamento contábil necessário para eliminar o déficit. A revisão permanecerá aberta enquanto houver saldo devedor.',
      debitWarningDoesNotBlock: 'Isso não impede criar a estrutura agora.',
      sharedFooterNote:
        'Ao continuar, a estrutura é criada e a aprovação é retomada. Valores históricos sem origem identificada ficam sinalizados para revisão contábil, sem impedir a aprovação ou o pagamento.',
      diagramTitle: 'Estrutura contábil que será criada',
      currentAccount: 'Passivo tributário atual',
      currentAccountDetail: 'Será reorganizado como Conta legado.',
      legacyAccount: 'Conta legado',
      assessedAccount: 'Apurado',
      guidesPayableAccount: 'Em Guias a Pagar',
      preserved: 'preservada',
      created: 'criada automaticamente',
      effectiveDate: 'Data efetiva',
      effectiveDateTooltip:
        'Data a partir da qual a conta legada será reorganizada e o saldo será reclassificado para Apurado.',
      assessmentDate: 'Competência de apuração',
      assessmentDateTooltip:
        'Mês que representa o período fiscal dos saldos migrados. Vem preenchido com a apuração da guia; altere quando o saldo legado pertencer a outra competência.',
      assessmentDatePlaceholder: 'Selecione o mês',
      assessmentDateGuideDefault: 'Apuração da guia: {{period}}',
      assessmentDateInvalid: 'Informe um mês de apuração válido.',
      book: 'Livro contábil',
      bookBalanceContext: 'Os saldos do livro {{bookName}} serão reclassificados para Apurado.',
      cancel: 'Cancelar',
      confirm: 'Aplicar e continuar',
      submitting: 'Configurando...',
      configured: 'Configuração concluída. A conta continua pendente de aprovação.',
      createsStructure: 'Criar Apurado e Em Guias a Pagar, preservando o histórico.',
      migratesBalance: 'O saldo legado será transferido para Apurado na data informada.',
      reusesStructure: 'Usar a estrutura contábil existente.',
      evidence: 'Evidências contábeis',
      evidenceCounts:
        '{{journals}} lançamentos · {{origins}} origens · {{allocations}} alocações · {{snapshots}} guias',
      unpaidRetentions: 'Retenções ainda não pagas: {{amount}}',
    },
    header: {
      toast: {
        selectVendor: 'Selecione um fornecedor',
        saveSuccess: 'Cabeçalho salvo',
        saveError: 'Erro ao salvar cabeçalho',
      },
      dueOn: 'Vence {{date}}',
      linkedRecurrenceBadge: 'Recorrente · {{frequency}}',
      linkedRecurrenceBadgeGeneric: 'Recorrente',
      detectedRecurrenceBadge: 'Recorrência detectada',
    },
    backToLedger: 'Voltar para o razão',
    fallbackTitle: 'Conta',
    loadingDescription: 'Carregando...',
    noDocuments: 'Nenhum documento anexado.',
    previewUnavailable: 'Pré-visualização não disponível',
    imageViewer: {
      zoomIn: 'Aumentar zoom',
      zoomOut: 'Diminuir zoom',
      fitToScreen: 'Ajustar à tela',
      zoomLevelTooltip: 'Percentual relativo ao ajuste à tela (100% = caber na área visível)',
      openInNewTab: 'Abrir em nova aba',
    },
    resizePanelsAria: 'Redimensionar painéis',
    selectBill: 'Selecione uma conta',
    showDocument: 'Mostrar documento',
    hideDocument: 'Ocultar documento',
    viewDocumentsLabel: 'Ver documentos',
    paymentNetMismatch:
      'O total dos pagamentos ({{scheduled}}) não corresponde ao valor líquido da conta ({{net}}). Ajuste os pagamentos ou as retenções para continuar.',
    paymentNetMismatchFallback:
      'O total dos pagamentos não corresponde ao valor líquido da conta. Ajuste os pagamentos ou as retenções para continuar.',
    netAmountMismatch:
      'O líquido da conta ({{recorded}}) não bate com o bruto menos as retenções ({{expected}}). Corrija as retenções ou o valor antes de enviar para aprovação.',
    classificationAmountReview:
      'Valor salvo, mas o rateio da classificação não fecha com o novo valor. Revise os valores da classificação antes de aprovar.',
    paymentNetMismatchRepair: {
      action: 'Corrigir valor líquido',
      success: 'Valor líquido corrigido. Agora ele bate com o pagamento.',
      error: 'Não foi possível corrigir o valor líquido. Tente novamente.',
      saveToFinish: 'Agora o líquido bate com o pagamento. Salve para concluir.',
    },
    entityMismatch: {
      title: 'Revisar entidade',
      description:
        'O CNPJ do tomador não corresponde a nenhuma entidade cadastrada. Selecione a entidade correta para esta conta.',
      label: 'Entidade',
      placeholder: 'Selecione a entidade',
      goToDetails: 'Ir para Detalhes',
    },
    missingVendor: {
      alert: 'Fornecedor não identificado. Selecione o fornecedor para concluir a conta.',
      ambiguous:
        'Fornecedor não identificado: o emissor do documento corresponde a mais de um cadastro ({{names}}). Escolha um para concluir a conta.',
      goToDetails: 'Ir para Detalhes',
    },
    sections: {
      details: 'Detalhes',
      classification: 'Classificação',
      withholdings: 'Retenções',
      payments: 'Pagamentos',
      approval: 'Aprovação',
      activities: 'Atividades',
      navAria: 'Navegação entre seções da conta',
      toggleDetailsAria: 'Alternar Detalhes',
      toggleClassificationAria: 'Alternar Classificação',
      toggleWithholdingsAria: 'Alternar Retenções',
      togglePaymentsAria: 'Alternar Pagamentos',
      toggleApprovalAria: 'Alternar Aprovação',
      toggleActivitiesAria: 'Alternar Atividades',
      statusTooltip: {
        pendingCount_one: '{{count}} campo obrigatório nesta seção',
        pendingCount_other: '{{count}} campos obrigatórios nesta seção',
        complete: 'Seção completa',
        approval: 'Fluxo de aprovação',
        activities: 'Comentários e histórico',
        withholdingsNeutral: 'Retenções não aplicadas nesta conta.',
      },
      approvalEmptyStateDraft: 'O fluxo de aprovação será criado ao enviar a conta para aprovação.',
      approvalEmptyStateOther: 'Nenhum fluxo de aprovação configurado para esta conta.',
      approvalFailureTitle: 'Falha na aprovação',
      approvalFailureDescription: '{{message}}',
      approvalFailureOrphanDescription:
        'A conta ficou pendente de aprovação com um fluxo marcado como aprovado. Use o botão abaixo para corrigir e tentar novamente.',
      approvalRetryButton: 'Tentar aprovar novamente',
      approvalRetrying: 'Tentando aprovar…',
      approvalRetrySuccess: 'Aprovação reprocessada com sucesso',
      segregationDialogTitle: 'Segregação de Funções',
      segregationDialogGotIt: 'Entendi',
    },
    rightPanel: {
      title: 'Detalhes',
      vendorLabel: 'Pagar a',
      vendorFallback: 'Fornecedor não informado',
      employeePayeeLabel: 'Colaborador',
      reimbursementTag: 'Reembolso',
      advancedOptionsLabel: 'Opções avançadas',
      advancedOptionsDescription: 'despesa reembolsável, pedido de compra',
      totalLabel: 'Total (NF)',
      subtotalLabel: 'Subtotal (base)',
      interestLabel: 'Juros',
      fineLabel: 'Multa',
      withholdingsTotalLabel: 'Total retenções',
      removeWithholdingsLink: 'Remover',
      removeWithholdingsAria: 'Remover retenções desta conta',
      removeWithholdingsTooltip:
        'Desativa as retenções para esta conta. Você pode reativá-las na seção Retenções.',
      originalAmountLabel: 'Valor original',
      exchangeRateLabel: 'Câmbio',
      exchangeRatePending: 'Pendente',
      exchangeRateEdit: 'Editar taxa de câmbio',
      exchangeRateSave: 'Salvar taxa de câmbio',
      exchangeRateSaveSuccess: 'Taxa de câmbio alterada',
      exchangeRateCancel: 'Cancelar edição da taxa de câmbio',
      exchangeRateDialogTitle: 'Editar taxa de câmbio',
      exchangeRateDialogDescription:
        'Informe a nova taxa e registre a justificativa para esta alteração manual.',
      exchangeRateAutoFillTitle: 'Calcular automaticamente',
      exchangeRateAutoFillAction: 'Usar PTAX automaticamente',
      exchangeRateAutoFillCompetencyDescription:
        'Busca a PTAX de fechamento usando a data de competência ({{date}}).',
      exchangeRateAutoFillIssueDescription:
        'Busca a PTAX de fechamento usando a data de emissão ({{date}}).',
      exchangeRateAutoFillError: 'Não foi possível calcular a taxa de câmbio automaticamente',
      exchangeRateValueLabel: 'Nova taxa de câmbio',
      exchangeRateValueAria: 'Nova taxa de câmbio',
      exchangeRateReasonLabel: 'Justificativa da alteração',
      exchangeRateReasonAria: 'Justificativa da alteração da taxa de câmbio',
      exchangeRateReasonPlaceholder: 'Ex.: taxa contratual aprovada pelo financeiro',
      exchangeRateSaveError: 'Não foi possível salvar a taxa de câmbio',
      exchangeRateFixAction: 'Informar câmbio',
      valuesLabel: 'Valores',
      grossAmountLabel: 'Valor bruto',
      payableSuffix: 'a pagar',
      noWithholdingsHint: 'Sem retenções.',
      noWithholdingsAndEqualAmountsHint: 'Sem retenções. Bruto e líquido são iguais.',
      exchangeRateDateHint: 'Câmbio de {{date}}.',
      exchangeRateInlineLabel: 'Câmbio',
      exchangeRateDateContext: 'de {{date}}',
      dueOnLabel: 'Vence em {{date}}',
      openOfAmount: 'em aberto de {{amount}}',
      netPayableInlineLabel: 'líquido a pagar',
      grossAmountContext: 'de um bruto de {{gross}}',
      viewWithholdingsLabel: 'Ver retenções',
      settledPaymentsLabel_one: 'Já pago {{count}} pagamento',
      settledPaymentsLabel_other: 'Já pago {{count}} pagamentos',
      settledLabel: 'quitado',
      settledInPaymentsLabel: 'quitados em {{count}} pagamentos',
      paidOnLabel: 'em {{date}}',
      lastPaidOnLabel: 'último em {{date}}',
      paidWithholdingsHint:
        'De um bruto de {{gross}}, com {{withholdings}} retidos e recolhidos pela Alchemy.',
      viewCompositionLabel: 'Ver composição',
      openBalanceLabel: 'Saldo em aberto',
      paidPercentageLabel: '{{percent}}% quitado',
      nextPaymentDueLabel: 'vence em {{date}}',
      overpaidLabel: 'Pago a maior',
      overpaidInlineLabel: 'pagos a maior',
      overpaidHint: 'Diferença a recuperar do fornecedor ou a compensar em conta futura.',
      netPayableLabel: 'Valor líquido a pagar',
      netBillAmountLabel: 'Valor líquido da conta',
      paidAmountLabel: 'Valor pago',
      remainingPayableLabel: 'Saldo a pagar',
      recurringNetVariationMatch: '= valor esperado',
      recurringNetVariationAbove: '+{{percent}}% vs esperado',
      recurringNetVariationBelow: '-{{percent}}% vs esperado',
      recurringNetVariationMatchAverage: '= média',
      recurringNetVariationAboveAverage: '+{{percent}}% vs média',
      recurringNetVariationBelowAverage: '-{{percent}}% vs média',
      recurringNetVariationOpenOccurrencesAria:
        'Abrir detalhes e destacar contas desta recorrência',
      descriptionLabel: 'Descrição',
      descriptionEmpty: 'Sem descrição',
      vendorBillNumberLabel: 'Número do documento do fornecedor',
      vendorBillNumberEmpty: 'Não informado',
      descriptionOriginalText: 'Texto original',
      descriptionPeriodLabel: 'Período',
      descriptionReceiptLabel: 'Recibo',
      datesLabel: 'Datas',
      issueDateLabel: 'Emissão',
      issueDateTooltip: 'Data em que o documento fiscal/comprovante foi criado',
      competencyDateLabel: 'Competência',
      competencyDateTooltip:
        'Data contábil, será usada para o registro de provisionamento no livro razão',
      managerialDateLabel: 'Gerencial',
      managerialDateTooltip:
        'Geralmente igual à data contábil, representa a data que será utilizada em relatórios gerenciais',
      paymentScheduledDateLabel: 'Programado para pagar',
      paymentDocumentDueDateLabel: 'Vencimento',
      nextPaymentLabel: 'Próximo pagamento',
      viewAllPaymentsLink: 'Ver todos os pagamentos',
      secondaryDatesExpandAria: 'Mostrar data gerencial e emissão',
      secondaryDatesCollapseAria: 'Ocultar data gerencial e emissão',
      paymentDatesClosestTooltip:
        'Com várias parcelas, mostramos a data agendada mais próxima de hoje.',
      reconciledPaymentDatesLockedTooltip:
        'Não é possível alterar as datas de um pagamento conciliado.',
      originLabel: 'Origem',
      originManual: 'Lançada manualmente',
      originEmail: 'Recebida por e-mail',
      originWebhook: 'Recebida por webhook',
      originApi: 'Enviada via API',
      originExternalForm: 'Enviada por formulário',
      originDefault: 'Lançada',
      originBy: 'por',
      originOn: 'em {{date}}',
      requestedByLabel: 'Solicitado por',
      viewSourceEmailLink: 'Ver e-mail original',
      viewSourceWebhookLink: 'Ver dados recebidos',
      sourceWebhookDialogTitle: 'Dados recebidos',
      sourceExternalRecord: 'Registro {{id}} no {{provider}}',
      sourceExternalOpen: 'Abrir no {{provider}}',
      sourceWebhookEmpty: 'Este recebimento não tem campos de texto armazenados.',
      sourceWebhookFields: {
        requester: 'Solicitante',
        company: 'Empresa',
        vendor: 'Fornecedor',
        taxId: 'CPF/CNPJ',
        description: 'Descrição',
        amount: 'Valor',
        currency: 'Moeda',
        dueDate: 'Vencimento',
        dueDateAsWritten: 'Vencimento informado',
        scheduledDate: 'Data programada',
        paymentMethod: 'Forma de pagamento',
        pixKey: 'Chave Pix',
        barcode: 'Linha digitável',
        category: 'Categoria',
        department: 'Departamento',
        notes: 'Observações',
        attachments: 'Anexos',
      },
      sourceEmailDialogTitle: 'E-mail original',
      sourceEmailFrom: 'De:',
      sourceEmailReceived: 'Recebido em:',
      sourceEmailNoSubject: '(Sem assunto)',
      sourceEmailLoading: 'Carregando…',
      sourceEmailLoadError: 'Não foi possível carregar o e-mail.',
      sourceEmailEmptyBody: 'Este e-mail não tem corpo de texto ou HTML armazenado.',
      viewSourceApiPayloadLink: 'Ver payload da API',
      sourceApiPayloadDialogTitle: 'Payload enviado via API',
      sourceApiPayloadExternalReference: 'Referência externa:',
      sourceApiPayloadSubmittedAt: 'Enviado em:',
      sourceApiPayloadLoading: 'Carregando…',
      sourceApiPayloadLoadError: 'Não foi possível carregar o payload da API.',
      taxesLabel: 'Impostos',
      emptyValue: '—',
      billTypeLabel: 'Tipo',
      viewVendor: 'Ver fornecedor',
      taxIdValid: 'Documento válido',
      taxIdInvalid: 'Documento inválido',
      taxIdForeign: 'Fornecedor estrangeiro',
      taxIdGovernment: 'Órgão público',
      taxIdUnknown: 'Documento não informado',
      vendorBillsCount_one: '{{count}} conta vinculada',
      vendorBillsCount_other: '{{count}} contas vinculadas',
      changeVendor: 'Trocar fornecedor',
      entityLabel: 'Conta de',
      entityFallback: 'Entidade não informada',
      changeEntity: 'Trocar entidade',
      editEntity: 'Editar entidade',
      finishEditEntity: 'Concluir edição da entidade',
      entityChangeClassificationDialog: {
        title: 'Limpar classificação e trocar entidade?',
        description:
          'Esta conta será movida para {{entityName}} e sua classificação atual será excluída. Campos customizados com o mesmo nome em {{entityName}} mantêm o valor. Essa ação não pode ser desfeita.',
        leavesApproval:
          'A conta está aguardando aprovação: a troca cancela a aprovação e ela precisará ser enviada de novo.',
        customFieldsLoading: 'Conferindo campos customizados...',
        customFieldsError: 'Não foi possível conferir quais campos customizados serão mantidos.',
        carriedTitle: 'Campos customizados mantidos',
        droppedTitle: 'Campos customizados removidos',
        droppedVaryingValue: 'vários valores',
        dropReasons: {
          NO_MATCHING_FIELD: 'não existe em {{entityName}}',
          TYPE_MISMATCH: 'em {{entityName}} o campo é de outro tipo',
          NO_MATCHING_VALUE: 'a lista de {{entityName}} não tem essa opção',
          SPLIT_VALUES: 'rateado entre vários valores',
          VARYING_VALUES: 'valor diferente por item ou rateio',
          AMBIGUOUS_MATCH: 'mais de um campo ou opção parecido em {{entityName}}',
        },
        missingRequired:
          'Campos obrigatórios de {{entityName}} que ficarão vazios: {{fields}}. Preencha antes de enviar para aprovação.',
        cancel: 'Manter entidade atual',
        confirm: 'Limpar classificação e trocar entidade',
        confirming: 'Limpando classificação...',
        processingTitle: 'Atualizando entidade',
        processingDescription:
          'Aguarde enquanto limpamos a classificação e atualizamos a entidade da conta.',
        successTitle: 'Entidade atualizada',
        successDescription: 'A conta foi movida para {{entityName}}.',
        successResult:
          'A classificação anterior foi limpa e a entidade foi atualizada. Os campos customizados com o mesmo nome foram mantidos.',
        done: 'Concluir',
        errorTitle: 'Não foi possível atualizar a entidade',
        errorDescription: 'A alteração não foi concluída. Revise o erro e tente novamente.',
        errorFallback: 'Ocorreu um erro inesperado ao atualizar a entidade.',
        close: 'Fechar',
        retry: 'Tentar novamente',
      },
      descriptionShowMore: 'Ver mais',
      descriptionShowLess: 'Ver menos',
    },
    activities: {
      title: 'Atividades',
      empty: 'Nenhum comentário ainda.',
      commentPlaceholder: 'Escreva um comentário...',
      submitCommentAria: 'Enviar comentário',
    },
    activitiesTimeline: {
      classificationModeBill: 'Conta inteira',
      classificationModeLine: 'Por item',
      reconciledAtPrefix: 'Em {{datetime}}',
      paymentMismatch: {
        PAYMENT_CANCELLED: 'Cancelou o pagamento de {{amount}} na conciliação',
        REMAINDER_SCHEDULED: 'Agendou o saldo de {{amount}} na conciliação',
        PAYMENT_RESTORED: 'Restaurou o pagamento de {{amount}} ao desfazer a conciliação',
        REMAINDER_REMOVED: 'Removeu o saldo de {{amount}} ao desfazer a conciliação',
        bankAmount: 'Valor no banco: {{amount}}',
        justification: 'Justificativa: {{text}}',
        bankCancellationConfirmed: 'Confirmou que cancelou o pagamento no banco',
      },
      singleAction: 'realizou uma atividade',
      groupedActions: 'realizou {{count}} atividades',
      emptyValue: '—',
      customBreakdown: 'Rateio personalizado',
      customBreakdownItem: 'Rateio {{number}}',
      sources: {
        user: 'Usuário',
        ai: 'IA',
        system: 'Sistema',
      },
      allocation: {
        simple: 'Simples',
        customBreakdown: 'Rateio personalizado',
      },
      vendorRuleDivergence: {
        detected: 'Divergência com a regra do fornecedor detectada em {{summary}}',
        detectedPlain: 'Divergência com a regra do fornecedor detectada',
        cleared: 'Divergência com a regra do fornecedor encerrada',
        kept: 'Valores do documento mantidos',
        applied: 'Regra aplicada: {{summary}}',
        appliedPlain: 'Regra aplicada',
        newVersion: 'Nova versão da regra a partir de {{competence}}',
        newVersionPlain: 'Nova versão da regra a partir da competência desta conta',
        updated: 'Atualizou a divergência com a regra do fornecedor',
        withholdingItem: '{{field}} (documento {{document}} · regra {{rule}})',
        appliedItem: '{{field}} {{value}}',
        emptyValue: 'não informado',
        yes: 'Sim',
        no: 'Não',
        fields: {
          irrfWithholdingAmount: 'IRRF',
          pisWithholdingAmount: 'PIS',
          cofinsWithholdingAmount: 'COFINS',
          csllWithholdingAmount: 'CSLL',
          inssWithholdingAmount: 'INSS',
          issWithholdingAmount: 'ISS',
          generatesPisCredit: 'crédito de PIS',
          generatesCofinsCredit: 'crédito de COFINS',
          creditType: 'natureza do crédito',
          pisCofinsCreditRevenueUse: 'destinação do crédito',
        },
      },
      fields: {
        allocationType: 'Tipo de alocação',
        glAccounts: 'Conta contábil',
        departments: 'Departamento',
        costCenters: 'Centro de custo',
        managerialAccounts: 'Conta gerencial',
        segments: 'Segmento',
        customBreakdown: 'Rateio personalizado',
        notes: 'Observações',
        source: 'Origem',
      },
    },
    netAmountEdit: {
      title: 'Editar valor líquido a pagar',
      description: 'Informe o novo valor líquido e selecione o motivo da alteração.',
      amountLabel: 'Novo valor líquido',
      reasonLabel: 'Motivo da alteração',
      reasonAiError: 'A IA cometeu um erro na extração do valor',
      reasonAdjustments: 'Há descontos, juros ou multa a serem aplicados sobre o valor extraído',
      reasonOther: 'Outros — justifique',
      justificationLabel: 'Justificativa',
      justificationPlaceholder: 'Descreva o motivo da alteração…',
      conciliationNotice:
        'No momento da conciliação do pagamento será possível lançar o desconto/juros/multa, desta forma, o lançamento contábil será realizado corretamente.',
      cancel: 'Cancelar',
      save: 'Salvar',
      success: 'Valor líquido alterado',
      editButton: 'Editar',
      impliedRatePreview:
        'Para chegar a {{amount}} mantendo {{originalCurrency}} {{originalAmount}}, o câmbio será {{rate}}. Ao salvar, o valor original será preservado.',
    },
    grossAmountEdit: {
      title: 'Editar valor bruto',
      description: 'Informe o novo valor bruto e selecione o motivo da alteração.',
      amountLabel: 'Novo valor bruto',
      reasonLabel: 'Motivo da alteração',
      reasonAiError: 'A IA cometeu um erro na extração do valor',
      reasonAdjustments: 'Há descontos, juros ou multa a serem aplicados sobre o valor extraído',
      reasonOther: 'Outros — justifique',
      justificationLabel: 'Justificativa',
      justificationPlaceholder: 'Descreva o motivo da alteração…',
      conciliationNotice:
        'No momento da conciliação do pagamento será possível lançar o desconto/juros/multa, desta forma, o lançamento contábil será realizado corretamente.',
      cancel: 'Cancelar',
      save: 'Salvar',
      success: 'Valor bruto alterado',
      editButton: 'Editar',
      impliedRatePreview:
        'Para chegar a {{amount}} mantendo {{originalCurrency}} {{originalAmount}}, o câmbio será {{rate}}. Ao salvar, o valor original será preservado.',
    },
    editPending: {
      rate: 'Informe a taxa de câmbio.',
      reason: 'Informe o motivo.',
      currency: 'Escolha a moeda.',
      amount: 'Informe um valor maior que zero.',
      unchanged: 'Mude o valor ou a moeda.',
      reasonChoice: 'Escolha o motivo.',
      justification: 'Descreva o motivo.',
    },
    currencyEdit: {
      title: 'Alterar moeda',
      confirmTitle: 'Confirmar moeda',
      alert:
        'A moeda do documento não foi reconhecida ("{{rawValue}}"). Confirme a moeda para aprovar, pagar e contabilizar a conta.',
      description: 'Escolha a moeda impressa no documento e informe o valor como aparece nele.',
      pendingReviewNotice:
        'A moeda do documento não foi reconhecida ("{{rawValue}}"). Confirme a moeda para liberar a aprovação, o pagamento e a contabilização.',
      currencyLabel: 'Moeda do documento',
      currencyAria: 'Moeda do documento',
      currencyPlaceholder: 'Escolha a moeda',
      suggestedHint:
        'Moeda sugerida pelo endereço, banco ou CNPJ/tax ID do emissor. Confira no documento antes de confirmar.',
      amountLabel: 'Valor no documento',
      functionalHint: 'O valor fica em {{currency}}, sem conversão.',
      foreignHint:
        'O valor fica em {{currency}}. O sistema converte para {{functionalCurrency}} pela PTAX da data de competência da nota (ou da emissão, sem competência).',
      reasonLabel: 'Motivo',
      reasonPlaceholder: 'Ex.: a fatura é de um fornecedor dos EUA e o valor está em dólares.',
      cancel: 'Cancelar',
      save: 'Salvar',
      success: 'Moeda alterada',
      confirm: 'Confirmar moeda',
      editButton: 'Alterar moeda',
    },
    originalAmountEdit: {
      title: 'Editar valor original',
      description: 'Informe o novo valor original e selecione o motivo da alteração.',
      amountLabel: 'Novo valor original',
      currencyLabel: 'Moeda',
      currencyAria: 'Moeda do valor original',
      reasonLabel: 'Motivo da alteração',
      reasonAiError: 'A IA cometeu um erro na extração do valor',
      reasonAdjustments: 'Há ajustes a serem aplicados sobre o valor extraído',
      reasonOther: 'Outros — justifique',
      justificationLabel: 'Justificativa',
      justificationPlaceholder: 'Descreva o motivo da alteração…',
      conciliationNotice:
        'Descontos, juros e multas devem ser lançados durante a conciliação para preservar a contabilização correta.',
      cancel: 'Cancelar',
      save: 'Salvar',
      success: 'Valor original alterado',
      editButton: 'Editar',
      editButtonAria: 'Editar valor original',
    },
    netAmountChangeBanner: {
      justificationLabel: 'Justificativa',
    },
    netAmountChangeAlert: {
      introByUser:
        'O valor líquido a pagar foi alterado em relação ao valor derivado do documento por {{user}}.',
      unknownUser: 'Um usuário',
    },
    grossAmountChangeAlert: {
      introByUser:
        'O valor bruto foi alterado em relação ao valor derivado do documento por {{user}}.',
      unknownUser: 'Um usuário',
    },
    billMode: {
      success: 'Tipo da conta alterado',
    },
    documentActions: {
      uploadButton: 'Enviar documentos',
      detachButtonAria: 'Excluir {{name}}',
      detachTitle: 'Excluir documento?',
      detachDescription:
        'O documento "{{name}}" será excluído e não ficará mais disponível para esta conta.',
      detachConfirm: 'Excluir',
      detachSuccess: 'Documento excluído com sucesso',
      detachError: 'Não foi possível excluir o documento',
    },
    documentUpload: {
      title: 'Upload de documentos',
      modalDescription: 'Envie arquivos para extracao e criacao de contas a pagar.',
      processWithAiLabel: 'Processar com IA',
      processWithAiDescription:
        'Ativado: extrai dados e reconcilia com esta conta. Desativado: mantém apenas como histórico.',
      filesFallbackName: 'Arquivos',
      loadingEntities: 'Carregando entidades...',
      noEntityAvailable: 'Nenhuma entidade disponível',
      clearSelection: 'Limpar',
      addInstructionsLink: '(Opcional) Adicionar instruções',
      instructionsLabel: 'Instruções para processamento',
      instructionsPlaceholder:
        'Ex: Esta é uma conta de energia da CEMIG, a segunda página contém o boleto',
      uploadButton: 'Enviar {{count}} arquivo(s)',
      uploading: 'Enviando...',
      noFiles: 'Selecione arquivos para enviar',
      success: '{{count}} documento(s) enviado(s) com sucesso',
      duplicates: '{{count}} documento(s) duplicado(s)',
      duplicatesWithNames: '{{count}} documento(s) duplicado(s): {{names}}',
      duplicatesExistingBillsDescription: 'Já vinculado à(s) conta(s): {{billIds}}',
      duplicatesLinkedToCurrentBill:
        '{{count}} documento(s) duplicado(s) vinculado(s) a esta conta.',
      duplicatesLinkFailed:
        '{{count}} documento(s) duplicado(s) já existe(m), mas não foi possível vincular automaticamente. Use a opção "Vincular a esta conta" no painel de processamento.',
      failed: '{{count}} documento(s) falharam: {{errors}}',
      error: 'Falha ao enviar documentos',
      aiProcessing: 'Documento sendo processado pelo agente de contas a pagar',
    },
    recurringSection: {
      sectionTitle: 'Recorrência',
      patternIdentifiedLabel: 'Análise da Alchemy',
      stateNone: 'Nenhuma configurada',
      stateSuggested: 'Sugestão pendente',
      stateLinked: 'Vinculada · {{frequency}}',
      stateLinkedLoading: 'Recorrência vinculada',
      linkedDetailsLoading: 'Carregando detalhes...',
      emptyHelp:
        'Contas recorrentes reservam orçamento a cada período e classificam automaticamente as próximas cobranças.',
      suggestionTitle: 'Isto parece uma cobrança recorrente',
      suggestionTitleMatching: 'Esta conta parece pertencer a uma recorrente que você já tem',
      suggestionTitleMultiple: '{{count}} recorrentes deste fornecedor podem receber esta conta',
      suggestionChoose: 'Escolha qual período esta cobrança consome.',
      suggestionCandidateSummary:
        '{{frequency}} · esperado {{amount}} por período · {{count}} contas vinculadas',
      suggestionSameVendor: 'Mesmo fornecedor',
      suggestionLastCharge: 'Última cobrança {{period}}',
      suggestionPeriodWithoutBill: '{{period}} sem conta',
      suggestionPeriodOpen: '{{period}} em aberto',
      suggestionLinkImpactVariance:
        'Vinculando, esta conta consome o período de {{period}}. O valor está {{amount}} {{direction}} do esperado ({{percent}}%) — a variação fica registrada na recorrente.',
      suggestionLinkImpactNoVariance:
        'Vinculando, esta conta consome o período de {{period}} e a ocorrência fica registrada na recorrente.',
      suggestionVariationAbove: 'acima',
      suggestionVariationBelow: 'abaixo',
      suggestionNoMatchDetail:
        'Nenhuma recorrente deste fornecedor foi encontrada. Criar reserva de {{amount}} por período no orçamento.',
      linkExistingTemplateAria: 'Vincular à recorrência {{name}}',
      linkThisSuggestion: 'Vincular a esta',
      linkSelectedSuggestion: 'Vincular à selecionada',
      openSuggestedTemplateAria: 'Abrir recorrência {{name}}',
      createAnotherRecurring: 'Criar outra recorrente',
      linkExistingAction: 'Vincular a uma existente',
      notRecurringAction: 'Não é recorrente',
      activeStatus: 'Ativa',
      viewRecurringAction: 'Ver recorrência',
      unlinkAction: 'Desvincular',
      expectedAmountLabel: 'Valor esperado',
      nextOccurrenceLabel: 'Próxima',
      noNextOccurrence: 'Sem próxima data',
      frequencyDaySuffix: ' · dia {{day}}',
      viewRecurringLink: 'Ver {{count}} conta(s) recorrente(s) deste fornecedor',
      selectTemplateLabel: 'Recorrência',
      selectTemplatePlaceholder: 'Selecione uma recorrência',
      selectorSearchPlaceholder: 'Digite para buscar recorrência...',
      selectorEmpty: 'Nenhuma recorrência encontrada.',
      selectorNotFound: 'Nenhuma recorrência corresponde à busca.',
      selectorTypeToSearch: 'Digite para buscar recorrências disponíveis.',
      selectorSubtitleWithEndDate: '{{amount}} • Até {{date}}',
      selectorSubtitleNoEndDate: '{{amount}} • Sem fim definido',
      clearTemplateAria: 'Limpar recorrência selecionada',
      createNewOption: 'Criar nova conta recorrente',
      createPrompt:
        'Crie um template de recorrência para vincular esta conta e reservar orçamento.',
      createRecurringButton: 'Criar conta recorrente',
      lineItemLinkBlocked:
        'Contas com classificação por item não podem ser vinculadas a uma recorrência. Altere para classificação inteira primeiro.',
      editTemplateButton: 'Editar',
      editTemplateAria: 'Editar template de recorrência selecionado',
      inactiveTemplateWarning: 'Conta recorrente inativa — comprometimentos futuros removidos',
      recurringNameLabel: 'Nome da conta recorrente',
      recurringNamePlaceholder: 'Conta recorrente',
      frequencyLabel: 'Frequência',
      selectFrequencyPlaceholder: 'Selecione',
      frequency: {
        WEEKLY: 'Semanal',
        MONTHLY: 'Mensal',
        BIMONTHLY: 'Bimestral',
        QUARTERLY: 'Trimestral',
        SEMESTERLY: 'Semestral',
        YEARLY: 'Anual',
      },
      dueDayLabel: 'Dia do vencimento',
      expectedDueDayOfMonthLabel: 'Dia esperado de vencimento',
      expectedCompetencyDayOfMonthLabel: 'Dia esperado da competência',
      recurringAmountLabel: 'Valor recorrente esperado',
      periodLimitAmountLabel: 'Limite por período (orçamento)',
      classificationReuseHelp:
        'Ao salvar, a classificação desta conta será copiada para o template e usada nos compromissos de orçamento.',
      commitmentPreview:
        'Prévia de compromisso: {{count}} períodos × {{periodAmount}} = {{total}} reservados',
      recurrenceTypeLabel: 'Tipo de recorrência',
      recurrenceTypeFixedTitle: 'Fixa',
      recurrenceTypeFixedDescription: 'Valor contratado. Variação compara vs. valor esperado.',
      recurrenceTypeVariableTitle: 'Variável',
      recurrenceTypeVariableDescription: 'Valor muda. Variação compara vs. média recente.',
      variableComparisonMonthsLabel: 'Comparar últ. N meses',
      variableComparisonMonthsTooltip:
        'O sistema usará esse número de meses para comparar a variação de cada conta',
      variableComparisonMonthsTooltipAria: 'Ajuda: meses para comparação de variação',
      durationLabel: 'Duração',
      durationInfinite: 'Sem fim definido',
      durationInfiniteHint: 'contrato contínuo',
      durationFor: 'Por',
      durationMonths: 'meses',
      durationUntilDate: 'Até data definida',
      occurrencesHeading: 'Contas dessa recorrência',
      occurrencesCountBadge: '{{count}} contas',
      occurrencesColumnMonthYear: 'Mês/Ano',
      occurrencesColumnStatus: 'Status',
      occurrencesColumnVariation: 'Variação',
      occurrencesColumnAmount: 'Valor',
      occurrenceVariationMatch: '= 0%',
      occurrenceVariationAbove: '+{{percent}}%',
      occurrenceVariationBelow: '-{{percent}}%',
      variationComparisonTooltipFixed: 'vs valor base de {{amount}}',
      variationComparisonTooltipVariable: 'vs média últimos {{count}} meses de {{amount}}',
      variationComparisonTooltipCurrentBill: 'vs conta atual de {{amount}}',
      selectDatePlaceholder: 'Selecionar data',
      templateDetailsExpandAria: 'Mostrar vínculo ao template e detalhes da recorrência',
      templateDetailsCollapseAria: 'Ocultar vínculo ao template e detalhes da recorrência',
      recurringDetailsExpandAria: 'Mostrar detalhes da conta recorrente',
      recurringDetailsCollapseAria: 'Ocultar detalhes da conta recorrente',
      recurringDetailsExpandLabel: 'Outras ocorrências',
      recurringDetailsCollapseLabel: 'Ver menos',
      openOccurrenceBillAria: 'Abrir esta ocorrência em nova aba',
    },
  },
  withholdings: {
    sectionTitle: 'Retenções & Créditos',
    socialContributions: 'Contribuições sociais retidas',
    reviewRequired: 'Revise o tipo de serviço e retenções.',
    reviewRequiredSubtitle:
      'Caso necessário, use Ajustar valores para editar retenções individualmente',
    aggregateReviewDescription:
      'Valor agregado informado na nota. Revise a divisão entre PIS, COFINS e CSLL antes da aprovação; este valor não é CSLL isoladamente.',
    meiBadge: 'Não aplicável - MEI',
    toggleSectionAria: 'Alternar Retenções de impostos',
    meiMessage:
      'Fornecedor MEI — isento de retenções na fonte e sem direito a créditos tributários.',
    payableAmount: 'Valor a pagar',
    serviceTypeTitle: 'Tipo de Serviço',
    serviceTypePlaceholder: 'Digite para buscar o tipo de serviço',
    serviceTypeDisabled: 'Retenções desativadas — tipo de serviço não é obrigatório.',
    calculatedTitle: 'Retenções Calculadas',
    toggleLabel: 'Aplicar retenções',
    toggleAria: 'Habilitar retenções',
    recalculateAria: 'Recalcular retenções (exceto ISS)',
    recalculateLabel: 'Recalcular (exceto ISS)',
    editDisableAria: 'Desativar edição de retenções',
    editEnableAria: 'Editar retenções',
    recalculateButton: 'Recalcular e salvar',
    recalculateSaved: 'Retenções recalculadas e salvas',
    recalculateSaveError:
      'Não foi possível recalcular e salvar as retenções. Confira o tipo de serviço e os valores da NFS-e e tente novamente.',
    recalculateConfirm: {
      title: 'Recalcular as retenções?',
      dropsRetentions:
        'O recálculo diminui as retenções desta conta. Os valores que você digitou serão substituídos.',
      breaksPayments:
        'Depois do recálculo, o valor líquido não vai bater com os pagamentos ({{payments}}).',
      current: 'Atual',
      recalculated: 'Recalculado',
      lines: {
        irrf: 'IRRF',
        pis: 'PIS',
        cofins: 'COFINS',
        csll: 'CSLL',
        inss: 'INSS',
        iss: 'ISS',
        iva: 'IVA',
      },
      net: 'Valor líquido',
      keep: 'Manter valores',
      recalculate: 'Recalcular mesmo assim',
    },
    recalculateErrors: {
      AP_BILL_RETENTIONS_ALREADY_RECOGNIZED:
        'O PIS, a COFINS e a CSLL desta conta já foram reconhecidos no pagamento. Os valores não podem mais mudar.',
      AP_BILL_WITHHOLDINGS_LOCKED: 'As retenções só podem ser recalculadas antes da aprovação.',
    },
    retainedDespiteExemption:
      'Retido na nota, mas o fornecedor consta como {{regime}}. Confira o regime do fornecedor.',
    vendorRuleOverridesExemption:
      'Retido pela regra do fornecedor, que prevalece sobre a dispensa do fornecedor {{regime}}.',
    ruleOverridesExemption:
      'Fornecedor do Simples Nacional: a regra do fornecedor retém {{tax}} {{rate}} mesmo assim.',
    ruleOverridesMeiExemption:
      'Fornecedor MEI: a regra do fornecedor retém {{tax}} {{rate}} mesmo assim.',
    serviceTypeNotRecalculated:
      'A conta já tem pagamento. As retenções não foram recalculadas para o novo tipo de serviço. Use "Recalcular e salvar" se quiser recalcular.',
    adjustValuesButton: 'Ajustar valores',
    finishAdjustingButton: 'Concluir ajuste',
    headerAppliedSummary: '{{taxes}} · total {{total}}',
    headerNone: 'Nenhuma',
    emptyTitle: 'Esta conta não tem retenções.',
    emptyDescription:
      'Retenções na fonte se aplicam a serviços prestados por pessoa jurídica. Informe o tipo de serviço quando for o caso.',
    informServiceType: 'Digite para buscar o tipo de serviço',
    retainedTaxesTitle: 'Tributos retidos',
    rulesLoading: 'Carregando regras fiscais…',
    ruleBadgeNotApplicable: 'Sem {{tax}}',
    ruleBadgeUnknown: '{{tax}} a definir',
    ruleBadgeRate: '{{tax}} {{rate}}',
    derivation: '{{base}} × {{rate}}',
    derivationBaseUnavailable: '{{base}} não disponível; valor informado',
    derivationUnavailable: 'Valor informado; regra de cálculo não disponível',
    issRateMissing: 'Alíquota do município competente ainda não definida',
    toDefine: 'a definir',
    nonApplicableSummary: '{{count}} tributos não se aplicam a este serviço.',
    viewWhich: 'Ver quais',
    hideWhich: 'Ocultar',
    valuesTitle: 'Valores',
    nonApplicableReasons: {
      vendorRegime: 'Fornecedor {{regime}} isento desta retenção.',
      pcc: 'Fora da lista de serviços sujeitos à retenção da Lei 10.833/2003.',
      inss: 'O serviço não está marcado como cessão de mão de obra ou empreitada.',
      irrf: 'O tipo de serviço não possui natureza sujeita a IRRF.',
      issDocument: 'O cálculo automático reconciliou a nota sem retenção de ISS pelo tomador.',
      noRule: 'Não há regra aplicável para este tipo de serviço.',
      vendorRule: 'A regra do fornecedor não retém {{tax}}.',
      belowMinimum: 'IRRF abaixo do valor mínimo de retenção, então não há retenção.',
      divergencePending: 'A divergência com a regra do fornecedor ainda não foi resolvida.',
    },
    obligation: {
      summary: '{{document}} · {{deadline}}',
      document: {
        DARF: 'DARF {{code}}',
        GPS_DCTFWEB: 'GPS/DCTFWeb {{code}}',
        MUNICIPAL_GUIDE: 'Guia municipal',
      },
      deadline: {
        LAST_BUSINESS_DAY_SECOND_TEN_DAY_PERIOD_NEXT_MONTH:
          'recolhimento até o último dia útil do 2º decêndio do mês seguinte',
        DAY_20_NEXT_MONTH_PREVIOUS_BUSINESS_DAY: 'recolhimento até o dia 20 do mês seguinte',
        MUNICIPAL_RULE: 'prazo conforme o município competente',
      },
    },
    issResolutionDescription: 'Confirme o município competente e a alíquota aplicável.',
    notApplied: 'Não aplicado',
    emptyValue: '—',
    vendorTaxRegime: {
      mei: 'MEI',
      simples: 'Simples Nacional',
    },
    tax: {
      irrf: 'IRRF',
      pis: 'PIS',
      cofins: 'COFINS',
      csll: 'CSLL',
      iss: 'ISS',
      inss: 'INSS',
      pcc: 'PIS/COFINS/CSLL',
    },
    taxRuleIss: '{{taxType}} (varia por município)',
    taxRuleDefault: '{{taxType}} ({{rate}})',
    calculationBase: {
      totalValue: 'Valor bruto',
      netValue: 'Valor líquido',
      serviceValue: 'Valor do serviço',
      laborValue: 'Valor de mão-de-obra',
    },
    exemptBadge: 'Isento',
    notApplicable: 'Não aplicável',
    validationUnavailable: 'Sem validação disponível.',
    exemptTitle: '{{taxType}} (Isento)',
    exemptDescription: 'Fornecedor {{regime}} — isento de retenção de IRRF.',
    exemptMismatch: 'Atenção: NF apresenta valor de {{value}}, mas deveria ser R$ 0,00.',
    issTitle: 'ISS (varia por município)',
    issExtractedLabel: 'Extraído NF:',
    issValidationNote: 'Validação: valor da NF considerado correto (alíquota varia por município).',
    baseLabel: 'Base',
    baseUnavailable: '{{base}} (indisponível)',
    expectedLabel: 'Esperado',
    extractedLabel: 'Extraído NF',
    requestedBaseLabel: ' • regra pede: {{requestedBase}}',
    diffLabel: ' • Dif: {{diff}}',
    diffMissingLabel: ' • Dif: (NF sem valor)',
    ruleMissing: 'Sem regra para {{taxType}} neste tipo de serviço.',
    ruleMissingMismatchSuffix: ' (validação falhou)',
    validationTolerance: 'Tolerância de validação: ±{{cents}} centavo(s).',
    validationOk: 'Validação OK',
    validationMismatch: 'Validação divergente',
    grossAmount: 'Valor bruto',
    totalWithholdings: 'Total retenções',
    netPayable: 'Valor líquido a pagar',
    creditType: {
      label: 'Natureza do crédito de PIS/COFINS',
      placeholder: 'Classifique a aquisição',
      options: {
        BASIC: 'Compra comum',
        FIXED_ASSET: 'Ativo imobilizado',
        PRESUMED: 'Crédito presumido (tratamento específico)',
        IMPORT: 'Importação (tratamento específico)',
      },
      description:
        'A natureza da aquisição define o tratamento fiscal; habilitar créditos não confirma, por si só, sua elegibilidade. Importação e crédito presumido exigem fluxos específicos.',
      fixedAssetHint:
        'O valor exibido é o potencial da compra. A disponibilidade depende da forma de apropriação e da evidência registradas na revisão do ativo; a aprovação da conta não libera automaticamente todo o crédito. Atualmente, o fluxo de imobilizado aceita somente uso direto em receitas não cumulativas. Informe a destinação e sua evidência abaixo.',
    },
    taxCreditsTitle: 'Créditos PIS/COFINS',
    taxCreditsFixedAssetPotentialTitle: 'Potencial de crédito PIS/COFINS do imobilizado',
    taxCreditsToggleAria: 'Habilitar créditos PIS/COFINS',
    taxCreditsDescription: 'Esta aquisição gera crédito de PIS/COFINS (regime não-cumulativo)',
    taxCreditPis: 'Crédito PIS',
    creditRevenueUse: {
      label: 'Destinação do crédito em receitas mistas',
      expand: 'Classificar destinação em receitas mistas',
      collapsedDescription:
        'Use somente quando a apuração do período combinar receitas cumulativas e não cumulativas.',
      placeholder: 'Classificar uso do crédito',
      description:
        'Esta classificação define o rateio do crédito entre receitas mistas; ela não define se a compra gera crédito.',
      evidenceCode: 'Código da evidência',
      evidenceDescription: 'Descrição da evidência',
      evidenceRequired:
        'Informe o código e a descrição da evidência da destinação do crédito antes de continuar.',
      options: {
        DIRECT_CUMULATIVE: 'Uso direto em receita cumulativa',
        DIRECT_NON_CUMULATIVE: 'Uso direto em receita não cumulativa',
        COMMON: 'Uso comum entre regimes',
      },
    },
    taxCreditCofins: 'Crédito COFINS',
    taxCreditsTotal: 'Total créditos a apropriar',
    // ALC-4558: origem dos valores quando uma regra do fornecedor preencheu o crédito.
    vendorCredit: {
      origin: 'Preenchido pela regra do fornecedor (vigente desde {{since}})',
      generates: 'Gera crédito',
      generatesYes: 'Sim',
      generatesNo: 'Não',
      rates: 'Alíquotas',
      ratesValue: 'PIS {{pis}} / COFINS {{cofins}}',
      revenueUseSetByHand:
        'A destinação do crédito em receitas mistas foi definida manualmente e a regra do fornecedor não a altera.',
    },
    // ALC-4559: o documento difere da regra do fornecedor; a conta não segue para aprovação.
    vendorRuleDivergence: {
      regionLabel: 'Divergência com a regra do fornecedor',
      title: 'O documento difere da regra do fornecedor',
      description:
        'A conta só pode ser enviada para aprovação depois que você escolher quais valores valem. A comparação usa a regra vigente desde {{since}}.',
      descriptionPending:
        'A divergência impede a aprovação desta conta até você escolher quais valores valem. A comparação usa a regra vigente desde {{since}}.',
      columns: {
        field: 'Item',
        document: 'Documento',
        rule: 'Regra do fornecedor',
      },
      fields: {
        irrfWithholdingAmount: 'IRRF retido',
        pisWithholdingAmount: 'PIS retido',
        cofinsWithholdingAmount: 'COFINS retida',
        csllWithholdingAmount: 'CSLL retida',
        inssWithholdingAmount: 'INSS retido',
        generatesPisCredit: 'Gera crédito de PIS',
        generatesCofinsCredit: 'Gera crédito de COFINS',
        creditType: 'Natureza do crédito',
        pisCofinsCreditRevenueUse: 'Destinação do crédito',
      },
      amountWithRate: '{{amount}} ({{rate}})',
      notWithheld: 'Não retém',
      emptyValue: 'Não informado',
      yes: 'Sim',
      no: 'Não',
      chooseLabel: 'Como resolver',
      options: {
        KEEP_DOCUMENT: {
          label: 'Manter os valores do documento nesta conta',
          description: 'A regra continua como está para as próximas contas deste fornecedor.',
        },
        APPLY_RULE: {
          label: 'Aplicar a regra do fornecedor',
          description:
            'Os valores da regra substituem os do documento, e o valor a pagar é recalculado.',
        },
        NEW_RULE_VERSION: {
          label: 'Manter os valores do documento e atualizar a regra',
          description:
            'Cria uma nova versão da regra a partir de {{competence}} com os valores do documento. As competências anteriores continuam com a versão atual.',
        },
      },
      confirm: 'Confirmar escolha',
      newVersionUnavailable:
        'Criar uma nova versão da regra a partir de {{competence}} não está disponível: a regra já tem uma versão que começa nesse mês. Ajuste essa versão na aba Regras do fornecedor.',
      scheduledPayment_one: 'O pagamento agendado passa de {{from}} para {{to}}.',
      scheduledPayment_other: 'Os pagamentos agendados passam de {{from}} para {{to}}.',
      paymentBlocksApply:
        'O pagamento {{paymentNumber}}{{paymentMethod}} está {{paymentStatus}} e impede aplicar a regra. Escolha outra opção, ou ajuste esse pagamento antes.',
      paymentStatus: {
        PAID: 'pago',
        PROCESSING: 'em processamento',
        PENDING: 'pendente',
        RECONCILED: 'conciliado',
        FAILED: 'com falha',
        REVERSED: 'estornado',
        SCHEDULED: 'agendado',
      },
      paymentMethod: {
        PIX: 'PIX',
        TED: 'TED',
        DOC: 'DOC',
        BOLETO: 'boleto',
        CREDIT_CARD: 'cartão de crédito',
        DEBIT_CARD: 'cartão de débito',
        GUIA: 'guia',
        CHECK: 'cheque',
        OTHER: 'outro meio',
      },
      cancel: 'Cancelar',
      close: 'Fechar',
      notice: {
        draft: 'Você escolhe os valores de {{fields}} ao enviar para aprovação.',
        pendingApproval: 'A aprovação fica bloqueada até alguém escolher os valores de {{fields}}.',
        choose: 'Escolher valores',
      },
      dialog: {
        description: 'Escolha quais valores valem para esta conta.',
        descriptionSubmit:
          'Antes de enviar para aprovação, escolha quais valores valem. Depois, a conta segue para aprovação.',
        confirmAndSend: 'Confirmar e enviar para aprovação',
      },
      pendingEdits: 'Salve ou descarte as alterações da conta antes de resolver a divergência.',
      noPermission:
        'Você não tem permissão para editar esta conta. Peça a quem pode editá-la para resolver a divergência.',
      success: 'Decisão registrada',
      resolved: {
        KEEP_DOCUMENT: 'Esta conta usa os valores do documento, não os da regra do fornecedor.',
        APPLY_RULE:
          'As retenções e os créditos desta conta estão de acordo com a regra do fornecedor.',
        NEW_RULE_VERSION:
          'A regra do fornecedor passou a usar os valores do documento a partir de {{competence}}.',
      },
      chosenBy: 'Escolhido por {{name}} em {{date}}.',
      chosenOn: 'Escolhido em {{date}}.',
      openVendorRules: 'Ver regras do fornecedor',
      errors: {
        AP_BILL_VENDOR_RULE_DIVERGENCE_STALE:
          'Os valores da conta ou a regra mudaram desde que a página foi aberta. Atualize a conta e escolha de novo.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_NOT_OPEN:
          'Esta divergência já foi resolvida. Atualize a conta para ver a decisão.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_CLEARED:
          'A divergência deixou de existir porque os valores da conta mudaram. Atualize a conta para ver os valores atuais.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_MIXED_CREDIT_FLAGS:
          'Esta conta gera crédito de PIS e de COFINS de formas diferentes, e a regra do fornecedor guarda uma só resposta para os dois. Escolha manter os valores do documento ou aplicar a regra.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_NEW_VERSION_FORBIDDEN:
          'Você não tem permissão para alterar regras do fornecedor. Escolha outra opção ou peça a quem gerencia o conhecimento do fornecedor.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_VERSION_EXISTS:
          'A regra já tem uma versão que começa em {{competence}}. Ajuste essa versão na aba Regras do fornecedor.',
        AP_RETENTION_EDIT_PAYMENT_LOCKED:
          'O pagamento {{paymentNumber}}{{paymentMethod}} já foi pago, enviado ou está numa remessa, então a regra não pode ser aplicada. Os valores da conta continuam como estão.',
        AP_RETENTION_EDIT_PAYMENT_LOCKED_GENERIC:
          'Há um pagamento pago, enviado ou numa remessa, então a regra não pode ser aplicada. Os valores da conta continuam como estão.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_BILL_NOT_EDITABLE:
          'A conta já passou da aprovação e os valores não podem mais mudar.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_RATE_MISMATCH:
          'A alíquota do documento, com duas casas decimais, não dá o mesmo valor retido nesta conta. O mínimo do IRRF também pode mudar o valor. Ajuste a alíquota na aba Regras do fornecedor, atualize a comparação ou escolha outra opção.',
        AP_BILL_PAYMENT_PLAN_AMOUNT_MISMATCH:
          'Aplicar a regra muda o valor a pagar, e esta conta já tem pagamentos programados. Ajuste o cronograma de pagamentos e tente de novo.',
        AP_BILL_RETENTIONS_ALREADY_RECOGNIZED:
          'O PIS, a COFINS e a CSLL desta conta já foram reconhecidos no pagamento, então a regra não pode mais ser aplicada.',
        generic: 'Não foi possível registrar a decisão. Tente de novo em instantes.',
      },
      reload: 'Atualizar conta',
      ruleChanged:
        'A regra do fornecedor mudou depois que esta divergência foi encontrada. A versão em vigor começa em {{since}}.',
      ruleChangedNoVersion:
        'A regra do fornecedor mudou depois que esta divergência foi encontrada. Não há uma versão em vigor para esta competência.',
      refreshComparison: 'Atualizar comparação',
      comparisonUpdated:
        'A regra do fornecedor mudou desde que a tela foi aberta. A comparação foi atualizada; revise e confirme de novo.',
      comparisonCleared:
        'A comparação foi atualizada. A conta agora concorda com a regra do fornecedor.',
    },
    // ALC-4561: ofertas de conhecimento na conta (serviço novo e lembrar).
    knowledgeOffer: {
      regionLabel: 'Oferta de conhecimento do fornecedor',
      loading: 'Carregando a oferta…',
      loadError: 'Não foi possível carregar a oferta. Atualize a conta.',
      thisVendor: 'este fornecedor',
      thisService: 'este serviço',
      rememberDescription:
        'Os valores definidos nesta conta passam a valer para este serviço a partir de {{competence}}. As competências anteriores não mudam.',
      simplesRuleWithholds:
        'Fornecedor do Simples Nacional: a regra do fornecedor retém {{tax}} {{rate}} mesmo assim.',
      meiRuleWithholds: 'Fornecedor MEI: a regra do fornecedor retém {{tax}} {{rate}} mesmo assim.',
      valuesLabel: 'O que será salvo',
      withheld: '{{tax}} retém {{rate}}',
      withheldAtSystemRate: '{{tax}} retém pela alíquota do sistema',
      notWithheld: '{{tax}} não retém',
      creditGenerates: 'Gera crédito de PIS e COFINS',
      creditDoesNotGenerate: 'Não gera crédito de PIS e COFINS',
      creditType: 'Natureza do crédito: {{type}}',
      revenueUse: 'Destinação do crédito: {{use}}',
      save: 'Salvar regra',
      saving: 'Salvando regra…',
      thisBillOnly: 'Só nesta conta',
      dismiss: 'Dispensar',
      rateLabel: 'Alíquota de {{tax}}',
      identifyServiceTitle: 'Informe o tipo de serviço desta conta',
      identifyServiceDescription:
        'Sem o tipo de serviço não há regra para lembrar. Escolha o tipo de serviço nas retenções desta conta.',
      nothingToSave:
        'Esta conta não tem valores para virar regra. Defina a regra na aba Regras do fornecedor.',
      versionExists:
        'A regra já tem uma versão que começa em {{competence}}. Ajuste essa versão na aba Regras do fornecedor.',
      pendingEdits: 'Salve ou descarte as alterações da conta antes de salvar a regra.',
      saved: 'Regra salva a partir de {{competence}}.',
      apply_one: 'Aplicar na conta em rascunho',
      apply_other: 'Aplicar nas {{count}} contas em rascunho',
      applying: 'Aplicando…',
      appliedNone: 'Nenhuma conta em rascunho mudou.',
      applied_one: 'Regra aplicada em {{count}} conta em rascunho.',
      applied_other: 'Regra aplicada em {{count}} contas em rascunho.',
      appliedKept: 'Valores definidos por alguém foram mantidos.',
      appliedNoValueChanged: 'Nenhum valor mudou.',
      divergenceOpened_one: '{{count}} conta em rascunho ficou com divergência para revisar.',
      divergenceOpened_other: '{{count}} contas em rascunho ficaram com divergência para revisar.',
      errors: {
        AP_BILL_KNOWLEDGE_OFFER_NOT_AVAILABLE:
          'Esta oferta não está mais disponível. Atualize a conta.',
        AP_BILL_KNOWLEDGE_OFFER_STALE:
          'Os valores da conta ou a regra mudaram desde que a página foi aberta. Atualize a conta e confira de novo.',
        AP_BILL_KNOWLEDGE_OFFER_VERSION_EXISTS:
          'A regra já tem uma versão que começa em {{competence}}. Ajuste essa versão na aba Regras do fornecedor.',
        AP_BILL_KNOWLEDGE_OFFER_NOTHING_TO_SAVE:
          'Não há valores para salvar. Defina a regra na aba Regras do fornecedor.',
        rate: 'A alíquota precisa ser 0 para não reter, ou ficar entre 0,01% e 100%.',
        accept: 'Não foi possível salvar a regra. Tente de novo em instantes.',
        keep: 'Não foi possível manter os valores só nesta conta. Tente de novo em instantes.',
        dismiss: 'Não foi possível dispensar a oferta. Tente de novo em instantes.',
        apply: 'Não foi possível aplicar a regra nas contas em rascunho. Tente de novo.',
        applyPartial_one: '{{count}} conta em rascunho não recebeu a regra. Tente de novo.',
        applyPartial_other: '{{count}} contas em rascunho não receberam a regra. Tente de novo.',
      },
      reload: 'Atualizar conta',
      saveAndSend: 'Salvar regra e enviar',
      thisBillOnlyAndSend: 'Só nesta conta e enviar',
      sendWithoutRule: 'Enviar sem regra',
      thisBillOnlyTooltip:
        'Usa estes valores só nesta conta. A regra do fornecedor não muda. A próxima conta com valores diferentes da regra mostra a oferta de novo.',
      dismissTooltip:
        'Para de oferecer esta regra para {{vendor}} neste serviço, em todas as contas da entidade. A regra do fornecedor não muda. Para voltar a ver, restaure na aba Regras do fornecedor.',
      strip: {
        text: 'Salve as retenções desta conta como regra do fornecedor.',
        review: 'Revisar regra',
      },
      dialog: {
        descriptionSubmit:
          'Antes de enviar para aprovação, você pode salvar as retenções desta conta como regra do fornecedor.',
        title: 'Salvar regra de retenções para {{vendor}}?',
        newServiceDescription:
          'As alíquotas de hoje continuam valendo nesta conta. A regra começa em {{competence}}.',
        serviceLabel: 'Serviço fiscal',
        rateRow: '{{tax}} retém',
        update: 'Atualizar regra',
      },
    },
  },
  classification: {
    // Cada entidade tem a própria cópia de um campo customizado; a API recusa a cópia de outra.
    customFieldOtherEntity:
      'Este campo é da entidade {{entity}}. Remova e adicione o campo da entidade da conta.',
    customFieldOtherEntityUnnamed:
      'Este campo é de outra entidade. Remova e adicione o campo da entidade da conta.',
    // ALC-1504: a seção de classificação é a ÚNICA coisa visível nos modos BYPASS e
    // AUTO_PROVISION (os seletores de dimensão ficam escondidos), então o texto precisa
    // explicar o que vai acontecer com esta guia — com as contas e o valor reais.
    statutoryTreatment: {
      mappingResolution: {
        tableHint: 'Escolha a obrigação de cada item e salve todas as escolhas ao terminar.',
        tax: 'Tributo',
        principal: 'Principal',
        saveAll: 'Salvar mapeamentos',
        saving: 'Salvando mapeamentos…',
        required: 'Selecione a obrigação nos itens pendentes.',
        saveSuccessTitle: 'Mapeamentos salvos com sucesso',
        saveSuccessHelp:
          'As escolhas foram registradas. Clique em Concluir para voltar à guia e conferir as próximas etapas.',
        saveFailureTitle: 'Não foi possível concluir o salvamento',
        backToMappings: 'Conferir mapeamentos',
        done: 'Concluir',
        optionalReason: 'Justificativa (opcional)',
        selectPlaceholder: 'Selecione',
        suggestedOptions: 'Sugestões',
        allOptions: 'Todas as obrigações',
        saveError: 'Não foi possível salvar este item. Tente novamente.',
        partial:
          '{{saved}} itens salvos; {{failed}} com erro. Confira as linhas indicadas e salve novamente.',
        saved_one: '{{count}} mapeamento salvo.',
        saved_other: '{{count}} mapeamentos salvos.',
        rowSaved: 'Salvo',
        rowConfirmed: 'Confirmado',
        rowPending: 'Pendente',

        question:
          'Alguns dos tributos dessa guia não foram automaticamente mapeados para as contas contábeis corretas, como prefere seguir?',
        ai: 'Resolver com IA',
        manual: 'Editar contas',
        title: 'Resolver mapeamento dos tributos',
        manualDescription: 'Confira a obrigação e a conta contábil de cada tributo.',
        previousDecisions: 'Esta guia possui destinos tributários confirmados manualmente.',
        viewDecisions: 'Conferir destinos',
      },
      slotFallback: {
        title: 'Tributo {{code}} não reconhecido',
        confirmedTitle: 'Destino de {{code}} confirmado manualmente',
        warning:
          'CSLL é uma sugestão, não a identificação do tributo. Selecione a obrigação correta.',
        accountingHelp: 'Impacto contábil da escolha',
        accountingDetails:
          'A escolha altera a obrigação contábil e pode gerar reconhecimento de provisão. A aprovação continua bloqueada até a confirmação e pode exigir configuração ou saldo provisionado.',
        confirmed: 'Destino: {{slot}}. Justificativa: {{reason}}.',
        slot: 'Obrigação tributária',
        reason: 'Justificativa da escolha',
        confirm: 'Confirmar destino para esta guia',
        permission:
          'Um usuário com permissão para gerenciar tratamento de guias deve confirmar o destino antes da aprovação.',
        prepare: 'Preparar tributos para conferência',
        refresh: 'Atualizar identificação dos tributos',
        clearBillOverride:
          'Remova a classificação manual da conta antes de confirmar uma obrigação tributária.',
        success: 'Destino tributário confirmado para esta guia.',
      },

      badge: 'Guia de impostos',
      reviewTitle: 'Tratamento da guia',
      reviewCount_one: '{{count}} tributo',
      reviewCount_other: '{{count}} tributos',
      fullAccountingView: 'Visão contábil completa',
      fullAccountingViewDescription:
        'Detalhes da guia, saldos provisionados e opções avançadas por tributo.',
      summaryTable: {
        tax: 'Tributo',
        assessmentPeriod: 'Apuração',
        amount: 'Valor',
        provisionedBalance: 'Saldo provisionado',
        account: 'C. Contábil',
        alerts: 'Alertas',
        actions: 'Ações',
        alertPresent: 'Há um alerta contábil neste tributo',
        provisionedBalanceBelowGuide:
          'Saldo provisionado de {{book}} ({{balance}}) é inferior ao valor da guia ({{amount}}).',
        stagedRailBlocked: 'A hierarquia contábil deste tributo está bloqueada.',
        viewOrigins: 'Ver origens',
        hideOrigins: 'Ocultar origens',
        assessmentPeriodPending: 'A apuração {{period}} será aplicada quando a conta for salva.',
      },
      bypassTitle: 'Guia de impostos — usar passivo de origem',
      bypassBody:
        'A aprovação reclassifica passivos já reconhecidos na origem, de Apurado para Em Guias a Pagar em cada livro contábil aplicável.',
      stagedBypassTitle: 'FGTS — passivo em etapas',
      stagedBypassBody:
        'A folha reconheceu o FGTS em Apurado. Na aprovação, o sistema transfere o principal para Em Guias a Pagar em cada livro BR_CPC e congela as origens correspondidas; valores sem origem ficam explícitos no rastro.',
      explanationTooltipLabel: 'Ver explicação do tratamento',
      stagedFlowLink: 'Ver fluxo contábil',
      stagedFlowDialogTitle: 'Fluxo contábil — {{tax}}',
      stagedFlowDialogDescription: 'Como este tributo se move entre contas de passivo',
      stagedFlowSourceTitlePayroll: 'Apuração da folha',
      stagedFlowSourceTitleGeneric: 'Reconhecimento do passivo',
      stagedFlowTransferTitle: 'Transferência para guia',
      stagedFlowPaymentTitle: 'Pagamento da guia',
      stagedFlowStatusCompleted: 'Concluído',
      stagedFlowStatusOnApproval: 'Ao aprovar',
      stagedFlowStatusPendingPayment: 'Na conciliação',
      stagedFlowStatusReversed: 'Estornado',
      stagedFlowDebitLabel: 'Débito',
      stagedFlowCreditLabel: 'Crédito',
      stagedFlowSourceDebitPayroll: 'Contas de despesa e provisão da folha',
      stagedFlowSourceDebitGeneric: 'Conta de origem do passivo',
      stagedFlowSourceNotePayroll: 'Lançado no fechamento da folha de {{period}}.',
      stagedFlowSourceNoteGeneric:
        'O passivo foi reconhecido na origem antes da aprovação desta guia.',
      stagedFlowTransferNoteCompleted:
        'O passivo apurado foi reclassificado para a conta que esta guia baixa no pagamento.',
      stagedFlowTransferNotePlanned:
        'Ao aprovar, o passivo apurado será reclassificado para a conta que esta guia baixa no pagamento.',
      stagedFlowPaymentCredit: 'Conta corrente (na conciliação)',
      stagedFlowPaymentNoteCompleted:
        'A baixa foi registrada quando o pagamento desta guia foi conciliado.',
      stagedFlowPaymentNotePending:
        'Só acontece quando o pagamento desta guia for conciliado no extrato.',
      bypassOriginWithholding: 'a retenção na nota do fornecedor',
      bypassOriginPayroll: 'a folha de pagamento',
      bypassOriginRevenue: 'o reconhecimento de receita',
      bypassOriginUpstream: 'o evento que a originou',
      stagedPlanTitle: 'Transferência prevista — {{book}}',
      stagedPlanVersion: 'Versão do plano de contas: {{version}}',
      stagedPlanBlocker: {
        NO_OPEN_PERIOD: 'Bloqueado: nenhum período contábil aberto cobre a data de aprovação.',
        NO_SCHEDULED_PAYMENT_DATE: 'Bloqueado: informe a data programada para pagamento.',
        NO_SETTLEMENT_PERIOD:
          'Bloqueado: a data de pagamento não possui um período contábil aberto utilizável.',
        STAGED_RAIL_NOT_CONFIGURED:
          'Bloqueado: a hierarquia deste tributo não está configurada nesta versão do plano de contas.',
        STAGED_SETTLEMENT_RAIL_NOT_CONFIGURED:
          'Bloqueado: a hierarquia deste tributo não está configurada no plano de contas do pagamento.',
        STAGED_SETTLEMENT_CROSSWALK_MISSING:
          'Bloqueado: as contas deste tributo não possuem um de-para auditado para o plano do pagamento.',
      },
      stagedEvidenceTitle: 'Rastro do passivo — {{book}}',
      stagedEvidenceAmounts: 'Correspondido: {{matched}} · sem origem: {{unmatched}}',
      stagedEvidenceSettlementAmounts:
        'Pago: {{paid}} · estornado: {{reversed}} · restante: {{remaining}}',
      stagedEvidenceMatched: 'Correspondido: {{matched}}',
      stagedEvidenceUnmatched: 'Sem origem: {{unmatched}}',
      stagedEvidenceReview: {
        PENDING: 'Revisão contábil pendente — o fechamento do período fica bloqueado',
        RESOLVED: 'Revisão resolvida automaticamente após matching de origens',
        CANCELLED: 'Revisão cancelada com a reversão da guia',
      },
      stagedEvidenceJournal: 'Lançamento de aprovação: {{journal}}',
      stagedEvidencePaymentJournal: 'Lançamento de pagamento: {{journal}}',
      stagedEvidencePaymentReversalJournal: 'Estorno do pagamento: {{journal}}',
      stagedEvidenceOrigin: 'Origem {{type}} {{source}} — {{amount}}',
      autoProvisionTitle: 'Guia de impostos — provisionamento automático',
      autoProvisionBody:
        'Ao aprovar, o sistema transfere o principal de {{amount}} de Apurado para Em Guias a Pagar, em cada livro contábil aplicável.',
      classifyTitle: 'Imposto de terceiros — classificar como despesa',
      classifyOwnTitle: 'Imposto próprio não provisionado — pendente de classificação',
      classifyOwnBody:
        'Esta guia é de um tipo de imposto não provisionado, portanto, confira a classificação para que seja provisionada na aprovação.',
      classifyBody:
        'O contribuinte da guia é {{payer}}, que não é uma das entidades deste tenant. Portanto nada nos seus livros provisionou este imposto: classifique-o como uma despesa normal.',
      classifyBodyNoPayer:
        'Esta guia precisa de classificação contábil completa, como qualquer outra despesa.',
      classifyReimbursableHint:
        'Se o custo será recuperado do cliente, marque a conta como reembolsável pelo cliente.',
      classifyOverriddenBody:
        'Esta guia foi marcada manualmente para classificação. Informe conta contábil, centro de custo e departamento como em qualquer despesa.',
      branchReviewNote:
        'O CNPJ da guia coincide apenas na raiz ({{taxId}}) — verifique se esta filial deveria estar cadastrada.',
      blockedTitle: 'Não é possível aprovar',
      documentTypeConfirmationTitle: 'Confirme o tipo da guia',
      documentTypeConfirmationBody:
        'Esta conta foi identificada como guia tributária, mas o tipo do documento precisa de confirmação.',
      documentTypeConfirmationCorrectionBody:
        'O tipo da guia foi confirmado anteriormente. Se a seleção estiver incorreta, escolha o tipo correto e atualize antes de aprovar.',
      documentTypeConfirmationSlotUnresolved:
        'Não foi possível identificar o tributo desta {{documentType}}.',
      documentTypeConfirmationLabel: 'Tipo da guia',
      documentTypeConfirm: 'Confirmar tipo',
      documentTypeCorrectionConfirm: 'Atualizar tipo',
      documentTypeConfirming: 'Confirmando…',
      documentTypeConfirmSuccess: 'Tipo da guia confirmado e tratamento tributário recalculado',
      documentTypeConfirmError: 'Não foi possível confirmar o tipo da guia: {{message}}',
      documentTypeConfirmationPermission:
        'Peça a alguém com permissão para gerenciar o tratamento tributário para confirmar o tipo.',
      documentTypeConfirmationAmbiguous:
        'Há mais de um documento possível nesta conta. Revise os anexos e peça a correção do documento antes de aprovar.',
      documentType: {
        DARF: 'DARF',
        GPS: 'GPS',
        GUIA_ISS: 'Guia de ISS',
        GUIA_ICMS: 'Guia de ICMS',
        GRF: 'GRF',
        DAS: 'DAS',
      },
      loading: 'Carregando o tratamento da guia…',
      openSettings: 'Abrir configurações de tratamento da guia',
      loadError:
        'Não foi possível carregar o tratamento da guia. A aprovação fica bloqueada até o status ser carregado.',
      refreshError:
        'Não foi possível atualizar o tratamento da guia. Os dados abaixo são da última consulta; tente novamente para liberar a aprovação.',
      retry: 'Tentar novamente',
      blockedSlotUnresolved: 'Não foi possível identificar o tributo desta guia.',
      blockedGeneric: 'Não foi possível resolver o tratamento tributário desta guia.',
      blockedProvisionDebitSlotUnpaired:
        'O tributo {{slot}} ainda não tem conta de contrapartida definida, então a provisão não tem onde ser lançada.',
      blockedProvisionAccountNotMapped:
        'O tributo {{slot}} não está mapeado para uma conta contábil nesta versão do plano de contas.',
      blockedPaymentSettlementAccountNotMapped:
        'O tributo {{slot}} não tem a conta passiva que será baixada no pagamento. Mapeie a conta antes de aprovar a guia.',
      blockedPayerIsOtherEntity:
        'Esta guia é da entidade {{entity}}, não da entidade desta conta. Reatribua a conta para a entidade correta.',
      blockedPayerTaxIdInvalid:
        'O CNPJ/CPF do contribuinte lido na guia ({{taxId}}) não passa na validação dos dígitos verificadores, então não dá para saber se o tributo é desta entidade ou de terceiros. Corrija o CNPJ, marque como imposto próprio ou classifique manualmente.',
      payerRecovery: {
        readLabel: 'CNPJ lido na guia',
        correctAction: 'Corrigir CNPJ',
        inputLabel: 'CNPJ/CPF correto do contribuinte',
        saveCorrection: 'Salvar CNPJ',
        cancel: 'Cancelar',
        checkDigitsInvalid: 'Os dígitos verificadores não conferem. Confira com a guia.',
        unchanged: 'Este é o mesmo CNPJ lido. Altere o dígito errado.',
        matchesBillEntity: 'Confere com {{entity}}. A guia vira imposto próprio.',
        matchesOtherEntity:
          'Confere com {{entity}}, outra empresa sua. A guia fica bloqueada até a conta ser movida para essa empresa.',
        matchesNoEntity:
          'CNPJ válido, mas não é de nenhuma empresa sua. A guia fica como imposto de terceiro.',
        suggestion: 'Parece o CNPJ de {{entity}}:',
        suggestionUse: 'Usar',
        orDecide: 'Ou decida sem corrigir:',
        ownAction: 'É imposto próprio',
        ownReasonLabel: 'Por que este imposto é de {{entity}}? (fica no histórico)',
        ownReasonLabelNoEntity: 'Por que este imposto é desta empresa? (fica no histórico)',
        ownReasonPlaceholder: 'Ex.: guia de ISS da própria empresa; CNPJ ilegível no PDF',
        ownReasonTooShort: 'Escreva pelo menos {{count}} caracteres.',
        ownConfirm: 'Confirmar imposto próprio',
        notThirdPartyLink: 'Este imposto não é de terceiro?',
        declaredCorrected: 'CNPJ do contribuinte corrigido de <from/> para <to/> em {{date}}.',
        declaredOwn:
          'Marcada como imposto próprio em {{date}}. O CNPJ lido na guia ({{taxId}}) não foi alterado. Motivo: “{{reason}}”',
        redundant:
          'O CNPJ lido na guia agora confere com a correção. A correção não muda mais nada e pode ser desfeita.',
        undo: 'Desfazer',
        undoTitle: 'Desfazer a decisão sobre o contribuinte?',
        undoBody:
          'A guia volta a usar o CNPJ lido pela IA ({{taxId}}). Se ele for inválido ou de terceiro, o tratamento da guia muda de novo.',
        undoConfirm: 'Desfazer',
        saveError: 'Não foi possível salvar: {{message}}',
        errors: {
          STATUTORY_PAYER_CORRECTION_TAX_ID_INVALID:
            'O CNPJ/CPF informado não passa na validação dos dígitos verificadores. Confira com a guia.',
          STATUTORY_PAYER_CORRECTION_TAX_ID_UNCHANGED:
            'O CNPJ/CPF informado é igual ao lido na guia. Altere o dígito errado.',
          STATUTORY_PAYER_DECLARATION_REASON_REQUIRED:
            'Explique por que a guia é imposto próprio, com pelo menos 5 caracteres.',
        },
      },
      blockedNoPeriod:
        'Nenhum período contábil cobre a competência desta guia, então não há plano de contas para resolver as contas.',
      blockedNoBrCpcBook:
        'Esta entidade não tem livro BR CPC, então a provisão não tem onde ser lançada. Configure um livro BR CPC para a entidade.',
      blockedMixedTreatment:
        'Esta guia mistura tributos com tratamentos diferentes — parte já provisionada, parte a classificar. Separe a guia em contas distintas ou escolha o tratamento manualmente.',
      blockedSourceProducerRequired:
        '{{slot}} deve ser reconhecido pelo evento tributável de origem antes da aprovação da guia.',
      blockedSourceProducerTitle: 'Não é possível aprovar sem passivo de origem',
      blockedSourceProducerDescription:
        "Esse tributo está configurado para obrigar passivos de origem e não poderá ser aprovado porque não há passivos no Balancete. Ou a provisão deverá ocorrer antes da aprovação, ou altere o tratamento desse tributo para 'Provisionamento automático' nas configurações de tratamento de guias",
      blockedMappingLink: 'Configurar mapeamento de contas fiscais',
      blockedOverrideHint:
        'Ou marque a guia para classificação manual e informe a conta contábil você mesmo.',
      sharedHierarchyTitle:
        'Esta guia não pode ser aprovada — é um problema de configuração, não desta guia',
      sharedHierarchyBody:
        'Os {{count}} tributos abaixo dependem de uma hierarquia do plano de contas que ainda não existe na versão ativa. Isso afeta esta guia e as próximas guias com esses tributos até que a hierarquia seja criada.',
      sharedHierarchyConfigureAction: 'Configurar hierarquia no plano de contas',
      sharedHierarchyOverrideAction: 'Classificar esta guia manualmente',
      pendingMappingSummaryTitle_one: '{{count}} de {{total}} tributo está impedindo a aprovação',
      pendingMappingSummaryTitle_other:
        '{{count}} de {{total}} tributos estão impedindo a aprovação',
      pendingMappingSummaryBodySingle:
        '{{tax}} não tem conta passiva mapeada para baixa no pagamento. Os outros {{readyCount}} tributos já estão prontos.',
      pendingMappingSummaryBodyMultiple:
        '{{count}} tributos não têm conta passiva mapeada para baixa no pagamento. Os outros {{readyCount}} já estão prontos.',
      pendingMappingJumpAction: 'Ir para o item pendente',
      readySummaryCount_one: '{{count}} pronto para aprovação',
      readySummaryCount_other: '{{count}} prontos para aprovação',
      pendingSummaryCount_one: '{{count}} pendente de mapeamento',
      pendingSummaryCount_other: '{{count}} pendentes de mapeamento',
      summaryTotalLabel: 'Total:',
      pendingLineAlert: 'Sem conta passiva mapeada para este tributo.',
      showOtherReadyLines_one: 'Mostrar o outro tributo pronto',
      showOtherReadyLines_other: 'Mostrar os outros {{count}} tributos prontos',
      staleTreatmentNotice:
        'O tratamento desta guia mudou desde a última verificação (agora seria {{treatment}}). Recarregue a conta antes de aprovar — a aprovação será recusada até que o novo tratamento seja revisado.',
      overrideAction: 'Classificar manualmente',
      overrideClearAction: 'Voltar ao tratamento automático',
      overrideSuccess: 'Guia marcada para classificação manual',
      overrideClearSuccess: 'Tratamento automático restaurado',
      overrideError: 'Não foi possível alterar o tratamento: {{message}}',
      unmappedAccount: 'conta não mapeada',
      bookSpecificMappingRequired: 'Selecione a conta por livro nas configurações',
      unidentifiedTax: 'Tributo não identificado',
      taxLabel: 'Tributo',
      settlementAccount: 'Conta baixada no pagamento',
      currentBalance: 'Saldo atual — {{book}}: {{amount}}',
      provisionedBalanceLabel: 'Saldo provisionado',
      provisionedBalanceValue: '{{amount}}',
      balanceContextTooltipLabel: 'Ver contexto do saldo',
      provisionedBalanceWarningLabel: 'Ver alerta de saldo provisionado',
      provisionedBalanceWarningText:
        'Esse tributo tem valor maior que o que está atualmente provisionado',
      balanceContextNote:
        'O saldo é o total contábil atual da conta mapeada e serve apenas como contexto. Ele não comprova que esta guia específica foi provisionada.',
      lineTreatment: 'Tratamento',
      switchToExpenseLink: 'Mudar para despesa',
      showTaxTreatmentOptionsAction: 'Autoprovisionar como imposto',
      assessmentPeriod: 'Período de apuração',
      amountLabel: 'Valor',
      assessmentPeriodValue: '{{month}}/{{year}}',
      assessmentPeriodAmbiguous: 'Período de apuração não determinado',
      assessmentPeriodUnsupported: 'Período de apuração não suportado ({{raw}})',
      assessmentPeriodRawUnknown: 'desconhecido',
      summaryPeriodPrefix: 'Período',
      principalAmount: 'Principal',
      fineAmount: 'Multa',
      interestAmount: 'Juros',
      toggleLineAria: 'Expandir detalhes de {{tax}}',
      treatment: {
        BYPASS: 'Usar passivo de origem',
        AUTO_PROVISION: 'Provisionamento automático',
        CLASSIFY: 'Classificar como despesa',
      },
      editScopeHint:
        'Esta alteração vale só para esta guia. O Tratamento de Guia e o mapeamento padrão da entidade não mudam.',
      oneOffAccountHint: 'Conta de baixa sobrescrita só nesta guia.',
      frozenBadge: 'Congelado',
      eligibleOrigins: 'Origens elegíveis',
      noEligibleOrigins: 'Nenhuma origem elegível no mesmo período.',
      applyOriginSelection: 'Usar origens selecionadas',
      clearOriginSelection: 'Voltar à alocação automática',
      manualOriginsReadOnly: 'Origens selecionadas manualmente para esta guia.',
      overrideReasonLabel: 'Motivo da alteração',
      overrideReasonPlaceholder: 'Descreva por que esta correção pontual é necessária',
      confirmTreatmentTitle: 'Alterar o tratamento desta linha?',
      confirmTreatmentDescription:
        'O tratamento de {{tax}} nesta guia passará a {{treatment}}. Futuras guias e o Tratamento de Guia da entidade não mudam.',
      confirmAccountTitle: 'Alterar a conta de baixa desta linha?',
      confirmAccountDescription:
        'A conta de baixa de {{tax}} será sobrescrita só nesta guia. O mapeamento padrão do plano de contas não muda.',
      confirmOriginsTitle: 'Restringir as origens desta linha?',
      confirmOriginsDescription:
        'A aprovação de {{tax}} consumirá apenas as origens elegíveis selecionadas (mesmas regras de entidade, livro, tributo e período).',
      cancel: 'Cancelar',
      confirm: 'Confirmar alteração',
      policyUpdateSuccess: 'Tratamento da linha atualizado só nesta guia',
      accountUpdateSuccess: 'Conta de baixa sobrescrita só nesta guia',
      originsUpdateSuccess: 'Seleção de origens atualizada só nesta guia',
      policyUpdateError: 'Não foi possível atualizar a linha: {{message}}',
    },
    title: 'Classificação',
    expandAction: 'Expandir Classificação',
    mode: {
      billLevel: 'Conta inteira',
      lineItem: 'Por item da nota',
      custom: 'Por rateio',
    },
    // D44 "Remover item": the trash action on a line of "Por item da nota".
    removeLineItem: {
      action: 'Remover item {{number}}',
      title: 'Remover item {{number}} · {{amount}}?',
      description:
        'O item sai da nota junto com a classificação e as provisões escolhidas para ele. O valor total da conta não muda: se precisar, ajuste o total separadamente.',
      confirm: 'Remover item',
      done: 'Item removido da nota.',
    },
    switchDialog: {
      toLineItemTitle: 'Mudar para classificação por item?',
      toLineItemDescription:
        'Ao mudar para classificação por item, a classificação atual da conta inteira será removida. Você precisará classificar cada item individualmente. Esta ação não pode ser desfeita.',
      toBillLevelTitle: 'Mudar para classificação da conta inteira?',
      toBillLevelDescription:
        'Ao mudar para classificação da conta inteira, todas as classificações dos itens individuais serão removidas. Você precisará classificar a conta como um todo. Esta ação não pode ser desfeita.',
      toCustomTitle: 'Mudar para classificação customizada?',
      toCustomDescription:
        'A classificação atual será herdada pela primeira linha, com 100% do valor. Use este modo quando duas ou mais dimensões variam juntas.',
      recurringToCustomTitle: 'Usar classificação Por rateio?',
      recurringToCustomDescription:
        'A classificação atual será herdada pelo primeiro rateio em 100%. Use este modo quando duas ou mais dimensões variam juntas.',
      recurringToBillLevelTitle: 'Usar classificação da Conta inteira?',
      recurringToBillLevelDescription:
        'Os rateios deixarão de ser usados neste template. Revise a classificação única antes de salvar.',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
    },
    custom: {
      addAllocation: 'Adicionar rateio',
      allocatedTotal: 'Total alocado',
      adjustToFull: 'Ajustar para 100%',
      allocationPlaceholder: 'Rateio {{index}}',
      percentLabel: 'Percentual',
      amountLabel: 'Valor',
      glAccount: 'Conta contábil',
      managerialAccount: 'Conta Gerencial',
      costCenter: 'Centro de Custo',
      department: 'Departamento',
      splitValue: 'Rateio',
      select: 'Selecione...',
      customFieldsTitle: 'Campos customizados',
      customFieldsDescription: 'Adicione segmentos customizados para este rateio.',
      addCustomField: 'Adicionar campo',
      textValuePlaceholder: 'Digite o valor',
      textValueLabel: 'Valor de texto do campo customizado',
      textValueCounter: '{{count}}/{{max}}',
      textValueTooLong: 'O valor excede {{max}} caracteres',
      segmentUsedInConditions: ' (usado nas condições)',
      segmentNoConfiguredValues: ' (sem valores configurados)',
      segmentUnavailableInRecurringTemplate: ' (não disponível em modelos recorrentes)',
      segmentTextValueUnsupported: ' (texto livre não suportado aqui)',
      removeAllocation: 'Remover rateio',
      multiDimensionHint:
        'Use Por rateio quando duas ou mais dimensões variam juntas. Cada linha leva a classificação completa.',
      sharedAcrossLines: 'Igual nas {{count}} linhas',
      editSharedFields: 'Editar campos comuns',
      hideSharedFields: 'Ocultar campos comuns',
      editLine: 'Editar linha {{index}}',
      duplicateLine: 'Duplicar linha {{index}}',
      removeLine: 'Remover linha {{index}}',
      addLine: 'Adicionar linha',
    },
    secondSplit: {
      singleSplitHint:
        'Se outra dimensão também variar, use Por rateio para definir as combinações explicitamente.',
      title: 'Dois campos rateados criam combinações',
      description:
        'Este segundo rateio gera {{count}} combinações. Confira as linhas antes de escolher como classificá-las.',
      convertAction: 'Converter para Por rateio',
      keepCrossProductAction: 'Manter cruzamento',
      undoAction: 'Desfazer segundo rateio',
    },
    validation: {
      customBreakdownMissing: 'Adicione pelo menos um rateio',
      customBreakdownTotal: 'Total alocado deve ser 100% (atual: {{total}}%)',
      customBreakdownGlRequired: '{{label}}: Informe uma conta contábil (GL)',
      glManagerialAccountTypeMismatch:
        'Contas contábeis e gerenciais divergem entre custo (COGS) e despesa operacional. Revise a classificação para consistência na DRE.',
      dimensionPercentTotal:
        '{{prefix}}{{dimension}}: A soma dos percentuais deve ser 100% (atual: {{total}}%)',
      segmentPercentTotal:
        '{{prefix}}Segmento {{segment}}: A soma dos percentuais deve ser 100% (atual: {{total}}%)',
      billLevelDimensionRequired: 'Informe pelo menos uma dimensão de classificação',
      allocationLabelFallback: 'Rateio {{index}}',
      dimensionGlAccount: 'Conta contábil (GL)',
      dimensionManagerialAccount: 'Conta gerencial',
      dimensionCostCenter: 'Centro de custo',
      dimensionDepartment: 'Departamento',
      lineItemPrefix: 'Item {{lineNumber}}',
      requiredTenantDimension: '{{dimension}} é obrigatório',
    },
    reimbursementLineItemMismatch:
      'O total do reembolso não corresponde à soma dos itens. Reclassifique como Conta inteira ou Por rateio para continuar.',
    reimbursementBillOrCustomBanner:
      'Reembolsos devem ser classificados como Conta inteira ou Por rateio',
    recurringLockBanner:
      'Classificação derivada da conta recorrente. Desvincule a recorrência para editar.',
    recurringTemplateEditBanner:
      'Alterações feitas aqui serão salvas na conta recorrente e aplicadas às contas vinculadas em pré-aprovação.',
    recurringTemplateEditConfirmTitle: 'Atualizar a classificação da conta recorrente?',
    recurringTemplateEditConfirmDescription:
      'Esta alteração atualizará a conta recorrente e todas as contas vinculadas que ainda estão em rascunho, enviadas ou pendentes de aprovação.',
    recurringTemplateEditConfirmSave: 'Atualizar e salvar',
    recurringTemplateEditConfirmSubmit: 'Atualizar e enviar',
    recurringTemplateEditMissingVendor:
      'Não foi possível atualizar a conta recorrente porque o fornecedor não está definido.',
    recurringLockGoToRecurringAction: 'Ir para recorrência',
    recurringLockSaveBlocked: 'Classificação bloqueada — desvincule a recorrência para editar.',
    recurringLockNeedsUnlink:
      'A aprovação falhou porque a classificação derivada está incompleta. Desvincule a recorrência para ajustar a classificação desta conta.',
    aiFieldTooltip: 'Classificação feita pela Alchemy',
    subtitleWithAmount: '{{count}} item • {{amount}}',
    subtitleWithAmount_plural: '{{count}} itens • {{amount}}',
    lineItemsEmpty: 'Nenhum item encontrado.',
    notesLabel: 'Observações',
    notesPlaceholder: 'Notas internas',
    compactDimension: {
      distributionValueHeading: 'Distribuição do valor',
      completeTo100: 'Completar para 100%',
      divideEqually: 'Dividir igualmente',
      removeSplitAria: 'Remover',
      totalNot100Warning: 'Total deve ser 100% para salvar',
      splitAction: 'Ratear',
      splitSummary: 'Rateio de {{amount}} em {{count}} contas',
      addAllocation: 'Adicionar conta',
      removeNamedAllocation: 'Remover {{name}} do rateio',
      roundingAbsorber: 'Esta linha absorve o arredondamento',
      distributedStatus: '100% distribuídos',
      missingStatus: 'Faltam {{amount}} · {{percent}}%',
      excessStatus: 'Excedem {{amount}} · {{percent}}%',
      lastRowRoundingRule: 'A última linha absorve o arredondamento.',
    },
    workflowSuggestion: {
      bankLabel: 'Banco',
      apply: 'Aplicar sugestões',
      dismiss: 'Ignorar',
      badgeRule: 'Regra',
      badgeHistory: 'Histórico',
      badgeOther: 'Sugestão',
      defaultTitle: 'Sugestão da IA',
      hiddenCount_one:
        'Mais {{count}} sugestão disponível (prioridade: regra > histórico > outras)',
      hiddenCount_other:
        'Mais {{count}} sugestões disponíveis (prioridade: regra > histórico > outras)',
    },
    aiInstructionUse: {
      regionLabel: 'Instruções do fornecedor usadas pela IA',
      summaryCostCenter: 'A IA sugeriu o centro de custo onde as regras deixaram esse campo vazio.',
      summaryCategoryAndCostCenter:
        'A IA sugeriu a categoria e o centro de custo onde as regras deixaram esses campos vazios.',
      usedInstruction: 'Usou a instrução "{{text}}"',
      usedInstructionWithoutText: 'Usou uma instrução do fornecedor',
      instructionTextHidden: 'O texto da instrução aparece para quem pode ver os fornecedores.',
      openInstruction: 'Abrir a instrução na aba Conhecimento do fornecedor',
      noteLeadIn: 'Nota da IA:',
      split: {
        title: 'Sugestão de rateio da IA',
        dimension: {
          costCenters: 'Centro de custo',
          departments: 'Departamento',
          managerialAccounts: 'Conta gerencial',
        },
        targetWithPercent: '{{target}} {{percent}}',
        rejectedTitle: 'Rateio da IA não aplicado',
        rejectedRow: '{{dimension}}: {{reason}}',
        rejection: {
          AI_SPLIT_PERCENT_INVALID:
            'cada percentual deve ser maior que zero, com no máximo duas casas decimais',
          AI_SPLIT_PERCENT_SUM_INVALID: 'os percentuais não somam exatamente 100%',
          AI_SPLIT_TARGET_UNKNOWN:
            'uma das opções não existe, está inativa ou não pode receber lançamentos',
          AI_SPLIT_TARGET_DUPLICATE: 'a mesma opção aparece mais de uma vez',
        },
      },
      saveRefusal: {
        title: 'Classificação da IA não aplicada',
        reason: {
          AI_CLASSIFICATION_AMOUNT_TOTAL_INVALID: 'os valores alocados não somam o valor da conta',
        },
      },
      skippedReference: {
        title: 'A IA seguiu a instrução e não copiou o histórico',
        pattern: {
          LINE_ITEM_LEVEL:
            'A última conta classificada do fornecedor é classificada por item, sem um rateio único para copiar.',
          CUSTOM_BREAKDOWN:
            'A última conta classificada do fornecedor usa rateio personalizado, sem um rateio único para copiar.',
        },
        openBill: 'Abrir a conta {{bill}}',
      },
      creditFlags: {
        title: 'Créditos de PIS e COFINS não copiados da última conta',
        reason: {
          AI_INSTRUCTION_CREDIT_FLAGS_NOT_COPIED:
            'A instrução escolheu outra conta contábil que a da última conta do fornecedor. Confira os créditos desta conta.',
          AI_INSTRUCTION_CREDIT_FLAGS_COPY_FAILED:
            'A cópia não foi concluída. Confira os créditos desta conta.',
        },
        openBill: 'Abrir a conta {{bill}}',
      },
    },
  },
  dashboard: {
    pendingApproval: {
      title: 'Aprovações pendentes',
      emptyState: 'Nenhuma conta pendente de aprovação',
      totalPending: '{{count}} conta(s) pendente(s)',
      totalAmount: 'Total: {{amount}}',
      tableHint: 'Revise as contas pendentes e aprove ou rejeite direto pela lista.',
      morePending: '+ {{count}} contas pendentes',
      columns: {
        dueDate: 'Vencimento',
        vendor: 'Fornecedor',
        classification: 'Classificação',
        amount: 'Valor',
      },
      dueRelative: {
        today: 'vence hoje',
        inDays_one: 'em {{count}} dia',
        inDays_other: 'em {{count}} dias',
        overdue: 'atrasado {{count}}d',
      },
      risksLabel: 'Riscos · {{count}}',
      attentionLabel: 'Atenção · {{count}}',
      risksTooltipTitle: 'Riscos identificados',
      attentionTooltipTitle: 'Pontos de atenção',
      noVendor: 'Fornecedor não informado',
      lineItemsSummary: '{{count}} itens classificados',
      payeeLineBase: '{{vendor}} a ser pago',
      payeeLineReimbursementPrefix: 'Reembolso para ',
      payeeLineVia: ' via {{method}}',
      payeeLineIn: ' em {{details}}',
      approve: 'Aprovar',
      reject: 'Rejeitar',
      review: 'Revisar',
      dueLabel: 'Vence em {{date}}',
      competencyLabel: 'Competência: {{date}}',
      rejectDialog: {
        title: 'Rejeitar conta',
        description:
          'Esta rejeição será registrada permanentemente e a conta voltará para revisão do solicitante. Essa ação não pode ser desfeita.',
        reasonLabel: 'Motivo da rejeição (obrigatório)',
        placeholder: 'Motivo da rejeição...',
        cancel: 'Cancelar',
        confirm: 'Confirmar rejeição',
      },
      classification: {
        split: 'Rateio de {{dimension}}',
        glAccount: 'Conta contábil',
        department: 'Departamento',
        costCenter: 'Centro de custo',
        managerialAccount: 'Conta gerencial',
        segment: 'Segmento',
        lineItems: {
          sectionTitle: 'Itens',
          label: '#{{lineNumber}} - {{description}}',
        },
      },
      paymentsLabel: '{{count}} pagamento(s)',
      toast: {
        approved: 'Conta aprovada com sucesso',
        rejected: 'Conta rejeitada',
        approveError: 'Erro ao aprovar conta',
        approveSettlementGroupTotalMismatch:
          'O total dos pagamentos conciliados não corresponde ao valor da transação vinculada. Revise a conciliação desta conta: o valor alocado precisa ser igual ao valor da transação do cartão ou do banco.',
        approveSettlementPaymentSourceMismatch:
          'A origem do pagamento (cartão ou conta bancária) não corresponde à transação conciliada. Revise a conciliação desta conta.',
        approvePaidCardPaymentNeedsReconciliation:
          'Este pagamento já está marcado como quitado no cartão, mas ainda não foi conciliado com uma transação da fatura do cartão. Concilie a compra correspondente ou, se o pagamento foi lançado por engano, remova-o. Depois, tente aprovar novamente.',
        approveSettlementGenericWithCode:
          'Falha ao lançar o pagamento conciliado no razão ({{code}}). Revise a conciliação desta conta e tente novamente.',
        approveNoOpenPeriod:
          'Não é possível aprovar essa conta porque não há um período contábil aberto para a competência selecionada.',
        noOpenPeriodPhaseBill: 'Provisão da conta no razão (competência)',
        noOpenPeriodPhasePayment: 'Baixa do pagamento reconciliado',
        noOpenPeriodPhaseDeferralRecognition: 'Apropriação mensal do diferimento',
        noOpenPeriodPhaseDeferralReclassify: 'Reclassificação CP/LP do diferimento',
        noOpenPeriodPhaseDeferralWriteoff: 'Write-off do diferimento',
        noOpenPeriodPhaseDeferralVendorRefund: 'Reembolso de fornecedor (diferimento)',
        noOpenPeriodPhaseRevenueRecognition: 'Reconhecimento da receita',
        noOpenPeriodPhaseFixedAssetCapitalization: 'Capitalização do bem no imobilizado',
        noOpenPeriodPhaseFixedAssetDepreciation: 'Depreciação do bem (competência)',
        noOpenPeriodPhaseFixedAssetDisposal: 'Baixa do bem do imobilizado',
        noOpenPeriodPhaseFixedAssetTransfer:
          'Transferência do bem em andamento para a classe final',
        noOpenPeriodPhaseLeaseRecognition:
          'Reconhecimento do arrendamento (direito de uso e passivo)',
        noOpenPeriodPhaseLeaseLiabilityReclassification:
          'Reclassificação do passivo de arrendamento entre curto e longo prazo',
        noOpenPeriodPhaseLeaseRemeasurement: 'Remensuração do arrendamento',
        noOpenPeriodClosedPeriod:
          'Existe o período {{periodId}} (de {{start}} a {{end}}), mas o status é {{status}}. Reabra esse período ou ajuste a data do lançamento.',
        noOpenPeriodMissingPeriod:
          'Não há período cadastrado no livro {{bookId}} que cubra essa data.',
        noOpenPeriodPaymentRef: ' Pagamento: {{paymentId}}.',
        noOpenPeriodElaboration:
          '{{phase}} não pôde ser registrada. Data de referência do lançamento: {{date}}. Livro: {{bookId}}. {{periodSentence}}{{paymentSuffix}}',
        noOpenPeriodGoToPeriods: 'Ir para períodos contábeis',
        approveVendorOffsetSettingsMissing:
          'Para aprovar, configure o mapeamento da conta raiz de fornecedores (passivo) em Configurações → Contabilidade e Fiscal → Mapeamento de contas (aba AP).',
        approveVendorOffsetSettingsAction: 'Ir para Preferências',
        approveVendorOffsetSettingsInvalid:
          'Não foi possível aprovar porque a conta contábil do fornecedor usada neste lançamento está inválida para a competência (pode estar fora da versão do plano ou fora da raiz de Fornecedores). Para corrigir, ajuste a configuração em Preferências → Contábil e Fiscal → Mapeamento (aba AP) e tente aprovar novamente.',
        approveVendorOffsetRootInvalid:
          'Conta raiz de fornecedores inválida (não encontrada ou em outra versão do plano de contas). Ajuste a configuração nas Preferências do tenant.',
        approveSupplierAdvanceSettingsMissing:
          'Para aprovar, configure a conta raiz de adiantamentos a fornecedores em Configurações → Contabilidade e Fiscal → Mapeamento de contas (aba AP).',
        approveSupplierAdvanceSettingsInvalid:
          'A conta de adiantamentos a fornecedores está inválida para esta competência. Remapeie a conta na versão vigente do plano de contas e tente novamente.',
        approveCreditCardSettingsMissing:
          'Para aprovar, configure a conta raiz de cartões de crédito em Configurações → Contabilidade e Fiscal → Mapeamento de contas (aba AP).',
        approveCreditCardSettingsInvalid:
          'A conta contábil do cartão está inválida ou conflita com outra conta da mesma instituição. Revise o mapeamento do cartão na versão vigente e tente novamente.',
        approveCoaVersionNotApplied:
          'O período contábil referente à competência da conta não está aberto ou não tem plano de contas aplicado. Abra o período ou aplique o plano de contas para aprovar.',
        approveTaxAccountNotMapped:
          'Para aprovar, configure o mapeamento das contas tributárias (retenções IRRF, PIS, COFINS, CSLL, INSS) no plano de contas da competência.',
        approveTaxAccountNotMappedAction: 'Ir para Mapeamento',
        approveTaxAccountSlotNotFound:
          'Conta tributária não configurada. Verifique o mapeamento de contas tributárias nas Preferências.',
        approveSystemAccountNotConfigured:
          'Para aprovar, configure as contas do sistema nas Preferências do tenant.',
        approveSystemAccountMappingMissing:
          'Conta do sistema sem mapeamento. Verifique a configuração nas Preferências.',
        approveSystemAccountMappingMissingWithSlot:
          'Conta do sistema "{{slotName}}" ({{slotCode}}) sem mapeamento. Para corrigir, acesse Preferências → Contábil e Fiscal → Mapeamento de contas (aba AP) → Contas do sistema, configure essa conta e tente aprovar novamente.',
        approveSystemAccountSlotNotFound:
          'Conta do sistema não encontrada. Verifique a configuração nas Preferências.',
        approveSystemAccountSlotNotFoundWithSlot:
          'Conta do sistema "{{slotName}}" ({{slotCode}}) não encontrada. Para corrigir, acesse Preferências → Contábil e Fiscal → Mapeamento de contas (aba AP) → Contas do sistema, provisione/configure essa conta e tente aprovar novamente.',
        clientReimbursableHasTaxCredits:
          'Contas reembolsáveis pelo cliente não podem ter créditos de impostos (PIS/COFINS ou IVA). Remova os créditos antes de enviar para aprovação.',
        leaseSettlementHasTaxCredits:
          'Esta conta quita uma parcela de arrendamento e tem créditos de impostos (PIS/COFINS ou IVA). Remova os créditos ou lance a conta fora do arrendamento.',
        documentDiscountHasDeferral:
          'O documento tem desconto e esta conta tem um diferimento ativo, calculado sobre o valor bruto. Cancele o diferimento antes de lançar a conta.',
        documentDiscountHasCapitalization:
          'O documento tem desconto e esta conta tem uma capitalização pendente, calculada sobre o valor bruto. Descarte a capitalização pendente antes de lançar a conta.',
        approveSystemAccountMappingAction: 'Ir para Contas do sistema',
        approveChainBootstrapFailed:
          'A conta foi enviada para aprovação, mas o fluxo de aprovação padrão não pôde ser criado. Revise as configurações de aprovação ou contate o suporte.',
        entitySuggestionUnconfirmed: 'Confirme a entidade sugerida antes de aprovar',
        approveAssetCreditPolicyRequired:
          'Revise o candidato do imobilizado e registre a forma de apropriação de PIS/COFINS com sua evidência antes de aprovar a conta.',
        approveAssetCreditRecoverabilityRequired:
          'O ativo rascunho vinculado ainda não tem uma conclusão contábil de recuperabilidade. Abra Ativos → Cadastro, localize o bem desta conta e confirme a expectativa de recuperação integral com a projeção ou política contábil que a sustenta. Depois, tente aprovar novamente.',
        approveAssetCreditSourceInvalid:
          'O crédito de imobilizado não está vinculado de forma inequívoca ao item e ao bem. Revise os itens, a classificação e os candidatos da conta de origem antes de aprovar.',
        approveAssetCreditOwnerTypeMismatch:
          'A natureza do crédito de PIS/COFINS não corresponde ao ativo vinculado. Revise a classificação da compra e o vínculo com o ativo antes de aprovar.',
        approveAssetCreditFirstUseBeforeInService:
          'A primeira utilização fiscal registrada antecede a entrada em serviço do ativo. Revise a data e a decisão fiscal do bem antes de aprovar; não altere datas silenciosamente para liberar o crédito.',
        approveAssetCreditAcquisitionAfterInService:
          'A data de aquisição ou recebimento confirmada ficou posterior à entrada em serviço determinada pela contabilização da conta. Revise a data e a decisão fiscal do ativo antes de aprovar; não altere datas silenciosamente para liberar o crédito.',
        approveAssetCreditStatutoryBookRequired:
          'A provisão do crédito de imobilizado precisa de um lançamento no livro estatutário da entidade. Revise o livro e a contabilização da conta; o livro fiscal auxiliar não substitui essa provisão.',
        approveAssetCreditRevenueUseUnsupported:
          'A destinação do crédito deste imobilizado ainda não tem tratamento completo neste fluxo. Mantenha a classificação real e a conta pendente; não altere a destinação apenas para conseguir aprovar.',
        approveAssetCreditScheduleInvalid:
          'Não foi possível gerar parcelas fiscais coerentes com a regra e o potencial de crédito do bem. Revise a regra fiscal da classe e os valores de PIS/COFINS; se persistir, solicite a revisão da origem antes de aprovar.',
        approveAssetCreditTypeStatusNotEditable:
          'A natureza do crédito de PIS/COFINS só pode ser alterada antes da aprovação da conta. Atualize a página para conferir o status e a classificação registrados.',
        approveAssetCreditTypeCapitalizationImmutable:
          'Já existe um ativo criado a partir desta conta, por isso a natureza do crédito não pode mais ser alterada. Revise a classificação da compra e o ativo vinculado.',
        approveAssetCapitalizationAmountDrift:
          'A conta a pagar mudou depois que a capitalização foi confirmada: o débito atual da classificação não é mais o valor com que os bens foram medidos. Decida o candidato de novo contra os valores atuais antes de lançar.',
        approveAssetSourceReviewImmutable:
          'A decisão fiscal confirmada do ativo congelou os itens, os dados fiscais e os valores de PIS/Cofins desta conta. Campos apenas informativos continuam editáveis. Este fluxo ainda não permite corrigir esses fatos congelados; mantenha a conta sem aprovação e encaminhe-a para revisão contábil e fiscal.',
        approveAssetMethodNotImplemented:
          'A classe de um dos bens capitalizados usa um método de depreciação que ainda não é suportado: somente o método linear está implementado. Ajuste o método na régua da classe antes de aprovar.',
        approveClassificationTotalMismatch:
          'A soma da classificação contábil não bate com o total da conta. Abra a classificação, confira as contas e os valores e salve de novo antes de aprovar.',
        approvePaymentScheduleAmountExceeded:
          'Os pagamentos desta conta já somam mais do que o valor dela. Confira os pagamentos na aba Pagamentos e cancele o que estiver duplicado antes de aprovar.',
        mxLegacyIvaRepostUnsupported:
          'Não foi possível relançar esta conta do México: ela foi provisionada com o IVA no custo e os pagamentos dela continuam lançados, então o IVA já pago ficaria pendente de crédito. Desfaça a conciliação bancária dos pagamentos, relance a conta e concilie de novo. Se o período de algum pagamento estiver fechado, reabra-o antes.',
        rejectError: 'Erro ao rejeitar conta',
      },
      customAllocationDialog: {
        badgeFallback: 'Rateio customizado',
        badgeMultiple: '{{count}} rateios customizados',
        tooltipHint: 'Clique para ver detalhes do rateio',
        title: 'Detalhamento do Rateio Customizado',
        description: 'Esta conta possui o valor rateado entre as seguintes dimensões:',
        splitFallbackLabel: 'Rateio {{index}}',
        glAccount: 'Conta Contábil:',
        department: 'Departamento:',
        costCenter: 'Centro de Custo:',
        managerialAccount: 'Conta Gerencial:',
        segmentFallback: 'Segmento',
        close: 'Fechar',
      },
    },
    title: 'Dashboard',
    subtitle: 'Resumo das contas para sua aprovação',
    widgetTitle: 'Contas para aprovação',
    approve: 'Aprovar',
    approveSuccess: 'Conta aprovada',
    approveError: 'Erro ao aprovar conta',
    reject: 'Rejeitar',
    rejecting: 'Rejeitando...',
    rejectSuccess: 'Conta rejeitada',
    rejectError: 'Erro ao rejeitar conta',
    rejectDescription: 'Informe um motivo para a rejeição',
    rejectPlaceholder: 'Motivo da rejeição',
    review: 'Revisar',
    cancel: 'Cancelar',
    countAndTotal: '{{count}} contas • {{total}} total',
    pendingCount: '{{count}} pendentes',
    dueLabel: 'Vence {{date}}',
    empty: 'Nenhuma conta pendente de aprovação.',
    recurringDashboard: {
      header: {
        title: 'Contas recorrentes',
        subtitle: 'Projeção e acompanhamento por competência',
        entityLabel: 'Entidade',
        entityPlaceholder: 'Selecione a entidade',
        allEntities: 'Todas as entidades',
        costCenterFilterLabel: 'Filtro por centro de custo',
        costCenterFilterPlaceholder: 'Filtrar centros de custo',
        departmentFilterLabel: 'Filtro por departamento',
        departmentFilterPlaceholder: 'Filtrar departamentos',
        prevMonthAria: 'Mês anterior',
        nextMonthAria: 'Próximo mês',
      },
      states: {
        selectEntity: 'Selecione uma entidade para carregar o dashboard de recorrência.',
        loading: 'Carregando contas recorrentes',
        error: 'Não foi possível carregar a projeção de recorrência.',
      },
      kpis: {
        projected: {
          label: 'Projeção mensal',
          detail: '{{count}} recorrências previstas',
        },
        received: {
          label: 'Recebidas',
          detail: '{{count}} de {{total}} recebidas',
        },
        pending: {
          label: 'Pendentes',
          detail: '{{count}} aguardando recebimento',
        },
        overdue: {
          label: 'Atrasadas',
          detail: '{{count}} em atraso',
        },
      },
      alerts: {
        overdue: {
          title: 'Recorrências atrasadas',
          description:
            '{{count}} recorrências estão sem conta recebida após o vencimento esperado.',
          cta: 'Ver detalhes',
        },
        variation: {
          title: 'Variações detectadas',
          description: '{{count}} recorrências apresentaram variação de valor.',
          cta: 'Investigar',
        },
      },
      filters: {
        all: 'Todas',
        received: 'Recebidas',
        pending: 'Pendentes',
        overdue: 'Atrasadas',
      },
      table: {
        showing: 'Exibindo {{visible}} de {{total}} linhas',
        columns: {
          vendor: 'Fornecedor / descrição',
          frequency: 'Frequência',
          expected: 'Valor esperado',
          actual: 'Valor real',
          variation: 'Variação',
          previousCompetencies: 'Comp. anteriores',
          concentration: 'Concentração',
          dueDate: 'Vencimento',
          costCenter: 'Centro de custo',
          department: 'Departamento',
          status: 'Status',
          actions: 'Ações',
        },
        openVendorNewTab: 'Abrir fornecedor em nova aba: {{vendor}}',
        expandOccurrences: 'Expandir contas vinculadas de {{vendor}}',
        collapseOccurrences: 'Recolher contas vinculadas de {{vendor}}',
        editRecurring: 'Editar conta recorrente',
        edit: {
          loadError: 'Não foi possível carregar a recorrência para edição.',
          notFound: 'Recorrência não encontrada para este fornecedor.',
        },
        merge: {
          action: 'Combinar recorrências',
          loadError: 'Não foi possível carregar os templates para combinação.',
          tooltip: 'Deduplicar recorrências',
        },
        link: {
          action: 'Vincular contas',
          tooltip: 'Gerenciar vínculo de contas',
          loadError: 'Não foi possível carregar contas para vinculação.',
        },
        sort: {
          aria: 'Ordenar por {{field}}{{direction}}',
          direction: {
            asc: 'crescente',
            desc: 'decrescente',
          },
        },
        occurrences: {
          loading: 'Carregando contas vinculadas...',
          error: 'Não foi possível carregar as contas vinculadas.',
          retry: 'Tentar novamente',
          empty: 'Nenhuma conta vinculada encontrada para esta recorrência.',
          openBillLink: 'Abrir conta',
          columns: {
            monthYear: 'Competência',
            dueDate: 'Vencimento',
            status: 'Status',
            netAmount: 'Valor líquido',
            totalAmount: 'Valor total',
            openBill: 'Conta',
          },
        },
        previousCompetencies: {
          expand: 'Ver histórico de competências desta recorrência',
          collapse: 'Ocultar histórico de competências',
          loading: 'Carregando linha do tempo...',
          error: 'Não foi possível carregar a linha do tempo.',
          retry: 'Tentar novamente',
          empty: 'Nenhum mês aplicável no período.',
          issuesBadge: '{{count}} problema(s)',
          healthTooltip: {
            OK: 'Competências anteriores em dia (sem faturas ausentes ou vencidas não pagas).',
            WARNING: 'Há competências anteriores com fatura ausente ou vencida sem pagamento.',
            CRITICAL:
              'Vários meses com fatura ausente ou vencida sem pagamento — revisar com prioridade.',
          },
        },
        timeline: {
          columns: {
            monthYear: 'Competência',
            dueDate: 'Vencimento',
            status: 'Status',
            totalAmount: 'Valor total',
            openBill: 'Conta',
          },
          status: {
            RECEIVED: 'Recebida / paga',
            PENDING: 'Pendente',
            OVERDUE: 'Vencida',
            MISSING: 'Ausente',
          },
          openBillLink: 'Abrir conta',
        },
        timelineDrawer: {
          title: 'Competências anteriores',
          description: 'Histórico da recorrência para {{vendor}}.',
          healthSummary:
            'Janela: {{total}} meses • Cobertos: {{covered}} • Ausentes: {{missing}} • Vencidos: {{overdue}}',
        },
        variation: {
          awaiting: 'Aguardando',
          notReceived: 'Não recebida',
        },
        footer: {
          counts: 'Recebidas: {{received}} • Pendentes: {{pending}} • Atrasadas: {{overdue}}',
          paginationShowingItems: 'Mostrando {{from}} a {{to}} de {{total}}',
          paginationPage: 'Página {{page}} de {{total}}',
          itemsPerPage: 'Itens por página',
        },
      },
      status: {
        received: 'Recebida',
        receivedReview: 'Em revisão',
        receivedApproved: 'Aprovada',
        receivedPaid: 'Paga',
        receivedPartialPrefix: 'Parcialmente {{status}}',
        pending: 'Pendente',
        overdue: 'Atrasada',
      },
      linkDialog: {
        title: 'Vincular contas recebidas',
        description: 'Selecione as contas que devem ficar vinculadas a esta recorrência.',
        contextTitle: 'Vínculos para',
        vendorLabel: 'Fornecedor',
        templateLabel: 'Template',
        loading: 'Carregando contas...',
        empty: 'Nenhuma conta disponível para vínculo.',
        save: 'Salvar vínculos',
        linkSummary:
          '{{linked}} vinculadas, {{unlinked}} desvinculadas, {{reassigned}} reatribuídas.',
        columns: {
          selected: 'Vincular',
          competency: 'Competência',
          dueDate: 'Vencimento',
          amount: 'Valor',
          status: 'Status',
          currentTemplate: 'Template atual',
          openBill: 'Conta',
        },
        currentTemplate: {
          none: 'Sem vínculo',
        },
        toast: {
          success: 'Vínculos atualizados com sucesso.',
          error: 'Não foi possível atualizar os vínculos.',
        },
      },
      sidebar: {
        coverage: {
          title: 'Cobertura da competência',
          dashboardTitle: 'Contas recorrentes',
          received: 'Recebido:',
          projected: 'Projetado:',
        },
        projection: {
          title: 'Projeção 6 meses',
        },
        concentration: {
          title: 'Concentração por fornecedor',
          empty: 'Sem recorrências para o período selecionado.',
        },
        anomalies: {
          title: 'Variações & anomalias',
          empty: 'Nenhuma variação detectada no período.',
        },
        extraBills: {
          title: 'Contas fora da recorrência',
          loading: 'Verificando contas adicionais na competência...',
          detected: '{{count}} recorrência(s) com contas extras na competência selecionada.',
          clear: 'Nenhuma conta extra detectada na competência selecionada.',
          cta: 'Abrir sugestão de vínculo',
        },
      },
    },
    variationsWidget: {
      title: 'Variações & Anomalias',
      empty: 'Nenhuma variação relevante nas contas recorrentes recebidas.',
      loadError: 'Não foi possível carregar as variações.',
      subtitleVsAverage: '{{pct}} vs. média {{count}} meses',
      subtitleVsTemplate: '{{pct}} vs. valor do template',
    },
  },
  approvalFlow: {
    approveSuccess: 'Etapa aprovada com sucesso',
    approveError: 'Erro ao aprovar etapa',
    rejectError: 'Erro ao rejeitar etapa',
    retryError: 'Erro ao tentar aprovar novamente',
    segregationTitle: 'Segregação de Funções',
    approveStepAria: 'Aprovar esta etapa',
    approveStepSaveAndApproveAria: 'Salvar alterações e aprovar esta etapa',
  },
  payments: {
    addPaymentDisabledTooltip:
      'Não é possível adicionar um pagamento: os pagamentos já totalizam o valor da conta.',
    reimbursementSwitch: 'Reembolso',
    reimbursementToggleConfirmTitle: 'Alterar tipo de pagamento?',
    reimbursementToggleConfirmToReimbursement:
      'Os pagamentos atuais serão excluídos e o formulário de reembolso será preenchido com os valores e a data de vencimento deles. Após a aprovação da conta, um novo pagamento de reembolso será gerado. Deseja continuar?',
    reimbursementToggleConfirmToRegular:
      'Ao desativar o Reembolso, as informações de reembolso serão excluídas. Deseja continuar?',
    reimbursementToggleConfirm: 'Confirmar',
    reimbursementSwitchSuccess:
      'Reembolso ativado: pagamentos removidos e solicitação preenchida com os dados anteriores.',
    reimbursementSwitchDeleteError: 'Não foi possível excluir os pagamentos ou salvar o reembolso.',
    reimbursementSwitchMissingBill: 'Conta indisponível. Atualize a página e tente novamente.',
    reimbursementSwitchDeleteBlockedStatus:
      'Só é possível converter em reembolso com exclusão de pagamentos em rascunho, em revisão ou pendente de aprovação.',
    reimbursementSwitchDeleteBlockedPayments:
      'Um ou mais pagamentos não podem ser excluídos (por exemplo, reconciliados). Ajuste antes de ativar o reembolso.',
    pendingLabel: 'Pendente:',
    ofLabel: 'de',
    cancelPaymentTitle: 'Cancelar pagamento',
    cancelPaymentDescription: 'Tem certeza que deseja cancelar o pagamento nº {{paymentNumber}}?',
    cancelPaymentConfirm: 'Cancelar pagamento',
    cancelPaymentSuccess: 'Pagamento cancelado com sucesso',
    cancelPaymentError: 'Erro ao cancelar o pagamento',
    advanceNotice: 'Fornecedor com saldo de adiantamento: {{amount}}',
    useAdvanceBalance: 'Usar saldo',
    viewAdvanceHistory: 'Ver histórico',
    advanceInDevelopmentToast: 'Funcionalidade em desenvolvimento',
    useAdvanceDialog: {
      title: 'Usar saldo de adiantamento',
      description:
        'Consumir saldo disponível do fornecedor para quitar (total ou parcialmente) esta conta.',
      availableLabel: 'Saldo disponível',
      billTotalLabel: 'Total da conta',
      amountLabel: 'Valor a consumir',
      effectiveDateLabel: 'Data efetiva',
      notesLabel: 'Observações',
      submit: 'Confirmar',
      cancel: 'Cancelar',
      success: 'Pagamento por adiantamento registrado com sucesso',
      successReserved:
        'Saldo reservado com sucesso. A contabilização ocorrerá na aprovação da conta.',
      errorInsufficient: 'Saldo insuficiente',
      errorGeneric: 'Erro ao registrar pagamento por adiantamento',
      reserveNoticeTitle: 'Reserva de saldo',
      reserveNoticeDescription:
        'Esta conta ainda não está aprovada. O saldo do fornecedor será reservado agora e a contabilização (lançamentos no livro) ocorrerá quando a conta for aprovada.',
    },
    paymentMode: {
      title: 'Como deseja pagar?',
      total: 'Total',
      partial: 'Parcial',
      installments: 'Parcelamento',
      totalValue: '{{amount}}',
      defineValue: 'Definir valor',
      multiple: 'Múltiplos',
    },
    installments: {
      count: 'Número de Parcelas',
      interval: 'Intervalo',
      preview: 'Preview das Parcelas',
      installmentLabel: '{{number}}ª parcela',
      days7: '7 dias',
      days15: '15 dias',
      days30: '30 dias (mensal)',
      days60: '60 dias (bimestral)',
    },
    hints: {
      remaining: 'Restante: {{amount}}',
      lockedTotal: 'Valor bloqueado (total da conta)',
    },
    validation: {
      pixKeyRequired: 'Chave PIX é obrigatória',
      pixKeyInvalid: 'Chave PIX inválida. Informe CPF, CNPJ, email, telefone ou chave aleatória.',
      barcodeRequired: 'Código de barras é obrigatório',
      barcodeInvalid: 'Código de barras inválido. Deve ter 44 ou 47 dígitos.',
      barcodeInvalidChecksum: 'Código de barras com dígito verificador inválido',
      bankAccountRequired: 'Conta bancária é obrigatória para TED/DOC',
      noBankAccounts: 'Nenhuma conta bancária cadastrada para este fornecedor',
      creditCardRequired: 'Cartão de crédito é obrigatório',
      noCreditCards: 'Nenhum cartão de crédito cadastrado',
    },
    pixKeyTypes: {
      CPF: 'CPF',
      CNPJ: 'CNPJ',
      EMAIL: 'Email',
      PHONE: 'Telefone',
      RANDOM: 'Chave Aleatória',
    },
  },
  reimbursement: {
    policyApplied: 'Política aplicada: {{name}}',
    viewPolicy: 'Ver política',
    noPolicyMatched: 'Nenhuma política aplicada',
    policyDialogTitle: 'Política de reembolso',
    policyDialogEmpty: 'Nenhuma política encontrada para este reembolso.',
    dueDate: 'Vencimento do reembolso',
    method: 'Dados para pagamento',
    methodHelper: 'Selecione a conta ou chave PIX que receberá o reembolso',
    originAccountHelper: 'Conta da empresa de onde sairá o pagamento do reembolso',
    dueDateHelper: 'Informe a data de vencimento para o pagamento do reembolso',
    addPaymentMethod: 'Adicionar método de pagamento',
    selectPaymentMethod: 'Selecione o método de pagamento',
    noPaymentMethods: 'Nenhum método de reembolso disponível',
    selectRequesterForPaymentMethods: 'Selecione o solicitante para ver métodos de pagamento',
    selectRequesterForPaymentMethodsHint:
      'Selecione o solicitante do reembolso para exibir os métodos de pagamento disponíveis.',
    generatedPayment: 'Pagamento gerado',
    requestSectionLabel: 'Solicitação de reembolso',
    reimburseFor: 'Reembolsar para',
    selectBeneficiary: 'Selecione o beneficiário',
    selectEmployeeBeneficiary: 'Selecione o colaborador',
    selectVendorBeneficiary: 'Fornecedor',
    beneficiaryModeEmployee: 'Colaborador',
    beneficiaryModeVendor: 'Fornecedor',
    beneficiaryVendorLinked: 'Beneficiário (fornecedor) vinculado',
    expenseClassification: 'Classificação da despesa',
    refundAmount: 'Valor de reembolso',
    billTotalHint: 'Total: {{total}}',
    fullAmount: 'Valor total',
    amountAboveTotalLabel: 'Valor acima do total',
    amountAboveTotalTooltip: 'O valor informado ({amount}) ultrapassa o total da conta.',
    policyLimit: 'Limite por transação',
    periodLimit: 'Limite no período',
    periodUsed: 'Usado no período',
    periodRemaining: 'Restante no período',
    periodUsageLoading: 'Calculando uso do período...',
    exceedsPolicyLimit: 'Valor acima do limite por transação',
    exceedsPeriodLimit: 'Valor acima do limite do período',
    nearPeriodLimit: 'Limite do período quase atingido',
    justification: 'Justificativa',
    justificationPlaceholder: 'Descreva o motivo do reembolso...',
    instructionsLabel: 'Orientações para preenchimento',
    required: 'Obrigatório',
    optional: 'Opcional',
    requiredByPolicy: 'Exigido pela política de reembolso vigente',
    optionalByPolicy: 'Opcional pela política de reembolso vigente',
    alertsTitle: 'Alertas',
    alertsApprovalTitle: 'Alertas para aprovação',
    flags: {
      noPolicyMatched:
        'Essa conta está identificada como reembolso, mas nenhuma política está aplicada',
      goToPayments: 'Ir para Pagamentos',
      exceedsTransactionLimit: 'Valor acima do limite por transação',
      exceedsPeriodLimit: 'Valor acima do limite do período',
      nearPeriodLimit: 'Limite do período quase atingido',
      lateSubmission: 'Despesa fora do prazo da política',
      autoApproved: 'Reembolso autoaprovado',
      policyOverride: 'Política ajustada manualmente',
      taxIdMissing: 'Documento sem CPF/CNPJ da entidade',
    },
    validation: {
      missingInfo: 'Informações do reembolso são obrigatórias',
      requesterRequired: 'Selecione o solicitante do reembolso',
      expenseDateRequired: 'A data da despesa é obrigatória',
      dueDateRequired: 'A data de vencimento do reembolso é obrigatória',
      paymentMethodRequired: 'Selecione o método de pagamento',
      originAccountRequired: 'Selecione a conta de origem do reembolso',
      expenseClassificationRequired: 'Informe a classificação da despesa',
      amountRequired: 'O valor de reembolso deve ser maior que zero',
      justificationRequired: 'A justificativa do reembolso é obrigatória',
    },
  },
  countryTaxes: {
    source: 'Valores do documento fiscal',
    empty: 'Sem impostos informados no documento',
    retainedToggle: 'Retenções se aplicam',
    payableImpact:
      'O líquido a pagar passa de {{from}} para {{to}}; os pagamentos agendados são ajustados a esse valor.',
    netPayable: 'Líquido a pagar',
    notRetainedHint: 'Sem retenção: o fornecedor recebe o valor bruto da conta.',
    ivaTransferred: { label: 'IVA trasladado', hint: 'Acreditável; fica pendente até o pagamento' },
    ivaWithheld: { label: 'IVA retenido', hint: 'Retido na fonte; a entidade recolhe ao SAT' },
    isrWithheld: { label: 'ISR retenido', hint: 'Retido na fonte; a entidade recolhe ao SAT' },
    salesTax: { label: 'Sales tax', hint: 'Incluído no custo, sem crédito' },
    taxEdit: {
      save: 'Salvar',
      effectiveRate: 'Alíquota efetiva: {{rate}} sobre o valor antes do imposto',
      invalid:
        'O imposto precisa ser maior ou igual a zero e menor que o total da nota ({{total}}).',
      belowWithheld: 'O IVA trasladado não pode ser menor que o IVA retenido ({{withheld}}).',
    },
    salesTaxInCost: '+ {{amount}} de sales tax incluído no custo',
    unusualRate: {
      ivaTransferred:
        'IVA trasladado de {{rate}} do valor antes do imposto; o usual é 16%, 8% ou 0%. Confira o documento.',
      ivaWithheld:
        'IVA retenido de {{rate}} do valor antes do imposto; o usual é 2/3 do IVA trasladado, ou 4% em fretes. Confira o documento.',
      isrWithheld:
        'ISR retenido de {{rate}} do valor antes do imposto; o usual é 10% ou 1,25%. Confira o documento.',
    },
  },
  aiPosting: {
    badge: 'IA',
    pending: 'Gerando a postagem contábil...',
    processingStarted: 'Enviado. A postagem contábil está sendo gerada.',
    provisionFailed: 'Provisão não postada. {{reason}} O fechamento fica bloqueado até resolver.',
    provisionRefused:
      'A IA e a provisão padrão recusaram esta conta. {{reason}} Corrija e reenvie para aprovação.',
    provisionPendingBadge: 'Provisão pendente',
    provisionRefusedBadge: 'Provisão recusada',
    fixWithholdings: 'Corrigir retenções',
    failureReasons: {
      AP_AI_POSTING_INPUT_INCOMPLETE:
        'As retenções de IVA e ISR não batem com o total retido, ou faltam dados da conta (competência, fornecedor). Corrija as retenções.',
      generic: 'Revise valores, retenções e classificação.',
      AP_WITHHOLDING_SLOT_UNSUPPORTED:
        'A conta tem uma retenção que não se aplica a esta entidade (ex.: retenção brasileira numa conta do México). Revise as retenções.',
      AP_WITHHOLDING_TOTAL_MISMATCH:
        'As retenções de IVA e ISR não somam o total retido da conta. Corrija as retenções.',
      TAX_ACCOUNT_NOT_MAPPED:
        'Mapeie as contas de retenção e de IVA em Configurações → Contábil & Fiscal → Mapeamento de contas (aba Fiscal).',
      OP_BANK_ACCOUNT_MISSING:
        'Mapeie a conta de Contas bancárias em Configurações → Contábil & Fiscal → Mapeamento de contas (aba Tesouraria): o pagamento já quitado é lançado contra ela.',
      AP_BILL_FOREIGN_CURRENCY_NO_RATE:
        'Falta a cotação da moeda da conta na data de competência. Cadastre a cotação.',
      AP_BILL_CURRENCY_REVIEW_REQUIRED:
        'A moeda da conta precisa ser revisada antes de lançar. Confira a moeda.',
    },
    retry: 'Tentar novamente com IA',
    retryConfirmTitle: 'Tentar novamente com IA?',
    retryConfirmDescription:
      'A IA propõe o lançamento de novo. Se a proposta for válida, ela é postada no razão; se não, a provisão padrão é tentada.',
    retryPosted: 'A IA postou a provisão.',
    retryFallback: 'A IA não validou; a provisão padrão foi postada.',
    retryStillFailed: 'A provisão ainda não pôde ser postada. {{reason}}',
    paymentLegNotPosted:
      'Pagamento não lançado no razão. {{reason}} O fechamento fica bloqueado até resolver.',
    paymentLegReasons: {
      generic:
        'A provisão foi postada, mas o lançamento do pagamento ficou de fora. Revise o pagamento e tente de novo.',
    },
    paymentLegRetry: 'Lançar pagamento',
    paymentLegRetryTitle: 'Lançar pagamento novamente?',
    paymentLegRetryDescription:
      'Os pagamentos desta conta que ficaram fora do razão são lançados de novo. A provisão não muda.',
    paymentLegStillNotPosted: 'O pagamento ainda não pôde ser lançado. {{reason}}',
    paymentLegPosted: 'Pagamento lançado no razão.',
    postedBanner: 'Postagem contábil realizada via IA',
    viewEntry: 'Ver lançamento',
    reviewed: 'Revisado',
    reviewedAt: 'Revisado em {{date}}',
    awaitingReview: 'Aguardando revisão no fechamento',
    confidence: 'Confiança: {{value}}%',
    reportedConfidence: '(informada pela IA: {{value}}%)',
    divergesFromClassification: 'Conta diferente da classificação',
    provenance: 'Modelo {{model}} · prompt {{version}}',
    markReviewed: 'Marcar revisado',
    correct: 'Corrigir',
    notCorrectableHint:
      'Depois do pagamento ou com o período fechado, corrija por reclassificação.',
    saveCorrection: 'Reverter e lançar',
    correctConfirmTitle: 'Reverter e lançar de novo?',
    correctConfirmDescription:
      'O lançamento da IA será revertido e um novo será postado com estas linhas. A correção conta como revisão.',
    cancelEdit: 'Cancelar',
    amount: 'Valor',
    addLine: 'Adicionar linha',
    removeLine: 'Remover linha',
    account: 'Conta',
    memo: 'Histórico da linha',
    debit: 'Débito',
    credit: 'Crédito',
    splitLine: 'Dividir linha',
    balanced: 'Balanceado',
    empty: 'Sem valores',
    difference: 'Diferença de {{amount}}',
    totalTarget: 'Débitos e créditos precisam fechar em {{amount}} (líquido + retenções da conta)',
    lineType: 'Tipo de linha',
    lineTypePlaceholder: 'Escolha o tipo',
    classification: 'Classificação',
    classificationPlaceholder: 'Classificação',
    roles: {
      EXPENSE: 'Despesa',
      ASSET: 'Ativo',
      TAX_RECOVERABLE: 'Imposto recuperável',
      TAX_WITHHELD_IVA: 'Retenção de IVA',
      TAX_WITHHELD_ISR: 'Retenção de ISR',
      AP_VENDOR: 'Fornecedor',
    },
    errors: {
      AP_AI_POSTING_UNBALANCED:
        'Débitos e créditos não fecham, ou o fornecedor não recebe exatamente o líquido a pagar da conta.',
      AP_AI_POSTING_ROLE_ACCOUNT_INVALID:
        'Uma linha usa uma conta incompatível com o tipo dela (ex.: passivo numa linha de despesa).',
      AP_AI_POSTING_ASSET_OUTSIDE_CLASSIFICATION:
        'A IA lançou custo num ativo que a classificação da conta não escolheu; foi usado o lançamento padrão.',
      AP_AI_POSTING_ALLOCATION_MISMATCH:
        'O custo de cada classificação da conta precisa continuar o mesmo. Ao dividir uma despesa, mantenha a linha nova na classificação da linha original.',
      AP_AI_POSTING_TAX_AMOUNT_MISMATCH:
        'Os valores de imposto ou retenção não batem com os informados no documento.',
      AP_AI_POSTING_ACCOUNT_INVALID:
        'Uma das contas não existe, está inativa ou não aceita lançamento no plano desta entidade.',
      AP_AI_POSTING_AMOUNT_INVALID: 'Há um valor inválido: use valores positivos com até 2 casas.',
      AP_AI_POSTING_CLASSIFICATION_REQUIRED:
        'Linhas de despesa ou ativo precisam vir de uma classificação da conta.',
      AP_AI_POSTING_CLASSIFICATION_INVALID: 'A linha aponta para uma classificação de outra conta.',
      AP_AI_POSTING_AP_SIDE_INVALID: 'A linha do fornecedor precisa ser a crédito.',
      AP_AI_POSTING_TAX_SIDE_INVALID: 'Linhas de retenção precisam ser a crédito.',
      AP_AI_POSTING_NOT_POSTED: 'Esta conta não tem lançamento postado pela IA. Atualize a página.',
      AP_AI_POSTING_NOT_CORRECTABLE:
        'Este lançamento não pode mais ser corrigido aqui (conta paga ou período fechado). Use reclassificação.',
      AP_AI_POSTING_VENDOR_LOCKED: 'A linha do fornecedor não pode mudar na correção.',
      AP_AI_POSTING_RETRY_UNAVAILABLE:
        'Esta conta não está mais aguardando nova tentativa. Atualize a página.',
      AP_AI_POSTING_SCOPE_CHANGED:
        'A conta mudou e não se enquadra mais no lançamento pela IA (ex.: virou reembolsável, ganhou pedido de compra, recorrência ou orçamento).',
      AP_AI_POSTING_STALE:
        'O lançamento mudou desde que você abriu esta tela. Recarregamos a versão atual; confira antes de agir de novo.',
    },
    toasts: {
      corrected: 'Lançamento revertido e lançado de novo.',
    },
  },
  glPosting: {
    editAndPostDescription:
      'Esta conta ainda não tem lançamento contábil: nada será estornado. A conta é corrigida e a provisão é lançada. Informe a justificativa.',
    editAndRepostSuccess:
      'Alterações aplicadas. Quando a conta é postada pela IA, a nova provisão é gerada em seguida e aparece na conta.',
    linkLabel: 'Provisionado no Livro Razão',
    title: 'Provisionado no Livro Razão',
    description: 'Visualize os lançamentos contábeis gerados para a conta em cada livro.',
    comprehensiveTitle: 'Lançamentos no Livro Razão',
    accountingEntriesTitle: 'Lançamentos Contábeis',
    comprehensiveDescription:
      'Visualize os lançamentos contábeis de provisão e de pagamento em cada livro.',
    provisionSectionTitle: 'Provisão',
    paymentSectionTitle: 'Pagamento',
    summaryProvisionedLabel: 'Provisionado',
    summaryPaidLabel: 'Pago',
    summaryPassiveLabel: 'Saldo passivo',
    payroll: {
      entryMonthly: 'Provisão/Folha',
      entryVacationAdvance: 'Adiantamento de férias',
      entryReceipt: 'Recibo',
      entryAutonomous: 'RPA',
      entryAdvance: 'Adiantamento',
      entryThirteenth: '13º salário',
      entryProLabore: 'Pró-labore',
      summaryGeneratedLabel: 'Obrigações geradas',
      summaryPaidLabel: 'Pagas',
      summaryOpenLabel: 'Em aberto',
      summaryGeneratedHint:
        'Soma dos créditos em contas de passivo nos lançamentos desta folha, sem os estornados.',
      summaryPaidHint:
        'Soma das contas a pagar desta folha já pagas (status Pago). Pagamentos parciais ainda não entram.',
      summaryOpenHint: 'Obrigações geradas menos Pagas.',
      summaryUnavailable: '—',
    },
    reversedShort: 'Estornado',
    entryProvision: 'Provisão',
    entryRevaluation: 'Reavaliação cambial',
    entryRevenueTax: 'Provisão de PIS/COFINS',
    entryRevenueTaxReversal: 'Estorno da provisão de PIS/COFINS',
    entryRevenueTaxOther: 'Imposto sobre a receita',
    revenueTaxGroup: 'Outros impostos sobre receita provisionados',
    afterRevenueTaxGroup: 'Demais lançamentos',
    entryPayment: 'Pagamento',
    statusPosted: 'Postado',
    statusPending: 'Pendente',
    statusReversed: 'Estornado',
    statusReversal: 'Estorno',
    periodShort: 'Período',
    lineCountLines: '{{count}} linhas',
    collapsedDebitCredit: 'D {{d}} · C {{c}}',
    totalLabel: 'Total',
    generatedAt: 'Gerado em {{date}}',
    export: 'Exportar',
    empty: 'Nenhum lançamento encontrado.',
    loadError:
      'Não foi possível carregar os lançamentos contábeis. Verifique suas permissões ou tente novamente.',
    retry: 'Tentar novamente',
    journalDate: 'Data',
    period: 'Período',
    status: 'Status',
    memo: 'Histórico',
    account: 'Conta',
    amountColumn: 'Valor',
    debit: 'Débito',
    credit: 'Crédito',
    reversed: 'Estornado',
    reversal: 'Estorno',
    reviewButton: 'Revisar',
    revertApproval: 'Reverter aprovação',
    revertApprovalTitle: 'Reverter aprovação?',
    revertApprovalDescription:
      'Esta conta aprovada não tem lançamento contábil de provisão para estornar. Ela voltará para revisão como enviada, preservando documentos, pagamentos e histórico.',
    revertApprovalConfirm: 'Reverter aprovação',
    revertApprovalConfirming: 'Revertendo...',
    revertApprovalSuccess: 'Aprovação revertida. A conta voltou para revisão.',
    revertApprovalHistoryReason:
      'Aprovação revertida para revisão operacional sem estorno contábil.',
    editGlEntries: 'Editar lançamentos',
    saveEditMode: 'Salvar alterações',
    flagForReview: 'Marcar para revisão contábil',
    flagForReviewDescription:
      'Esta conta será marcada para revisão contábil. Informe as observações sobre o que precisa ser verificado.',
    observations: 'Observações',
    observationsPlaceholder: 'Descreva o que precisa ser revisado...',
    flagForReviewConfirm: 'Marcar para revisão',
    flagForReviewConfirming: 'Marcando...',
    revertBill: 'Reverter conta',
    revertToEditing: 'Reverter para edição',
    voidBill: 'Cancelar conta',
    voidSuccess: 'Conta invalidada com sucesso.',
    voidPostedBillDescription:
      'Os lançamentos contábeis serão estornados no período selecionado e a conta será invalidada. Selecione o período e informe a justificativa.',
    voidConfirmTitle: 'Invalidar esta conta?',
    voidConfirmDescription:
      'Esta ação invalida a conta de forma permanente. O fluxo de aprovação é cancelado e vínculos de documentos podem ser removidos.',
    confirmVoidInvalidation: 'Confirmar invalidação',
    selectPeriod: 'Selecionar período',
    justification: 'Justificativa',
    justificationPlaceholder: 'Informe o motivo da reversão...',
    reversalDialogTitle: 'Reverter conta',
    reversalActionLabel: 'Ação',
    reversalDialogDescription:
      'Escolha entre cancelar a conta ou reverter para edição. Serão gerados estornos dos lançamentos contábeis no período selecionado.',
    reversalDialogSuccess: 'Reversão concluída',
    reversalDialogRetry: 'Tentar novamente',
    reversalCompletedVoidOnly: 'Contas concluídas só podem ser invalidadas.',
    reversalDialogConfirm: 'Confirmar reversão',
    reversalDialogConfirming: 'Revertendo...',
    modifyClassifications: 'Modificar classificações contábeis',
    modifyClassificationsBanner:
      'Ao modificar a conta contábil, serão lançadas reversões das postagens anteriores no livro razão e, na sequência, uma nova postagem.',
    saveReclassification: 'Salvar modificação contábil',
    requestClassificationChange: 'Solicitar alteração de classificação',
    classificationChangeRequestTitle: 'Solicitar alteração de classificação',
    classificationChangeRequestDescription:
      'Informe a nova conta contábil desejada e a justificativa. Uma solicitação será enviada para revisão contábil.',
    newGlAccount: 'Nova conta contábil',
    newGlAccountPlaceholder: 'Selecione a conta contábil',
    classificationChangeRequestConfirm: 'Enviar solicitação',
    classificationChangeRequestConfirming: 'Enviando...',
    reclassify: 'Modificar conta contábil',
    reclassifyConfirm:
      'Será gerada uma reversão no livro razão do lançamento realizado previamente, e na sequência um novo lançamento',
    sendToReview: 'Uma solicitação de revisão contábil será criada',
    classificationChangeRequestSuccess: 'Solicitação de revisão enviada',
    reclassifySuccess: 'Classificação alterada',
    applyEdits: 'Aplicar alterações',
    rejectEdits: 'Rejeitar alterações',
    markAsReviewed: 'Marcar como revisada',
    markAsReviewedDescription:
      'Adicione observações sobre a revisão realizada e confirme para concluir.',
    markAsReviewedConfirm: 'Marcar como revisada',
    markAsReviewedConfirming: 'Salvando...',
    markAsReviewedSuccess: 'Revisão concluída',
    requestedByLine: '{{date}} · {{name}}',
    pendingReview: 'Revisão contábil pendente',
    awaitingReview: 'Aguardando revisão',
    fallbackReclassification: {
      action: 'Definir conta final',
      title: 'Definir conta contábil final',
      description:
        'Transfira o saldo temporário para a conta contábil correta. Você pode concluir o saldo inteiro ou registrar uma parte agora.',
      book: 'Livro',
      temporaryAccount: 'Conta temporária',
      remaining: 'Saldo a reclassificar',
      finalAccount: 'Conta contábil final',
      finalAccountPlaceholder: 'Selecione a conta final',
      journalDate: 'Data do lançamento',
      amount: 'Valor',
      submit: 'Registrar reclassificação',
      posting: 'Registrando...',
      partialSuccess: '{{amount}} reclassificado. O saldo restante continua em revisão.',
      completedSuccess:
        'Saldo temporário zerado. A revisão já pode ser concluída em Aplicar alterações.',
      reviewCompletedSuccess: 'Saldo reclassificado e revisão contábil concluída.',
      submitError: 'Não foi possível registrar a reclassificação.',
      loadErrorTitle: 'Não foi possível carregar o saldo',
      loadErrorDescription: 'Tente novamente para conferir os valores atuais.',
      noOpenPeriodTitle: 'Nenhum período aberto',
      noOpenPeriodDescription:
        'Abra um período contábil deste livro antes de registrar a reclassificação.',
      clearedTitle: 'Saldo temporário zerado',
      clearedDescription: 'Feche esta janela e conclua a revisão em Aplicar alterações.',
      history: 'Correções registradas',
      historyAction: 'Ver correções contábeis',
      reverse: 'Estornar correção',
      reversed: 'Estornada',
      reverseTitle: 'Estornar esta correção contábil?',
      reverseDescription:
        'O saldo voltará para a conta temporária e a revisão contábil será reaberta.',
      reverseConfirm: 'Confirmar estorno',
      reverseSuccess: 'Correção estornada. A revisão contábil foi reaberta.',
      reverseAuditReason: 'Estorno solicitado na revisão contábil da conta a pagar.',
    },
    pendingReviewFlagDetail:
      'Conta marcada para revisão contábil (sem alteração de classificação).',
    flagForReviewSuccess: 'Conta marcada para revisão contábil.',
    revertToSubmittedSuccess: 'Conta revertida para edição.',
    postingsToReverse: 'Lançamentos que serão estornados',
    paymentReversalSamePeriod:
      'A reconciliação do pagamento será desfeita. A transação bancária voltará para pendente e poderá ser reconciliada com outro pagamento.',
    paymentReversalDifferentPeriod:
      'O pagamento e a reconciliação bancária serão preservados. O saldo resultante em Contas a Pagar precisará ser reclassificado.',
    paymentReversalNoReconciledPayments:
      'Não há pagamentos reconciliados nesta conta. A reversão segue as regras contábeis dos lançamentos provisionados e de pagamento já postados.',
  },
  alertTypes: {
    BILL_ALREADY_APPROVED: 'Conta já aprovada',
    BILL_ALREADY_PAID: 'Conta já paga',
    DUPLICATE_DOCUMENT: 'Documento duplicado',
    ENTITY_MISMATCH: 'CNPJ do tomador não corresponde a nenhuma entidade',
    ENTITY_MISMATCH_NO_MATCH: 'CNPJ do tomador não corresponde a nenhuma entidade',
    ENTITY_MISMATCH_AMBIGUOUS: 'CNPJ do tomador corresponde a múltiplas entidades',
    ENTITY_MISMATCH_TAX_ID_CONFLICT:
      'O CNPJ do tomador aponta para outra entidade; confirme a entidade antes de aprovar',
    ENTITY_MISMATCH_DOCUMENT_DISAGREES:
      'O documento indica outra entidade; confirme a entidade antes de aprovar',
    ENTITY_MISMATCH_USER_CHOICE:
      'O CNPJ do tomador aponta para outra entidade, mas a entidade escolhida no e-mail foi mantida; confirme antes de aprovar',
    ENTITY_SUGGESTION_AVAILABLE: 'Sugestão de entidade: {{entityName}} — confirme antes de aprovar',
    EXTERNAL_FORM_TAXPAYER_MISMATCH:
      'Contribuinte do card difere do documento; confira o CNPJ/CPF antes de aprovar',
    AP_BILL_NET_AMOUNT_INCOHERENT:
      'Atualização automática recusada: o documento traria bruto {{grossAmount}} e líquido {{netPaymentAmount}}, mas o líquido esperado é {{expectedNetAmount}} (bruto mais juros e multa, menos retenções de {{retainedAmount}}). Valores da conta mantidos; confira o documento',
    AP_BILL_NET_AMOUNT_INCOHERENT_GROSS_EQUALS_NET:
      'Atualização automática recusada: o documento traria bruto {{grossAmount}} e líquido {{netPaymentAmount}}, mas a conta tem retenções, então o líquido e o bruto não podem ser iguais. Valores da conta mantidos; confira o documento',
    AP_BILL_NET_AMOUNT_INCOHERENT_WITH_DISCOUNT:
      'Atualização automática recusada: o documento traria bruto {{grossAmount}} e líquido {{netPaymentAmount}}, mas o líquido esperado é {{expectedNetAmount}} (bruto mais juros e multa, menos retenções de {{retainedAmount}}), ou {{expectedDiscountedNetAmount}} com o desconto de {{discountAmount}}. Valores da conta mantidos; confira o documento',
    RECONCILED_WITHOUT_APPROVAL: 'Reconciliado sem aprovação',
    AMOUNT_ANOMALY: 'Variação de valor fora do esperado',
    AMOUNT_ANOMALY_CURRENCY_UNRESOLVED:
      'Moeda da transação Clara não identificada — confirme a moeda antes de aprovar',
    AMOUNT_ANOMALY_SETTLEMENT_CURRENCY_SKIPPED:
      'Baixa contábil do pagamento não lançada por divergência de moeda — verifique a moeda da conta e da origem financeira',
    VENDOR_CONCENTRATION: 'Concentração elevada em fornecedor',
    EXPIRING_CONTRACT: 'Contrato recorrente próximo do vencimento',
    CORPORATE_GROUP_MIXED_CNPJ:
      'Esta conta contém documentos de diferentes CNPJs do mesmo grupo empresarial',
  },
  corporateGroupMixedCnpjTooltip:
    "Caso queira desvincular, acesse as configurações do fornecedor e desabilite 'Unificar contas do grupo empresarial', e depois exclua a conta e reprocesse os arquivos",
  historyActions: {
    reconciled: 'Reconciliado',
  },
  newBill: {
    title: 'Nova conta a pagar',
    tabForm: 'Formulário',
    tabChat: 'Chat com IA',
    cancel: 'Cancelar',
    createDraft: 'Criar como rascunho',
    createBill: 'Criar conta',
    hintForm: 'Enter para salvar',
    hintChat: 'Enter para enviar · Shift+Enter nova linha',
    addBill: 'Conta',
    addBillShortcut: 'Alt+Shift+N',
    chatShortcut: 'Alt+Shift+C',
    vendor: {
      label: 'Fornecedor',
      placeholder: 'Selecione um fornecedor...',
      search: 'Buscar',
      notFound: 'Fornecedor não encontrado para',
      createViaRF: 'Criar via Receita Federal',
      createManual: 'Criar manualmente',
      consultingRF: 'Consultando CNPJ (Serpro)...',
      resolvingFromDda: 'Buscando fornecedor pelo CNPJ do boleto...',
      creatingFromDda: 'Criando fornecedor...',
      autoResolved: 'Fornecedor identificado automaticamente',
      autoResolveFailed:
        'Não foi possível criar o fornecedor automaticamente — confira o CNPJ abaixo',
      createSuccess: 'Fornecedor criado com sucesso',
      createError: 'Erro ao criar fornecedor',
      duplicateCnpj: 'Já existe um fornecedor com esse CNPJ',
      duplicateCpf: 'Já existe um fornecedor com esse CPF',
      createNewSection: 'Criar novo fornecedor',
      namePlaceholder: 'Nome do fornecedor',
      cnpjCpfPlaceholder: 'CNPJ ou CPF do fornecedor',
      ddaDiscovery: {
        knownHint: 'Fornecedor identificado pelo CNPJ do boleto DDA.',
        unknownTitle: 'Fornecedor ainda não cadastrado',
        unknownDescription:
          'Este CNPJ do boleto DDA não está no seu cadastro. Confira os dados abaixo antes de criar o fornecedor — útil para verificar possíveis fraudes.',
        previewSourceRegistry: 'Dados consultados na Receita (Serpro)',
        previewSourceDda: 'Nome informado no boleto DDA (cadastro não consultado)',
        createForMe: 'Criar fornecedor para mim',
        createSuccess: 'Fornecedor criado e selecionado',
        previewFailedTitle: 'Não foi possível consultar o CNPJ',
        previewFailedDescription: 'Confira o CNPJ {{cnpj}} e crie o fornecedor manualmente abaixo.',
      },
    },
    form: {
      value: 'Valor',
      currency: 'Moeda',
      exchangeRate: 'Taxa de câmbio (opcional)',
      exchangeRatePlaceholder: 'Ex: 5,4321',
      exchangeRateHelp:
        'Se vazio, usaremos o PTAX de fechamento da data de competência automaticamente.',
      competency: 'Competência',
      description: 'Descrição',
      descriptionPlaceholder: 'Ex: Serviços de SST - Mai/2025',
      dueDate: 'Vencimento',
      managerialDate: 'Data gerencial',
      classification: 'Classificação',
      glAccount: 'Conta contábil',
      managerialAccount: 'Conta gerencial',
      costCenter: 'Centro de custo',
      department: 'Departamento',
      payment: 'Pagamento',
      paymentMethod: 'Forma de pagamento',
      bankAccount: 'Conta bancária',
      bankAccountOption: 'Conta bancária',
      creditCardOption: 'Cartão de crédito',
      creditCardAccount: 'Cartão de crédito',
      vendorPaymentDetails: 'Dados bancários do fornecedor',
      vendorBankAccount: 'Conta bancária',
      vendorDefaultAccount: 'Conta principal',
      vendorDefaultBadge: 'Principal',
      vendorUsAccountBadge: 'EUA',
      vendorRoutingNumber: 'Routing number',
      vendorUsAccountNumber: 'Conta',
      vendorUsAccountTitle: 'Conta nos EUA',
      vendorNoBankAccounts: 'Este fornecedor não tem dados bancários cadastrados.',
      addVendorBankAccount: 'Adicionar dados bancários ao fornecedor',
      recurring: 'Conta recorrente',
      recurringHelp: 'Automatizar lançamento nos próximos meses',
      observations: 'Observações',
      observationsPlaceholder: 'Notas internas...',
      selectPlaceholder: 'Selecione...',
      entity: 'Entidade',
      classificationSuggestion: 'Reutilizar classificação de conta anterior deste fornecedor?',
      classificationSuggestionBadge: 'Histórico',
      classificationSuggestionManagerial: 'Gerencial',
      classificationSuggestionDept: 'Depto',
      classificationSuggestionApply: 'Aplicar sugestão',
      classificationSuggestionIgnore: 'Ignorar',
      classificationSuggestionDismiss: 'Fechar sugestão',
      classificationRateioHint: 'Você poderá criar rateios após salvar a conta.',
    },
    validation: {
      requiredFields: 'Preencha os campos obrigatórios',
      entityRequired: 'Selecione uma entidade',
      amountRequired: 'Informe um valor válido',
      exchangeRateInvalid: 'Informe uma taxa de câmbio válida (número positivo)',
      functionalCurrency: {
        idle: 'Selecione uma entidade para consultar a moeda funcional.',
        loading: 'Consultando a moeda funcional dos livros da entidade...',
        error: 'Não foi possível consultar a moeda funcional. Tente novamente.',
        unresolved: 'A entidade precisa ter livros com uma única moeda funcional configurada.',
        retry: 'Tentar novamente',
        retrying: 'Consultando novamente...',
      },
      glAccountRequired: 'Selecione uma conta contábil',
      managerialAccountRequired: 'Selecione uma conta gerencial',
      costCenterRequired: 'Selecione um centro de custo',
      departmentRequired: 'Selecione um departamento',
    },
    submit: {
      creating: 'Criando conta...',
      success: 'Conta criada com sucesso',
    },
    errors: {
      entityNotFound: 'Entidade não encontrada',
      vendorNotFound: 'Fornecedor não encontrado. Tente buscar novamente.',
      classificationError: 'Erro na classificação: {{message}}',
      classificationPartialFailure:
        'Conta criada, mas houve um erro ao salvar a classificação. Você pode ajustá-la na página de detalhe.',
      paymentError:
        'Conta criada, mas houve erro ao agendar o pagamento. Você pode adicionar o pagamento na página de detalhe.',
      bankAccountNotFound: 'Conta bancária não encontrada ou inativa',
      paymentDestinationMissing:
        'Conta criada sem pagamento: o fornecedor não tem dados bancários ou chave Pix cadastrados. Cadastre-os e agende o pagamento escolhendo o destino.',
      bankAccountWrongEntity: 'Conta bancária não pertence à entidade selecionada',
      paymentMethodIncompatible: 'Método de pagamento incompatível: {{message}}',
      createBillError: 'Erro ao criar conta: {{message}}',
      networkError: 'Erro de conexão. Verifique sua internet e tente novamente.',
      serverError: 'Erro interno do servidor. Tente novamente.',
      unexpectedError: 'Erro inesperado. Tente novamente.',
    },
    classification: {
      aiBanner: {
        billLevel: 'Classificação feita pela Alchemy',
        lineItem: 'Classificação deste item feita pela Alchemy',
      },
    },
    chat: {
      welcome:
        'Descreva a conta que quer lançar e eu cuido do resto. Posso criar fornecedores, classificar e agendar pagamento.',
      examplesLabel: 'Exemplos do que você pode pedir:',
      placeholder: 'Descreva a conta a pagar...',
      example1:
        'Lançar conta de R$100 para 006.408.289-06, pagar via Itaú. Se o fornecedor não existir, crie. Classificar como despesa de software.',
      example2:
        'NFS-e de R$3.500 do fornecedor CLIMEC, serviços de SST, competência fev/2026, vencimento 25/03/2026, boleto via Santander.',
      example3:
        'Assinatura mensal do AWS de R$4.200, classificar como custo de serviços > infra, centro de custo Engineering.',
    },
  },
  import: {
    breadcrumbBills: 'Contas a pagar',
    pageTitle: 'Importar Contas via CSV',
    steps: {
      upload: 'Upload',
      uploadDesc: 'Selecione o arquivo CSV',
      mapping: 'Mapeamento',
      mappingDesc: 'Associe colunas aos campos',
      review: 'Revisão',
      reviewDesc: 'Valide e ajuste os dados',
      confirm: 'Confirmação',
      confirmDesc: 'Importe as contas',
    },
    upload: {
      title: 'Selecione o arquivo CSV',
      dropzone: 'Arraste e solte um arquivo CSV aqui ou clique para selecionar',
      entityLabel: 'Entidade',
      entityPlaceholder: 'Selecione a entidade',
      entityRequired: 'Selecione uma entidade antes de continuar',
      fileSelected: 'Arquivo selecionado',
      rows: 'linhas',
      columns: 'colunas',
      removeFile: 'Remover arquivo',
      fileSizeExceeded: 'O arquivo excede o limite de 5 MB',
      emptyFile: 'O CSV está vazio ou não pôde ser lido',
      invalidHeaderLine: 'Número de linha do cabeçalho inválido para este arquivo',
      headerLineLabel: 'Linha do cabeçalho (opcional)',
      headerLinePlaceholder: 'Automático',
      headerLineHint:
        'Informe a linha 1-based do cabeçalho da tabela se o arquivo tiver texto antes das colunas (ex.: extratos). Deixe em branco para detecção automática.',
      defaultTaxIdLabel: 'CPF/CNPJ padrão (opcional)',
      defaultTaxIdPlaceholder: 'Ex.: 00.000.000/0001-91',
      defaultTaxIdHint:
        'Use quando o CSV não tiver CPF/CNPJ por linha (ex.: cartão corporativo). Este padrão aceita apenas documentos brasileiros; identificadores internacionais devem vir por linha com a coluna Tipo.',
    },
    mapping: {
      title: 'Mapeie as colunas do CSV',
      targetField: 'Campo',
      csvColumn: 'Coluna do CSV',
      noMapping: 'Não mapear',
      required: 'Obrigatório',
      optional: 'Opcional',
      requiredMissing: 'Campos obrigatórios não mapeados: {{fields}}',
      taxIdTypeGuidanceTitle: 'Como informar o tipo do identificador fiscal',
      taxIdTypeGuidance:
        'A coluna Tipo é opcional. Sem ela, o sistema identifica automaticamente CPF (11 caracteres) ou CNPJ (14 caracteres). Para fornecedores estrangeiros já cadastrados, mapeie Tipo e informe INTERNATIONAL (EIN ou outro identificador estrangeiro) ou RFC (México) em cada linha. Também mapeie Moeda e informe o código ISO (ex.: USD ou MXN); a linha será bloqueada se a moeda estiver ausente ou não for suportada. Cadastre antes o país e o regime tributário do fornecedor estrangeiro.',
      fields: {
        vendorTaxId: 'Identificador fiscal',
        vendorTaxIdType: 'Tipo do identificador fiscal',
        vendorName: 'Nome do Fornecedor',
        totalAmount: 'Valor',
        currencyCode: 'Moeda do documento',
        dueDate: 'Data de Vencimento',
        vendorBillNumber: 'Número do documento do fornecedor',
        billCompetencyDate: 'Data de Competência',
        billManagerialDate: 'Data Gerencial',
        description: 'Descrição',
        memo: 'Observações',
        glAccountName: 'Conta Contábil',
        managerialAccountName: 'Conta Gerencial',
        costCenterName: 'Centro de Custo',
        departmentName: 'Departamento',
      },
    },
    review: {
      title: 'Revise os dados importados',
      loading: 'Processando...',
      loadError: 'Falha ao carregar a prévia da importação',
      retry: 'Tentar novamente',
      summaryTitle: 'Resumo',
      totalRows: 'Total de linhas',
      totalRowsSubtitle: 'no arquivo',
      validRows: 'Linhas válidas',
      validRowsSubtitle: 'prontas para importar',
      errorRows: 'Linhas com erro',
      errorRowsSubtitle: 'precisam de ajuste',
      newVendors: 'Novos fornecedores',
      newVendorsSubtitle: 'serão criados',
      duplicates: 'Possíveis duplicatas',
      duplicatesSubtitle: 'requerem atenção',
      vendor: 'Fornecedor',
      taxId: 'Identificador fiscal',
      taxIdPlaceholder: 'Informe o CPF, CNPJ, RFC ou identificador internacional',
      amount: 'Valor',
      dueDate: 'Vencimento',
      competencyDate: 'Competência',
      managerialDate: 'Gerencial',
      description: 'Descrição',
      descriptionPlaceholder: 'Descrição da conta',
      memo: 'Observações',
      memoPlaceholder: 'Observações internas',
      glAccount: 'Conta contábil',
      glAccountPlaceholder: 'Selecione a conta contábil',
      managerialAccount: 'Conta gerencial',
      managerialAccountPlaceholder: 'Selecione a conta gerencial',
      costCenter: 'Centro de custo',
      costCenterPlaceholder: 'Selecione o centro de custo',
      department: 'Departamento',
      departmentPlaceholder: 'Selecione o departamento',
      suggestions: 'Sugestões',
      noSuggestions: 'Sem sugestões',
      status: 'Status',
      errors: 'Erros',
      warnings: 'Possível duplicata',
      valid: 'Válido',
      classificationWarning: 'Aviso',
      excluded: 'Excluído',
      newVendor: 'Novo',
      existingVendor: 'Existente',
      invalidData: 'Dado inválido',
      validationErrors: {
        taxIdRequired: 'O identificador fiscal é obrigatório',
        invalidCnpj: 'CNPJ inválido',
        invalidCpf: 'CPF inválido',
        taxIdDigits: 'CPF/CNPJ deve ter 11 ou 14 caracteres',
        taxIdTypeRequired:
          'Não foi possível identificar o tipo. Mapeie a coluna Tipo e use INTERNATIONAL ou RFC.',
        invalidRfc: 'RFC inválido. Use o formato mexicano de 12 ou 13 caracteres.',
        unsupportedTaxIdType:
          'Tipo não aceito. Use CPF, CNPJ, INTERNATIONAL ou RFC na coluna Tipo.',
        foreignVendorRegistrationRequired:
          'Fornecedor estrangeiro não encontrado. Cadastre-o com país e regime tributário antes da importação e envie a planilha novamente.',
        amountRequired: 'Valor é obrigatório e deve ser um número válido',
        dueDateRequired: 'Data de vencimento é obrigatória (DD/MM/AA)',
        competencyDateInvalid: 'Data de competência deve ser uma data válida',
        managerialDateInvalid: 'Data gerencial deve ser uma data válida',
        unknown: 'Erro de validação',
      },
      classificationWarnings: {
        glNotPostable: 'A conta contábil selecionada não é lançável. Selecione uma conta lançável.',
      },
      amountWarnings: {
        highAmount: 'Valor excepcionalmente alto — verifique se está correto.',
      },
      viewDuplicateBill: 'Ver conta duplicada',
      scrollToRow: 'Ir para linha {{row}}',
      noValidRows: 'Nenhuma linha válida para importar',
      lockColumns: 'Fixar colunas ao rolar',
      freezeUpToColumn: 'Congelar até essa coluna',
      unlockColumns: 'Desfixar colunas',
      excludeRow: 'Excluir linha',
      excludeRowConfirmTitle: 'Excluir linha da importação?',
      excludeRowConfirmDescription:
        'Esta linha não será importada. Você pode incluir novamente antes de confirmar.',
      fixInvalidRowsTooltip: 'Corrija as linhas inválidas para importar',
      importConfirmTitle: 'Confirmar importação',
      importConfirmDescription: 'Deseja prosseguir com a importação das contas?',
      importConfirmDuplicatesTitle: 'Possíveis duplicatas',
      importConfirmDuplicatesDescription:
        'Existem possíveis duplicatas nas linhas selecionadas. Deseja continuar mesmo assim?',
      includeRow: 'Incluir linha',
      searchDimensions: 'Buscar opções',
      loadingDimensions: 'Carregando opções...',
      suggested: 'Sugestões encontradas',
      noMatches: 'Nenhuma opção encontrada',
      clearSelection: 'Limpar seleção',
      confidence: 'Confiança: {{value}}%',
      source: {
        rule: 'Regra',
        history: 'Histórico',
        context: 'Contexto',
        csv_text: 'CSV',
      },
      importAsMigrated: 'Importar como migradas',
      importAsMigratedHelp:
        'histórico do sistema anterior: sem lançamento contábil, visível só na aba Todas',
    },
    confirm: {
      title: 'Importando contas...',
      success: '{{count}} conta(s) importada(s) com sucesso',
      vendorsCreated: '{{count}} fornecedor(es) criado(s)',
      errors: '{{count}} conta(s) com erro',
      errorDetailsTitle: 'Detalhes dos erros',
      vendorErrorDetailsTitle: 'Falhas ao criar fornecedores',
      vendorErrorTaxId: 'Identificador fiscal {{taxId}}',
      warningDetailsTitle: 'Importadas com aviso (classificação não aplicada)',
      errorRow: 'Linha {{row}}',
      viewBill: 'Ver conta',
      goToBills: 'Ir para Contas a Pagar',
      importMore: 'Importar mais',
      done: 'Importação concluída',
      downloadFailedRows: 'Baixar linhas com erro',
      knownErrors: {
        glNotPostable:
          'A conta contábil selecionada não é lançável. Edite a classificação na conta importada.',
        vendorNotFound: 'Fornecedor não encontrado e não pôde ser criado',
      },
    },
    actions: {
      next: 'Próximo',
      back: 'Voltar',
      import: 'Importar',
      cancel: 'Cancelar',
      preview: 'Pré-visualizar',
    },
  },
} as const;

export const billsEn = {
  needsReview: {
    title: 'Payments under review',
    subtitle:
      'Payments with a bank result Alchemy ERP does not apply on its own. Check with the bank, act on the payment and record the decision.',
    views: 'Reasons',
    viewAll: 'All',
    reason: {
      REVERSED_AFTER_PAYMENT: 'Reversed after payment',
      REVERSED_AFTER_RECONCILIATION: 'Reversed after reconciliation',
      NO_PROVIDER_RESULT_BY_DUE_TIME: 'No result in time',
      PROVIDER_RESULT_CODE_UNKNOWN: 'Unrecognised result',
      PROVIDER_RESULT_AMOUNT_MISMATCH: 'Different amount paid',
      PROVIDER_RESULT_UNMATCHED: 'Result with no matching send',
      PROVIDER_PAYMENT_HELD: 'Held by the bank',
      UNKNOWN: 'Unrecognised reason',
    },
    reasonHint: {
      REVERSED_AFTER_PAYMENT:
        'The bank reversed a payment that was already paid. The payment now reads reversed, but its journal entry and the bill still read paid: reverse the entry and reopen the bill if needed.',
      REVERSED_AFTER_RECONCILIATION:
        'The bank reversed a payment that was already reconciled. Its status was not changed: check the statement and undo the reconciliation if needed.',
      NO_PROVIDER_RESULT_BY_DUE_TIME:
        'The payment gateway accepted the send and gave no result in time. Check with the bank whether the payment went out before resending.',
      PROVIDER_RESULT_CODE_UNKNOWN:
        'The payment gateway reported a bank result Alchemy ERP does not apply on its own. Check with the bank what happened to the payment before acting.',
      PROVIDER_RESULT_AMOUNT_MISMATCH:
        'The bank reported the payment as made, but with an amount or date that does not match the send. The payment was not settled: check with the bank and settle it manually if needed.',
      PROVIDER_RESULT_UNMATCHED:
        'The payment gateway returned a result with this payment reference that does not match the send on record (another origin account, an unconfirmed send or a payment already closed). Nothing was applied: check with the bank.',
      PROVIDER_PAYMENT_HELD:
        'The bank held the payment in its security checks or invalidated it. It was neither paid nor refused: check in the bank app what is missing and, if needed, cancel the send before sending again.',
      UNKNOWN:
        'Alchemy ERP does not recognise this review reason. Nothing was applied to the payment: check with the bank what happened before acting and, if it persists, contact support.',
    },
    columns: {
      vendor: 'Vendor',
      reason: 'Reason',
      amount: 'Amount',
      scheduledDate: 'Pay on',
      status: 'Status',
      raisedAt: 'Under review since',
      origin: 'Origin account',
    },
    unknownVendor: 'No vendor',
    resolve: 'Resolve',
    resolveRow: 'Resolve the review of the payment to {{name}}',
    emptyTitle: 'No payments under review',
    emptyDescription:
      'When the bank reverses a payment or gives no result for a send in time, it shows up here.',
    dialog: {
      title: 'Resolve review',
      description:
        'Record what you checked and what was done. The payment status does not change here: pay it, cancel the send or reverse the entry through the payment flows.',
      noteLabel: 'Decision',
      notePlaceholder: 'E.g. checked with the bank, reversal credited; entry reversed.',
      noteHint: 'At least {{min}} characters. Kept on the payment history.',
      submit: 'Record decision',
      cancel: 'Cancel',
      success: 'Review resolved.',
      refusal: {
        PAYMENT_NOT_IN_NEEDS_REVIEW:
          'This payment no longer awaits review: someone resolved it or the bank sent the result. Refresh the list.',
      },
    },
  },
  postApprovalOffers: {
    stepBar: '{{current}} of {{total}}',
    stepError: {
      title: 'Could not show this suggestion',
      next: 'Next',
      close: 'Close',
    },
  },
  // ALC-4565: the approval-rule step of the post-approval dialog, its hint and its lines.
  approvalOffer: {
    hint: 'Create a rule for the next {{vendorShort}} bills like this one',
    hintButton: 'Create rule',
    billRef: 'NF {{number}}',
    title: 'Automate the next {{vendorShort}} bills?',
    titleApproveOnly: 'Approve the next {{vendorShort}} bills on their own?',
    basedOn: 'Based on {{billRef}}, which you approved · {{entity}}',
    basedOnNoRef: 'Based on this bill, which you approved · {{entity}}',
    basedOnByRule: 'Based on {{billRef}}, which a rule approved · {{entity}}',
    basedOnByRuleNoRef: 'Based on this bill, which a rule approved · {{entity}}',
    whenTitle: 'When a {{vendorShort}} bill arrives with',
    rows: {
      glAccounts: 'GL account',
      costCenters: 'Cost center',
      departments: 'Department',
      managerialAccounts: 'Managerial account',
      segment: 'Segment',
      paymentMethod: 'Payment method',
      withholding: 'Withholdings',
      clientReimbursable: 'Reimbursable',
      amount: 'Total up to',
    },
    editAria: 'Edit {{label}}',
    removeFromRule: 'Remove from the rule',
    outOfRule: 'Not in the rule:',
    backToRule: 'Put {{label}} back in the rule',
    variesRecently: 'varies in recent bills',
    done: 'Done',
    yes: 'Yes',
    no: 'No',
    joinAnd: ' and ',
    joinOr: ' or ',
    modeGroup: 'How to compare {{name}}',
    mode: {
      all: 'Split between these',
      exact: 'Exactly these %',
      any: 'At least one of these',
    },
    modeHelp: {
      all: 'Uses all of them, with any %. No others.',
      exact: 'Uses all of them, with these %. No others.',
      any: 'Uses at least one. Others may join.',
    },
    modeRule: {
      all: 'Passes if it uses only these {{count}}, with any %.',
      exact: 'Passes only with {{percents}}. No others.',
      any: 'Passes if it uses at least one of these. Others may join.',
    },
    onBill: 'On {{billRef}}',
    onBillNoRef: 'On this bill',
    removeTarget: 'Remove {{name}}',
    add: '+ Add',
    addAria: 'Add {{name}}',
    percentAria: 'Percent of {{name}}',
    percentInvalid: 'Enter a percent from 0 to 100, such as 60 or 33.33.',
    percentSum: 'The total must be 100%. Today it is {{sum}}.',
    examplesTitle: 'Other bills:',
    examplePasses: 'Passes',
    exampleStops: 'Stops',
    paymentGroup: 'Accepted payment methods',
    paymentRule: 'The bill passes with any of the selected ones.',
    withholdingNone: 'no withholding',
    withholdingRule: 'The bill passes only with the same withholdings: {{profile}}.',
    reimbursableRule: 'The bill passes only if the client reimburses it.',
    amountOnBill: 'On {{billRef}}: {{amount}}',
    amountOnBillNoRef: 'On this bill: {{amount}}',
    amountAria: 'Total up to, in reais',
    amountHint: 'Suggested: {{ceiling}} (this bill + 10%).',
    amountError: 'Enter an amount above zero, such as 2,200.00.',
    sourceFails: '{{billRef}} would not pass this rule ({{rows}}). You can still save.',
    sourceFailsNoRef: 'This bill would not pass this rule ({{rows}}). You can still save.',
    thenTitle: 'Do this',
    send: {
      title: 'Send to approval',
      body: 'Skips review. A person approves.',
      active: 'Already on',
    },
    approve: {
      title: 'Approve',
      body: 'Approves in your name. The rule checks your approval limit on every bill.',
      bodyLimit: 'Approves in your name, within your approval limit of {{limit}}.',
      useLimit: 'Use a ceiling of {{limit}}',
    },
    pickOne: 'Select at least one option to save.',
    reasons: {
      NO_AUTHORITY: 'You do not approve {{vendorShort}} bills.',
      APPROVED_BY_RULE: '{{billRef}} was approved by a rule, not by you.',
      APPROVED_BY_RULE_NO_REF: 'This bill was approved by a rule, not by you.',
      MULTI_APPROVER_CHAIN: 'This bill has more than one approver. Approval stays with them.',
      CURRENCY_NOT_BRL: 'Approving on its own only works for entities in Brazilian reais.',
      PROFILE_UNAVAILABLE:
        "Without this bill's withholdings in the rule, approval stays with a person.",
      NO_CEILING:
        'Without "Total up to", the rule has no ceiling. Your approval limit is {{limit}}.',
      NO_CEILING_NO_LIMIT: 'Without "Total up to", the rule has no ceiling.',
      CEILING_ABOVE_AUTHORITY: 'The ceiling is {{ceiling}}. Your approval limit is {{limit}}.',
      NO_COST_CENTER: 'Without a cost center, your approval limit does not cover the bill.',
      ANY_MODE_COST_CENTER:
        'With "At least one of these", the bill may have other cost centers. Your approval limit only covers {{ccs}}.',
      COST_CENTER_NOT_COVERED: 'Your approval limit does not cover {{ccs}}.',
      DEPARTMENT_NOT_COVERED: 'Your approval limit does not cover the {{depts}} department.',
      VENDOR_NOT_COVERED: 'Your approval limit does not cover {{vendorShort}}.',
    },
    dismissNote:
      '"Do not offer again" applies to everyone in this entity. You can bring it back on the vendor page.',
    dismiss: 'Do not offer again for this vendor',
    notNow: 'Not now',
    saveOne: 'Save rule',
    saveTwo: 'Save 2 rules',
    saving: 'Saving…',
    dismissing: 'Dismissing…',
    saved: {
      titleOne: 'Rule saved',
      titleTwo: '2 rules saved',
      bodySend: 'Saved: send to approval.',
      bodyApprove: 'Saved: approve in your name.',
      bodyTwo: 'Saved: send to approval and approve in your name.',
      link: 'See it in the {{vendor}} Knowledge tab',
      close: 'Close',
      next: 'Next',
    },
    savedLineOne: 'Rule saved. See it in the {{vendor}} Knowledge tab.',
    savedLineTwo: '2 rules saved. See them in the {{vendor}} Knowledge tab.',
    dismissedLine: 'Rule offer dismissed in entity {{entity}}. Turn it back on on the vendor page.',
    ruleName: {
      send: '{{vendor}} — send to approval',
      approve: '{{vendor}} — approve',
    },
    ruleDescription: 'Offered on bill {{billRef}}',
    ruleDescriptionNoRef: 'Offered on an approved bill',
    saveError: 'Could not save the rule. Try again.',
    dismissError: 'Could not dismiss the offer. Try again.',
  },
  lineItemsEditor: {
    title: 'Bill items',
    description: 'Record the actual document items before classifying them.',
    add: 'Add item',
    empty: 'No items recorded.',
    unnamed: 'Unnamed item',
    edit: 'Edit item',
    delete: 'Delete item',
    createTitle: 'Add bill item',
    editTitle: 'Edit bill item',
    dialogDescription:
      'Quantity, amount, and NCM help identify items that should proceed to asset review.',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save item',
    saving: 'Saving…',
    saved: 'Item saved.',
    saveError: 'Could not save the item.',
    sourceReviewImmutable:
      'The confirmed asset tax decision froze this bill’s items, fiscal facts, and PIS/COFINS amounts. Informational fields remain editable. This flow cannot yet correct those frozen facts; keep the bill unapproved and send it for accounting and tax review.',
    deleted: 'Item deleted.',
    deleteError: 'Could not delete the item.',
    deleteTitle: 'Delete item?',
    deleteDescription: 'The item “{{description}}” will be removed from this bill.',
    deleteConfirm: 'Delete item',
    fields: {
      description: 'Description',
      type: 'Type',
      unit: 'Unit',
      quantity: 'Quantity',
      unitPrice: 'Unit price',
      amount: 'Total amount',
      ncm: 'NCM',
      cfop: 'CFOP',
      cst: 'CST',
      notes: 'Notes',
    },
    types: {
      SERVICE: 'Service',
      INTEREST: 'Interest',
      FINE: 'Fine',
      DISCOUNT: 'Discount',
      OTHER: 'Other',
    },
  },
  taxLiabilityAssistance: {
    analysisExpired: 'The second analysis attempt timed out. Try again.',
    analysisInconclusive:
      'The analysis finished without a decision that allows approval. Analyze again, or ask someone who manages tax guide treatment to classify it manually.',
    analysisFailed: 'The tax guide analysis failed. Analyze again before continuing.',
    thirdPartyPayerConfirmation:
      "Third-party tax guide ({{payer}}): it will post as this bill's expense, without settling any provisioned tax. Check that the taxpayer is not one of the company's entities.",
    thirdPartyPayerUnnamedConfirmation:
      "Third-party tax guide: it will post as this bill's expense, without settling any provisioned tax. Check that the taxpayer is not one of the company's entities.",
    derivedClassifyConfirmation:
      "This guide's treatment was set automatically: this bill's expense, without settling any provisioned tax. Confirm before approving.",
    issues: {
      permissionRequired: 'An authorized person must approve this step.',
      consentRequired: 'Confirm the warnings before continuing.',
      notCompleted: 'The action did not complete. Check the approval step.',
      planChanged: 'The guide changed. Review the updated decisions.',
      explicitLineAllocationRequired:
        'Review how earlier payments were allocated before partially settling this guide.',
      generic:
        'The action could not be completed. Try again; if the problem continues, contact support.',
      validationGeneric: "Review the bill's required details before continuing.",
      genericWarning: 'There is a warning about this bill. Review its details before continuing.',
    },
    analysisStale:
      'The bill changed after analysis. Refresh the analysis to review current suggestions. Your classification will be preserved.',
    analysisStalePermission:
      'The bill changed after analysis. Ask someone with permission to submit bills for approval to refresh the analysis.',
    configurationSaved: 'Accounts saved. Approval was not completed.',
    actionRefreshFailed: 'The action completed. Refresh the bill to see the result.',
    wholeBillClassification: 'This choice applies to all {{count}} components of the bill.',
    acknowledgeWarnings: 'I have read the warnings and want to continue.',
    selectTemporaryAccounting: 'Use temporary accounting with mandatory review',
    accountProposalTitle: 'Confirm suggested accounts',
    approvingBill: 'Tax guide {{number}}',
    accountDecisionTitle: 'Before approval, choose where to post {{slot}}',
    accountSteps: 'Accounts to confirm',
    accountStepPosition: 'Account {{current}} of {{total}}',
    previousAccount: 'Previous account',
    nextAccount: 'Next account',
    liabilityAccountLabel: 'Liability GL account',
    expenseAccountLabel: 'Expense GL account',
    accountAppliesTo: 'Applies to the entire {{version}} chart',
    dueDate: 'due {{date}}',
    aiSuggestedBadge: 'Suggested by AI',
    userSelectedBadge: 'Selected by you',
    newAccountBadge: 'New account',
    createSpecificAccount: 'Create a dedicated account',
    createSpecificAccountHint:
      'Keep this tax separate. The code and scope will be shown before confirmation.',
    chooseOtherAccount: 'Choose another account',
    chooseOtherAccountPlaceholder: 'Select a liability account',
    chooseOtherAccountRequired: 'Select an account to continue.',
    confirmAccount: 'Confirm account',
    proposalAccount: 'Account',
    proposalPurpose: 'Purpose',
    taxSuggestionTitle: 'Suggested tax guide treatment',
    taxSuggestionDescription:
      'The current choice remains in place until you accept the suggestion. Review the document evidence.',
    classifySuggestion: 'Treat as an expense on this bill',
    slotSuggestion: 'Change the tax to {{slot}}',
    currentTaxChoice: 'Current choice: {{slot}}',
    acceptTaxSuggestion: 'Accept suggestion',
    keepCurrentTaxChoice: 'Keep current choice',
    applyingTaxSuggestion: 'Recording your decision and preparing the bill…',
    suggestionError: 'Could not record this decision. Refresh the analysis and try again.',
    confirmingAccounts: 'Applying the confirmed change and validating the bill…',
    accountProposalRefreshed:
      'The accounting configuration changed. Review the updated proposal and confirm again.',
    railAccountConflict:
      '{{slot}} is already set up on account {{account}}. Choose account {{legado}}, which is part of the same structure.',
    railAccountConflictRefresh:
      '{{slot}} is already set up on account {{account}}. Refresh the analysis to continue with that account.',
    continueWithReview: 'Continue with accounting review',
    continuingWithReview: 'Preparing the accounting review and resuming the workflow…',
    resuming: 'Resuming the requested workflow…',
    retryRequestedAction: 'Try again',
    fallbackPending: 'Accounting review pending',
    fallbackAccountingPending:
      'The temporary account will be used on approval. The accounting review remains pending until reclassification.',
    resolvedLabel: 'Tax issues resolved',
    followProgress: 'Track analysis',
    viewResult: 'View result',
    viewAccountSuggestion: 'View suggestion',
    accountChoiceAtApproval:
      'A tax still has no active mapping. Your Alchemist has analyzed it and created a suggestion.',
    accountChoicesAtApproval:
      'Some taxes still have no active mapping. Your Alchemist has analyzed them and created suggestions.',
    taxSuggestionsPending: 'Review the suggested tax treatment before continuing.',
    pendingIdentity: 'The tax type still needs to be identified for {{count}} guide items.',
    pendingCodes: 'Pending codes: {{codes}}.',
    activeDebtOrigin:
      'These active-debt codes may represent payroll or individual-contributor contributions. Check the suggestion and confirm the tax. Finding the original ledger entry is not required to approve the guide.',
    identityHelp:
      'Check the complete revenue code and description on the document to identify the correct obligation.',
    checkPending: 'Check pending taxes',

    mappingSummary: 'Resolved mappings',
    mappingReason: 'Reason for mapping {{code}}',
    mappingAccountPending: 'Ledger account still pending',
    title: 'Prepare bill for approval',
    question: 'Let’s review the accounts and treatment for this tax guide before continuing.',
    manual: 'Configure manually',
    ai: 'Ask AI to resolve',
    reanalyze: 'Analyze again',
    auditLabel: 'With a later accounting review',
    auditHint:
      'AI applies evidence-backed decisions and records its inputs, outputs and changes. Unresolved items remain pending.',
    starting: 'Submitting for analysis…',
    queued: 'Analysis queued',
    running: 'AI is preparing the bill…',
    ready: 'Tax blockers resolved. The bill can proceed to approval.',
    needs_review: 'Analysis finished. Review what remains for this bill.',
    failed: 'Preparation could not be completed. Try again or continue with accounting review.',
    background: 'You can close this window. Processing continues in the background.',
    actionWaiting: 'Wait while the bill is prepared to complete the requested action.',
    reviewPending: 'An accounting review was opened to check the AI decisions.',
    deferProposal:
      'This tax guide can proceed to approval while historical items remain under accounting review.',
    accept: 'Accept and proceed to approval',
    acceptAndReview: 'Accept and review later',
    accepting: 'Recording your decision and resuming approval…',
    approved: 'Bill approved. The accounting review remains recorded.',
    approvalPending: 'Decision recorded. The bill remains in the approval workflow.',
    prepared: 'Decision recorded. The bill is prepared to continue through approval.',
    acceptError: 'Could not resume approval. Please try again.',

    viewAudit: 'View AI history',
    markForAccountingReview: 'Mark for accounting review',
    confirmAndApprove: 'Confirm and approve',
    auditTrail: {
      navigation: 'AI history navigation',
      accountChoice: 'Account choice',
      back: 'Back to account choice',
      backToTimeline: 'Back to history',
      timeline: 'Account decision history',
      fullTrailPage: 'Full trail',
      candidates: 'Candidates',
      evidence: 'Evidence',
      fullTrail: 'Open full trail',
      record: 'Permanent record for this bill',
      loading: 'Loading AI history…',
      agent: 'Accounts payable Alchemist',
      requested: 'Analysis requested',
      started: 'The Alchemist started the analysis',
      readGuide: 'Read the tax guide data',
      checkedAccounts: 'Checked the chart of accounts',
      suggestedAccount: 'Suggested {{account}}',
      suggestedAccounts: 'Suggested accounts for confirmation',
      reason: 'Reason',
      accountingReview: 'Flagged a historical difference for accounting review',
      awaitingChoice: 'Waiting for your choice',
      finished: 'Analysis complete',
    },
    error: 'Could not load or start the analysis. Please try again.',
    close: 'Close',
  },
  statutoryLiabilityReview: taxLiabilityReviewEn,
  costMatches: costMatchesEn,
  preferredBank: {
    title: 'Preferred bank',
    placeholder: 'Select the origin bank',
    none: 'No preferred bank',
    unavailable: 'Unavailable for new payments',
    unavailableOption: '{{name}} · unavailable',
    updated: 'Preferred bank updated.',
    cardDisabled: 'Card bills use the account configured for the card.',
    statusDisabled: 'The preferred bank cannot be changed on paid or void bills.',
    source: { user: 'User', rule: 'Rule {{name}}', system: 'System' },
  },
  workflowRules: {
    entity: {
      label: 'Entity',
      placeholder: 'Select an entity',
      tenantWide: 'All entities',
      optionalHint: 'Optional: without an entity, the rule applies tenant-wide.',
      derivedHint: 'Required, derived from: {{fields}}.',
      changeTitle: 'Change rule entity?',
      changeConfirm:
        'Changing the entity will remove selections linked to the current entity. Continue?',
      changeAction: 'Change entity',
      requiredError: 'Select an entity for this rule.',
      sources: {
        cost_center: 'Cost center',
        department: 'Department',
        gl_account: 'GL account',
        managerial_account: 'Managerial account',
        set_gl_account: 'GL account',
        set_cost_center: 'Cost center',
        set_custom_segment: 'Custom segment',
        set_department: 'Department',
        set_managerial_account: 'Managerial account',
        set_origin_bank_account: 'Origin bank',
        segment: 'Custom segment',
      },
    },
    list: {
      createRule: 'New Rule',
      allEntities: 'All entities',
      tenantWide: 'All',
      filterByEntity: 'Filter by entity',
      entityColumn: 'Entity',
    },
    // Describe a rule in words (ALC-4564): a dialog that reads a sentence into a draft rule, then
    // opens the rule builder on that draft. Nothing is saved until the person saves the rule.
    describe: {
      openButton: 'Create from a description',
      title: 'Create a rule from a description',
      description: 'Write what the rule should do. You check it in the editor before saving.',
      textLabel: 'Describe the rule',
      textPlaceholder: 'E.g. Send ACME bills up to R$ 5,000.00 straight to approval',
      textHelp:
        'Only rules that send the bill to approval can be created this way. Up to {{max}} characters.',
      entityLabel: 'Entity',
      entityPlaceholder: 'Select an entity',
      entityRequired: 'Select an entity.',
      textRequired: 'Write what the rule should do.',
      cancel: 'Cancel',
      preview: 'Preview rule',
      retry: 'Try again',
      loading: 'Reading your description…',
      partsLabel: 'Words I cannot use:',
      candidatesTitle: '"{{text}}" matches {{count}} options. Pick one and try again.',
      candidatesGroup: 'Options for "{{text}}"',
      pickRequired: 'Pick an option to try again.',
      codeLine: 'Code: {{code}}',
      genericError: 'The rule could not be read right now. Try again in a minute.',
      back: 'Describe again',
      builderTitle: 'Review the rule',
      builderDescription:
        'Check the conditions before saving. Nothing is saved until you save the rule.',
    },
    // The read-only line that says where a rule came from, and the review notes of a proposal.
    origin: {
      description: 'Created from your description: «{{text}}»',
      descriptionSaved: 'Created from a description: «{{text}}»',
      approvalOffer: 'Created from an approval offer on the bill.',
      openBill: 'Open the bill',
      createdBy: 'by {{name}}',
      createdAt: 'on {{date}}',
      kindDescription: 'Description',
      kindApprovalOffer: 'Approval offer',
    },
    review: {
      sourcesTitle: 'How we read your description',
      sourcesHint: 'Check that each phrase became the condition you meant.',
      replayNone:
        'As described, this rule would have matched none of the last {{total}} bills of this entity.',
      replayEmpty: 'This entity has no bills yet to test the rule on.',
      replayMatched:
        'As described, this rule would have matched {{matched}} of the last {{total}} bills of this entity: {{numbers}}{{more}}.',
      replayMore: ' and {{count}} more',
      replayNoNumber: 'no number',
      replayEdited:
        'You changed the conditions. The bill count only holds for the rule as it was described.',
      // The amount a rule compares is the net amount to pay (total minus withholdings).
      netAmountField: 'Net amount to pay',
      netAmountNote:
        "The rule's amount is the bill's net amount to pay (the total minus the taxes withheld), not the total amount.",
    },
    actions: {
      delete: 'Delete action',
      deleteGroup: 'Delete action group',
      types: {
        set_gl_account: 'Set GL account',
        set_department: 'Set department',
        set_cost_center: 'Set cost center',
        set_managerial_account: 'Set managerial account',
        set_custom_segment: 'Set custom segment',
        set_project: 'Set project',
        require_approval: 'Require approval',
        require_project: 'Require project',
        require_split: 'Require split',
        block_transaction: 'Block transaction',
      },
    },
    condition: {
      fieldLabel: 'Condition field',
      textValuePlaceholder: 'Enter {{field}}',
      noTextValues: 'No values added',
      textValuesPlaceholder: 'Type and press Enter to add',
      operatorLabel: 'Condition operator',
      joinLabel: 'Link to the previous condition',
      delete: 'Delete condition',
      save: 'Save',
      cancel: 'Cancel',
      fieldSearchPlaceholder: 'Search field...',
      fieldSearchEmpty: 'No field found.',
      fieldGroups: {
        properties: 'Properties',
        classifications: 'Classifications',
        custom: 'Custom fields',
        facts: 'Current bill state',
      },
    },
    // ANY/ALL choice of a line-item condition (ALC-4629): how the lines of the bill combine.
    lineQuantifier: {
      label: 'Must hold on',
      options: {
        ALL: 'Every line',
        ANY: 'At least one line',
      },
      hint: {
        ALL: 'Every line of the bill must meet the condition. A bill with no lines does not meet it.',
        ANY: 'At least one line must meet it. Item conditions linked by AND or OR with this same option are checked together, on that same line. A bill with no lines does not meet it.',
      },
      summary: {
        ALL: 'on every line',
        ANY: 'on at least one line',
      },
    },
    // Labels of the bill fields of the conditions editor. Keep in step with `pt-BR`.
    fields: {
      entity: 'Entity',
      vendor: 'Vendor',
      description: 'Description',
      amount: 'Amount',
      cfop: 'CFOP',
      billIssueDate: 'Issue date',
      billType: 'Bill type',
      paymentMethod: 'Payment method',
      costCenter: 'Cost center',
      project: 'Project',
      department: 'Department',
      category: 'Category',
      glAccount: 'GL account',
      managerialAccount: 'Managerial account',
      lineItemId: 'Bill item',
      lineDescription: 'Item description',
      lineAmount: 'Item amount',
      cfopCode: 'Item CFOP',
      purchaseType: 'Purchase type',
      needByDate: 'Need-by date',
      segment: 'Segment',
    },
    operators: {
      equals: 'equals',
      not_equals: 'does not equal',
      contains: 'contains',
      not_contains: 'does not contain',
      starts_with: 'starts with',
      ends_with: 'ends with',
      in: 'is in',
      not_in: 'is not in',
      greater_than: 'greater than',
      less_than: 'less than',
      greater_or_equal: 'greater than or equal to',
      less_or_equal: 'less than or equal to',
      between: 'between',
      is_null: 'is empty',
      is_not_null: 'is not empty',
      has_any_of: 'has any of',
      has_none_of: 'has none of',
      same_profile_as: 'has the same profile as',
      all_within: 'are all within',
      same_targets: 'Split among these',
      exact_split: 'Exactly these %',
      any_within: 'At least one of these',
    },
    // One label per registered bill_updated fact (ALC-4625). Keep in step with `pt-BR`.
    facts: {
      reconciliationState: 'Reconciliation status',
      openAlertTypes: 'Open alerts',
      barcodePresence: 'Payments with a barcode',
      barcodeValid: 'Valid barcode',
      barcodeAmountMatches: 'Barcode amount matches the payment',
      isClientReimbursable: 'Client reimbursable',
      fiscalServiceType: 'Fiscal service type',
      withholdingProfile: 'Withholding profile',
      creditProfile: 'PIS/COFINS credit profile',
      allocationsWithinTargets: 'Allocation targets',
      serviceVendorRule: 'Vendor rule for the service',
    },
    boolean: {
      placeholder: 'Select',
      true: 'Yes',
      false: 'No',
    },
    enum: {
      valueLabel: 'Condition value',
      placeholder: 'Select a value',
      billType: {
        EXPENSE: 'Expense',
        PAYROLL: 'Payroll',
        TAXES: 'Taxes',
        BANK_FEE: 'Bank fee',
        VENDOR_ADVANCEMENT: 'Vendor advance',
        CREDIT_CARD_INVOICE: 'Credit card invoice',
      },
      paymentMethod: {
        BOLETO: 'Bank slip',
        PIX: 'PIX',
        TED: 'Bank transfer (TED)',
        DOC: 'DOC',
        CREDIT_CARD: 'Credit card',
        DEBIT_CARD: 'Debit card',
        GUIA: 'Tax guide',
        CHECK: 'Check',
        OTHER: 'Other',
        ADVANCE: 'Advance',
        ACH: 'ACH transfer (US)',
      },
      reconciliationState: {
        NONE: 'Not reconciled',
        PARTIAL: 'Partially reconciled',
        FULL: 'Fully reconciled',
      },
      barcodePresence: {
        ALL: 'All',
        SOME: 'Some',
        NONE: 'None',
      },
      // D41: "no service type" is not "no rule"; the three answers are kept apart.
      serviceVendorRule: {
        has_rule: 'Has a rule',
        no_rule: 'Has no rule',
        no_service_type: 'Bill has no service type',
      },
      // Only the types `alertTypes` cannot carry (see the pt-BR block).
      openAlertTypes: {
        EXTERNAL_FORM_DOCUMENT_MISMATCH: 'Document differs from the external form',
        INTAKE_DOCUMENTS_MISSING: 'Intake documents missing',
        INTAKE_METADATA_MISMATCH: 'Intake data differs from the document',
        ONFLY_CLASSIFICATION_INCOMPLETE: 'Automatic classification incomplete',
        ONFLY_DEFAULT_COST_CENTER_USED: 'Default cost center applied automatically',
        ONFLY_AUTO_APPROVAL_BLOCKED: 'Automatic approval blocked',
        ONFLY_POSTING_FAILED: 'Automatic posting failed',
      },
    },
    // The remove button of a chip in a multi-select value (the chip's name is the label)
    removeValueAria: 'Remove {{name}}',
    serviceType: {
      valueLabel: 'Service type',
      placeholder: 'Select the service type',
      addPlaceholder: 'Add a service type',
      // F-07: without settings:read the catalog is not read; the saved ids still show
      noCatalogAccess: 'You do not have access to the service type catalog.',
    },
    // ALC-4563: editors of the facts whose value is a profile or a set of targets.
    profile: {
      withholding: {
        groupLabel: 'Withholding profile',
        intro:
          'Check the taxes the bill withholds and give the rate of each. The condition compares what was decided, never the amount withheld.',
        applies: 'Withholds {{tax}}',
        rateLabel: '{{tax}} rate (%)',
        ratePlaceholder: 'e.g. 1.50',
        rateInvalid: 'Use a rate above 0 and up to 100, with up to 4 decimal places.',
        rateRequired: 'Enter the rate: the condition compares the decided rate of each tax.',
        noneSelected: 'No tax checked: the condition matches bills with no withholding.',
        none: 'No tax withheld',
        rateMissing: 'no rate',
      },
      credit: {
        groupLabel: 'PIS/COFINS credit profile',
        intro:
          'Give what the bill decided about the PIS/COFINS credit. The condition compares the decision, never amounts.',
        generatesPis: 'Generates PIS credit',
        generatesCofins: 'Generates COFINS credit',
        notStated: 'Not stated',
        creditType: 'Credit type',
        revenueUse: 'Credit allocation',
        onlyWhenGenerates: 'Credit type and allocation count only when the bill generates credit.',
        type: {
          BASIC: 'Ordinary purchase',
          FIXED_ASSET: 'Fixed asset',
          PRESUMED: 'Presumed credit',
          IMPORT: 'Import',
        },
        use: {
          DIRECT_CUMULATIVE: 'Direct use in cumulative revenue',
          DIRECT_NON_CUMULATIVE: 'Direct use in non-cumulative revenue',
          COMMON: 'Common use across regimes',
        },
        summary: {
          both: 'Generates PIS and COFINS credit',
          none: 'Generates neither PIS nor COFINS credit',
          pisYes: 'Generates PIS credit',
          pisNo: 'Does not generate PIS credit',
          cofinsYes: 'Generates COFINS credit',
          cofinsNo: 'Does not generate COFINS credit',
        },
      },
    },
    allocationTargets: {
      groupLabel: 'Allocation targets',
      intro:
        'Choose the allowed targets in each dimension. The condition holds when the bill has a target in the dimension and every row and line of its allocation is on the list; a bill that is not classified, or only partly, does not match. A dimension with no selection is not tested; percentages and amounts are not compared.',
      entityRequired:
        'Select the rule entity to list GL accounts, cost centers, departments and managerial accounts.',
      none: 'The bill cannot have a target in this dimension',
      introSameTargets:
        'Choose the targets of each dimension. The condition holds when the bill uses exactly these targets, at any percentage: none more, none fewer. A bill that is not classified, or only partly, does not match. A dimension with no selection is not tested.',
      introExactSplit:
        'Choose the targets and the percentage of each; the percentages of each dimension add up to 100.00%. The condition holds when the split of the bill has exactly these targets with these percentages. A split by line item, a custom split or a bill with more than one split row does not match. A dimension with no selection is not tested.',
      introAnyWithin:
        'Choose the targets of each dimension. The condition holds when the bill uses at least one of these targets; other targets are accepted too. A bill with no target in the dimension does not match. A dimension with no selection is not tested.',
      percentLabel: 'Percentage of {{target}}',
      percentPlaceholder: '0.00',
      percentRequired: 'Enter the percentage',
      percentInvalid: 'Use a percentage above 0 and up to 100, with up to 2 decimal places',
      splitSum: 'Sum: {{sum}}',
      splitSumNot100: 'The percentages of this dimension must add up to 100.00%',
      dimensions: {
        glAccounts: 'GL accounts',
        costCenters: 'Cost centers',
        departments: 'Departments',
        managerialAccounts: 'Managerial accounts',
      },
      segmentsSummary: 'Segments',
      summaryNone: 'none',
    },
    bankAction: {
      itemLabel: 'origin bank',
      label: 'Set origin bank',
      placeholder: 'Select an active bank account',
      entityRequired: 'Select the rule entity before selecting a bank.',
      empty: 'No active bank accounts for this entity.',
      save: 'Save',
      cancel: 'Cancel editing',
    },
    submitAction: {
      label: 'Send for approval',
    },
    waitingBillCheck: {
      title: 'Apply this rule to bills already in draft or review?',
      hint: 'The check runs once. Bills in approval are left as they are.',
      yes: 'Apply to existing bills',
      no: 'Save without applying',
      cancel: 'Cancel',
    },
  },
  paymentMethods: {
    boleto: 'Boleto',
    pix: 'PIX',
    ted: 'TED',
    doc: 'DOC',
    creditCard: 'Credit Card',
    debitCard: 'Debit Card',
    guia: 'Guia',
    check: 'Check',
    advance: 'Advance',
    manual: 'Manual payment',
    ach: 'ACH transfer (US)',
    other: 'Other',
  },
  paymentsPage: {
    entityFilter: 'Filter by entity',
    allEntities: 'All entities',
    queueTotal: 'Total to pay',
    summaryMeta: '{{payments}} · {{entities}}',
    entityCount_one: '{{count}} entity',
    entityCount_other: '{{count}} entities',
    insufficientBalance: 'Insufficient balance in {{account}} — {{amount}} short',
    overdue: 'Overdue',
    today: 'Today',
    nextSevenDays: 'Next 7 days',
    later: 'Later',
    none: 'none',
    amountToPay: 'To pay',
    available: 'Available',
    balanceUnavailable: 'Balance unavailable',
    balanceUpdatedUnderHour: 'Balance updated less than 1 hour ago',
    balanceUpdatedHours_one: 'Balance updated {{count}} hour ago',
    balanceUpdatedHours_other: 'Balance updated {{count}} hours ago',
    balanceStale_one: 'Balance not updated for over {{count}} day',
    balanceStale_other: 'Balance not updated for over {{count}} days',
    refreshBalance: 'Refresh balance',
    refreshBalanceFor: 'Refresh balance for {{account}}',
    balanceRefreshSuccess: 'Balance refreshed',
    balanceRefreshError: 'Could not refresh balance',
    noOriginAccount: 'No origin account',
    queueEmpty: 'No pending payments',
    noVisibleCheckingAccounts: 'No visible checking accounts with pending payments',
    paymentCount_one: '{{count}} payment',
    paymentCount_other: '{{count}} payments',
    loadError: 'Could not load summary',
    sectionLabel: 'Payment summary',
    expandSummary: 'Show balances',
  },
  paymentSection: {
    transfer: 'Transfer',
    registerDestination: 'Add destination',
    amountDefinedByCode: 'Set by the code',
    amountDiffersFromCode:
      "This payment's Pix Copy and Paste code is for {{amount}}, which differs from the payment amount. Adjust the amount or paste another code.",
    useCodeAmount: 'Use the code amount',
    transferDestinationRequired: 'Choose where to send the transfer.',
    transferDestination: {
      label: 'Destination',
      registeredKey: 'Pix key',
      registeredBank: 'Bank details',
      railLabel: 'How to send',
      railPixBankData: 'PIX by bank details (default)',
      railTed: 'TED',
      pixBankDataUnavailable: 'The origin account cannot send PIX by bank details. Use TED.',
      tedUnavailable:
        'The origin account cannot send TED. Use PIX by bank details or change the origin account.',
      savedAccountMissing: "The payee's account no longer exists; choose another destination.",
      noRailAvailable: 'The origin account cannot send transfers to bank details.',
      noRegisteredTitle: 'No saved destination',
      noRegisteredDescription:
        "Add the payee's Pix key or bank details. You can also pay a one-off key or a Pix Copy and Paste code.",
      adHocKey: 'One-off Pix key',
      adHocKeyDescription: 'Pays a key that is not saved for this payee.',
      adHocBadge: 'One-off destination',
      adHocKeyType: 'Key type',
      adHocKeyTypePlaceholder: 'Type',
      adHocKeyInput: 'Pix key',
      adHocKeyPlaceholder: 'CPF, CNPJ, email, mobile or random key',
      adHocKeyAmbiguous: 'This value can be more than one key type. Which one is it?',
      adHocKeyNormalized: 'Will be sent as',
      adHocKeyInvalid: 'Invalid key for type {{type}}.',
      saveToVendor: 'Save to vendor',
      copiaECola: 'Pix Copy and Paste',
      copiaEColaDescription: 'Paste the complete QR Code text.',
      copiaEColaPlaceholder: 'Paste the Pix Copy and Paste code',
      copiaEColaInvalid: 'Invalid or incomplete Pix code. Copy the complete code again.',
      previewReceiver: 'Receiver',
      previewCity: 'City',
      previewAmount: 'Amount',
      previewAmountOpen: 'Open',
      previewTxid: 'Reference (txid)',
      dynamicNote: "Dynamic QR Code: the amount and reference stay at the receiver's PSP.",
      dynamicUnsupported:
        'The origin account cannot pay dynamic QR Codes. Change the origin account or ask the vendor for a static QR Code.',
      keyTypes: {
        CPF: 'CPF',
        CNPJ: 'CNPJ',
        EMAIL: 'Email',
        PHONE: 'Mobile',
        RANDOM: 'Random key',
      },
    },
    transferErrors: {
      PIX_COPIA_E_COLA_INVALID: 'Invalid or incomplete Pix code. Copy the complete code again.',
      PIX_COPIA_E_COLA_AMOUNT_MISMATCH:
        'The payment amount differs from the amount set in the Pix Copy and Paste code.',
      PIX_KEY_INVALID: 'The Pix key is not valid for the selected type.',
      PIX_KEY_TYPE_REQUIRED: 'Select the Pix key type.',
      TED_REGISTERED_ACCOUNT_REQUIRED: "TED requires one of the payee's saved bank accounts.",
      PIX_BANK_DATA_ACCOUNT_REQUIRED:
        "PIX by bank details requires one of the payee's saved bank accounts.",
      PIX_BANK_DATA_WITH_KEY: 'PIX by bank details does not take a Pix key or Copy and Paste code.',
      PIX_BANK_DATA_ACCOUNT_INCOMPLETE:
        'The payee account lacks the bank, agency and account number PIX by bank details needs.',
      PAYMENT_VENDOR_BANK_ACCOUNT_INVALID:
        "The bank account is not an active account of this bill's vendor. Choose one of the vendor's accounts.",
    },
    transferWarnings: {
      PIX_DYNAMIC_QR_ORIGIN_UNSUPPORTED:
        'Payment saved, but the origin account cannot pay dynamic QR Codes. Change the origin account before sending.',
      PIX_BANK_DATA_ORIGIN_UNSUPPORTED:
        'Payment saved, but the origin account cannot send PIX by bank details. Change the origin account or use TED before sending.',
      TED_ORIGIN_UNSUPPORTED:
        'Payment saved, but the origin account cannot send TED. Change the origin account or use PIX before sending.',
    },
    pixInstructionLabel: 'Pix key or Copy and Paste code',
    pixInstructionValid: 'Valid Pix details',
    pixInstructionPlaceholder: 'Paste a Pix key or the complete Copy and Paste code',
    pixInstructionInvalid: 'Invalid or incomplete Pix code. Copy the complete code again.',
    title: 'Payment',
    reimbursement: 'Reimbursement',
    transferMethod: 'Transfer method',
    usAccountInfo: 'Routing ••••{{routing}} · Account ••••{{account}} · USA',
    usAccountTitle: 'US account',
    routing: 'Routing',
    achBadge: 'ACH',
    usAccountsOnly: 'Only US accounts are listed, since they are paid by ACH.',
    methodAch: 'ACH transfer (US)',
    achDestination: 'Payee account in the US',
    achAddAccount: 'Add a US account',
    achAccountRequired: 'Choose the payee account in the US to send by ACH.',
    noUsAccounts: 'This vendor has no US account yet. Add one to pay by ACH.',
    pixKey: 'PIX Key',
    bank: 'Bank',
    agency: 'Agency',
    account: 'Account',
    favorecido: 'Payee',
    totalValue: 'Total value',
    dueDate: 'Due date',
    documentDueDateLabel: 'Document due date',
    scheduledFor: 'Scheduled for',
    automaticBankPayment: 'Automatic bank debit',
    editDueDate: 'Edit due date',
    editScheduledDate: 'Edit scheduled date',
    originAccount: 'Origin account',
    default: 'Default',
    confirmPayment: 'Confirm payment',
    parcelOrSplit: 'Parcel or split payment',
    payViaPixOrTed: 'Pay via PIX or TED',
    supplierAccount: 'Supplier account',
    noBankAccount: 'Fill in the payment details',
    noBankAccountDescription:
      'Add the supplier bank account for transfers (PIX or TED), or continue to boleto, tax slip, card, and other options below.',
    useBoleto: 'Use boleto',
    addBankAccount: 'Add bank account',
    createBankAccount: 'Create bank account',
    createBoleto: 'Create boleto',
    createNewAccount: 'Create new account',
    addBankAccountModalTitle: 'Add banking details',
    addBankAccountModalSubtitle: 'Register how to pay this vendor',
    addBankAccountSuccess: 'Bank account created successfully',
    addBankAccountError: 'Could not create bank account',
    paymentMethod: 'Payment method',
    bankSearchPlaceholder: 'Search by name or code...',
    accountType: 'Account type',
    accountNickname: 'Account nickname',
    accountNicknamePlaceholder: 'E.g.: Main PIX, Payments Account',
    accountNicknameHint: 'Optional — helps differentiate when the vendor has multiple methods',
    pixKeyTypeAutoDetected: 'Type automatically detected by key format',
    beneficiaryBank: 'Beneficiary bank',
    beneficiaryBankHint:
      'Which bank this key is registered with. Branch and account are not required.',
    beneficiaryBankRequiredBadge: '{{bank}} requires it',
    beneficiaryBankRequiredTooltip:
      'Your origin account {{origin}} needs to know which bank the key is registered with to ' +
      'complete the PIX. Without it the payment is rejected on dispatch. Branch and account are ' +
      'not required for PIX by key.',
    alsoHasPixKey: 'Does this account also have a PIX key?',
    alsoHasPixKeyHint: 'Add it to pay the same account by PIX without registering a second method.',
    optionalPixKeyPlaceholder: 'Optional',
    defaultMethod: 'Default method',
    defaultMethodHint: 'Used automatically when paying this vendor',
    pixKeyDuplicate: 'A PIX method with this key already exists.',
    tedDuplicate: 'An account with this bank, branch and account already exists.',
    prefilledFromVendor: 'Pre-populated from vendor registration',
    saving: 'Saving...',
    saveBankAccount: 'Save',
    methodOther: 'Other',
    methodUndefined: 'Payment method not defined',
    selectPaymentMethod: 'Select payment method',
    loadingInformation: 'Loading payment information...',
    updatingInformation: 'Updating payment information...',
    statusScheduled: 'Scheduled',
    statusPending: 'Pending',
    statusCompleted: 'Completed',
    statusReconciled: 'Reconciled',
    reconciliationLoading: 'Finalizing reconciliation…',
    createdPaymentLoading: 'Loading payment…',
    edit: 'Edit',
    delete: 'Delete',
    deletePayment: 'Delete payment',
    typePrincipal: 'Principal',
    dueDateShort: 'Due',
    paidOn: 'Paid on: {{date}}',
    debitCardLabel: 'Debit',
    creditCardLabel: 'Credit',
    adjustmentPrincipal: 'Principal',
    scheduledAmountContext: 'Originally scheduled amount',
    adjustmentInterest: 'Interest',
    adjustmentPenalty: 'Penalty',
    adjustmentDiscount: 'Discount',
    adjustmentsTitle: 'Reconciliation adjustments',
    adjustmentAddedBy: 'by {{name}}',
    adjustmentDescriptionFallback: 'No description',
    adjustmentAdvanceExplanation:
      'The total transaction was {{total}}, and {{advance}} was posted as a vendor advance',
    adjustmentUnknownUser: 'Unknown user',
    viewTransactionDetails: 'View transaction details',
    refreshBankStatus: 'Check bank status',
    bankStatusUpdated: 'Bank status updated: {{status}}',
    bankStatusUnchanged: 'No change at bank: {{status}}',
    bankStatusError: 'Could not fetch the status from the payment gateway',
    gatewayPaymentStatus: {
      CREATED: 'Created (awaiting return)',
      SCHEDULED: 'Scheduled',
      PAID: 'Paid at bank',
      CANCELLED: 'Cancelled',
      REJECTED: 'Rejected',
      REFUNDED: 'Refunded',
    },
    downloadReceipt: 'Download receipt',
    receiptUnavailable: 'TecnoSpeed is still building the receipt. Try again in a moment.',
    receiptNotPaid: 'TecnoSpeed only issues a receipt for a payment the bank confirmed.',
    receiptError: 'Could not download the receipt.',
    autoExtracted: 'Auto-extracted',
    digitalLine: 'Digital line',
    digitalLineEmpty: 'Not filled',
    fillDigitalLine: 'Fill',
    editDigitalLine: 'Edit digital line',
    boletoValidationValid: 'Boleto valid',
    boletoValidationInvalid: 'Boleto invalid',
    boletoUnavailable: 'Boleto data not available yet.',
    boletoLinhaDigitavelUnavailable:
      'Boleto digital line is not available. Use the PIX option below or pay via automatic bank debit.',
    pixCopiaECola: 'PIX copy and paste',
    clickToCopy: 'Click to copy',
    copiedToClipboard: 'Copied to clipboard',
    readonlyNotice: 'Account approved — payment cannot be changed',
    pageTitle: 'Payments — Redesign',
    pageDescription: 'Payment section within Bill Detail. Select a scenario:',
    selectScenario: 'Select scenario',
    scenarios: {
      smartDefault: 'Smart default',
      multiBank: 'Multiple accounts',
      confirmed: 'Confirmed',
      noBank: 'No bank account',
      readonly: 'Read-only',
      boleto: 'Boleto (NFS-e)',
    },
    annotations: {
      smartDefault:
        '80% case: Supplier has 1 bank account with PIX key. System builds payment inline — no dialog, no "Add" click. Just confirm.',
      multiBank:
        'Multiple accounts: Supplier has 2+ bank accounts registered. User selects which to use. Primary is pre-selected.',
      confirmed:
        'Payment confirmed. Compact card with expandable details. Edit button for adjustments before approval.',
      noBank:
        'No bank details: Supplier has no registered account. Offers path to add or use another method (boleto).',
      readonly:
        'Read-only: Account approved or in later stage. Payment visible but not editable (except master approver).',
      boleto:
        'Boleto (auto-extracted): NFS-e came with boleto. System extracts digital line automatically and builds payment. Same inline pattern.',
    },
    subtitleScheduled: '{{amount}} scheduled',
    subtitlePending: '{{amount}} pending',
    subtitlePartial: '{{paid}} of {{total}}',
    emptyState: 'No payments registered',
    emptyStateDescription:
      'Use Create payment and pick a method: transfer (PIX or TED), boleto, tax slip, card, or another available option.',
    createPayment: 'Create payment',
    editingPayment: 'Editing payment',
    creatingPayment: 'New payment',
    amount: 'Amount',
    amountRequiredLabel: 'Amount *',
    dueDateRequiredLabel: 'Due date *',
    paymentStatusRequiredLabel: 'Payment status *',
    originAccountRequiredLabel: 'Origin account *',
    requiredFieldHint: 'Fields marked with * are required.',
    transferAccountOptionalWithPixCode:
      'Optional when using PIX copy-and-paste — select an account if you want to link it to the payment.',
    pixCopiaEColaPlaceholder: 'Paste the PIX code here',
    transferMethodMismatchHint:
      'No vendor account matches the selected transfer method. Add a suitable account or switch between PIX and TED.',
    transferPayeeOrPixCodeRequired: 'Enter PIX copy-and-paste or select a payee account to save.',
    transferPayeeRequired: 'Select a payee account to save this TED payment.',
    method: 'Method',
    notes: 'Notes',
    notesPlaceholder: 'Internal notes about this payment',
    save: 'Save',
    cancel: 'Cancel',
    deleteConfirmTitle: 'Delete payment',
    deleteConfirmMessage: 'Are you sure you want to delete this payment?',
    paymentCreated: 'Payment created successfully',
    paymentUpdated: 'Payment updated successfully',
    paymentUpdateError: 'Could not update the payment.',
    paymentRefreshError: 'Payment created, but the list could not be refreshed. Reload the page.',
    paymentDeleted: 'Payment deleted successfully',
    newPaymentSeparator: 'New payment',
    bulkPaymentsCreated: '{{count}} payments created successfully',
    confirmAmount: 'Confirm {{amount}}',
    confirmInstallments: 'Confirm {{count}} installments',
    remainingAfterPartial: 'You can schedule the remaining ({{amount}}) later',
    backToSinglePayment: 'Back to single payment',
    generatedInstallments: 'GENERATED INSTALLMENTS',
    installmentsSummary: 'Payments match',
    firstDueDate: 'First due date',
    paymentValueLabel: 'Value of this payment',
    installmentsHeaderSummary: '{{amount}} in {{count}} installments',
    installmentsBadge: '{{count}} installments',
    intervalAuto: 'Automatic interval',
    customDates: 'Custom dates',
    totalLabel: 'Total',
    methodBoleto: 'Boleto',
    methodGuia: 'Guia',
    slipOrTaxPaymentType: 'Payment type (boleto or tax slip)',
    methodCard: 'Card',
    otherMethods: 'Other methods',
    methodDoc: 'DOC',
    methodCheck: 'Check',
    creditCard: 'Credit card',
    debitCard: 'Debit card',
    selectCard: 'Select card',
    noCreditCards: 'No card on file. Add a card in Settings.',
    guiaVsBoletoHint:
      'Guia: taxes and agency slips (DARF, GPS, collections). Boleto: bank-issued bill. Both accept digital line or barcode.',
    noBankNextStepHint:
      'No supplier bank account: pick the origin account above and continue to boleto/guia, or register the supplier account.',
    continueToSlipOrTax: 'Continue to boleto or guia',
    invalidBarcodeBoletoGuia: 'Invalid barcode',
    boletoTypeBankSlip: 'Bank slip',
    boletoTypeUtilityCollection: 'Collection - utility',
    boletoTypeTaxCollection: 'Collection - tax',
    boletoTypeCollection: 'Collection',
    barcode: 'Barcode',
    barcodeOrDigitalLine: 'Barcode / digital line',
    barcodeOrDigitalLinePlaceholder: 'Paste barcode or digital line',
    barcodeHint: 'Accepts 44, 47, or 48 digits. You can paste with mask.',
    nossoNumero: 'Nosso número',
    quickSuggestionTitle: 'Alchemy Suggestion',
    quickSuggestionPrimary: 'Primary method configured',
    quickSuggestionHistory: 'Based on history • Used {{count}}x',
    quickSuggestionCta: 'Quick create payment - {{method}}',
    quickSuggestionSubmitting: 'Creating...',
    quickSuggestionSubmit: 'Create Payment',
    quickPaymentDueDateDialogTitle: 'Enter due date',
    quickPaymentDueDateLabel: 'Due date *',
    quickPaymentValueLabel: 'Amount',
    quickPaymentMethodLabel: 'Method',
    quickPaymentPixKeyLabel: 'PIX Key',
    quickPaymentBankLabel: 'Bank',
    quickPaymentAccountLabel: 'Account',
    quickPaymentCancel: 'Cancel',
    quickPaymentCreated: 'Payment created successfully',
    quickPaymentError: 'Error creating payment',
    quickPaymentDueDateRequired: 'Please enter the due date',
    sourceToggleBankAccount: 'Bank account',
    sourceToggleCreditCard: 'Credit card',
    paymentStatus: 'Payment status',
    statusToPay: 'To pay',
    statusPaid: 'Paid',
    paidStatusWarning:
      'Selecting "Paid" means this bill will not be sent to Payments after approval. The payment will still need to be reconciled with the bank or credit card transaction.',
    ccInstallments: 'Installments',
    ccInstallmentsSuffix: 'x',
    payWithCreditCard: 'Pay with credit card',
    selectCreditCard: 'Select card',
    noCreditCardsRegistered: 'No credit card registered for this entity',
    paymentDate: 'Payment date',
    saveCreditCardPayment: 'Save payment',
  },
  billStatuses: {
    draft: 'Draft',
    submitted: 'Submitted',
    pendingApproval: 'Pending approval',
    processingApproval: 'Processing approval',
    approved: 'Approved',
    rejected: 'Rejected',
    partiallyPaid: 'Partially paid',
    paid: 'Paid',
    completed: 'Completed',
    void: 'Voided',
    migrated: 'Migrated',
  },
  paymentValidation: {
    pixKeyRequired: 'PIX key is required',
    pixKeyInvalid: 'Invalid PIX key. Enter a CPF, CNPJ, email, phone or random key.',
    barcodeRequired: 'Barcode is required',
    barcodeLength: 'Barcode must have 44, 47 or 48 digits.',
    barcodeChecksum: 'Invalid barcode: wrong check digit.',
    digitalLineChecksum: 'Invalid digitable line: digit check failed.',
    bankAccountRequired: 'A bank account is required for TED/DOC',
    creditCardRequired: 'Credit card is required',
    fieldRequired: 'Required field',
    barcodeInvalid: 'Invalid barcode',
  },
  common: {
    select: 'Select',
    loading: 'Loading...',
    define: 'Define',
    gotIt: 'Got it',
    cancel: 'Cancel',
    back: 'Back',
  },
  actions: {
    discard: 'Back',
    saveDraft: 'Save draft',
    save: 'Save',
    cancel: 'Cancel',
    sendForApproval: 'Send for approval',
    void: 'Void account',
    voidShort: 'Void',
    moreActions: 'More actions',
    returnToReview: 'Return to review',
    returnToReviewConfirmTitle: 'Return this bill to review?',
    returnToReviewConfirmDescription:
      'The current approval workflow will be cancelled. The bill will return to review and can be adjusted before being sent again.',
    returnToReviewConfirm: 'Return to review',
    returnToReviewSuccess: 'Approval cancelled. The bill is back in review.',
    deferral: 'Deferral/Prepaid expense',
    approve: 'Approve',
    saveAndApprove: 'Save and approve',
  },
  sendForApproval: {
    success: 'Sent for approval',
    budgetHardBlockRejected: 'Budget exceeded',
    budgetHardBlockRejectedDescription:
      'This bill was automatically rejected because it exceeds a hard budget limit. Adjust the bill or budget line, then send for approval again.',
  },
  listPage: {
    overview: {
      sectionLabel: 'Bills overview',
      sampleNotice:
        'Sample data — preview of the new overview; values are not connected to bills yet.',
      sampleActionNotice:
        'The overview uses sample data. The list below shows real bills and may not match the amount shown.',
      sampleMonth: 'October',
      overdue: 'Overdue',
      nextSevenDays: 'Next 7 days',
      remainingMonth: 'Rest of {{month}}',
      stillOpenMonth: 'Still open in {{month}}',
      historicalOverlap: 'May include bills also counted in Overdue.',
      reconciled: 'Reconciled payments',
      timelineLabel: 'Amounts by day of the month',
      timelineDayFilter: 'Chart day',
      indicatorFilter: 'Indicator',
      timelineEmpty: 'No bills in this period',
      timelineEmptyDescription: 'Daily amounts will appear when this month has bills.',
      timelineConversionUnavailable: 'Amounts unavailable for this period',
      timelineConversionUnavailableDescription:
        'There are bills this month, but an exchange rate is missing for their amounts.',
      expand: 'Expand overview',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      chartCurrency: 'Chart currency',
      reportingCurrency: 'Reporting currency',
      entityFilter: 'Filter overview by entity',
      allEntities: 'All entities',
      incompleteConversion:
        'Some bills could not be converted. The displayed totals are incomplete.',
      fxUnavailable: 'Unavailable',
      fxRateFallbackLabel: 'PTAX',
      openAmount: 'Open',
      reconciledAmount: 'Reconciled',
      dayPaymentCount_one: '{{count}} payment',
      dayPaymentCount_zero: '{{count}} payments',
      dayPaymentCount_other: '{{count}} payments',
      dayBillCount_one: '{{count}} bill',
      dayBillCount_other: '{{count}} bills',
      dayAriaLabel: '{{date}}: {{count}}, {{amount}}',
      today: 'today',
      automaticCapture: 'Automatic capture',
      newVendors: 'New vendors this month',
      missingDetails: 'Bills with missing data now',
      missingPaymentDetails: 'Payment data missing now',
      drilldownVendor: 'Vendor',
      drilldownDescription: 'Description',
      drilldownContributingAmount: 'Amount in this metric',
      drilldownSource: 'Source',
      drilldownAutomatic: 'Automatic',
      drilldownManual: 'Manual',
      drilldownTotal: 'Metric total',
      drilldownCaptureCount: '{{automatic}} of {{eligible}} bills captured automatically',
      drilldownBillCount_one: '{{count}} bill',
      drilldownBillCount_other: '{{count}} bills',
      drilldownEmpty: 'No bills in this metric.',
      loading: 'Loading bills overview',
      empty: 'No data available for this period.',
      noResults: 'No bills match the filters.',
      error: 'Could not load the bills overview.',
      retry: 'Try again',
      returnToCurrentMonth: 'Return to current month',
    },
    stats: {
      total: 'Total',
      paid: 'Paid',
      pending: 'Pending',
      overdue: 'Overdue',
      bill: 'bill',
      bills: 'bills',
    },
    periodHeader: {
      prevAria: 'Previous months',
      nextAria: 'Next months',
      complete: 'Settled',
      loadError: 'Could not load period statistics.',
      retry: 'Retry',
      periodPlaceholder: 'Period',
      allPeriods: 'All periods',
    },
    summaryStrip: {
      total: 'Total',
      paid: 'Paid',
      pending: 'Pending',
      overdue: 'Overdue',
      withCount: '{{amount}} ({{count}})',
    },
    bankFileDialog: {
      title: 'Generate bank file',
      description:
        'The preflight validates payments without changing their status or confirming submission to the bank.',
      loading: 'Validating selection…',
      selected: 'Selected',
      eligible: 'Eligible',
      cnabEligible: 'CNAB eligible',
      blocked: 'Blocked',
      blockingReasons: 'Payments left out',
      blockedAreLeftOut_one:
        '{{count}} payment is left out of this file. Fix the reason below and generate another file afterwards if you want.',
      blockedAreLeftOut_other:
        '{{count}} payments are left out of this file. Fix the reasons below and generate another file afterwards if you want.',
      generateEligible_one: 'Generate XLSX with 1 payment',
      generateEligible_other: 'Generate XLSX with {{count}} payments',
      ineligible: 'Payment is not eligible.',
      cancel: 'Cancel',
      generate: 'Generate XLSX',
      processing: 'Generating bank file…',
      successTitle: 'Bank file generated',
      successDescription:
        'The file was saved for audit. Submission to the bank still needs to be confirmed.',
      errorTitle: 'Could not generate the bank file',
      errorFallback: 'Try again or close this window.',
      preflightErrorTitle: 'Could not validate the selection',
      preflightErrorDescription: 'Try again to load the preflight.',
      retry: 'Try again',
      close: 'Close',
    },
    vendorBills: {
      requesterLabel: 'Requester',
      emptyState: 'No bills found for this vendor',
      summary: {
        totalBills: 'Total bills',
        totalPaid: 'Total paid',
        openBills: 'Open bills',
      },
      statusFilter: {
        placeholder: 'All statuses',
        all: 'All statuses',
        DRAFT: 'Draft',
        SUBMITTED: 'In review',
        PENDING_APPROVAL: 'Pending approval',
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
        PARTIALLY_PAID: 'Partially paid',
        PAID: 'Paid',
        COMPLETED: 'Completed',
        VOID: 'Voided',
      },
      columns: {
        number: 'Number',
        description: 'Description',
        issueDate: 'Issue date',
        dueDate: 'Due date',
        amount: 'Amount',
        status: 'Status',
        actions: 'Actions',
      },
      actions: {
        viewDetails: 'View details',
        openInNewTab: 'View in new tab',
        replicateClassifications: 'Replicate classifications',
      },
      installmentLabel: 'Installment {{paymentNumber}}',
      replication: {
        trigger: 'Replicate classifications',
        title: 'Replicate classifications',
        description:
          'Copy the classifications applied to this bill to other bills from the same vendor that are still in pre-approval.',
        loading: 'Loading eligible bills...',
        empty: 'No eligible bills were found to receive the classifications.',
        targetLabel: 'Select target bills',
        sourceBill: 'Source bill',
        selectionCount: '{{count}} selected',
        confirm: 'Replicate classifications',
        submitting: 'Replicating...',
        cancel: 'Cancel',
        success: 'Classifications replicated to {{count}} bill(s).',
        partialSuccess:
          'Classifications replicated to {{successCount}} bill(s). {{failedCount}} failed.',
        genericError: 'Could not replicate classifications.',
        candidateMeta: '{{status}} · issue {{issueDate}} · due {{dueDate}}',
      },
    },
    horizon: {
      overdue: 'Overdue',
      dueToday: 'Due today',
      next7Days: 'Next 7 days',
      thisMonth: 'This month',
      nextMonth: 'Next month',
      sublabel: {
        overdue: 'Up to 45 days ago',
        until: 'Until',
      },
    },
    recurringAlerts: {
      overdueTitle: 'Overdue recurring bills',
      overdueBody: 'For {{period}}, these obligations are overdue ({{count}}):',
      overdueDueSuffix: ' — due {{date}}',
      moreCount: '+{{count}} more…',
      variationTitle: 'Value variation detected',
      variationLead: 'Deviations above the 15% threshold this period ({{count}}):',
      variationLine: 'expected {{expected}} vs. actual {{actual}} ({{pct}}%).',
      contractsTitle: 'Contracts / coverage ending soon',
      contractsBody: '{{count}} template(s) end within the next {{days}} days.',
      ctaDetails: 'View details',
      ctaInvestigate: 'Investigate',
      ctaReview: 'Review',
    },
    simple: {
      moreFiltersAria: 'More filters',
      moreFiltersTitle: 'More filters',
      columnsAria: 'Table columns',
      columnsTitle: 'Columns',
      retryCustomColumns: 'Custom fields failed to load. Retry',
      columnsGroupReorderHint: 'Drag the handle to reorder columns within each group.',
      customColumnsEmpty: 'No active custom fields available to add as columns.',
      columnsDefaultsHint:
        'Every column can be shown or hidden. The table starts with a default set.',
      columnsReorderHint: 'Drag the handle to reorder columns in the table.',
      columnDragHandleAria: 'Drag to reorder column',
      resetColumnOrder: 'Reset column order',
      includeNfeAiLabel: 'Include AI-created',
      includeNfeAiFilterExcluded: 'Hidden',
      columnDataType: {
        date: 'Date',
        text: 'Text',
        status: 'Status',
        currency: 'Amount',
        classification: 'Classification',
        boolean: 'Yes/No',
        payment: 'Payment',
        actions: 'Actions',
      },
      applyDescription: 'Apply',
      descriptionPlaceholder: 'Description contains…',
      rowCompetencyDate: 'Competency date',
      rowPaymentMethod: 'Payment method',
      rowRecurring: 'Recurring',
      rowDescription: 'Description',
      rowCostCenter: 'Cost center',
      rowDepartment: 'Department',
      rowCategoria: 'Category',
      customSegments: 'Custom fields',
      customSegmentsEmpty: 'No custom fields for this entity.',
      customSegmentsEmptyAll: 'No custom fields available to filter.',
      customSegmentTextPlaceholder: 'Field value…',
      customSegmentsLoading: 'Loading...',
      customSegmentsLoadError: 'Could not load custom fields.',
      customSegmentsRetry: 'Try again',
      customSegmentsPermissionRequired: 'You do not have permission to view custom fields.',
      rowSource: 'Source',
      rowTaxRetention: 'Tax retention',
      yes: 'Yes',
      no: 'No',
      noEntityForSelectors: 'No entity available for this selector.',
      categoriaSegmentMissing: 'Categoria segment is not set up for this entity.',
      categoriaNoPredefinedValues: 'Categoria has no predefined values to filter by.',
      searchPlaceholder: 'Search or add filter…',
      mainTabsMenuAria: 'Choose list view',
      workflowTabsAria: 'Workflow stages',
      mainTabsMore: '{{count}} more…',
      mainTabsOverflowSearchPlaceholder: 'Search tab…',
      mainTabsOverflowNoResults: 'No tab found',
      savedViewsOverflowSearchPlaceholder: 'Search for a view…',
      savedViewsOverflowNoResults: 'No view found',
      savedViewsOverflowMenuAria: 'More saved views',
      savedViewsOverflowTriggerFallback: 'Saved views',
      unifiedViewsOverflowMenuAria: 'List tabs and saved views',
      unifiedStripWorkflowHeading: 'List tabs',
      unifiedStripSavedHeading: 'Saved views',
      unifiedViewsOverflowNoResults: 'No matches',
      statusFilterLabel: 'Status',
      statusFilterPickerTitle: 'Filter by status',
      removeStatusAria: 'Remove {{status}}',
      tabContext: {
        overview: 'All bills in scope',
        drafts: 'Drafts and submitted for review',
        'for-approval': 'Waiting for approval',
        approved: 'Approved, partially paid, paid and completed',
        'for-payment': 'Ready for payment',
        'automatic-debit': 'Automatic debit pending reconciliation',
        'accepted-waiting': 'Waiting for bank return',
      },
      savedViews: {
        saveViewCircleLabel: 'Save view',
        defaultViewShortLabel: 'Default',
        defaultViewSaved: 'This view will open when you enter the list (no tab in the URL).',
        defaultViewCleared: 'Default view removed from preferences.',
        visibilityToggleTooltip:
          'Private: only you can see and use this view. Shared: anyone in the organization can see and use it.',
        visibilityToggleAria: 'Saved view visibility',
        saveAction: 'Save view…',
        saveDisabledHint: 'Change filters or columns to enable',
        saveSuccess: 'View saved',
        saveError: 'Could not save the view',
        loadSuccess: 'View applied',
        loadError: 'Could not load the view',
        loadInvalid: 'Invalid view state',
        sharedBadge: 'shared',
        dialogTitle: 'Save current view',
        nameLabel: 'Name',
        namePlaceholder: 'e.g. My list — approval',
        visibilityLabel: 'Visibility',
        privateOption: 'Private (only you)',
        sharedOption: 'Shared (whole organization)',
        privateHint: 'Only you will see this view in the list.',
        sharedHint: 'Anyone in the tenant can see and use this view.',
        cancel: 'Cancel',
        confirmSave: 'Save',
        privateShort: 'Private',
        sharedShort: 'Shared',
        privateScopeTooltip: 'Only you can see and use this view.',
        sharedScopeTooltip: 'Anyone in the organization can see and use this view.',
        deleteViewTooltip: 'Delete this saved view',
        defaultViewSetTooltip: 'Set as the default view when opening this list',
        defaultViewClearTooltip: 'Remove as default view',
        deleteView: 'Delete view',
        deleteConfirmTitle: 'Delete this view?',
        deleteConfirmDescription: 'This cannot be undone.',
        deleteConfirmAction: 'Delete',
        deleteSuccess: 'View deleted',
        deleteError: 'Could not delete the view',
        scopeUpdated: 'Visibility updated',
        scopeUpdateError: 'Could not update visibility',
      },
    },
    retorno: {
      button: 'Return',
      import: 'Import Return',
    },
    retornoUpload: {
      title: 'Import CNAB Return',
      dropHere: 'Drop files here',
      dragOrClick: 'Drag return files or click to select',
      hint: 'CNAB 240 return files (.RET, .TXT) — up to {{max}} files',
      selectFiles: 'Select Files',
      filesSelected: '{{count}} file(s) selected',
      clear: 'Clear',
      uploading: 'Uploading...',
      send: 'Send {{count}} file(s)',
      invalidFiles: 'Select .RET or .TXT files',
      maxFiles: 'Maximum {{max}} files per upload',
      sentSingle: 'Return "{{name}}" sent for processing.',
      sentMultiple: '{{count}} return files sent for processing.',
      error: 'Error uploading return file(s).',
    },
    retornoProcessing: {
      title: 'Return processing',
      pending: '{{count}} processing',
      completed: '{{count}} completed',
      failed: '{{count}} failed',
      empty: 'No return files processing.',
      totalMatched: 'Total: {{count}} matched',
      status: {
        waiting: 'Queued',
        active: 'Processing',
        completed: 'Completed',
        failed: 'Failed',
        unknown: 'Unknown',
      },
      summary: {
        records: '{{count}} record(s)',
        matched: '{{count}} matched',
        changed: '{{count}} updated',
        unmatched: '{{count}} unmatched',
        awaitingProvider: '{{count}} awaiting TecnoSpeed reconciliation',
        errors: '{{count}} error(s)',
      },
    },
    cnabHistory: {
      title: 'Payment file history',
      button: 'File history',
      empty: 'No payment files found.',
      downloadError: 'Failed to download payment file.',
      unverified: 'unverified layout',
      fetchError: 'Error loading file history.',
      markedSent: 'File marked as sent.',
      markSentError: 'Error marking file as sent.',
      markSent: 'Sent',
      download: 'Download',
      colFile: 'File',
      colType: 'Type',
      colSeq: 'Seq.',
      colPayments: 'Pmts',
      colLotes: 'Lotes',
      lotesCount: '{{count}} lot(s)',
      colDate: 'Date',
      colImportedAt: 'Imported at',
      colStatus: 'Status',
      colActions: 'Actions',
      showingOf: 'Showing {{shown}} of {{total}}',
      fileType: {
        REMESSA: 'Remittance',
        RETORNO: 'Return',
      },
      status: {
        GENERATED: 'Generated',
        SENT: 'Sent',
        PROCESSED: 'Processed',
        REJECTED: 'Rejected',
        CANCELLED: 'Cancelled',
      },
      retornoRejectedNoMatch: 'No line of the file matched a payment',
      payments: {
        back: 'Back to files',
        description: 'The bank’s reply for the payment, and the payment’s current status.',
        untitled: 'Return {{sequence}}',
        empty: 'No payments are linked to this file.',
        colPayee: 'Payee',
        colAmount: 'Amount',
        colInstruction: 'Bank reply',
        colCurrentStatus: 'Current status',
        instructionMissing: 'The bank did not report an occurrence',
        bankReply: {
          AWAITING_RETURN: 'Awaiting the bank’s reply',
          SCHEDULED: 'Scheduled at the bank',
          PAID: 'Paid by the bank',
          REJECTED: 'Rejected by the bank',
          REFUNDED: 'Returned by the bank',
          CANCELLED: 'Cancelled at the bank',
        },
      },
    },
    timeline: {
      title: 'Payment history',
      empty: 'No events found for this payment.',
      remessaFile: 'Remittance file',
      retornoFile: 'Return file',
      viewTimeline: 'History',
      loading: 'Loading the history',
      loadError: 'Could not load the history',
      loadErrorDescription: 'Try again in a moment.',
      attemptTitle: 'Send to the payment gateway · attempt {{number}}',
      remittanceTitle: 'Remittance to the payment gateway',
      protocol: 'Reference {{id}}',
      remittanceError: 'The payment gateway reported an error on this remittance.',
      reversedHint:
        'The bank reversed this payment. If it was already paid, the journal entry and the bill status are not undone on their own: see Payments under review.',
      outcome: {
        IN_FLIGHT: 'Sending',
        ACCEPTED: 'Accepted',
        REJECTED: 'Refused',
        TRANSPORT_ERROR: 'No answer',
        ABANDONED: 'Closed',
      },
      remittanceStatus: {
        CREATING: 'Creating',
        PENDING_SEND: 'Awaiting send',
        PROCESSING: 'Processing',
        PROCESSED: 'Processed',
        SENT: 'Sent',
        FAILED: 'Failed',
        CANCELLED: 'Cancelled',
        UNKNOWN: 'Unknown status',
      },
      actions: {
        NEEDS_REVIEW: 'Sent to review',
        NEEDS_REVIEW_RESOLVED: 'Review resolved',
        PROVIDER_RESULT: 'Bank result',
        PROVIDER_RESULT_RECORDED: 'Bank result recorded',
        PROVIDER_DISPATCH_REJECTED: 'Send refused',
        PROVIDER_DISPATCH_ABANDONED: 'Send closed with no answer',
        PROVIDER_DISPATCH_ERROR: 'Send error',
        PROVIDER_PAYEE_REGISTERED: 'Payee registered at the payment gateway',
        PROVIDER_PAYEE_ACCOUNT_REGISTERED: 'Payee account registered at the payment gateway',
        FINNET_PRIOR_SEND_REFUSED: 'Resend refused: a send is already in progress',
        PRIOR_SEND_WITHDRAWN: 'Earlier send withdrawn',
        PRIOR_SEND_STILL_ACTIVE: 'Earlier send still active',
        CANCEL_REQUESTED: 'Cancellation requested',
        SEND_CANCEL_INTENT: 'Send cancellation requested',
        SEND_CANCELLED: 'Send cancelled',
        SENT_TO_PROVIDER: 'Sent to the payment gateway',
        CREATED: 'Created',
        UPDATE: 'Updated',
        UPDATED: 'Updated',
        STATUS_CHANGE: 'Status changed',
        CANCELLED: 'Cancelled',
        RETRY: 'New attempt',
        CNAB_REMESSA_EXPORT: 'Remittance generated',
        BANK_FILE_REMESSA_EXPORT: 'Payment file generated',
        BANK_FILE_XLSX_EXPORT: 'Bank spreadsheet',
        CNAB_RETORNO: 'Bank return',
        CNAB_RETORNO_AUDIT: 'Bank return',
        CNAB_REMESSA_FILE: 'Remittance file',
        CNAB_RETORNO_FILE: 'Return file',
        PROVIDER_ATTEMPT_IN_FLIGHT: 'Send in progress',
        PROVIDER_ATTEMPT_ACCEPTED: 'Send accepted',
        PROVIDER_ATTEMPT_REJECTED: 'Send rejected',
        PROVIDER_ATTEMPT_TRANSPORT_ERROR: 'Send failed',
        PROVIDER_ATTEMPT_ABANDONED: 'Send abandoned',
        PAYMENT_REMITTANCE_CREATING: 'File being created',
        PAYMENT_REMITTANCE_PENDING_SEND: 'File waiting to be sent',
        PAYMENT_REMITTANCE_PROCESSING: 'File processing',
        PAYMENT_REMITTANCE_PROCESSED: 'File processed',
        PAYMENT_REMITTANCE_SENT: 'File sent',
        PAYMENT_REMITTANCE_FAILED: 'File failed',
        PAYMENT_REMITTANCE_CANCELLED: 'File cancelled',
        PAYMENT_REMITTANCE_UNKNOWN: 'Payment file',
      },
      actionUnknown: 'Payment event',
      statusUnknown: 'Unidentified status',
      remittanceUnknown: 'Payment file',
      statuses: {
        ACCEPTED: 'Accepted',
        REJECTED: 'Rejected',
        TRANSPORT_ERROR: 'Communication failure',
        IN_FLIGHT: 'In progress',
        PENDING_SEND: 'Waiting to be sent',
        SENT: 'Sent',
        PROCESSED: 'Processed',
        GENERATED: 'Generated',
        CREATING: 'Being created',
        UNKNOWN: 'Unidentified',
        ABANDONED: 'Abandoned',
      },
      remittanceTypes: {
        CNAB240: 'CNAB 240',
        CNAB400: 'CNAB 400',
        BTG_XLSX: 'Bank spreadsheet',
        REMESSA: 'Remittance',
      },
      reasons: {
        FAILED_PAYMENT_SUPERSEDED_BY_SETTLEMENT:
          'Failed payment cancelled: the bill was settled by payment {{paymentId}}.',
        PAYMENT_UPDATED_RECALCULATE_BILL_STATUS:
          'Bill status recalculated after a payment was edited.',
      },
    },
    table: {
      vendor: 'Vendor',
      reimbursementBadge: 'Reimbursement',
      billNumber: 'Invoice #',
      copyBillNumber: 'Copy invoice #',
      status: 'Status',
      billTypeColumn: 'Type',
      amount: 'Amount',
      contabil: 'Accounting',
      gerencial: 'Managerial',
      gerencialExpand: 'Expand',
      gerencialCollapse: 'Collapse',
      paymentMethod: 'Payment method',
      competencyDateColumn: 'Competency',
      descriptionColumn: 'Description',
      sourceColumn: 'Source',
      recurringColumn: 'Recurring',
      costCenterColumn: 'Cost center',
      departmentColumn: 'Department',
      entityColumn: 'Entity',
      categoriaColumn: 'Category',
      taxCreditsColumn: 'PIS/COFINS credits',
      retainedAmountColumn: 'Withheld amount',
      taxRetentionColumn: 'Retention',
      dueDate: 'Due date',
      scheduledDate: 'Scheduled for',
      postedDate: 'Posted',
      actions: 'Actions',
      selectAll: 'Select all',
      loading: 'Loading...',
      empty: 'No bills found.',
      page: 'Page',
      of: 'of',
      results: 'results',
      previous: 'Previous',
      next: 'Next',
      perPage: '/ page',
      openMenu: 'Open menu',
      selectBill: 'Select {{vendor}}',
      cnabExportDisabledTooltip:
        'Bill has no payment eligible for CNAB export. Set payment method (PIX, TED, DOC) on scheduled payments.',
      bulkActionSelectionDisabledTooltip:
        'Only draft, submitted, or pending approval bills can be selected for bulk actions.',
      subheaderBill: 'Bill',
      subheaderPayment: 'Payment',
      total: 'Total',
      method: 'Method',
      installmentValue: 'Installment',
      sortAsc: 'ascending',
      sortDesc: 'descending',
      sortBy: 'Sort by {{field}}{{direction}}',
      filterVendor: 'Filter by vendor',
      sortTooltip: 'Click to sort by this column',
      filterVendorTooltip: 'Click to filter vendors',
      filterEntityTooltip: 'Click to filter by entity',
      filterColumnTooltip: 'Click to filter by this column',
      filterDescriptionMinChars: 'At least 2 characters to filter.',
      filterRecurringYesOnly: 'Recurring only',
      filterPaymentMethodNone: 'No payment method',
      filterRetentionYesOnly: 'With retention only',
      openBill: 'Open bill',
      openVendorInNewTab: 'Open vendor in new tab',
      openPayrollRun: 'Open payroll run in new tab',
      payrollRunLink: 'View payroll',
      openBillRow: 'Open bill: {{vendor}}',
      reimbursementPayeePending: 'Payee to be defined',
      agentCapBadge: 'Created by CAP agent',
      agentCapTooltip: 'Created by the accounts payable alchemist',
      recentlyCreatedSubtitle: 'Created less than 30 minutes ago',
      approvalFailureBadgeAria: 'Last approval attempt failed',
      approvalFailureTooltipTitle: 'Approval failed',
      approvalFailureTooltipHint: 'Open the bill to retry the approval.',
    },
    forPayment: {
      paymentNumber: 'Payment {{n}}',
      statusHeader: 'Status',
      payOnHeader: 'Pay on',
      originBankHeader: 'Origin Account',
      bankMessageHeader: 'Observations',
      payDate: {
        tooltipNext:
          'You chose {{requested}} ({{reason}}). The bank pays on the next business day: {{paidOn}}.',
        tooltipPrevious:
          'You chose {{requested}} ({{reason}}). A tax due on a non-business day is paid on the business day before: {{paidOn}}.',
        tooltipLate: 'Paid after the due date ({{due}}); interest may apply.',
        reasonSaturday: 'Saturday',
        reasonSunday: 'Sunday',
        reasonHoliday: 'national holiday: {{holiday}}',
        holidays: {
          NEW_YEAR: "New Year's Day",
          CARNIVAL_MONDAY: 'Carnival',
          CARNIVAL_TUESDAY: 'Carnival',
          GOOD_FRIDAY: 'Good Friday',
          TIRADENTES: 'Tiradentes',
          LABOUR_DAY: 'Labour Day',
          CORPUS_CHRISTI: 'Corpus Christi',
          INDEPENDENCE: 'Independence Day',
          OUR_LADY_APARECIDA: 'Our Lady of Aparecida',
          ALL_SOULS: "All Souls' Day",
          REPUBLIC: 'Proclamation of the Republic',
          BLACK_CONSCIOUSNESS: 'Black Consciousness Day',
          CHRISTMAS: 'Christmas',
        },
      },
      paymentStatus: {
        SCHEDULED: 'Scheduled',
        PENDING: 'Pending',
        PROCESSING: 'Processing',
        PAID: 'Paid',
        RECONCILED: 'Reconciled',
        FAILED: 'Bank error / rejected',
        CANCELLED: 'Cancelled',
        REVERSED: 'Reversed',
      },
      onlyExportable: 'Only exportable',
      beneficiaryLabel: 'Beneficiary',
      inActiveCnabFile:
        'Payment already in an active CNAB file; wait for return or cancel the send to re-export.',
      pastDueBanner: 'Past-due payments will be generated with today’s date in the CNAB file.',
      cnabDownloadInstructions:
        'To update payment status, send the file via your bank’s Internet Banking. After processing, use "Import return" on this page.',
      cnabDownloadAlertTitle: 'Next steps',
    },
    acceptedWaiting: {
      workflowBanner:
        'Payments accepted by the bank. Import the return file (Transmission > Return) to update statuses.',
      resetToPending: 'Back to pending send',
      resetDialogTitle: 'Back to pending send',
      resetDialogDescription:
        'This will unlink the payments from the sent file and put them back as pending to send. Only use if the bank rejected the file or you cancelled the send with the bank. Otherwise the bank may treat a new send as duplicate.',
      resetDialogConfirm: 'Back to pending',
      resetDialogCancel: 'Cancel',
      resetSuccess: '{{count}} payment(s) set back to pending send.',
      resetError: 'Error setting payments back to pending.',
      cannotResetAlreadyScheduled: 'Cannot revert: payment already scheduled at bank (BD/BE).',
      phases: {
        agendado: 'Scheduled at bank',
        data_alterada: 'Date changed',
        aguardando_efetivacao: 'Awaiting return',
      },
    },
    tabs: {
      all: 'All',
      inReview: 'In review',
      inApproval: 'In approval',
      approved: 'Approved',
      // Legacy keys kept for any saved view labels still referencing them; new code should use
      // `all` / `inReview` / `inApproval` / `approved`.
      overview: 'All',
      drafts: 'In review',
      forReview: 'In review',
      forApproval: 'In approval',
      forPayment: 'For payment',
      automaticDebit: 'Automatic debit',
      history: 'Approved',
      acceptedWaiting: 'Accepted / waiting confirmation',
    },
    filters: {
      vendorLoading: 'Loading…',
      title: 'Filters',
      active: 'Active filters:',
      clearAll: 'Clear all',
      clearAllDates: 'Clear all',
      statusLabel: 'Status',
      vendorLabel: 'Vendor',
      entityLabel: 'Entity',
      entityPlaceholder: 'Select entity',
      entityAll: 'All entities',
      vendorPlaceholder: 'Select vendor',
      vendorsCount: 'Vendors ({{count}})',
      billNumberLabel: 'Invoice #',
      billNumberPlaceholder: 'Invoice number',
      originBankLabel: 'Origin Account',
      originBankPlaceholder: 'Filter by origin account',
      originBankAll: 'All accounts',
      amountLabel: 'Amount',
      totalAmountLabel: 'Total amount',
      amountPlaceholder: 'Amount',
      issueDateLabel: 'Issue date',
      issueDatePlaceholder: 'Issue date range',
      dueDateLabel: 'Due date',
      dueDatePlaceholder: 'Due date range',
      scheduledDateLabel: 'Scheduled for',
      scheduledDatePlaceholder: 'Scheduled date range',
      postedDatePlaceholder: 'Posted date range',
      competencyDateLabel: 'Competency date',
      competencyDatePlaceholder: 'Competency date range',
      datePlaceholder: '...',
      billTypeLabel: 'Transaction type',
      billTypeColumn: 'Type',
      billType: {
        all: 'All',
        payable: 'Expense',
        payroll: 'Payroll (salary)',
        tax: 'Taxes',
        fee: 'Bank Fee',
      },
      billTypeValues: {
        EXPENSE: 'Expense',
        PAYROLL: 'Payroll (salary)',
        TAXES: 'Taxes',
        BANK_FEE: 'Bank Fee',
        VENDOR_ADVANCEMENT: 'Vendor advance',
        CREDIT_CARD_INVOICE: 'Credit card invoice',
      },
      payrollSubTypeValues: {
        SALARY: 'Payroll (salary)',
        ADVANCE: 'Payroll (advance)',
        THIRTEENTH: 'Payroll (13th)',
        VACATION: 'Payroll (vacation)',
        PRO_LABORE: 'Payroll (pro-labore)',
        RPA: 'Payroll (RPA)',
        TERMINATION_NET: 'Payroll (termination)',
        ALIMONY: 'Payroll (alimony)',
        CONSIGNED: 'Payroll (consigned loan)',
        UNION: 'Payroll (union)',
        TAX: 'Payroll (tax)',
      },
      reimbursementsOnlyLabel: 'Reimbursements only',
      recurringOnlyLabel: 'Recurring only',
      includeSalariesLabel: 'Include salaries',
      withholdingsOnlyLabel: 'With tax retention',
      withTaxCreditsOnlyLabel: 'With PIS/COFINS credits',
      withRetainedAmountOnlyLabel: 'With an actual withheld amount',
      descriptionContainsLabel: 'Description',
      classificationCostCenterLabel: 'Cost center',
      classificationGlAccountLabel: 'GL account',
      classificationManagerialAccountLabel: 'Managerial account',
      classificationDepartmentLabel: 'Department',
      classificationCategoriaLabel: 'Category',
      classificationSelectedCount: '{{count}} selected',
      paymentMethodFilterLabel: 'Payment method',
      billSource: {
        MANUAL: 'Manual entry',
        EMAIL: 'Email',
        OCR: 'OCR',
        API: 'API integration',
        EXTERNAL_FORM: 'External form',
        NFE_AUTO_IMPORT: 'Focus NFe integration',
        BANK_FEE_AUTO: 'Bank fee (auto)',
        VENDOR_ADVANCEMENT_RECONCILIATION: 'Vendor advance (reconciliation)',
        PAYROLL_PROCESSING: 'Payroll',
        CSV_IMPORT: 'CSV import',
        CONTA_SIMPLES_CARD_ATTACHMENT: 'Conta Simples attachment',
        CARD_FAST_TRACK: 'Card fast track',
        REIMBURSEMENT_REQUEST: 'Reimbursement request',
      },
    },
    status: {
      all: 'All',
    },
    amountOperators: {
      eq: 'Equal',
      gte: 'Greater or equal',
      lte: 'Less or equal',
      eqSymbol: '=',
      gteSymbol: '≥',
      lteSymbol: '≤',
    },
    upload: {
      duplicatesWithNames: '{{count}} duplicate document(s): {{names}}',
      duplicates: '{{count}} duplicate document(s)',
      duplicateSingleFallback: 'Duplicate document',
      reimbursementRouted:
        'Document routed to a reimbursement request. Track it under Pending — the bill appears in the list only after approval.',
      filesFallback: 'Files',
      failed: '{{count}} document(s) failed: {{errors}}',
      globalDrop: {
        title: 'Drop files to start upload',
        subtitle: 'Document upload opens automatically',
      },
    },
    processing: {
      pending: 'Pending',
    },
    newBillsProcessed: 'Refresh table — {{count}} new bill(s) processed',
    sendBills: 'Send bills',
    newBillButton: 'New bill',
    newBillMenuTitle: 'Create a bill',
    newBillMenu: {
      upload: {
        title: 'Upload documents',
        badge: 'Fastest',
        description: 'Upload boletos, invoices or receipts — AI fills in automatically',
      },
      chat: {
        title: 'Describe to AI',
        description: 'Describe the bill in plain language — AI creates, classifies and schedules',
      },
      form: {
        title: 'Fill out form',
        description: 'Manual entry with all fields',
      },
      email: {
        title: 'Send by email',
        description: 'Forward documents to your company inbox',
      },
      csvImport: {
        title: 'Import CSV',
        description: 'Batch import bills from a CSV spreadsheet',
      },
      paymentsSection: 'Payments',
      importRetorno: {
        title: 'Import return file',
        description: 'Update payment status from the bank return file',
      },
    },
    emailInboxesButtonAria: 'View email inboxes',
    sendByEmailLabel: 'Send bills by email:',
    emailInboxesLoading: 'Loading email inboxes...',
    emailInboxesEmpty: 'No inboxes available.',
    emailInboxesError: 'Failed to load inboxes.',
    emailInboxesCopied: 'Email copied.',
    emailInboxesCopyError: 'Unable to copy email.',
    emailInboxesEditLink: 'Edit emails',
    cnabPreflight: {
      title: 'Check before generating',
      allReady_one: '{{count}} payment ready for the remittance.',
      allReady_other: '{{count}} payments ready for the remittance.',
      someBlocked_one: '{{ready}} of {{total}} payments will be included. {{count}} will not.',
      someBlocked_other: '{{ready}} of {{total}} payments will be included. {{count}} will not.',
      blockedHeading: 'Not included in this remittance',
      blockedHint: 'Fix the payee record and generate again, or go ahead with the rest.',
      blockedHintNothingReady: 'Fix the payee record and generate again.',
      generateNothing: 'Nothing to generate',
      back: 'Back',
      generate_one: 'Generate with {{count}} payment',
      generate_other: 'Generate with {{count}} payments',
      nothingToSend: 'No payment can be sent. Fix the payees listed above.',
      checkFailed: 'Could not check the remittance before generating. Try again in a moment.',
    },
    cnabOriginDialog: {
      title: 'Select origin account',
      titleMultiOrigin: 'Generate files — multiple origin accounts',
      description:
        'Selected payments have different origin accounts. Choose the account used to generate the appropriate bank file. All payments will be updated to this account.',
      descriptionMultiOrigin:
        'Selected payments use more than one origin account. We recommend exporting by origin account (one remessa set per account inside a ZIP).',
      descriptionAllMissing:
        'No selected payment has an origin account. Choose the account used for the bank file. All payments will be updated to this account.',
      paymentCount_one: '{{count}} payment',
      paymentCount_other: '{{count}} payments',
      paymentCount: '{{count}} payment(s)',
      noOriginWarning_one: '{{count}} payment has no origin account and will be skipped.',
      noOriginWarning_other: '{{count}} payments have no origin account and will be skipped.',
      noOriginWarning: '{{count}} payment(s) without origin account (will be skipped).',
      noOriginUsesSelection_one:
        '{{count}} payment has no origin account and will use the selected account.',
      noOriginUsesSelection_other:
        '{{count}} payments have no origin account and will use the selected account.',
      useForAll: 'Will be used for all selected payments.',
      cancel: 'Cancel',
      confirm: 'Confirm & Export',
      missingConvenioWarning:
        'Santander (033) account is missing its CNAB convênio. Set the convênio on the bank account before exporting.',
      cnabIncomplete: 'CNAB convênio not configured',
      supportedBanksHint: 'Supported files: Itaú, Santander and BanBajío (unverified pilot).',
      bankCodeLabel: 'Bank {{code}}',
      exportByBank: 'Export by origin account (ZIP)',
      exportByBankTitle: 'One ZIP with a folder per origin account',
      exportByBankHint:
        'Each origin account generates its remessa in the correct layout (Itaú may split PIX into a second file within the same folder).',
      useSingleAccount: 'Use a single account for all payments',
    },
    automaticDispatchDialog: {
      title: 'Review payment dispatch',
      description:
        '{{count}} payment(s) will be dispatched through the channel configured on the origin account.',
      multipleOriginsDescription:
        '{{count}} payment(s) use {{origins}} origin accounts. Choose how to dispatch them.',
      mixedCurrenciesWarning:
        'The selection contains different currencies; review the accounts before continuing.',
      keepCurrent: 'Keep current origin accounts',
      keepCurrentDetail: 'Each payment will use the channel configured on its own origin account.',
      useOneAccount: 'Use one origin account',
      useOneAccountDetail: 'Every payment will be moved to the selected account before dispatch.',
      differentEntities: 'One account cannot be used for payments from different entities.',
      noEligibleAccounts: 'No enabled and ready account can receive all selected payments.',
      assignmentFailed:
        'Could not change the account for every payment ({{updated}} updated, {{failed}} failed). Nothing was dispatched.',
      cancel: 'Cancel',
      confirm: 'Confirm dispatch',
      sending: 'Sending...',
    },
    sendToProviderOriginDialog: {
      title: 'Origin account for {{integration}}',
      description:
        'Some payments have different origin accounts or none. Choose how to send via {{integration}}.',
      noEligibleAccounts:
        'No eligible origin account for {{integration}}. Configure the integration under Settings → Integrations or assign an origin account to the payments.',
      option1:
        'Keep current account for those already linked to {{integration}} and pick one for the rest',
      option1Detail: '{{matching}} already linked, {{rest}} to update',
      option2: 'Use a single account for all selected payments',
      option2Detail: '{{count}} payment(s)',
      option3:
        'Send only those already with an account linked to {{integration}} (each to its own account)',
      option3Detail: '{{count}} sent, {{skipped}} skipped',
      pickAccountAll: 'Account for all',
      pickAccountRest: 'Account for the rest',
      cancel: 'Cancel',
      confirm: 'Confirm & Send',
    },
    selection: {
      changeOrigin: {
        button: 'Change origin account',
        title: 'Origin account for {{count}} payment(s)',
        description:
          'Every selected payment will leave from the chosen account. Payments already sent to the bank are not changed.',
        placeholder: 'Origin account',
        back: 'Back',
        apply: 'Apply',
      },
      reschedule: {
        button: 'Change payment date',
        title: 'New payment date for {{count}} payment(s)',
        description:
          'Changes only the date the payment goes out, not the bill due date. Payments already sent to the bank are not changed.',
        dateLabel: 'New payment date',
        back: 'Back',
        confirm: 'Change date',
      },
      clearAria: 'Clear selection',
      single: 'bill selected',
      multiple: 'bills selected',
      totalLabel: 'Total:',
      exportSelected: 'Export selected',
      bulkActions: 'Bulk actions',
      exportCnab: 'Generate bank file',
      exportInternalCnab: 'Generate internal CNAB',
      exportBankFile: 'Generate bank file',
      sendPayment: 'Send payments',
      sendViaGateway: 'Send via gateway',
      sendViaGatewayHint: 'Contingency through the reserve VAN while the API is being validated.',
      moreActions: 'More actions',
      sendViaGatewayConfirm: {
        title: 'Send {{count}} selected via gateway · {{total}}',
        description:
          "The whole selection goes through each origin account's reserve bank VAN instead of the bank's API, including what the API refused. The gateway has its own rules: the bank accepts only what the account has released on it, and what cannot leave is refused and stays in the table.",
        confirm: 'Send via gateway',
      },
      sispag: 'Itaú SISPAG',
      cnab240: 'CNAB 240 (Remessa)',
      kaminoDisabled: 'Kamino (coming soon)',
      forPayment: {
        single: 'payment selected',
        multiple: 'payments selected',
      },
      send: {
        count: 'Send {{count}}',
        checking: 'Checking…',
        nothing: 'Nothing to send',
        ready: '{{count}} ready',
        fixable: '{{count}} need a fix',
        blocked: '{{count}} cannot go',
        checkingBadge: 'checking…',
      },
      sendConfirm: {
        title: 'Send {{count}} payment(s) · {{total}}',
        description:
          'Through the payment gateway. Each payment leaves from its own origin account and the remittance downloads on its own once the bank accepts them all.',
        origins: 'From {{count}} origin accounts.',
        leftBehind: 'The {{count}} that are not ready stay selected in the table.',
        partialTotal: 'Partial amount: some ready payments are not on this page.',
        originLine: '{{count}} payment(s) · {{total}}',
        back: 'Back',
        confirm: 'Confirm',
      },
      cnabReview: {
        title: 'Generate the remittance for {{count}} payment(s)',
        blocked: '{{count}} stay out. Each reason is in Observações.',
        none: 'No payment can enter the remittance. Each reason is in Observações.',
        back: 'Back',
        confirm: 'Generate',
      },
    },
    cancelSend: {
      action: 'cancel send',
      title: 'Cancel the send of payment {{label}}',
      description:
        'Withdraws the payment from the payment gateway. If the bank has not received it yet it goes back to "For payment" and can be sent again; if it is already scheduled at the bank, the bank is notified and confirms the cancellation.',
      warning:
        'If the file has already been delivered to the bank, the payment may still show up for approval — check and cancel it at the bank as well.',
      reasonLabel: 'Reason for cancelling',
      reasonPlaceholder: 'E.g. wrong origin account',
      reasonHint: 'At least {{min}} characters. Recorded in the payment history.',
      back: 'Back',
      confirm: 'Cancel send',
      confirming: 'Cancelling…',
      unknownStatus: 'unknown',
      refusal: {
        SEND_NOT_CANCELLABLE:
          'This send can no longer be cancelled here: the gateway reports the status {{status}}.',
        PAYMENT_NOT_PROCESSING:
          'The payment is no longer processing (status {{status}}). Refresh the list.',
        NO_ACTIVE_PROVIDER_ATTEMPT:
          'This payment has no active send at the gateway (it may be in a CNAB file).',
        CANCEL_ALREADY_REQUESTED:
          'The cancellation was already requested from the bank. Wait for its confirmation.',
        PAYMENT_CHANGED_DURING_CANCEL:
          'The payment changed during the cancellation. Refresh the list and check the result.',
        PROVIDER_CALL_FAILED:
          'The payment gateway did not answer. Nothing was changed; try again in a moment.',
        FILE_MAY_BE_AT_BANK:
          'The remittance of this payment may already be at the bank. Check at the bank before cancelling the send.',
        SEND_CANCEL_AT_BANK_ONLY:
          'The payment gateway already accepted this send and cannot cancel it. Cancel the payment directly at the bank; the bank’s return updates the payment here.',
        SEND_PAST_CANCELLATION:
          'The bank already confirmed or processed this payment (status {{status}}). A confirmed payment can only be reversed by the bank: contact it.',
        SEND_NOT_YET_LISTED:
          'The bank does not show this payment yet. Wait for the next result check and try cancelling again.',
        SEND_CANCEL_NOT_CONFIRMED:
          'The bank did not confirm the cancellation. Nothing was changed here; the next result check shows the payment’s real state.',
        SEND_CANCEL_BUSY:
          'The bank connection is busy with a send or an access renewal. Try again in a few minutes.',
        SEND_CANCEL_UNAVAILABLE:
          'Cancelling through this payment gateway is not available in this environment. Contact support.',
        PROVIDER_CONNECTION_LAPSED:
          'The company’s connection to the bank lapsed. An administrator must reconnect it in Integrations, then try cancelling again.',
      },
      fileAtBank: {
        title: 'The remittance may already be at the bank',
        body: 'Deleting the send at the gateway does not recall a file already delivered to the bank: if it was received, resending the payment would duplicate the instruction. Check at the bank before continuing.',
        remittances: 'Remittance(s): {{ids}}',
        noRemittanceIds: 'The gateway did not report the remittance id.',
        confirm: 'I confirmed at the bank that the remittance was not received',
      },
      toast: {
        cancelled: 'Send cancelled. The payment can be sent again.',
        requested: 'Cancellation requested from the bank (protocol {{protocol}}).',
        requestedNoProtocol: 'Cancellation requested from the bank.',
      },
    },
    readiness: {
      checking: 'Checking…',
      ready: 'Ready',
      fix: {
        correct: 'fix',
        correctBankAccount: 'fix bank details',
        register: 'register',
      },
      payeeBankAccount: {
        summary: "The payee's branch or account is not in a format the bank accepts.",
        hint: "Fix the branch and account on the payee's record; the payment will not go out until then.",
        issues: {
          AGENCY_MISSING: "The payee's branch is missing.",
          AGENCY_HAS_CHECK_DIGIT:
            'The branch seems to include its check digit. Enter the branch with up to 4 digits and the digit in its own field.',
          ACCOUNT_MISSING: "The payee's account number is missing.",
          ACCOUNT_TOO_LONG:
            'The account has too many digits (maximum 12). Enter the account without its check digit.',
          ACCOUNT_DIGIT_MISSING: "The account's check digit is missing.",
          ACCOUNT_DIGIT_TOO_LONG: "The account's check digit has too many characters (maximum 2).",
        },
      },
      short: {
        PAYEE_BANK_ACCOUNT_INVALID: 'Invalid bank details',
        default: 'Cannot go',
        PAYEE_PIX_KEY_REQUIRED: 'No PIX key',
        PAYEE_IDENTITY_REQUIRED: 'No tax id',
        PAYEE_BANK_DETAILS_REQUIRED: 'Bank details missing',
        PAYEE_BANK_DETAILS_REQUIRED_bankCode: 'Bank missing',
        PAYEE_ACCOUNT_UNAVAILABLE: 'No account',
        BARCODE_REQUIRED: 'No barcode',
        BARCODE_INVALID: 'Invalid barcode',
        AMOUNT_BARCODE_MISMATCH: 'Amount differs from barcode',
        AMOUNT_INVALID: 'Invalid amount',
        SCHEDULED_DATE_REQUIRED: 'No date',
        DARF_FIELD_REQUIRED: 'DARF incomplete',
        DARF_AMOUNT_INVALID: 'DARF: invalid amount',
        DARF_MULTIPLE_REVENUE_CODES: 'DARF: several codes',
        PAYMENT_METHOD_UNSUPPORTED: 'Method not accepted',
        PAYMENT_CURRENCY_UNSUPPORTED: 'Currency not accepted',
        PAYEE_ACCOUNT_COUNTRY_UNSUPPORTED: 'Account country not served',
        PIX_QR_UNSUPPORTED: 'QR not accepted',
        PAYMENT_REFERENCE_MISSING: 'No reference number',
        PAYMENT_REFERENCE_INVALID_FOR_PROVIDER: 'Reference not accepted',
        ORIGIN_ACCOUNT_REQUIRED: 'No origin account',
        ORIGIN_ACCOUNT_CHANGED: 'Origin changed',
        ORIGIN_ACCOUNT_BANK_MISMATCH: 'Origin mismatch',
        ORIGIN_ACCOUNT_INCOMPLETE: 'Origin incomplete',
        ORIGIN_ACCOUNT_NOT_READY: 'Origin not enabled',
        ORIGIN_ACCOUNT_ENTITY_MISMATCH: "Another entity's origin",
        CAPABILITY_ROUTE_MISMATCH: 'Not enabled',
        CAPABILITY_NOT_CONFIGURED: 'Not enabled',
        CAPABILITY_BLOCKED: 'Blocked',
        AMOUNT_EXCEEDS_LIMIT: 'Over the limit',
        TAX_SEGMENT_NOT_ENABLED: 'Tax not enabled',
        FINNET_ACCOUNT_NOT_MAPPED: 'Account not linked',
        ORIGIN_BANK_FORM_UNSUPPORTED: 'Bank does not offer it',
        PAYMENT_NOT_FOUND: 'Not found',
        ALREADY_IN_FLIGHT: 'Being sent',
        ALREADY_IN_BANK_FILE: 'In a file',
        BARCODE_DUE_DATE_MISMATCH: 'Due date differs from barcode',
        TAX_FIELD_REQUIRED: 'Guide incomplete',
        TAX_AMOUNT_INVALID: "Guide amounts don't add up",
        PAYMENT_DATE_NOT_BUSINESS_DAY: 'Date is not a business day',
        PAYMENT_DATE_AFTER_DUE_DATE: 'After the due date',
      },
    },
    configIssues: {
      affected: '{{count}} selected payment(s) depend on it.',
      support: 'If you need help, contact support with the time.',
      dismiss: 'Dismiss',
    },
    inlineBankFix: {
      title: 'Beneficiary bank · {{payee}}',
      appliesToMany: "Applies to this payee's {{count}} selected payments.",
      appliesToOne: 'Applies to this payment.',
      placeholder: 'Search by name or code…',
      saveNote: "Saved on the payee's record. The row is checked again on apply.",
      failed: 'The bank could not be saved. Try again.',
      cancel: 'Cancel',
      apply: 'Apply',
    },
    bulkActions: {
      changeEntity: {
        button: 'Change entity',
        title: 'Change entity for selected bills',
        description:
          'Choose the destination entity for {{count}} bill(s). Bills already at the destination will remain unchanged.',
        entityLabel: 'Destination entity',
        entityPlaceholder: 'Select the destination entity',
        effects:
          'Existing classifications will be removed and reclassification will be requested in the background; it may require manual action. Bills without a linked document must be classified manually. Bills pending approval will return to review. The preferred bank account, card allocations and recurring links belonging to another entity will be removed. For unsent payments, an origin account belonging to another entity will be replaced by the destination entity’s default or only compatible account. If no compatible account is determined, the origin will be cleared and must be selected before sending. Line-level classifications will switch to bill-level.',
        consent:
          'I confirm the entity change and consent to removing existing classifications and requesting a new classification.',
        confirm: 'Change {{count}} bill(s)',
        processing: 'Changing entity and requesting classification…',
        cancel: 'Cancel',
        close: 'Close',
        retry: 'Retry {{count}} pending item(s)',
        recover: 'Recover classification for {{count}} bill(s)',
        requestError:
          'We could not confirm the result of the change. Use recovery to check the bills.',
        recoveryNotice:
          'The response was not received and some bills may have changed. Recovery only requests classification for bills already at the destination. To transfer the remaining bills, close and select them again in the list.',
        summary: '{{changed}} changed, {{unchanged}} unchanged and {{failed}} failed.',
        asyncNotice:
          'Classification runs in the background. A changed entity does not mean classification is complete.',
        refreshError:
          'The result was saved, but the list could not be refreshed. Refresh the page.',
        eligibility: 'Select up to 50 bills in review or pending approval.',
        outcome: {
          CHANGED: 'Entity changed',
          UNCHANGED: 'Already at the destination entity',
          FAILED: 'Entity not changed',
        },
        classification: {
          QUEUED: 'Classification requested for the destination entity.',
          NO_DOCUMENT: 'No linked document. Reclassify manually.',
          PENDING: 'Reclassification pending. Please retry.',
        },
        errors: {
          BULK_STATUS_NOT_ELIGIBLE: 'The bill is no longer in review or pending approval.',
          BULK_ENTITY_CHANGE_RETRY_ENTITY_MISMATCH:
            'The bill is not at the destination entity. Close and refresh the list before requesting a new transfer.',
          BILL_NOT_FOUND: 'Bill unavailable. Refresh the list.',
          BILL_ENTITY_CHANGE_SUBMIT_PERMISSION_REQUIRED:
            'You need permission to submit bills before changing the entity of a bill pending approval.',
          BULK_ENTITY_CHANGE_CONFLICT: 'The bill changed during the operation. Refresh and retry.',
          BILL_ENTITY_CHANGE_BLOCKED_BY_GL_POSTING:
            'The entity cannot change after accounting posting.',
          AP_BILL_ENTITY_TRANSFER_STALE_STATE:
            'The bill changed during entity transfer. Refresh and retry.',
          AP_BILL_ENTITY_TRANSFER_PAYMENT_ORIGIN_LOCKED:
            "The entity cannot change while a sent or settled payment still uses another entity's origin account.",
          AP_BILL_FOREIGN_CURRENCY_ENTITY_TRANSFER_UNSUPPORTED:
            'The bill currency prevents a direct transfer to this entity.',
          BULK_ENTITY_CHANGE_FAILED: 'Unable to complete the operation. Retry the pending item.',
        },
      },
      sendPayments: 'Bank integration',
      gatewayLabel: 'payment gateway',
      markPaid: 'Mark as Paid',
      markPosted: 'Mark as Posted',
      sendToBank: 'Send to Bank',
      approve: 'Approve',
      reject: 'Reject',
      sendToApproval: 'Send to approval',
      void: 'Void',
      classify: {
        button: 'Classify',
        title: 'Classify selected bills',
        selectedBadge: '{{count}} selected',
        vendorLabel: 'Vendor',
        vendorPlaceholder: 'Select a vendor',
        glLabel: 'GL account',
        glPlaceholder: 'Select a GL account',
        managerialLabel: 'Managerial account',
        managerialPlaceholder: 'Select a managerial account',
        costCenterLabel: 'Cost center',
        costCenterPlaceholder: 'Select a cost center',
        departmentLabel: 'Department',
        departmentPlaceholder: 'Select a department',
        notesLabel: 'Notes',
        notesPlaceholder: 'Add a note for the bulk classifications',
        cancel: 'Cancel',
        confirm: 'Classify {{count}} bill(s)',
        confirmLoading: 'Classifying...',
        statusBlocked:
          'Bulk classify is available only for draft, submitted, or pending-approval bills.',
        entityMismatch: 'Select bills from the same entity to classify in bulk.',
        detailLoadError: 'could not validate classification mode',
        saveError: 'failed to save classification',
        failedBillWithReason: '{{bill}} ({{reason}})',
        moreItems: '{{items}} +{{count}} more',
        toast: {
          success: '{{count}} bill(s) classified successfully.',
          partial:
            '{{success}} classified, {{skipped}} skipped (line-item mode), {{failed}} failed.',
          noneEligible: 'No bills were classified: all selected bills are in line-item mode.',
          noneSuccess: 'No bills were classified.',
          skippedDetails: 'Skipped (line-item mode): {{bills}}',
          failedDetails: 'Failed: {{bills}}',
        },
      },
      limitExceeded: 'Select at most {{max}} bills per bulk action.',
      limitSelectFirst: 'Selected the first {{max}} bills to avoid slow processing.',
      voidStatusBlocked:
        'Bulk void is available only for draft, submitted, or pending-approval bills.',
      sendApprovalStatusBlocked: 'Send to approval is available only for draft or submitted bills.',
      approveStatusBlocked: 'Approve and reject are available only for bills pending approval.',
      toast: {
        rescheduleSuccess: 'Payment date changed on {{count}} payment(s).',
        reschedulePartial:
          '{{updated}} payment(s) rescheduled, {{failed}} unchanged (already sent to the bank, paid or cancelled).',
        rescheduleError: 'Could not change the payment date.',
        scheduleError: 'Could not update the payment date.',
        schedulePartialBlocked:
          '{{updated}} payment(s) updated, {{failed}} failed. Send/export cancelled — fix the dates before continuing.',
        transitionSuccess: '{{count}} bill(s) changed to "{{status}}" successfully',
        transitionNone: 'No bills could be changed to "{{status}}"',
        transitionPartial: '{{succeeded}} bill(s) changed, {{failed}} failed',
        budgetHardBlockRejected:
          '{{count}} bill(s) automatically rejected for exceeding the budget limit',
        bulkError: 'Error processing bulk action',
        bulkPartialUnknown:
          'The bulk action failed. Some bills may already have been processed — the list has been refreshed; check before trying again.',
        approveFullySucceeded: '{{count}} bill(s) fully approved',
        approveAdvanced:
          '{{count}} bill(s) advanced to the next approver and remain Pending approval',
        approveAdvancedStillSelected:
          'They stay selected. You already approved your step; the next action is another approver’s.',
        failedStillSelected:
          'The bills that did not go through stay selected so you can fix them and try again.',
        approveSkipped: '{{count}} bill(s) could not be approved',
        rejectSucceeded: '{{count}} bill(s) rejected',
        rejectSkipped: '{{count}} bill(s) could not be rejected',
        validationError: 'Could not validate the selected bills.',
        resultFailures: '{{count}} bill(s) failed. Example: {{example}}',
        unknownFailure: 'Unknown failure',
        noVendor: 'No vendor',
        kaminoPartial: '{{valid}} bill(s) ready for send, {{invalid}} with issues:',
        kaminoSuccess: '{{count}} bill(s) ready for Kamino (preview mode)',
        kaminoError: 'Error checking Kamino send',
        kaminoDispatchQueued:
          'Kamino send queued. The list will refresh while processing completes.',
        providerDispatchQueued:
          '{{provider}} send queued. The list will refresh while processing completes.',
        providerDispatchQueuedPartial:
          '{{provider}}: {{queued}} payment(s) queued; {{skipped}} skipped (not eligible for Itaú).',
        providerDispatchTracking:
          '{{provider}}: {{count}} payment(s) being sent. Follow progress above the table; the remittance file downloads on its own once the bank accepts them all.',
        exportInProgress: 'Export in development',
        cnabError: 'Error generating CNAB file',
        cnabPaymentExcluded: '1 payment not included in CNAB: {{message}}',
        cnabPaymentsExcluded: '{{count}} payments not included in CNAB (e.g. {{example}}…)',
        cnabSomeExcluded: 'Some payments were not included in the CNAB file.',
        cnabSuccess:
          'CNAB file generated. Send via Internet Banking in File Transmission > Remittance.',
        cnabSuccessZip:
          'CNAB files generated (one per payment type when required by the bank). Send via Internet Banking in File Transmission > Remittance.',
        bankFileExportError: 'Could not generate the bank file.',
        sendSuccess: '{{count}} payment(s) sent to bank',
        sendFailed: 'Failed to send payments. {{error}}',
        sendPartial: '{{success}} sent, {{failed}} failed',
        sendError: 'Error sending payments to bank',
        itauNoEligiblePix:
          'No payments are eligible for Itaú PIX dispatch. Check method, PIX copy-and-paste, and Itaú origin account.',
        retornoSuccess: '{{count}} return(s) processed{{details}}',
        retornoStatusChanged: '{{count}} payment(s) had their status updated',
        retornoNoMatch:
          'No line of "{{fileName}}" matched a payment. The file was recorded as rejected in the file history; check that it belongs to this company and that the payments were sent from the ERP.',
        retornoNoMatchUnrecorded:
          'No line of "{{fileName}}" matched a payment and the file was not recorded: the origin account in its header (bank, branch and account) matches no registered bank account. Register that account or check that the file belongs to this company, then import it again.',
        retornoFailed: 'Could not process {{count}} return file(s).',
        retornoFailedFile: 'Could not process {{fileName}}',
        retornoFailure: {
          missingAccountConfig:
            'Payments in this file were sent through the payment gateway, and the origin account is not set up there. Without that setup it is not possible to tell whether the bank’s answer should be applied here. Set up the account in the gateway and upload the file again.',
          multiplePayers:
            'This file mixes payments from more than one company at the gateway. Split the return by company and upload it again.',
          multipleAccounts:
            'This file mixes payments from more than one origin account. Split the return by account and upload it again.',
          missingPayerTaxId:
            'The paying company has no tax ID on file. Complete the registration and upload the file again.',
          reconciliationUnavailable:
            'The return arrived, but the gateway reconciliation record is not available. Upload the file again shortly. If it continues, contact support with the time of the upload.',
          reconciliationBlocked:
            'This file was already sent to the gateway and reconciliation did not finish. It was not sent again, to avoid a duplicate answer. Wait for reconciliation, or contact support with the time of the upload.',
          reconciliationUnconfirmed:
            'The gateway received the file, but reconciliation was not confirmed here. Do not upload it again. Contact support with the time of the upload.',
          settlementEvidenceIncomplete:
            'The bank marked a payment as paid, but the file has no effective date or amount. Correct the return with the bank and upload it again.',
          settlementFailed:
            'A paid payment could not be settled. The file was not finished. Upload it again. If it continues, contact support with the time of the upload.',
          unknown:
            'The file was not processed. Upload it again. If it continues, contact support with the time of the upload.',
        },
      },
      dispatchProgress: {
        savedBanks: '{{count}} bank(s) saved on payees.',
        counter: '{{done}} of {{total}}',
        dismiss: 'Dismiss',
        skippedNote: '{{skipped}} payment(s) were not sent: see the reason on each table row.',
        partialEnqueue: {
          title: '{{provider}}: send interrupted with {{pending}} payment(s) in flight',
          description:
            'The payments that went out are being followed in the banner above and become a remittance once the bank confirms. The rest were never sent: resend only those.',
          sentWithoutFollowUp:
            '{{sent}} payment(s) went out through {{provider}} and {{failed}} were not sent. Only the ones that did not go stay selected: resend just those.',
        },
        nothingSent: {
          title: '{{provider}}: none of the {{skipped}} payment(s) could be sent',
          description:
            'Each table row shows what blocked the send and how to fix it. Correct and resend.',
        },
        running: {
          title: 'Sending {{total}} payment(s) via {{provider}}',
          description:
            'The bank confirms each payment individually. Keep working; the list refreshes on its own.',
        },
        success: {
          title: '{{provider}}: {{total}} payment(s) accepted for sending',
          description:
            'If a remittance file was downloaded, upload it in Internet Banking when the bank requires it.',
        },
        partial: {
          title: '{{provider}}: {{accepted}} accepted, {{rejected}} refused',
          description:
            'Refused payments are flagged in the table with the reason the bank gave. Fix the record and resend just those.',
        },
        failure: {
          title: '{{provider}}: none of the {{total}} payment(s) was accepted',
          description:
            'See the reason on each row. If they all point at the origin account or provider configuration, contact support with the time of the send.',
        },
        slowNote:
          'Taking longer than usual. The send continues; the list refreshes when the bank answers.',
        started: 'Started {{when}}.',
        startedBy: 'Started {{when}} by {{name}}.',
        fromAccount: 'Origin account: {{account}}.',
        ago: {
          justNow: 'just now',
          minutes: '{{count}} min ago',
          hours: '{{count}} h ago',
          at: 'on {{date}}',
        },
        stalled: {
          title: 'Send from {{when}} did not conclude',
          counter: '{{pending}} of {{total}} unanswered',
          select: 'Select the {{count}}',
          selectOne: 'Select the payment',
          conclude: 'Conclude send',
          concludeHint:
            'Until concluded, the payments of this send keep their origin account. Check the Internet Banking and conclude it to release them.',
          concludeQuestion:
            'Did the bank confirm the {{count}} without an answer were not executed?',
          concludeExplain:
            'Payments the gateway already accepted or refused, and the ones already settled in reconciliation, stay as they are. If the bank executed one, settle it first (reconciliation or manual settlement): it counts as accepted. Your answer covers only the rest and is recorded on the send.',
          concludeNotExecuted: 'Not executed — release for resend',
          concluded:
            'Send concluded. {{accepted}} accepted recorded; the rest are left as not sent and can be resent.',
          concludedPartial:
            'Concluded for {{concluded}} of {{total}} origin accounts. The rest stays open: {{reason}}',
          reason: {
            PAYMENTS_NEVER_TERMINAL:
              '{{pending}} payment(s) never reached a final state at the payment gateway. The other {{settled}} concluded and the remittance of the accepted ones was generated.',
            PROVIDER_STATUS_UNAVAILABLE:
              'The payment gateway did not answer the status query for the whole window. Check Internet Banking before resending.',
            MANIFEST_REJECTED:
              'This send can no longer be concluded automatically: its payments left the scope they were sent from. Contact support with the time of the send.',
            SCOPE_UNRESOLVED:
              'This send could not be attributed to one origin account. The remittance has to be generated by hand; contact support with the time of the send.',
            unknown:
              'The send stopped without concluding. Contact support with the time of the send.',
          },
        },
        paymentState: {
          QUEUED: 'Queued',
          SENDING: 'Sending…',
          RETRYING: 'Retrying…',
          ACCEPTED: 'Accepted by bank',
          AWAITING_BANK_APPROVAL: 'Awaiting bank approval',
          REJECTED: 'Refused',
          ABANDONED: 'Failed',
          SKIPPED: 'Not sent',
          queuedHint: 'Waiting for the send queue.',
          inFlightHint: 'Waiting for {{provider}} to answer.',
          awaitingReturn: 'Awaiting return…',
          retryingQueueHint:
            'Could not reach the {{provider}} right now. The queue tries again on its own; nothing needs to be done.',
          awaitingReturnHint:
            'The {{provider}} did not confirm this send, and it is not repeated on its own, so it is not paid twice. We wait for the bank return; if it does not arrive in time, the payment shows up under Payments under review.',
          unconfirmedHint:
            'The send is not confirmed by the {{provider}} yet. Follow it in the list: it updates when there is an answer.',
          externalId: 'Reference {{id}}',
          awaitingBankApprovalHint:
            'Accepted by {{provider}}. Someone with signing powers on the account must approve the send in the bank app; nothing else is needed in Alchemy ERP.',
          noReason: 'The bank gave no reason.',
          approvalExpired:
            'Not approved in time: nobody with signing powers approved the send in the bank app before the deadline. Send the payment again and approve it at the bank.',
          bankRefusedGeneric:
            'The bank or the payment gateway refused the payment. Check the payee and origin account details before resending; if it persists, give the time of the send to support.',
          bankRefusedGenericRetryable:
            'The bank or the payment gateway refused the payment for now. It can be resent later.',
          notApprovedInTime:
            'The payment was not approved in the bank app in time and was closed. Nothing was paid: send it again and ask for approval within the deadline.',
          bankRefused:
            'The bank refused the payment (occurrence {{code}}). Check the payee details before resending; if it persists, give the code to support.',
          bankRefusedRetryable:
            'The bank refused the payment for now (occurrence {{code}}). It can be resent later.',
          priorSendNotWithdrawable:
            'Not resent: this payment\'s earlier send is still active at the payment gateway (scheduled, paid, in another send in progress or in a remittance that may already be at the bank) and could not be withdrawn. No new request was created; check the earlier send and, to replace it, use "Cancel send", which asks for the bank\'s confirmation when the remittance may already have been sent.',
          priorSendOtherChannel:
            'Not resent: this payment was sent through another channel (CNAB file, direct PIX or Kamino). Nothing was withdrawn or created; cancel the send in that channel before resending. The payment history names the channel.',
        },
        remittanceFiles: {
          label: 'Remittance files',
          download: 'Download {{fileName}}',
          downloadAll: 'Download all (.zip)',
        },
        toast: {
          settledAll: '{{count}} payment(s) accepted by the bank. {{files}}',
          filesDownloaded_one:
            'Remittance file available. Check that the download finished; if the browser blocked it, get it from the send banner.',
          filesDownloaded_other:
            '{{count}} remittance files available; every one has to reach the bank. Check that all of them downloaded; if the browser blocked any, get it from the send banner.',
          settledAllNoFile: '{{count}} payment(s) accepted by the bank.',
          settledPartial:
            '{{accepted}} accepted, {{failed}} did not go. See the reasons in the table.',
        },
      },
      sendApprovalDialog: {
        title: 'Send bills to approval',
        description:
          '{{readyCount}} of {{total}} bill(s) are ready. {{blockedCount}} need adjustment before sending.',
        readyTitle: 'Ready ({{count}})',
        blockedTitle: 'Blocked ({{count}})',
        noReady: 'No bills are ready to send.',
        noBlocked: 'No blockers found.',
        cancel: 'Cancel',
        proceed: 'Send {{count}} ready',
      },
      voidDialog: {
        title: 'Void selected bills?',
        description:
          '{{eligibleCount}} of {{selectedCount}} bill(s) will be voided. This cancels approval flow and removes document links when applicable.',
        blockedNotice:
          '{{blockedCount}} bill(s) are not in an allowed status and will not be processed.',
        reasonLabel: 'Justification (required)',
        reasonPlaceholder: 'Enter the reason for voiding...',
        cancel: 'Cancel',
        confirm: 'Void bills',
        confirming: 'Voiding...',
      },
      approveDialog: {
        title: 'Approve selected bills?',
        description:
          'Your approval will be recorded on {{actionableCount}} of {{selectedCount}} selected bill(s).',
        nothingToDo: 'None of the selected bills are waiting on your approval right now.',
        commentLabel: 'Comment (optional)',
        commentPlaceholder: 'Add a comment for this approval...',
        cancel: 'Cancel',
        confirm: 'Approve {{count}} bill(s)',
        confirming: 'Approving...',
      },
      rejectDialog: {
        title: 'Reject selected bills?',
        description:
          'Your rejection will be recorded on {{actionableCount}} of {{selectedCount}} selected bill(s).',
        nothingToDo: 'None of the selected bills are waiting on your review right now.',
        reasonLabel: 'Rejection reason (required)',
        reasonPlaceholder: 'Enter the reason for rejecting...',
        cancel: 'Cancel',
        confirm: 'Reject {{count}} bill(s)',
        confirming: 'Rejecting...',
      },
      approvalPreview: {
        consentWarningsTitle: 'Confirmation required before approval',
        loading: 'Checking what will happen to each bill...',
        fullyApprovesTitle: '{{count}} bill(s) will be fully approved',
        fullyApprovesDetail:
          'Your approval completes the flow: the bill becomes Approved and is posted.',
        advancesTitle: '{{count}} bill(s) will advance to the next approver',
        advancesDetail:
          'Your step is approved, but the bill stays Pending approval until the remaining approvers decide.',
        stepPosition: 'Step {{current}} of {{total}}',
        thenWaitsFor: 'then waits for {{approvers}}',
        rejectsTitle: '{{count}} bill(s) will be rejected',
        rejectsDetail: 'Rejection ends the whole approval flow and the bill becomes Rejected.',
        retriesTitle: '{{count}} bill(s) will go through an approval retry',
        retriesDetail:
          'These bills got stuck on an earlier attempt. The action re-runs the approval flow and posting, like the "Retry" button on the bill.',
        retryCommentNotice: 'Retried bills record no comment — the retry flow does not accept one.',
        skippedTitle: '{{count}} bill(s) will be skipped',
        andMore: '+ {{count}} more',
        blockReasons: {
          billNotFound: 'Bill not found',
          statusNotEligible: 'Not pending approval',
          noApprovalChain: 'No active approval flow',
          alreadyApprovedByYou: 'You already approved your step — waiting on other approvers',
          awaitingOtherApprover: 'Not your turn yet — an earlier approver still has to act',
          noActionableStep: 'No step available to you right now',
          stepEscalated: 'Step escalated — it can no longer be actioned directly',
          previewUnavailable: 'This bill could not be checked — try again',
          notAnApprover: 'You are not an approver for the current step',
        },
      },
      merge: 'Merge',
      mergeStatusBlocked:
        'Merge is available only for draft, submitted, or pending-approval bills.',
      mergeDialog: {
        title: 'Merge bills',
        confirmationTitle: 'Confirm merge',
        confirmationSummary: '{{count}} bills will become one.',
        continue: 'Continue',
        back: 'Back',
        sumSuccessDescription: 'Review allocations and payment before approval.',
        errorTitle: 'Merge not completed',
        errorCode: 'Code: {{code}}',
        openMergedBill: 'Open merged bill',
        close: 'Close',
        reviewSelection: 'Review selection',
        retry: 'Retry',
        amountChoice: 'Bill amounts',
        loadingPreview: 'Updating preview',
        retentions: 'Withholdings',
        credits: 'Credits',
        recurrence: 'Recurrence',
        noRecurrence: 'No recurrence',
        chooseRecurrence: 'Choose a bill',
        recurrenceHelp:
          'Keeps the existing recurrence. If they differ, choose which link to keep. Recurring templates are not changed.',
        sourceRateHelp: 'Applies this bill’s effective rate of {{rate}} to the combined base.',
        sourceAmount: 'Original amount',
        combinedBase: 'Combined base',
        sumFiscalHelp:
          'Adds the amounts already recorded on the bills without extending one bill’s rate to the other.',
        applySourceToAll: 'Apply {{bill}} to all',
        keepBill: 'Keep bill {{number}}',
        sum: 'Sum',
        grossAmount: 'Combined gross',
        netAmount: 'Combined net',
        retainedAmount: 'Actual withholdings',
        creditAmount: 'PIS/COFINS credits',
        sumConsequences:
          'Invoices and allocations will remain in one draft. Source bills will be voided.',
        duplicateConsequences:
          'Documents will be combined. Source bills will be voided and marked as duplicates.',
        sumHelp: 'How summing works',
        sumDetails:
          'Adds invoice amounts. Choose credits, withholdings and recurrence by row. Preserves documents and allocations.',
        sumPaymentReview: 'Previous payments will be cancelled. Review the single payment.',
        paymentHelp: 'Single payment details',
        sumPaymentDetails:
          'Boleto and Pix instructions for individual amounts will not be reused. Enter the single payment details before approval.',
        vendorLinkHelp: 'Vendor grouping',
        sumUnavailable:
          'These bills cannot be summed. Review their fiscal and financial context first.',
        sumBlockers: {
          STATUS: 'Only bills awaiting approval can be summed.',
          ENTITY: 'The bills must belong to the same entity.',
          CURRENCY: 'Bills must use the entity’s functional currency, without currency conversion.',
          FUNCTIONAL_CURRENCY_UNRESOLVED:
            'Review the functional currency of the entity’s accounting books.',
          VENDOR: 'All invoices must have the same identified vendor.',
          SPECIAL_BILL:
            'Sum is available for ordinary expenses without payroll, reimbursement or purchase-order links.',
          COMPETENCY: 'Invoices must have the same accounting period.',
          RECURRENCE_SELECTION_REQUIRED:
            'The recurrences differ. Choose which bill supplies the link.',
          RECURRENCE_UNAVAILABLE: 'The selected recurrence is unavailable for these bills.',
          CREDIT_TYPE: 'Credit types differ. Choose a bill to apply its credit.',
          ISS_PERIOD: 'ISS assessment periods differ. Choose a bill in the withholdings row.',
          FINANCIAL_ACTIVITY:
            'A reconciliation, bank dispatch, posting or financial link exists. Resolve these links before summing.',
          BUDGET_EXCEPTION:
            'A budget exception is pending or has an open commitment. Resolve the exception before summing.',
          CHART_VERSION: 'Allocations must use the same chart-of-accounts version.',
          CLASSIFICATION_SCOPE:
            'Review allocations: classifications overlap or exceed the invoice gross amount.',
          AMOUNTS: 'Review invoice gross and net amounts before summing.',
          DUPLICATE_INVOICE:
            'Invoices share a fiscal identity. Keep one bill to resolve duplicates.',
          RETENTION_COMPONENTS:
            'Retained totals differ from their tax components. Review withholdings before summing.',
        },
        description: '{{count}} bills selected.',
        fieldColumnHeader: 'Field',
        noConflicts:
          'No conflicts found. The bill with the most items will become the primary bill.',
        conflictsTitle: 'Fields with different values',
        fieldLabel_vendorId: 'Vendor',
        fieldLabel_dueDate: 'Due date',
        fieldLabel_totalAmount: 'Total amount',
        fieldLabel_subtotalAmount: 'Subtotal',
        fieldLabel_taxTotalAmount: 'Tax amount',
        fieldLabel_description: 'Description',
        fieldLabel_vendorBillNumber: 'Bill number',
        fieldLabel_paymentMethod: 'Payment method',
        fieldLabel_billCompetencyDate: 'Competency date',
        fieldLabel_billManagerialDate: 'Managerial date',
        fieldLabel_nfeChaveAcesso: 'NF-e access key',
        fieldLabel_nfeNumero: 'NF-e number',
        fieldLabel_nfeSerie: 'NF-e series',
        fieldLabel_nfeDataEmissao: 'NF-e issue date',
        billLabel: 'Bill {{number}}',
        billBadgeRecommended: 'Recommended',
        rememberLinkLabel: 'Remember vendors',
        rememberLinkHint:
          'Future bills from these vendors will be grouped automatically into a single bill.',
        reasonLabel: 'Reason (optional)',
        reasonPlaceholder: 'Reason for merging',
        cancel: 'Cancel',
        confirm: 'Merge bills',
        confirming: 'Merging...',
        loadError: 'Could not load the preview.',
        success: 'Bills merged successfully',
        error: 'Could not merge the bills.',
      },
    },
    delete: {
      loading: 'Deleting bill...',
      success: 'Bill deleted successfully',
      error: 'Could not delete the bill. Please try again.',
    },
    billAction: {
      notFound: 'Bill not found',
      edit: 'Edit',
      delete: 'Delete',
      review: 'Review',
      viewBill: 'View bill',
      view: 'View',
      openMenu: 'Open menu',
      menuFor: 'Actions for {{vendor}}',
      deleteOnlyDraft: 'Only draft, submitted, pending approval, or rejected bills can be deleted',
      deleteDisabledTooltip: 'Bills can only be deleted before being approved',
    },
    deleteDialog: {
      title: 'Delete bill?',
      description:
        'This action will virtually delete the bill and linked documents. You can resend the documents later if needed.',
      justificationLabel: 'Justification (required)',
      justificationPlaceholder: 'Enter the reason for deletion...',
      cancel: 'Cancel',
      confirm: 'Delete',
      confirming: 'Deleting...',
      reconciled: {
        title_one: 'This bill has 1 reconciled transaction',
        title_other: 'This bill has {{count}} reconciled transactions',
        description:
          'Deleting will undo the reconciliation and return the transaction to pending, free to be reconciled against another document.',
        confirm: 'Unreconcile and delete',
        forbidden:
          'Undoing a reconciliation requires reconciliation management permission. Ask someone with that permission to undo the reconciliation, then delete this bill.',
      },
    },
  },
  billDetails: {
    intakeProvenance: {
      strip: {
        fromEmail_one: '{{count}} field came from the e-mail',
        fromEmail_other: '{{count}} fields came from the e-mail',
        divergence_one: '{{count}} discrepancy',
        divergence_other: '{{count}} discrepancies',
        checked: 'checked',
        open: 'See where the data came from',
      },
      tags: {
        document: 'from the document',
        email: 'from the e-mail',
        review: 'check',
      },
      panel: {
        title: 'Where the data came from',
        description:
          'What the e-mail filled in or disputed on this bill, and what the document says.',
        emailHeading: 'E-mail received',
        fieldsHeading: 'Fields',
        emailLoading: 'Loading the e-mail…',
        emailLoadError: 'We could not load the e-mail. The fields still hold.',
        emailEmpty: 'This e-mail has no text.',
        emailUnavailable: 'The source e-mail is not available. The fields still hold.',
        emailHtmlOnly:
          'This e-mail only has the formatted version. The parts used appear in each field.',
        from: 'From',
        received: 'Received',
        noSubject: '(no subject)',
        readOnly: 'This bill can no longer be changed. Reopen it to check the fields.',
      },
      field: {
        billValue: 'On the bill',
        documentValue: 'In the document',
        emailValue: 'In the e-mail',
        empty: 'no value',
        snippet: 'E-mail excerpt',
        paymentDateNote:
          'Changing the due date here does not move the payment already scheduled. Change the date in the Payments tab.',
        paymentScheduled_one:
          'There is a payment of {{amount}} on this bill. Adjust the payment before changing the amount.',
        paymentScheduled_other:
          'There are {{count}} payments on this bill, adding up to {{amount}}. Adjust the payments before changing the amount.',
        paymentsUnknown:
          'The payments on this bill could not be checked right now. Refresh the page and try again before changing the amount.',
      },
      actions: {
        useEmail: 'Use the e-mail value',
        keepDocument: 'Keep the document value',
        confirm: 'This is right',
        alreadyChecked: 'I have checked it',
      },
      resolved: {
        USE_EMAIL: 'The bill now uses the e-mail value.',
        KEEP_DOCUMENT: 'Kept the document value.',
        KEEP_NO_DOCUMENT_VALUE: 'Checked. The bill stays without this value.',
        CONFIRM: 'Value checked.',
        allDone: 'Everything is checked. The review alert was closed.',
      },
      amountConfirm: {
        title: 'Replace the bill amount with the e-mail amount?',
        description:
          'The bill amount changes from {{documentValue}} to {{emailValue}}. The change is recorded in the bill history.',
        confirm: 'Replace the amount',
        cancel: 'Cancel',
        success: 'Amount replaced. The bill now uses {{emailValue}}.',
      },
      approvalConsent: {
        title: 'Check the e-mail data before approving',
        description:
          'The e-mail filled in or disputed fields on this bill that nobody has checked yet. See what the document says and what the e-mail says.',
        documentValue: 'In the document',
        emailValue: 'In the e-mail',
        empty: 'no value',
        checkbox: 'I have checked this data',
        note: 'Tick the box to approve. The fields are recorded as checked by you.',
        confirm: 'Approve',
        cancel: 'Cancel',
      },
    },
    validation: {
      attachmentRequired:
        'Receipts are required. You can upload them using the "Upload documents" button in the header.',
    },
    deletedBill: {
      title: 'Bill deleted',
      description: 'This bill was deleted on {{deletedAt}} by {{deletedBy}}.',
      justificationLabel: 'Deletion justification',
      unknownDeleter: 'Unknown user',
      backToBills: 'Back to bills',
    },
    cardInvoice: {
      title: 'Credit card invoice',
      formLabel: 'Type',
      providerLabel: 'Provider',
      providerHelp:
        'The accounting settlement will be made against the issuer liability when the payment is reconciled.',
      providerPlaceholder: 'Select the invoice provider',
      institutionOption: 'Financial institution',
      institutionLabel: 'Issuing bank',
      providers: {
        CLARA: 'Clara',
        CONTA_SIMPLES: 'Conta Simples',
        BREX: 'Brex',
      },
    },
    unsavedChanges: {
      title: 'Unsaved changes',
      description:
        'You have unsaved changes. Do you want to leave without saving? Your changes will be lost.',
      proceed: 'Leave without saving',
      stay: 'Keep editing',
    },
    approvalWarnings: {
      title: 'Send for approval anyway?',
      description:
        'This bill has gaps that do not block submission. Confirming records your acceptance in the bill history.',
      confirm: 'Send anyway',
      approveTitle: 'Confirm entity and approve?',
      approveDescription:
        'This bill has a pending entity suggestion. Confirming keeps the current entity and records your acceptance.',
      approveConfirm: 'Confirm and approve',
    },
    clientReimbursable: {
      title: 'Charge this expense to a customer',
      description: 'Posts it as an advance and creates an amount to recover.',
      clearCreditsTitle: 'Clear PIS/COFINS credits?',
      clearCreditsDescriptionFlag:
        'Client-reimbursable expenses cannot take PIS/COFINS credits — the full amount is billed to the client via debit note. Confirming removes the credits from this bill. Cancelling keeps the bill unflagged.',
      clearCreditsDescriptionSend:
        'This bill is flagged as client-reimbursable but still carries PIS/COFINS credits. Reimbursable expenses cannot take credits — confirming removes the credits and sends the bill for approval. Cancelling aborts the submission.',
      clearCreditsConfirm: 'Clear credits and continue',
    },
    statutoryRailMigration: {
      title: 'Configure tax liability',
      description: 'Review the configuration and its accounting effects before continuing.',
      chartContext: 'Chart of accounts · {{code}} · {{name}}',
      sharedTitle: 'Configure shared {{taxName}} structure',
      sharedDescription:
        '{{members}} will continue sharing the same structure. This configuration turns the current account into Legacy and keeps its existing journals attached to it.',
      today: 'Today',
      afterShared: 'After · three stages in the same structure',
      currentSharedAccount: 'One structure, without stages · {{count}} journals',
      legacyAccountDetail: '{{code}} · current journals',
      assessedAccountDetail: 'recognized, not yet in a guide',
      guidesPayableAccountDetail: 'already included in a guide',
      sharedStagesExplanation:
        'The stages organize the tax lifecycle. They do not separate {{members}}; ownership of each amount is resolved during accounting review.',
      ledgerBalance: 'Ledger balance',
      debitBalance: 'debit balance',
      journals: 'Journals',
      evidenceCountsCompact:
        '{{origins}} origins · {{allocations}} allocations · {{snapshots}} guides',
      unpaidRetentionsLabel: 'Unpaid withholdings',
      ownershipReviewExplanation:
        'Without a linked origin, allocation, or guide, the system cannot determine how much of this balance belongs to {{members}}.',
      ownershipReviewRequiredExplanation:
        'Some historical amounts still need an accounting review to determine ownership between {{members}}.',
      debitWarningTitle: 'Debit balance of {{amount}} in a liability account',
      debitWarningDescription:
        'Assigning journals identifies which tax owns each amount, but does not correct the balance. After attribution, post the accounting correction needed to eliminate the deficit. The review remains open while the debit balance exists.',
      debitWarningDoesNotBlock: 'This does not prevent creating the structure now.',
      sharedFooterNote:
        'Continuing creates the structure and resumes approval. Historical amounts without an identified origin are flagged for accounting review without blocking approval or payment.',
      diagramTitle: 'Accounting structure to be created',
      currentAccount: 'Current tax liability',
      currentAccountDetail: 'It will be reorganized as the legacy account.',
      legacyAccount: 'Legacy account',
      assessedAccount: 'Assessed',
      guidesPayableAccount: 'Guides payable',
      preserved: 'preserved',
      created: 'created automatically',
      effectiveDate: 'Effective date',
      effectiveDateTooltip:
        'The date from which the legacy account is reorganized and its balance is reclassified to Assessed.',
      assessmentDate: 'Assessment period',
      assessmentDateTooltip:
        "The month representing the fiscal period of the migrated balances. It defaults to the guide's assessment period; change it when the legacy balance belongs to a different period.",
      assessmentDatePlaceholder: 'Select a month',
      assessmentDateGuideDefault: 'Guide assessment period: {{period}}',
      assessmentDateInvalid: 'Enter a valid assessment month.',
      book: 'Accounting book',
      bookBalanceContext: 'Balances in {{bookName}} will be reclassified to Assessed.',
      cancel: 'Cancel',
      confirm: 'Apply and continue',
      submitting: 'Configuring...',
      configured: 'Configuration completed. The bill is still awaiting approval.',
      createsStructure: 'Create Assessed and Guides payable, preserving existing history.',
      migratesBalance: 'The legacy balance will move to Assessed on the selected date.',
      reusesStructure: 'Use the existing account structure.',
      evidence: 'Accounting evidence',
      evidenceCounts:
        '{{journals}} journals · {{origins}} origins · {{allocations}} allocations · {{snapshots}} guides',
      unpaidRetentions: 'Unpaid withholdings: {{amount}}',
    },
    header: {
      toast: {
        selectVendor: 'Select a vendor',
        saveSuccess: 'Header saved',
        saveError: 'Could not save header',
      },
      dueOn: 'Due {{date}}',
      linkedRecurrenceBadge: 'Recurring · {{frequency}}',
      linkedRecurrenceBadgeGeneric: 'Recurring',
      detectedRecurrenceBadge: 'Recurrence detected',
    },
    backToLedger: 'Back to ledger',
    fallbackTitle: 'Bill',
    loadingDescription: 'Loading...',
    noDocuments: 'No documents attached.',
    previewUnavailable: 'Preview unavailable',
    imageViewer: {
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      fitToScreen: 'Fit to screen',
      zoomLevelTooltip: 'Relative to fit-to-screen (100% = fits the visible area)',
      openInNewTab: 'Open in new tab',
    },
    resizePanelsAria: 'Resize panels',
    selectBill: 'Select a bill',
    showDocument: 'Show document',
    hideDocument: 'Hide document',
    viewDocumentsLabel: 'View documents',
    paymentNetMismatch:
      'The payment total ({{scheduled}}) does not match the bill net amount ({{net}}). Adjust payments or withholdings to continue.',
    paymentNetMismatchFallback:
      'The payment total does not match the bill net amount. Adjust payments or withholdings to continue.',
    netAmountMismatch:
      'The bill net amount ({{recorded}}) does not match the gross amount less withholdings ({{expected}}). Fix the withholdings or the amount before sending for approval.',
    classificationAmountReview:
      'Amount saved, but the classification split no longer adds up to the new amount. Review the classification amounts before approving.',
    paymentNetMismatchRepair: {
      action: 'Fix net amount',
      success: 'Net amount fixed. It now matches the payment.',
      error: 'Could not fix the net amount. Try again.',
      saveToFinish: 'The net amount now matches the payment. Save to finish.',
    },
    entityMismatch: {
      title: 'Review entity',
      description:
        'The customer tax ID does not match any registered entity. Select the correct entity for this bill.',
      label: 'Entity',
      placeholder: 'Select entity',
      goToDetails: 'Go to Details',
    },
    missingVendor: {
      alert: 'Vendor not identified. Select a vendor to complete this bill.',
      ambiguous:
        'Vendor not identified: the document issuer matches more than one vendor ({{names}}). Pick one to complete this bill.',
      goToDetails: 'Go to Details',
    },
    sections: {
      details: 'Details',
      classification: 'Classification',
      withholdings: 'Withholdings',
      payments: 'Payments',
      approval: 'Approval',
      activities: 'Activities',
      navAria: 'Bill section navigation',
      toggleDetailsAria: 'Toggle Details',
      toggleClassificationAria: 'Toggle Classification',
      toggleWithholdingsAria: 'Toggle Withholdings',
      togglePaymentsAria: 'Toggle Payments',
      toggleApprovalAria: 'Toggle Approval',
      toggleActivitiesAria: 'Toggle Activities',
      statusTooltip: {
        pendingCount_one: '{{count}} required item pending in this section',
        pendingCount_other: '{{count}} required items pending in this section',
        complete: 'Section complete',
        approval: 'Approval flow',
        activities: 'Comments and history',
        withholdingsNeutral: 'Withholdings are not applied to this bill.',
      },
      approvalEmptyStateDraft:
        'The approval flow will be created when you submit the bill for approval.',
      approvalEmptyStateOther: 'No approval flow configured for this bill.',
      approvalFailureTitle: 'Approval failed',
      approvalFailureDescription: '{{message}}',
      approvalFailureOrphanDescription:
        'This bill is still pending approval but has an approved flow on record. Use the button below to repair and retry.',
      approvalRetryButton: 'Retry approval',
      approvalRetrying: 'Retrying approval…',
      approvalRetrySuccess: 'Approval reprocessed successfully',
      segregationDialogTitle: 'Segregation of Duties',
      segregationDialogGotIt: 'Got it',
    },
    rightPanel: {
      billTypeLabel: 'Type',
      vendorLabel: 'Payee',
      reimbursementTag: 'Reimbursement',
      advancedOptionsLabel: 'Advanced options',
      advancedOptionsDescription: 'reimbursable expense, purchase order',
      viewVendor: 'View vendor',
      taxIdValid: 'Valid tax ID',
      taxIdInvalid: 'Invalid tax ID',
      taxIdForeign: 'Foreign vendor',
      taxIdGovernment: 'Government agency',
      taxIdUnknown: 'Tax ID missing',
      vendorBillsCount_one: '{{count}} linked bill',
      vendorBillsCount_other: '{{count}} linked bills',
      changeVendor: 'Change vendor',
      entityLabel: 'Account of',
      entityFallback: 'Entity not set',
      changeEntity: 'Change entity',
      editEntity: 'Edit entity',
      finishEditEntity: 'Finish editing entity',
      entityChangeClassificationDialog: {
        title: 'Clear classification and change entity?',
        description:
          'This bill will move to {{entityName}} and its current classification will be deleted. Custom fields with the same name in {{entityName}} keep their value. This action cannot be undone.',
        leavesApproval:
          'This bill is pending approval: the change cancels the approval and the bill must be sent again.',
        customFieldsLoading: 'Checking custom fields...',
        customFieldsError: 'Could not check which custom fields will be kept.',
        carriedTitle: 'Custom fields kept',
        droppedTitle: 'Custom fields removed',
        droppedVaryingValue: 'several values',
        dropReasons: {
          NO_MATCHING_FIELD: 'does not exist in {{entityName}}',
          TYPE_MISMATCH: 'the field has another type in {{entityName}}',
          NO_MATCHING_VALUE: 'the {{entityName}} list has no such option',
          SPLIT_VALUES: 'split across several values',
          VARYING_VALUES: 'differs by line item or allocation',
          AMBIGUOUS_MATCH: 'several similar fields or options in {{entityName}}',
        },
        missingRequired:
          'Required custom fields of {{entityName}} left empty: {{fields}}. Fill them in before sending for approval.',
        cancel: 'Keep current entity',
        confirm: 'Clear classification and change entity',
        confirming: 'Clearing classification...',
        processingTitle: 'Updating entity',
        processingDescription:
          'Please wait while we clear the classification and update the bill entity.',
        successTitle: 'Entity updated',
        successDescription: 'The bill was moved to {{entityName}}.',
        successResult:
          'The previous classification was cleared and the entity was updated. Custom fields with the same name were kept.',
        done: 'Done',
        errorTitle: 'Could not update entity',
        errorDescription: 'The change was not completed. Review the error and try again.',
        errorFallback: 'An unexpected error occurred while updating the entity.',
        close: 'Close',
        retry: 'Try again',
      },
      descriptionShowMore: 'Show more',
      descriptionShowLess: 'Show less',
      vendorBillNumberLabel: 'Supplier document number',
      vendorBillNumberEmpty: 'Not provided',
      descriptionOriginalText: 'Original text',
      descriptionPeriodLabel: 'Period',
      descriptionReceiptLabel: 'Receipt',
      removeWithholdingsLink: 'Remove',
      removeWithholdingsAria: 'Remove withholdings from this bill',
      removeWithholdingsTooltip:
        'Turns off withholdings for this bill. You can turn them back on in the Withholdings section.',
      paymentScheduledDateLabel: 'Scheduled payment',
      paymentDocumentDueDateLabel: 'Due date',
      nextPaymentLabel: 'Next payment',
      viewAllPaymentsLink: 'View all payments',
      secondaryDatesExpandAria: 'Show managerial date and issue date',
      secondaryDatesCollapseAria: 'Hide managerial date and issue date',
      paymentDatesClosestTooltip:
        'When there are multiple installments, we show the scheduled date closest to today.',
      reconciledPaymentDatesLockedTooltip: 'Dates cannot be changed for a reconciled payment.',
      originLabel: 'Origin',
      originManual: 'Entered manually',
      originEmail: 'Received by email',
      originWebhook: 'Received by webhook',
      originApi: 'Submitted via API',
      originExternalForm: 'Submitted through a form',
      originDefault: 'Created',
      originBy: 'by',
      originOn: 'on {{date}}',
      requestedByLabel: 'Requested by',
      viewSourceEmailLink: 'View original email',
      viewSourceWebhookLink: 'View received data',
      sourceWebhookDialogTitle: 'Received data',
      sourceExternalRecord: '{{provider}} record {{id}}',
      sourceExternalOpen: 'Open in {{provider}}',
      sourceWebhookEmpty: 'This receipt has no stored text fields.',
      sourceWebhookFields: {
        requester: 'Requester',
        company: 'Company',
        vendor: 'Vendor',
        taxId: 'Tax ID',
        description: 'Description',
        amount: 'Amount',
        currency: 'Currency',
        dueDate: 'Due date',
        dueDateAsWritten: 'Due date as written',
        scheduledDate: 'Scheduled date',
        paymentMethod: 'Payment method',
        pixKey: 'Pix key',
        barcode: 'Barcode line',
        category: 'Category',
        department: 'Department',
        notes: 'Notes',
        attachments: 'Attachments',
      },
      sourceEmailDialogTitle: 'Original email',
      sourceEmailFrom: 'From:',
      sourceEmailReceived: 'Received:',
      sourceEmailNoSubject: '(No subject)',
      sourceEmailLoading: 'Loading…',
      sourceEmailLoadError: 'Could not load the email.',
      sourceEmailEmptyBody: 'This email has no stored plain text or HTML body.',
      viewSourceApiPayloadLink: 'View API payload',
      sourceApiPayloadDialogTitle: 'Payload submitted via API',
      sourceApiPayloadExternalReference: 'External reference:',
      sourceApiPayloadSubmittedAt: 'Submitted:',
      sourceApiPayloadLoading: 'Loading…',
      sourceApiPayloadLoadError: 'Could not load the API payload.',
      originalAmountLabel: 'Original amount',
      exchangeRateLabel: 'Exchange rate',
      exchangeRatePending: 'Pending',
      exchangeRateEdit: 'Edit exchange rate',
      exchangeRateSave: 'Save exchange rate',
      exchangeRateSaveSuccess: 'Exchange rate changed',
      exchangeRateCancel: 'Cancel exchange rate edit',
      exchangeRateDialogTitle: 'Edit exchange rate',
      exchangeRateDialogDescription:
        'Enter the new rate and record the justification for this manual change.',
      exchangeRateAutoFillTitle: 'Calculate automatically',
      exchangeRateAutoFillAction: 'Use PTAX automatically',
      exchangeRateAutoFillCompetencyDescription:
        'Fetch the PTAX closing rate using the competency date ({{date}}).',
      exchangeRateAutoFillIssueDescription:
        'Fetch the PTAX closing rate using the issue date ({{date}}).',
      exchangeRateAutoFillError: 'Could not calculate the exchange rate automatically',
      exchangeRateValueLabel: 'New exchange rate',
      exchangeRateValueAria: 'New exchange rate',
      exchangeRateReasonLabel: 'Change justification',
      exchangeRateReasonAria: 'Exchange rate change justification',
      exchangeRateReasonPlaceholder: 'E.g. contractual rate approved by finance',
      exchangeRateSaveError: 'Could not save the exchange rate',
      exchangeRateFixAction: 'Set exchange rate',
      valuesLabel: 'Amounts',
      grossAmountLabel: 'Gross amount',
      payableSuffix: 'to pay',
      noWithholdingsHint: 'No withholdings.',
      noWithholdingsAndEqualAmountsHint: 'No withholdings. Gross and net amounts are the same.',
      exchangeRateDateHint: 'Exchange rate from {{date}}.',
      exchangeRateInlineLabel: 'exchange rate',
      exchangeRateDateContext: 'on {{date}}',
      dueOnLabel: 'Due on {{date}}',
      openOfAmount: 'open out of {{amount}}',
      netPayableInlineLabel: 'net payable',
      grossAmountContext: 'from a gross amount of {{gross}}',
      viewWithholdingsLabel: 'View withholdings',
      settledPaymentsLabel_one: '{{count}} payment paid',
      settledPaymentsLabel_other: '{{count}} payments paid',
      settledLabel: 'paid',
      settledInPaymentsLabel: 'paid in {{count}} installments',
      paidOnLabel: 'on {{date}}',
      lastPaidOnLabel: 'last on {{date}}',
      paidWithholdingsHint:
        'From a gross amount of {{gross}}, with {{withholdings}} withheld and collected by Alchemy.',
      viewCompositionLabel: 'View composition',
      openBalanceLabel: 'Open balance',
      paidPercentageLabel: '{{percent}}% paid',
      nextPaymentDueLabel: 'due on {{date}}',
      overpaidLabel: 'Overpaid',
      overpaidInlineLabel: 'paid in excess',
      overpaidHint: 'Difference to recover from the vendor or offset against a future bill.',
      netPayableLabel: 'Net payable amount',
      employeePayeeLabel: 'Employee',
      netBillAmountLabel: 'Net bill amount',
      paidAmountLabel: 'Amount paid',
      remainingPayableLabel: 'Remaining payable',
      recurringNetVariationMatch: '= expected amount',
      recurringNetVariationAbove: '+{{percent}}% vs expected',
      recurringNetVariationBelow: '-{{percent}}% vs expected',
      recurringNetVariationMatchAverage: '= average',
      recurringNetVariationAboveAverage: '+{{percent}}% vs average',
      recurringNetVariationBelowAverage: '-{{percent}}% vs average',
      recurringNetVariationOpenOccurrencesAria:
        'Expand recurring details and highlight bills in this recurrence',
    },
    activities: {
      title: 'Activities',
      empty: 'No comments yet.',
      commentPlaceholder: 'Write a comment...',
      submitCommentAria: 'Send comment',
    },
    activitiesTimeline: {
      classificationModeBill: 'Whole bill',
      classificationModeLine: 'Per line item',
      reconciledAtPrefix: 'At {{datetime}}',
      paymentMismatch: {
        PAYMENT_CANCELLED: 'Cancelled the {{amount}} payment in reconciliation',
        REMAINDER_SCHEDULED: 'Scheduled the {{amount}} remaining amount in reconciliation',
        PAYMENT_RESTORED: 'Restored the {{amount}} payment by undoing the reconciliation',
        REMAINDER_REMOVED: 'Removed the {{amount}} remaining amount by undoing the reconciliation',
        bankAmount: 'Bank amount: {{amount}}',
        justification: 'Justification: {{text}}',
        bankCancellationConfirmed: 'Confirmed the payment was cancelled at the bank',
      },
      singleAction: 'recorded an activity',
      groupedActions: 'recorded {{count}} activities',
      emptyValue: '—',
      customBreakdown: 'Custom breakdown',
      customBreakdownItem: 'Split {{number}}',
      sources: {
        user: 'User',
        ai: 'AI',
        system: 'System',
      },
      allocation: {
        simple: 'Simple',
        customBreakdown: 'Custom breakdown',
      },
      vendorRuleDivergence: {
        detected: 'Vendor rule divergence detected on {{summary}}',
        detectedPlain: 'Vendor rule divergence detected',
        cleared: 'Vendor rule divergence closed',
        kept: 'Document values kept',
        applied: 'Rule applied: {{summary}}',
        appliedPlain: 'Rule applied',
        newVersion: 'New rule version from {{competence}}',
        newVersionPlain: "New rule version from this bill's competence",
        updated: 'Updated the vendor rule divergence',
        withholdingItem: '{{field}} (document {{document}} · rule {{rule}})',
        appliedItem: '{{field}} {{value}}',
        emptyValue: 'not stated',
        yes: 'Yes',
        no: 'No',
        fields: {
          irrfWithholdingAmount: 'IRRF',
          pisWithholdingAmount: 'PIS',
          cofinsWithholdingAmount: 'COFINS',
          csllWithholdingAmount: 'CSLL',
          inssWithholdingAmount: 'INSS',
          issWithholdingAmount: 'ISS',
          generatesPisCredit: 'PIS credit',
          generatesCofinsCredit: 'COFINS credit',
          creditType: 'credit type',
          pisCofinsCreditRevenueUse: 'credit allocation',
        },
      },
      fields: {
        allocationType: 'Allocation type',
        glAccounts: 'GL account',
        departments: 'Department',
        costCenters: 'Cost center',
        managerialAccounts: 'Managerial account',
        segments: 'Segment',
        customBreakdown: 'Custom breakdown',
        notes: 'Notes',
        source: 'Source',
      },
    },
    netAmountEdit: {
      title: 'Edit net payable amount',
      description: 'Enter the new net amount and select the reason for the change.',
      amountLabel: 'New net amount',
      reasonLabel: 'Reason for change',
      reasonAiError: 'AI made a mistake extracting the amount',
      reasonAdjustments:
        'There are discounts, interest or penalties to apply to the extracted amount',
      reasonOther: 'Other — please justify',
      justificationLabel: 'Justification',
      justificationPlaceholder: 'Describe the reason for the change…',
      conciliationNotice:
        'Discounts, interest, and penalties should be recorded during payment conciliation so the accounting entries are posted correctly.',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Net amount changed',
      editButton: 'Edit',
      impliedRatePreview:
        'To reach {{amount}} while keeping {{originalCurrency}} {{originalAmount}}, the exchange rate will be {{rate}}. The original amount will be preserved.',
    },
    grossAmountEdit: {
      title: 'Edit gross amount',
      description: 'Enter the new gross amount and select the reason for the change.',
      amountLabel: 'New gross amount',
      reasonLabel: 'Reason for change',
      reasonAiError: 'AI made a mistake extracting the amount',
      reasonAdjustments:
        'There are discounts, interest or penalties to apply to the extracted amount',
      reasonOther: 'Other — please justify',
      justificationLabel: 'Justification',
      justificationPlaceholder: 'Describe the reason for the change…',
      conciliationNotice:
        'Discounts, interest, and penalties should be recorded during payment conciliation so the accounting entries are posted correctly.',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Gross amount changed',
      editButton: 'Edit',
      impliedRatePreview:
        'To reach {{amount}} while keeping {{originalCurrency}} {{originalAmount}}, the exchange rate will be {{rate}}. The original amount will be preserved.',
    },
    editPending: {
      rate: 'Enter the exchange rate.',
      reason: 'Enter the reason.',
      currency: 'Choose the currency.',
      amount: 'Enter an amount above zero.',
      unchanged: 'Change the amount or the currency.',
      reasonChoice: 'Choose the reason.',
      justification: 'Describe the reason.',
    },
    currencyEdit: {
      title: 'Change currency',
      confirmTitle: 'Confirm currency',
      alert:
        'The document currency was not recognized ("{{rawValue}}"). Confirm the currency to approve, pay, and post the bill.',
      description:
        'Choose the currency printed on the document and enter the amount as it shows there.',
      pendingReviewNotice:
        'The document currency was not recognized ("{{rawValue}}"). Confirm the currency to unblock approval, payment, and posting.',
      currencyLabel: 'Document currency',
      currencyAria: 'Document currency',
      currencyPlaceholder: 'Choose the currency',
      suggestedHint:
        "Currency suggested from the issuer's address, bank, or tax ID. Check the document before you confirm.",
      amountLabel: 'Amount on the document',
      functionalHint: 'The amount stays in {{currency}}, with no conversion.',
      foreignHint:
        'The amount stays in {{currency}}. The system converts it to {{functionalCurrency}} at the PTAX rate of the bill competency date (or the issue date, when there is none).',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'E.g.: the invoice is from a US vendor and the amount is in dollars.',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Currency changed',
      confirm: 'Confirm currency',
      editButton: 'Change currency',
    },
    originalAmountEdit: {
      title: 'Edit original amount',
      description: 'Enter the new original amount and select the reason for the change.',
      amountLabel: 'New original amount',
      currencyLabel: 'Currency',
      currencyAria: 'Original amount currency',
      reasonLabel: 'Reason for change',
      reasonAiError: 'AI made a mistake extracting the amount',
      reasonAdjustments: 'There are adjustments to apply to the extracted amount',
      reasonOther: 'Other — please justify',
      justificationLabel: 'Justification',
      justificationPlaceholder: 'Describe the reason for the change…',
      conciliationNotice:
        'Discounts, interest, and penalties should be recorded during reconciliation to preserve correct accounting.',
      cancel: 'Cancel',
      save: 'Save',
      success: 'Original amount changed',
      editButton: 'Edit',
      editButtonAria: 'Edit original amount',
    },
    netAmountChangeBanner: {
      justificationLabel: 'Justification',
    },
    netAmountChangeAlert: {
      introByUser:
        'The net payable amount was changed from the amount implied by the document by {{user}}.',
      unknownUser: 'A user',
    },
    grossAmountChangeAlert: {
      introByUser:
        'The gross amount was changed from the amount implied by the document by {{user}}.',
      unknownUser: 'A user',
    },
    billMode: {
      success: 'Bill type changed',
    },
    documentActions: {
      uploadButton: 'Upload documents',
      detachButtonAria: 'Delete {{name}}',
      detachTitle: 'Delete document?',
      detachDescription:
        'The document "{{name}}" will be deleted and will no longer be available for this bill.',
      detachConfirm: 'Delete',
      detachSuccess: 'Document deleted successfully',
      detachError: 'Could not delete the document',
    },
    documentUpload: {
      title: 'Document upload',
      modalDescription: 'Upload files for extraction and accounts payable bill creation.',
      processWithAiLabel: 'Process with AI',
      processWithAiDescription:
        'On: extract data and reconcile with this bill. Off: keep as history only.',
      filesFallbackName: 'Files',
      loadingEntities: 'Loading entities...',
      noEntityAvailable: 'No entity available',
      clearSelection: 'Clear',
      addInstructionsLink: '(Optional) Add instructions',
      instructionsLabel: 'Processing instructions',
      instructionsPlaceholder:
        'E.g.: This is a utility bill from CEMIG, page 2 has the payment slip',
      uploadButton: 'Upload {{count}} file(s)',
      uploading: 'Uploading...',
      noFiles: 'Select files to upload',
      success: '{{count}} document(s) uploaded successfully',
      duplicates: '{{count}} duplicate document(s)',
      duplicatesWithNames: '{{count}} duplicate document(s): {{names}}',
      duplicatesExistingBillsDescription: 'Already linked to bill(s): {{billIds}}',
      duplicatesLinkedToCurrentBill: '{{count}} duplicate document(s) linked to this bill.',
      duplicatesLinkFailed:
        '{{count}} duplicate document(s) already exist, but could not be linked automatically. Use "Link to this bill" in the processing panel.',
      failed: '{{count}} document(s) failed: {{errors}}',
      error: 'Failed to upload documents',
      aiProcessing: 'Document is being processed by the accounts payable agent',
    },
    recurringSection: {
      sectionTitle: 'Recurring bill',
      patternIdentifiedLabel: 'Alchemy analysis',
      stateNone: 'None configured',
      stateSuggested: 'Suggestion pending',
      stateLinked: 'Linked · {{frequency}}',
      stateLinkedLoading: 'Linked recurrence',
      linkedDetailsLoading: 'Loading details...',
      emptyHelp:
        'Recurring bills reserve budget every period and automatically classify upcoming charges.',
      suggestionTitle: 'This looks like a recurring charge',
      suggestionTitleMatching: 'This bill appears to belong to a recurrence you already have',
      suggestionTitleMultiple: '{{count}} recurrences from this vendor can receive this bill',
      suggestionChoose: 'Choose which period this charge consumes.',
      suggestionCandidateSummary:
        '{{frequency}} · expected {{amount}} per period · {{count}} linked bills',
      suggestionSameVendor: 'Same vendor',
      suggestionLastCharge: 'Last charge {{period}}',
      suggestionPeriodWithoutBill: '{{period}} without a bill',
      suggestionPeriodOpen: '{{period}} open',
      suggestionLinkImpactVariance:
        'Linking consumes the {{period}} period. The amount is {{amount}} {{direction}} expected ({{percent}}%) — the variation remains recorded on the recurrence.',
      suggestionLinkImpactNoVariance:
        'Linking consumes the {{period}} period and records this occurrence on the recurrence.',
      suggestionVariationAbove: 'above',
      suggestionVariationBelow: 'below',
      suggestionNoMatchDetail:
        'No recurring bill was found for this vendor. Create a {{amount}} per-period budget reservation.',
      linkExistingTemplateAria: 'Link to {{name}} recurrence',
      linkThisSuggestion: 'Link this one',
      linkSelectedSuggestion: 'Link selected',
      openSuggestedTemplateAria: 'Open {{name}} recurrence',
      createAnotherRecurring: 'Create another recurrence',
      linkExistingAction: 'Link an existing one',
      notRecurringAction: 'Not recurring',
      activeStatus: 'Active',
      viewRecurringAction: 'View recurrence',
      unlinkAction: 'Unlink',
      expectedAmountLabel: 'Expected amount',
      nextOccurrenceLabel: 'Next',
      noNextOccurrence: 'No upcoming date',
      frequencyDaySuffix: ' · day {{day}}',
      viewRecurringLink: 'View {{count}} recurring bill(s) from this vendor',
      selectTemplateLabel: 'Recurrence',
      selectTemplatePlaceholder: 'Select a recurrence template',
      selectorSearchPlaceholder: 'Type to search recurrence...',
      selectorEmpty: 'No recurring templates found.',
      selectorNotFound: 'No recurring templates match the search.',
      selectorTypeToSearch: 'Type to search available recurring templates.',
      selectorSubtitleWithEndDate: '{{amount}} • Until {{date}}',
      selectorSubtitleNoEndDate: '{{amount}} • No defined end',
      clearTemplateAria: 'Clear selected recurrence template',
      createNewOption: 'Create new recurring bill',
      createPrompt: 'Create a recurrence template to link this bill and reserve budget.',
      createRecurringButton: 'Create recurring bill',
      lineItemLinkBlocked:
        'Bills with line-item classification cannot be linked to a recurring template. Switch to bill-level classification first.',
      editTemplateButton: 'Edit',
      editTemplateAria: 'Edit selected recurrence template',
      inactiveTemplateWarning: 'Inactive recurring bill — future commitments removed',
      recurringNameLabel: 'Recurring bill name',
      recurringNamePlaceholder: 'Recurring bill',
      frequencyLabel: 'Frequency',
      selectFrequencyPlaceholder: 'Select',
      frequency: {
        WEEKLY: 'Weekly',
        MONTHLY: 'Monthly',
        BIMONTHLY: 'Bimonthly',
        QUARTERLY: 'Quarterly',
        SEMESTERLY: 'Semiannual',
        YEARLY: 'Yearly',
      },
      dueDayLabel: 'Due day',
      expectedDueDayOfMonthLabel: 'Expected due day',
      expectedCompetencyDayOfMonthLabel: 'Expected competency day',
      recurringAmountLabel: 'Expected recurring amount',
      periodLimitAmountLabel: 'Per-period limit (budget)',
      classificationReuseHelp:
        'When saved, this bill classification will be copied to the template and used for budget commitments.',
      commitmentPreview:
        'Commitment preview: {{count}} periods × {{periodAmount}} = {{total}} reserved',
      recurrenceTypeLabel: 'Recurrence type',
      recurrenceTypeFixedTitle: 'Fixed',
      recurrenceTypeFixedDescription: 'Contracted value. Variation compares against expected.',
      recurrenceTypeVariableTitle: 'Variable',
      recurrenceTypeVariableDescription:
        'Value changes. Variation compares against recent average.',
      variableComparisonMonthsLabel: 'Compare with last N months',
      variableComparisonMonthsTooltip:
        'The system uses this number of months to compare variation for each bill',
      variableComparisonMonthsTooltipAria: 'Help: months used for variation comparison',
      durationLabel: 'Duration',
      durationInfinite: 'No defined end',
      durationInfiniteHint: 'ongoing contract',
      durationFor: 'For',
      durationMonths: 'months',
      durationUntilDate: 'Until specific date',
      occurrencesHeading: 'Bills in this recurrence',
      occurrencesCountBadge: '{{count}} bills',
      occurrencesColumnMonthYear: 'Month/Year',
      occurrencesColumnStatus: 'Status',
      occurrencesColumnVariation: 'Variation',
      occurrencesColumnAmount: 'Amount',
      occurrenceVariationMatch: '= 0%',
      occurrenceVariationAbove: '+{{percent}}%',
      occurrenceVariationBelow: '-{{percent}}%',
      variationComparisonTooltipFixed: 'vs base amount of {{amount}}',
      variationComparisonTooltipVariable: 'vs average of last {{count}} months of {{amount}}',
      variationComparisonTooltipCurrentBill: 'vs current bill of {{amount}}',
      selectDatePlaceholder: 'Select date',
      templateDetailsExpandAria: 'Show template link and recurring details',
      templateDetailsCollapseAria: 'Hide template link and recurring details',
      recurringDetailsExpandAria: 'Show recurring bill details',
      recurringDetailsCollapseAria: 'Hide recurring bill details',
      recurringDetailsExpandLabel: 'Other occurrences',
      recurringDetailsCollapseLabel: 'See less',
      openOccurrenceBillAria: 'Open this occurrence in a new tab',
    },
  },
  withholdings: {
    sectionTitle: 'Withholdings & Credits',
    socialContributions: 'Withheld social contributions',
    reviewRequired: 'Review the service type and withholdings.',
    reviewRequiredSubtitle: 'If needed, use Adjust amounts to edit withholdings individually',
    aggregateReviewDescription:
      'Aggregate reported on the invoice. Review the PIS, COFINS and CSLL split before approval; this amount is not CSLL alone.',
    meiBadge: 'Not applicable - MEI',
    toggleSectionAria: 'Toggle tax withholdings',
    meiMessage: 'MEI vendor — exempt from withholdings at source and not eligible for tax credits.',
    payableAmount: 'Payable amount',
    serviceTypeTitle: 'Service type',
    serviceTypePlaceholder: 'Type to search for a service type',
    serviceTypeDisabled: 'Withholdings disabled — service type is not required.',
    calculatedTitle: 'Calculated withholdings',
    toggleLabel: 'Apply withholdings',
    toggleAria: 'Enable withholdings',
    recalculateAria: 'Recalculate withholdings (except ISS)',
    recalculateLabel: 'Recalculate (except ISS)',
    editDisableAria: 'Disable withholdings editing',
    editEnableAria: 'Edit withholdings',
    recalculateButton: 'Recalculate and save',
    recalculateSaved: 'Withholdings recalculated and saved',
    recalculateSaveError:
      'Could not recalculate and save withholdings. Check the service type and NFS-e amounts, then try again.',
    recalculateConfirm: {
      title: 'Recalculate withholdings?',
      dropsRetentions:
        'The recalculation lowers the withholdings on this bill. The amounts you typed will be replaced.',
      breaksPayments:
        'After the recalculation, the net amount will not match the payments ({{payments}}).',
      current: 'Current',
      recalculated: 'Recalculated',
      lines: {
        irrf: 'IRRF',
        pis: 'PIS',
        cofins: 'COFINS',
        csll: 'CSLL',
        inss: 'INSS',
        iss: 'ISS',
        iva: 'IVA',
      },
      net: 'Net amount',
      keep: 'Keep values',
      recalculate: 'Recalculate anyway',
    },
    recalculateErrors: {
      AP_BILL_RETENTIONS_ALREADY_RECOGNIZED:
        'PIS, COFINS and CSLL on this bill were already recognised at payment. Their amounts can no longer change.',
      AP_BILL_WITHHOLDINGS_LOCKED: 'Withholdings can only be recalculated before approval.',
    },
    retainedDespiteExemption:
      'Withheld on the document, but the vendor is registered as {{regime}}. Check the vendor tax regime.',
    vendorRuleOverridesExemption:
      'Withheld by the vendor rule, which overrides the exemption of a {{regime}} vendor.',
    ruleOverridesExemption:
      'Simples Nacional vendor: the vendor rule withholds {{tax}} at {{rate}} anyway.',
    ruleOverridesMeiExemption: 'MEI vendor: the vendor rule withholds {{tax}} at {{rate}} anyway.',
    serviceTypeNotRecalculated:
      'This bill already has a payment. The withholdings were not recalculated for the new service type. Use "Recalculate and save" to recalculate.',
    adjustValuesButton: 'Adjust values',
    finishAdjustingButton: 'Finish adjustment',
    headerAppliedSummary: '{{taxes}} · total {{total}}',
    headerNone: 'None',
    emptyTitle: 'This bill has no withholdings.',
    emptyDescription:
      'Withholdings apply to services supplied by legal entities. Provide the service type when applicable.',
    informServiceType: 'Type to search for a service type',
    retainedTaxesTitle: 'Withheld taxes',
    rulesLoading: 'Loading tax rules…',
    ruleBadgeNotApplicable: 'No {{tax}}',
    ruleBadgeUnknown: '{{tax}} to define',
    ruleBadgeRate: '{{tax}} {{rate}}',
    derivation: '{{base}} × {{rate}}',
    derivationBaseUnavailable: '{{base}} unavailable; entered value shown',
    derivationUnavailable: 'Entered value; calculation rule unavailable',
    issRateMissing: 'Rate for the competent municipality has not been defined',
    toDefine: 'to define',
    nonApplicableSummary: '{{count}} taxes do not apply to this service.',
    viewWhich: 'See which',
    hideWhich: 'Hide',
    valuesTitle: 'Amounts',
    nonApplicableReasons: {
      vendorRegime: '{{regime}} vendor exempt from this withholding.',
      pcc: 'Outside the services subject to withholding under Law 10,833/2003.',
      inss: 'The service is not marked as labor assignment or contracting.',
      irrf: 'The service type has no nature subject to IRRF.',
      issDocument: 'Automatic calculation reconciled the invoice without customer-withheld ISS.',
      noRule: 'No applicable rule exists for this service type.',
      vendorRule: 'The vendor rule does not withhold {{tax}}.',
      belowMinimum: 'IRRF is below the withholding minimum, so nothing is withheld.',
      divergencePending: 'The vendor rule divergence is still unresolved.',
    },
    obligation: {
      summary: '{{document}} · {{deadline}}',
      document: {
        DARF: 'DARF {{code}}',
        GPS_DCTFWEB: 'GPS/DCTFWeb {{code}}',
        MUNICIPAL_GUIDE: 'Municipal tax slip',
      },
      deadline: {
        LAST_BUSINESS_DAY_SECOND_TEN_DAY_PERIOD_NEXT_MONTH:
          'remit by the last business day of the second ten-day period of the following month',
        DAY_20_NEXT_MONTH_PREVIOUS_BUSINESS_DAY: 'remit by day 20 of the following month',
        MUNICIPAL_RULE: 'deadline set by the competent municipality',
      },
    },
    issResolutionDescription: 'Confirm the competent municipality and applicable rate.',
    notApplied: 'Not applied',
    emptyValue: '—',
    vendorTaxRegime: {
      mei: 'MEI',
      simples: 'Simples Nacional',
    },
    tax: {
      irrf: 'IRRF',
      pis: 'PIS',
      cofins: 'COFINS',
      csll: 'CSLL',
      iss: 'ISS',
      inss: 'INSS',
      pcc: 'PIS/COFINS/CSLL',
    },
    taxRuleIss: '{{taxType}} (varies by municipality)',
    taxRuleDefault: '{{taxType}} ({{rate}})',
    calculationBase: {
      totalValue: 'Gross amount',
      netValue: 'Net amount',
      serviceValue: 'Service amount',
      laborValue: 'Labor amount',
    },
    exemptBadge: 'Exempt',
    notApplicable: 'Not applicable',
    validationUnavailable: 'No validation available.',
    exemptTitle: '{{taxType}} (Exempt)',
    exemptDescription: 'Vendor {{regime}} — exempt from IRRF withholding.',
    exemptMismatch: 'Warning: the NF shows {{value}}, but it should be R$ 0.00.',
    issTitle: 'ISS (varies by municipality)',
    issExtractedLabel: 'NF extracted:',
    issValidationNote: 'Validation: NF value is considered correct (rate varies by municipality).',
    baseLabel: 'Base',
    baseUnavailable: '{{base}} (unavailable)',
    expectedLabel: 'Expected',
    extractedLabel: 'NF extracted',
    requestedBaseLabel: ' • rule requests: {{requestedBase}}',
    diffLabel: ' • Diff: {{diff}}',
    diffMissingLabel: ' • Diff: (NF missing)',
    ruleMissing: 'No rule for {{taxType}} in this service type.',
    ruleMissingMismatchSuffix: ' (validation failed)',
    validationTolerance: 'Validation tolerance: ±{{cents}} cent(s).',
    validationOk: 'Validation OK',
    validationMismatch: 'Validation mismatch',
    grossAmount: 'Gross amount',
    totalWithholdings: 'Total withholdings',
    netPayable: 'Net payable',
    creditType: {
      label: 'PIS/COFINS credit category',
      placeholder: 'Classify the acquisition',
      options: {
        BASIC: 'Ordinary purchase',
        FIXED_ASSET: 'Fixed asset',
        PRESUMED: 'Presumed credit (specific treatment)',
        IMPORT: 'Import (specific treatment)',
      },
      description:
        'The acquisition category determines its tax treatment; enabling credits alone does not confirm eligibility. Imports and presumed credits require dedicated workflows.',
      fixedAssetHint:
        'The displayed amount is the purchase potential. Availability depends on the method and evidence recorded in the asset review; bill approval does not automatically release the entire credit. The fixed-asset workflow currently supports only direct use in non-cumulative revenue. Enter the allocation and supporting evidence below.',
    },
    taxCreditsTitle: 'PIS/COFINS credits',
    taxCreditsFixedAssetPotentialTitle: 'Fixed-asset PIS/COFINS credit potential',
    taxCreditsToggleAria: 'Enable PIS/COFINS credits',
    taxCreditsDescription: 'This purchase generates PIS/COFINS credits (non-cumulative regime).',
    taxCreditPis: 'PIS credit',
    creditRevenueUse: {
      label: 'Credit allocation across mixed revenue',
      expand: 'Classify allocation across mixed revenue',
      collapsedDescription:
        'Use only when the period assessment combines cumulative and non-cumulative revenue.',
      placeholder: 'Classify credit use',
      description:
        'This classification allocates the credit across mixed revenue; it does not determine whether the purchase is credit-eligible.',
      evidenceCode: 'Evidence code',
      evidenceDescription: 'Evidence description',
      evidenceRequired:
        'Enter the evidence code and description for the credit allocation before continuing.',
      options: {
        DIRECT_CUMULATIVE: 'Direct use in cumulative revenue',
        DIRECT_NON_CUMULATIVE: 'Direct use in non-cumulative revenue',
        COMMON: 'Common use across regimes',
      },
    },
    taxCreditCofins: 'COFINS credit',
    taxCreditsTotal: 'Total credits to appropriate',
    // ALC-4558: where the values come from when a vendor rule filled the credit.
    vendorCredit: {
      origin: 'Filled by the vendor rule (effective since {{since}})',
      generates: 'Generates credit',
      generatesYes: 'Yes',
      generatesNo: 'No',
      rates: 'Rates',
      ratesValue: 'PIS {{pis}} / COFINS {{cofins}}',
      revenueUseSetByHand:
        'The credit allocation across mixed revenue was set by hand and the vendor rule does not change it.',
    },
    // ALC-4559: the document differs from the vendor rule; the bill cannot go to approval.
    vendorRuleDivergence: {
      regionLabel: 'Divergence from the vendor rule',
      title: 'The document differs from the vendor rule',
      description:
        'This bill can be sent for approval only after you choose which values apply. The comparison uses the rule in effect since {{since}}.',
      descriptionPending:
        'The divergence blocks approval of this bill until you choose which values apply. The comparison uses the rule in effect since {{since}}.',
      columns: {
        field: 'Item',
        document: 'Document',
        rule: 'Vendor rule',
      },
      fields: {
        irrfWithholdingAmount: 'IRRF withheld',
        pisWithholdingAmount: 'PIS withheld',
        cofinsWithholdingAmount: 'COFINS withheld',
        csllWithholdingAmount: 'CSLL withheld',
        inssWithholdingAmount: 'INSS withheld',
        generatesPisCredit: 'Generates PIS credit',
        generatesCofinsCredit: 'Generates COFINS credit',
        creditType: 'Credit type',
        pisCofinsCreditRevenueUse: 'Credit allocation',
      },
      amountWithRate: '{{amount}} ({{rate}})',
      notWithheld: 'Not withheld',
      emptyValue: 'Not stated',
      yes: 'Yes',
      no: 'No',
      chooseLabel: 'How to resolve',
      options: {
        KEEP_DOCUMENT: {
          label: "Keep the document's values on this bill",
          description: "The rule stays as it is for this vendor's next bills.",
        },
        APPLY_RULE: {
          label: 'Apply the vendor rule',
          description:
            "The rule's values replace the document's, and the amount to pay is recalculated.",
        },
        NEW_RULE_VERSION: {
          label: "Keep the document's values and update the rule",
          description:
            "Creates a new version of the rule from {{competence}} with the document's values. Earlier competences keep the current version.",
        },
      },
      confirm: 'Confirm choice',
      newVersionUnavailable:
        'Creating a new rule version from {{competence}} is not available: the rule already has a version starting that month. Adjust that version on the vendor Rules tab.',
      scheduledPayment_one: 'The scheduled payment changes from {{from}} to {{to}}.',
      scheduledPayment_other: 'The scheduled payments change from {{from}} to {{to}}.',
      paymentBlocksApply:
        'Payment {{paymentNumber}}{{paymentMethod}} is {{paymentStatus}} and blocks applying the rule. Choose another option, or adjust that payment first.',
      paymentStatus: {
        PAID: 'paid',
        PROCESSING: 'processing',
        PENDING: 'pending',
        RECONCILED: 'reconciled',
        FAILED: 'failed',
        REVERSED: 'reversed',
        SCHEDULED: 'scheduled',
      },
      paymentMethod: {
        PIX: 'PIX',
        TED: 'TED',
        DOC: 'DOC',
        BOLETO: 'boleto',
        CREDIT_CARD: 'credit card',
        DEBIT_CARD: 'debit card',
        GUIA: 'tax slip',
        CHECK: 'check',
        OTHER: 'other method',
      },
      cancel: 'Cancel',
      close: 'Close',
      notice: {
        draft: 'You choose the values of {{fields}} when you send the bill for approval.',
        pendingApproval: 'Approval is blocked until someone chooses the values of {{fields}}.',
        choose: 'Choose values',
      },
      dialog: {
        description: 'Choose which values this bill keeps.',
        descriptionSubmit:
          'Before the bill goes for approval, choose which values it keeps. Then the bill goes for approval.',
        confirmAndSend: 'Confirm and send for approval',
      },
      pendingEdits: 'Save or discard your changes to the bill before resolving the divergence.',
      noPermission:
        'You do not have permission to edit this bill. Ask someone who can edit it to resolve the divergence.',
      success: 'Decision recorded',
      resolved: {
        KEEP_DOCUMENT: "This bill uses the document's values, not the vendor rule's.",
        APPLY_RULE: "This bill's withholdings and credits follow the vendor rule.",
        NEW_RULE_VERSION: "The vendor rule now uses the document's values from {{competence}}.",
      },
      chosenBy: 'Chosen by {{name}} on {{date}}.',
      chosenOn: 'Chosen on {{date}}.',
      openVendorRules: 'Open vendor rules',
      errors: {
        AP_BILL_VENDOR_RULE_DIVERGENCE_STALE:
          'The bill values or the rule changed after this page was opened. Refresh the bill and choose again.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_NOT_OPEN:
          'This divergence was already resolved. Refresh the bill to see the decision.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_CLEARED:
          "The divergence no longer exists because the bill's values changed. Refresh the bill to see the current values.",
        AP_BILL_VENDOR_RULE_DIVERGENCE_MIXED_CREDIT_FLAGS:
          'This bill takes the PIS and COFINS credits differently, and a vendor rule holds one answer for both. Keep the document values or apply the rule instead.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_NEW_VERSION_FORBIDDEN:
          'You do not have permission to change vendor rules. Choose another option or ask someone who manages vendor knowledge.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_VERSION_EXISTS:
          "The rule already has a version starting in {{competence}}. Adjust that version on the vendor's Rules tab.",
        AP_RETENTION_EDIT_PAYMENT_LOCKED:
          'Payment {{paymentNumber}}{{paymentMethod}} is already paid, sent or in a remittance, so the rule cannot be applied. The bill values stay as they are.',
        AP_RETENTION_EDIT_PAYMENT_LOCKED_GENERIC:
          'A payment is already paid, sent or in a remittance, so the rule cannot be applied. The bill values stay as they are.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_BILL_NOT_EDITABLE:
          'This bill is past approval, so its values can no longer change.',
        AP_BILL_VENDOR_RULE_DIVERGENCE_RATE_MISMATCH:
          "The document rate, with two decimal places, does not give the same withheld amount on this bill. The IRRF minimum can also change the amount. Adjust the rate on the vendor's Rules tab, refresh the comparison or choose another option.",
        AP_BILL_PAYMENT_PLAN_AMOUNT_MISMATCH:
          'Applying the rule changes the amount to pay, and this bill already has scheduled payments. Adjust the payment schedule and try again.',
        AP_BILL_RETENTIONS_ALREADY_RECOGNIZED:
          'PIS, COFINS and CSLL on this bill were already recognized at payment, so the rule can no longer be applied.',
        generic: 'The decision could not be recorded. Try again in a moment.',
      },
      reload: 'Refresh bill',
      ruleChanged:
        'The vendor rule changed after this divergence was found. The version in effect starts in {{since}}.',
      ruleChangedNoVersion:
        'The vendor rule changed after this divergence was found. There is no version in effect for this competence.',
      refreshComparison: 'Update comparison',
      comparisonUpdated:
        'The vendor rule changed since this screen was opened. The comparison was updated; review it and confirm again.',
      comparisonCleared: 'The comparison was updated. The bill now agrees with the vendor rule.',
    },
    // ALC-4561: knowledge offers on the bill (new service and remember).
    knowledgeOffer: {
      regionLabel: 'Vendor knowledge offer',
      loading: 'Loading the offer…',
      loadError: 'The offer could not be loaded. Refresh the bill.',
      thisVendor: 'this vendor',
      thisService: 'this service',
      rememberDescription:
        'The values set on this bill apply to this service from {{competence}}. Earlier competences stay as they are.',
      simplesRuleWithholds:
        'Simples Nacional vendor: the vendor rule withholds {{tax}} at {{rate}} anyway.',
      meiRuleWithholds: 'MEI vendor: the vendor rule withholds {{tax}} at {{rate}} anyway.',
      valuesLabel: 'What will be saved',
      withheld: '{{tax}} withheld at {{rate}}',
      withheldAtSystemRate: '{{tax}} withheld at the system rate',
      notWithheld: '{{tax}} not withheld',
      creditGenerates: 'Generates PIS and COFINS credit',
      creditDoesNotGenerate: 'Does not generate PIS and COFINS credit',
      creditType: 'Credit type: {{type}}',
      revenueUse: 'Credit allocation: {{use}}',
      save: 'Save rule',
      saving: 'Saving rule…',
      thisBillOnly: 'This bill only',
      dismiss: 'Dismiss',
      rateLabel: '{{tax}} rate',
      identifyServiceTitle: 'Identify the service type on this bill',
      identifyServiceDescription:
        'Without a service type there is no rule to remember. Choose the service type in this bill’s withholdings.',
      nothingToSave:
        "This bill has no values to turn into a rule. Set the rule in the vendor's Rules tab.",
      versionExists:
        "The rule already has a version starting in {{competence}}. Adjust that version in the vendor's Rules tab.",
      pendingEdits: 'Save or discard your changes to the bill before saving the rule.',
      saved: 'Rule saved from {{competence}}.',
      apply_one: 'Apply to the draft bill',
      apply_other: 'Apply to the {{count}} draft bills',
      applying: 'Applying…',
      appliedNone: 'No draft bill changed.',
      applied_one: 'Rule applied to {{count}} draft bill.',
      applied_other: 'Rule applied to {{count}} draft bills.',
      appliedKept: 'Values someone set were kept.',
      appliedNoValueChanged: 'No value changed.',
      divergenceOpened_one: '{{count}} draft bill was left with a divergence to review.',
      divergenceOpened_other: '{{count}} draft bills were left with a divergence to review.',
      errors: {
        AP_BILL_KNOWLEDGE_OFFER_NOT_AVAILABLE:
          'This offer is no longer available. Refresh the bill.',
        AP_BILL_KNOWLEDGE_OFFER_STALE:
          'The bill values or the rule changed after this page was opened. Refresh the bill and review it again.',
        AP_BILL_KNOWLEDGE_OFFER_VERSION_EXISTS:
          "The rule already has a version starting in {{competence}}. Adjust that version in the vendor's Rules tab.",
        AP_BILL_KNOWLEDGE_OFFER_NOTHING_TO_SAVE:
          "There are no values to save. Set the rule in the vendor's Rules tab.",
        rate: 'The rate must be 0 to not withhold, or from 0.01% to 100%.',
        accept: 'The rule could not be saved. Try again in a moment.',
        keep: 'The values could not be kept for this bill only. Try again in a moment.',
        dismiss: 'The offer could not be dismissed. Try again in a moment.',
        apply: 'The rule could not be applied to the draft bills. Try again.',
        applyPartial_one: '{{count}} draft bill did not get the rule. Try again.',
        applyPartial_other: '{{count}} draft bills did not get the rule. Try again.',
      },
      reload: 'Refresh bill',
      saveAndSend: 'Save rule and send',
      thisBillOnlyAndSend: 'This bill only and send',
      sendWithoutRule: 'Send without a rule',
      thisBillOnlyTooltip:
        'Uses these values on this bill only. The vendor rule does not change. The next bill whose values differ from the rule shows the offer again.',
      dismissTooltip:
        'Stops offering this rule for {{vendor}} on this service, on every bill of the entity. The vendor rule does not change. To see it again, restore it in the Vendor rules tab.',
      strip: {
        text: "Save this bill's withholdings as a vendor rule.",
        review: 'Review rule',
      },
      dialog: {
        descriptionSubmit:
          "Before the bill goes for approval, you can save this bill's withholdings as a vendor rule.",
        title: 'Save a withholding rule for {{vendor}}?',
        newServiceDescription:
          "Today's rates still apply to this bill. The rule starts in {{competence}}.",
        serviceLabel: 'Fiscal service',
        rateRow: '{{tax}} withholds',
        update: 'Update rule',
      },
    },
  },
  classification: {
    customFieldOtherEntity:
      "This field belongs to {{entity}}. Remove it and add the field of the bill's entity.",
    customFieldOtherEntityUnnamed:
      "This field belongs to another entity. Remove it and add the field of the bill's entity.",
    title: 'Classification',
    expandAction: 'Expand classification',
    mode: {
      billLevel: 'Full bill',
      lineItem: 'Per invoice item',
      custom: 'By allocation',
    },
    // D44 "Remover item": the trash action on a line of "Per invoice item".
    removeLineItem: {
      action: 'Remove item {{number}}',
      title: 'Remove item {{number}} · {{amount}}?',
      description:
        'The item leaves the bill together with its classification and the provisions chosen for it. The bill total does not change: adjust the total separately if needed.',
      confirm: 'Remove item',
      done: 'Item removed from the bill.',
    },
    // ALC-1504 — see the pt-BR block: for BYPASS and AUTO_PROVISION this text is the only thing in
    // the section, so it has to carry the whole explanation.
    statutoryTreatment: {
      mappingResolution: {
        tableHint: 'Choose an obligation for each item and save all choices when finished.',
        tax: 'Tax',
        principal: 'Principal',
        saveAll: 'Save mappings',
        saving: 'Saving mappings…',
        required: 'Select an obligation for the pending items.',
        saveSuccessTitle: 'Mappings saved successfully',
        saveSuccessHelp:
          'Your choices were recorded. Click Done to return to the guide and check the next steps.',
        saveFailureTitle: 'Could not complete saving',
        backToMappings: 'Review mappings',
        done: 'Done',
        optionalReason: 'Reason (optional)',
        selectPlaceholder: 'Select',
        suggestedOptions: 'Suggestions',
        allOptions: 'All obligations',
        saveError: 'Could not save this item. Try again.',
        partial:
          '{{saved}} items saved; {{failed}} failed. Check the indicated rows and save again.',
        saved_one: '{{count}} mapping saved.',
        saved_other: '{{count}} mappings saved.',
        rowSaved: 'Saved',
        rowConfirmed: 'Confirmed',
        rowPending: 'Pending',

        question:
          'Some taxes on this guide could not be automatically mapped to the correct ledger accounts. How would you like to proceed?',
        ai: 'Resolve with AI',
        manual: 'Edit accounts',
        title: 'Resolve tax mappings',
        manualDescription: 'Review the obligation and ledger account for each tax.',
        previousDecisions: 'This guide contains manually confirmed tax destinations.',
        viewDecisions: 'Review destinations',
      },
      slotFallback: {
        title: 'Tax {{code}} is unrecognized',
        confirmedTitle: 'Destination for {{code}} manually confirmed',
        warning: 'CSLL is a suggestion, not the tax identity. Select the correct liability.',
        accountingHelp: 'Accounting impact of this choice',
        accountingDetails:
          'This choice changes the accounting liability and may recognize a provision. Approval remains blocked until confirmation and may still require configuration or an accrued balance.',
        confirmed: 'Destination: {{slot}}. Reason: {{reason}}.',
        slot: 'Tax liability',
        reason: 'Reason for this choice',
        confirm: 'Confirm destination for this guide',
        permission:
          'A user allowed to manage statutory treatment must confirm the destination before approval.',
        prepare: 'Prepare tax lines for review',
        refresh: 'Refresh tax identification',
        clearBillOverride:
          'Clear the bill-level manual classification before confirming a tax liability.',
        success: 'Tax destination confirmed for this guide.',
      },

      badge: 'Tax slip',
      reviewTitle: 'Tax-slip treatment',
      reviewCount_one: '{{count}} tax',
      reviewCount_other: '{{count}} taxes',
      fullAccountingView: 'Full accounting view',
      fullAccountingViewDescription:
        'Tax-slip details, provisioned balances, and advanced options for each tax.',
      summaryTable: {
        tax: 'Tax',
        assessmentPeriod: 'Assessment period',
        amount: 'Amount',
        provisionedBalance: 'Provisioned balance',
        account: 'GL account',
        alerts: 'Alerts',
        actions: 'Actions',
        alertPresent: 'This tax has an accounting alert',
        provisionedBalanceBelowGuide:
          'The {{book}} provisioned balance ({{balance}}) is lower than the tax-slip amount ({{amount}}).',
        stagedRailBlocked: 'This tax accounting hierarchy is blocked.',
        viewOrigins: 'View origins',
        hideOrigins: 'Hide origins',
        assessmentPeriodPending:
          'The {{period}} assessment period will apply when the bill is saved.',
      },
      bypassTitle: 'Tax slip — use source liability',
      bypassBody:
        'Approval reclassifies liabilities already recognized at the source, from Assessed to In Tax Slips Payable in each applicable accounting book.',
      stagedBypassTitle: 'FGTS — staged liability',
      stagedBypassBody:
        'Payroll recognized FGTS in Accrued. On approval, the system transfers principal to Guides Payable in every BR_CPC book and freezes matched origins; unmatched amounts remain explicit in the trace.',
      explanationTooltipLabel: 'View treatment explanation',
      stagedFlowLink: 'View accounting flow',
      stagedFlowDialogTitle: 'Accounting flow — {{tax}}',
      stagedFlowDialogDescription: 'How this tax moves across liability accounts',
      stagedFlowSourceTitlePayroll: 'Payroll accrual',
      stagedFlowSourceTitleGeneric: 'Liability recognition',
      stagedFlowTransferTitle: 'Transfer to tax slip',
      stagedFlowPaymentTitle: 'Tax slip payment',
      stagedFlowStatusCompleted: 'Completed',
      stagedFlowStatusOnApproval: 'On approval',
      stagedFlowStatusPendingPayment: 'On reconciliation',
      stagedFlowStatusReversed: 'Reversed',
      stagedFlowDebitLabel: 'Debit',
      stagedFlowCreditLabel: 'Credit',
      stagedFlowSourceDebitPayroll: 'Payroll expense and accrual accounts',
      stagedFlowSourceDebitGeneric: 'Source liability account',
      stagedFlowSourceNotePayroll: 'Posted when payroll closed for {{period}}.',
      stagedFlowSourceNoteGeneric:
        'The liability was recognized at the source before this slip is approved.',
      stagedFlowTransferNoteCompleted:
        'The accrued liability was reclassified to the account this slip clears on payment.',
      stagedFlowTransferNotePlanned:
        'On approval, the accrued liability will be reclassified to the account this slip clears on payment.',
      stagedFlowPaymentCredit: 'Bank account (on reconciliation)',
      stagedFlowPaymentNoteCompleted:
        'The clearing entry was posted when this slip payment was reconciled.',
      stagedFlowPaymentNotePending:
        'This only happens when the payment for this slip is reconciled on the bank statement.',
      bypassOriginWithholding: "the withholding on the supplier's invoice",
      bypassOriginPayroll: 'the payroll run',
      bypassOriginRevenue: 'revenue recognition',
      bypassOriginUpstream: 'the event that gave rise to it',
      stagedPlanTitle: 'Planned transfer — {{book}}',
      stagedPlanVersion: 'Chart-of-accounts version: {{version}}',
      stagedPlanBlocker: {
        NO_OPEN_PERIOD: 'Blocked: no open accounting period covers the approval date.',
        NO_SCHEDULED_PAYMENT_DATE: 'Blocked: enter the scheduled payment date.',
        NO_SETTLEMENT_PERIOD: 'Blocked: the payment date has no usable open accounting period.',
        STAGED_RAIL_NOT_CONFIGURED:
          "Blocked: this tax's staged hierarchy is not configured in this chart-of-accounts version.",
        STAGED_SETTLEMENT_RAIL_NOT_CONFIGURED:
          "Blocked: this tax's staged hierarchy is not configured in the payment chart of accounts.",
        STAGED_SETTLEMENT_CROSSWALK_MISSING:
          "Blocked: this tax's accounts have no audited crosswalk to the payment chart of accounts.",
      },
      stagedEvidenceTitle: 'Liability trace — {{book}}',
      stagedEvidenceAmounts: 'Matched: {{matched}} · unmatched: {{unmatched}}',
      stagedEvidenceSettlementAmounts:
        'Paid: {{paid}} · reversed: {{reversed}} · remaining: {{remaining}}',
      stagedEvidenceMatched: 'Matched: {{matched}}',
      stagedEvidenceUnmatched: 'Unmatched: {{unmatched}}',
      stagedEvidenceReview: {
        PENDING: 'Accounting review pending — period close remains blocked',
        RESOLVED: 'Review resolved automatically after origin matching',
        CANCELLED: 'Review cancelled with guide reversal',
      },
      stagedEvidenceJournal: 'Approval journal: {{journal}}',
      stagedEvidencePaymentJournal: 'Payment journal: {{journal}}',
      stagedEvidencePaymentReversalJournal: 'Payment reversal journal: {{journal}}',
      stagedEvidenceOrigin: 'Origin {{type}} {{source}} — {{amount}}',
      autoProvisionTitle: 'Tax slip — automatic provisioning',
      autoProvisionBody:
        'On approval, the system transfers the {{amount}} principal from Assessed to In Tax Slips Payable in each applicable accounting book.',
      classifyTitle: 'Third-party tax — classify as an expense',
      classifyOwnTitle: 'Unprovisioned own tax — pending classification',
      classifyOwnBody:
        'This slip is for a tax type that has not been provisioned, so review the classification so it can be provisioned on approval.',
      classifyBody:
        "The taxpayer on this slip is {{payer}}, which is not one of this tenant's entities. Nothing in your books provisioned this tax, so classify it as a normal expense.",
      classifyBodyNoPayer:
        'This slip needs full accounting classification, like any other expense.',
      classifyReimbursableHint:
        'If the cost will be recovered from the client, mark the bill as client-reimbursable.',
      classifyOverriddenBody:
        'This slip was manually marked for classification. Fill in the GL account, cost center and department as for any expense.',
      branchReviewNote:
        "The slip's tax ID matches only on the root ({{taxId}}) — check whether this branch should be registered.",
      blockedTitle: 'Cannot approve',
      documentTypeConfirmationTitle: 'Confirm guide type',
      documentTypeConfirmationBody:
        'This bill was identified as a tax guide, but its document type needs confirmation.',
      documentTypeConfirmationCorrectionBody:
        'The guide type was previously confirmed. If it is incorrect, select the right type and update it before approval.',
      documentTypeConfirmationSlotUnresolved:
        'The tax on this {{documentType}} could not be identified.',
      documentTypeConfirmationLabel: 'Guide type',
      documentTypeConfirm: 'Confirm type',
      documentTypeCorrectionConfirm: 'Update type',
      documentTypeConfirming: 'Confirming…',
      documentTypeConfirmSuccess: 'Guide type confirmed and tax treatment recalculated',
      documentTypeConfirmError: 'Could not confirm the guide type: {{message}}',
      documentTypeConfirmationPermission:
        'Ask a user who can manage tax treatment to confirm the guide type.',
      documentTypeConfirmationAmbiguous:
        'There is more than one possible document for this bill. Review the attachments and correct the document before approval.',
      documentType: {
        DARF: 'DARF',
        GPS: 'GPS',
        GUIA_ISS: 'ISS guide',
        GUIA_ICMS: 'ICMS guide',
        GRF: 'GRF',
        DAS: 'DAS',
      },
      loading: 'Loading tax-slip treatment…',
      openSettings: 'Open tax-slip treatment settings',
      loadError:
        'Could not load the tax-slip treatment. Approval stays blocked until the status loads successfully.',
      refreshError:
        'Could not refresh the tax-slip treatment. The details below are from the last successful check; try again before approval.',
      retry: 'Try again',
      blockedSlotUnresolved: 'The tax on this slip could not be identified.',
      blockedGeneric: 'The tax treatment for this slip could not be resolved.',
      blockedProvisionDebitSlotUnpaired:
        'Tax slot {{slot}} has no offsetting account defined yet, so its provision has nowhere to post.',
      blockedProvisionAccountNotMapped:
        'Tax slot {{slot}} is not mapped to a GL account in this chart of accounts version.',
      blockedPaymentSettlementAccountNotMapped:
        'Tax slot {{slot}} has no liability account to clear on payment. Map the account before approving the slip.',
      blockedPayerIsOtherEntity:
        "This slip belongs to entity {{entity}}, not this bill's entity. Reassign the bill to the correct entity.",
      blockedPayerTaxIdInvalid:
        "The taxpayer CNPJ/CPF read from the slip ({{taxId}}) fails its check digits, so it cannot tell whether the tax is this entity's own or a third party's. Correct the CNPJ, mark it as the entity's own tax, or classify it manually.",
      payerRecovery: {
        readLabel: 'CNPJ read from the slip',
        correctAction: 'Correct CNPJ',
        inputLabel: "Taxpayer's correct CNPJ/CPF",
        saveCorrection: 'Save CNPJ',
        cancel: 'Cancel',
        checkDigitsInvalid: 'The check digits do not match. Compare it with the slip.',
        unchanged: 'This is the same CNPJ that was read. Change the wrong digit.',
        matchesBillEntity: "Matches {{entity}}. The slip becomes the entity's own tax.",
        matchesOtherEntity:
          'Matches {{entity}}, another of your companies. The slip stays blocked until the bill is moved to that company.',
        matchesNoEntity:
          "Valid CNPJ, but it is not one of your companies. The slip stays a third party's tax.",
        suggestion: 'Looks like the CNPJ of {{entity}}:',
        suggestionUse: 'Use',
        orDecide: 'Or decide without correcting:',
        ownAction: "It is the entity's own tax",
        ownReasonLabel: 'Why is this tax owed by {{entity}}? (kept in the history)',
        ownReasonLabelNoEntity: "Why is this tax the company's own? (kept in the history)",
        ownReasonPlaceholder: "E.g. the company's own ISS slip; CNPJ unreadable in the PDF",
        ownReasonTooShort: 'Write at least {{count}} characters.',
        ownConfirm: 'Confirm own tax',
        notThirdPartyLink: "Not a third party's tax?",
        declaredCorrected: 'Taxpayer CNPJ corrected from <from/> to <to/> on {{date}}.',
        declaredOwn:
          "Marked as the entity's own tax on {{date}}. The CNPJ read from the slip ({{taxId}}) was not changed. Reason: “{{reason}}”",
        redundant:
          'The CNPJ read from the slip now matches the correction. The correction no longer changes anything and can be undone.',
        undo: 'Undo',
        undoTitle: 'Undo the taxpayer decision?',
        undoBody:
          "The slip goes back to the CNPJ the AI read ({{taxId}}). If it is invalid or a third party's, the slip's treatment changes again.",
        undoConfirm: 'Undo',
        saveError: 'Could not save: {{message}}',
        errors: {
          STATUTORY_PAYER_CORRECTION_TAX_ID_INVALID:
            'The CNPJ/CPF entered fails its check digits. Compare it with the slip.',
          STATUTORY_PAYER_CORRECTION_TAX_ID_UNCHANGED:
            'The CNPJ/CPF entered is the same one read from the slip. Change the wrong digit.',
          STATUTORY_PAYER_DECLARATION_REASON_REQUIRED:
            "Explain why the slip is the entity's own tax, in at least 5 characters.",
        },
      },
      blockedNoPeriod:
        "No accounting period covers this slip's competency date, so there is no chart of accounts to resolve the accounts against.",
      blockedNoBrCpcBook:
        'This entity has no BR CPC book, so the provision has no book to post to. Set up a BR CPC book for the entity.',
      blockedMixedTreatment:
        'This slip mixes taxes with different treatments — part already provisioned, part to classify. Split it into separate bills or choose the treatment manually.',
      blockedSourceProducerRequired:
        '{{slot}} must be recognized by its source taxable event before guide approval.',
      blockedSourceProducerTitle: 'Cannot approve without a source liability',
      blockedSourceProducerDescription:
        'This tax requires source liabilities and cannot be approved because no liabilities are available in the trial balance. Either provision it before approval or change this tax to Automatic provisioning in the tax-slip treatment settings',
      blockedMappingLink: 'Configure fiscal account mapping',
      blockedOverrideHint:
        'Or mark the slip for manual classification and pick the GL account yourself.',
      sharedHierarchyTitle:
        'This slip cannot be approved — it is a configuration issue, not a slip-specific one',
      sharedHierarchyBody:
        'The {{count}} taxes below depend on a chart-of-accounts hierarchy that does not exist in the active version yet. This affects this slip and future slips with those taxes until the hierarchy is created.',
      sharedHierarchyConfigureAction: 'Configure hierarchy in chart of accounts',
      sharedHierarchyOverrideAction: 'Classify this slip manually',
      pendingMappingSummaryTitle_one: '{{count}} of {{total}} tax is blocking approval',
      pendingMappingSummaryTitle_other: '{{count}} of {{total}} taxes are blocking approval',
      pendingMappingSummaryBodySingle:
        '{{tax}} has no mapped liability account to clear on payment. The other {{readyCount}} taxes are already ready.',
      pendingMappingSummaryBodyMultiple:
        '{{count}} taxes have no mapped liability account to clear on payment. The other {{readyCount}} are already ready.',
      pendingMappingJumpAction: 'Jump to pending item',
      readySummaryCount_one: '{{count}} ready for approval',
      readySummaryCount_other: '{{count}} ready for approval',
      pendingSummaryCount_one: '{{count}} pending mapping',
      pendingSummaryCount_other: '{{count}} pending mappings',
      summaryTotalLabel: 'Total:',
      pendingLineAlert: 'No liability account is mapped for this tax.',
      showOtherReadyLines_one: 'Show the other ready tax',
      showOtherReadyLines_other: 'Show the other {{count}} ready taxes',
      staleTreatmentNotice:
        "This slip's treatment changed since it was last derived (it would now be {{treatment}}). Reload the bill before approving — approval is refused until the new treatment has been reviewed.",
      overrideAction: 'Classify manually',
      overrideClearAction: 'Back to automatic treatment',
      overrideSuccess: 'Slip marked for manual classification',
      overrideClearSuccess: 'Automatic treatment restored',
      overrideError: 'Could not change the treatment: {{message}}',
      unmappedAccount: 'unmapped account',
      bookSpecificMappingRequired: 'Select the account per book in settings',
      unidentifiedTax: 'Unidentified tax',
      taxLabel: 'Tax',
      settlementAccount: 'Account cleared on payment',
      currentBalance: 'Current balance — {{book}}: {{amount}}',
      provisionedBalanceLabel: 'Provisioned balance',
      provisionedBalanceValue: '{{amount}}',
      balanceContextTooltipLabel: 'View balance context',
      provisionedBalanceWarningLabel: 'View provisioned balance warning',
      provisionedBalanceWarningText: 'This tax amount is higher than what is currently provisioned',
      balanceContextNote:
        'The balance is the current total for the mapped GL account and is contextual only. It does not prove that this specific slip was provisioned.',
      lineTreatment: 'Treatment',
      switchToExpenseLink: 'Switch to expense',
      showTaxTreatmentOptionsAction: 'Auto-provision as tax',
      assessmentPeriod: 'Apuração period',
      amountLabel: 'Amount',
      assessmentPeriodValue: '{{month}}/{{year}}',
      assessmentPeriodAmbiguous: 'Apuração period not determined',
      assessmentPeriodUnsupported: 'Apuração period unsupported ({{raw}})',
      assessmentPeriodRawUnknown: 'unknown',
      summaryPeriodPrefix: 'Period',
      principalAmount: 'Principal',
      fineAmount: 'Fine',
      interestAmount: 'Interest',
      toggleLineAria: 'Expand details for {{tax}}',
      treatment: {
        BYPASS: 'Use source liability',
        AUTO_PROVISION: 'Automatic provisioning',
        CLASSIFY: 'Classify as expense',
      },
      editScopeHint:
        'This change applies only to this slip. Entity Tratamento de Guia and system-account defaults stay unchanged.',
      oneOffAccountHint: 'Settlement account overridden for this slip only.',
      frozenBadge: 'Frozen',
      eligibleOrigins: 'Eligible origins',
      noEligibleOrigins: 'No eligible same-period origins.',
      applyOriginSelection: 'Use selected origins',
      clearOriginSelection: 'Restore automatic allocation',
      manualOriginsReadOnly: 'Origins manually selected for this slip.',
      overrideReasonLabel: 'Reason for change',
      overrideReasonPlaceholder: 'Explain why this one-off correction is needed',
      confirmTreatmentTitle: 'Change this line treatment?',
      confirmTreatmentDescription:
        '{{tax}} on this slip will become {{treatment}}. Future slips and the entity Tratamento de Guia stay unchanged.',
      confirmAccountTitle: 'Change this line settlement account?',
      confirmAccountDescription:
        "{{tax}}'s settlement account will be overridden for this slip only. The system-account mapping stays unchanged.",
      confirmOriginsTitle: 'Restrict origins for this line?',
      confirmOriginsDescription:
        'Approval for {{tax}} will consume only the selected eligible origins (same entity/book/slot/period rules).',
      cancel: 'Cancel',
      confirm: 'Confirm change',
      policyUpdateSuccess: 'Line treatment updated for this slip only',
      accountUpdateSuccess: 'Settlement account overridden for this slip only',
      originsUpdateSuccess: 'Origin selection updated for this slip only',
      policyUpdateError: 'Could not update the tax line: {{message}}',
    },
    switchDialog: {
      toLineItemTitle: 'Switch to per-item classification?',
      toLineItemDescription:
        'Switching to per-item classification removes the current full-bill classification. You will need to classify each item individually. This action cannot be undone.',
      toBillLevelTitle: 'Switch to full-bill classification?',
      toBillLevelDescription:
        'Switching to full-bill classification removes all item classifications. You will need to classify the bill as a whole. This action cannot be undone.',
      toCustomTitle: 'Switch to customized classification?',
      toCustomDescription:
        'The current classification will be inherited by the first line at 100%. Use this mode when two or more dimensions vary together.',
      recurringToCustomTitle: 'Use By allocation classification?',
      recurringToCustomDescription:
        'The current classification will be inherited by the first allocation at 100%. Use this mode when two or more dimensions vary together.',
      recurringToBillLevelTitle: 'Use Full bill classification?',
      recurringToBillLevelDescription:
        'The allocations will no longer be used by this template. Review the single classification before saving.',
      cancel: 'Cancel',
      confirm: 'Confirm',
    },
    custom: {
      addAllocation: 'Add allocation',
      allocatedTotal: 'Allocated total',
      adjustToFull: 'Adjust to 100%',
      allocationPlaceholder: 'Allocation {{index}}',
      percentLabel: 'Percent',
      amountLabel: 'Amount',
      glAccount: 'GL account',
      managerialAccount: 'Managerial account',
      costCenter: 'Cost center',
      department: 'Department',
      splitValue: 'Allocation',
      select: 'Select...',
      customFieldsTitle: 'Custom fields',
      customFieldsDescription: 'Add custom segments for this allocation.',
      addCustomField: 'Add field',
      textValuePlaceholder: 'Enter the value',
      textValueLabel: 'Custom field text value',
      textValueCounter: '{{count}}/{{max}}',
      textValueTooLong: 'The value exceeds {{max}} characters',
      segmentUsedInConditions: ' (used in conditions)',
      segmentNoConfiguredValues: ' (no configured values)',
      segmentUnavailableInRecurringTemplate: ' (unavailable in recurring templates)',
      segmentTextValueUnsupported: ' (free text is not supported here)',
      removeAllocation: 'Remove allocation',
      multiDimensionHint:
        'Use By allocation when two or more dimensions vary together. Each line carries the full classification.',
      sharedAcrossLines: 'Same across {{count}} lines',
      editSharedFields: 'Edit shared fields',
      hideSharedFields: 'Hide shared fields',
      editLine: 'Edit line {{index}}',
      duplicateLine: 'Duplicate line {{index}}',
      removeLine: 'Remove line {{index}}',
      addLine: 'Add line',
    },
    secondSplit: {
      singleSplitHint:
        'If another dimension also varies, use By allocation to define the combinations explicitly.',
      title: 'Two split fields create combinations',
      description:
        'This second split creates {{count}} combinations. Review the lines before choosing how to classify them.',
      convertAction: 'Convert to By allocation',
      keepCrossProductAction: 'Keep cross-product',
      undoAction: 'Undo second split',
    },
    validation: {
      customBreakdownMissing: 'Add at least one allocation',
      customBreakdownTotal: 'Allocated total must be 100% (current: {{total}}%)',
      customBreakdownGlRequired: '{{label}}: Select a GL account',
      glManagerialAccountTypeMismatch:
        'GL and managerial accounts diverge between cost (COGS) and operating expense. Review classification for income statement consistency.',
      dimensionPercentTotal:
        '{{prefix}}{{dimension}}: Percentages must total 100% (current: {{total}}%)',
      segmentPercentTotal:
        '{{prefix}}Segment {{segment}}: Percentages must total 100% (current: {{total}}%)',
      billLevelDimensionRequired: 'Provide at least one classification dimension',
      allocationLabelFallback: 'Allocation {{index}}',
      dimensionGlAccount: 'GL account',
      dimensionManagerialAccount: 'Managerial account',
      dimensionCostCenter: 'Cost center',
      dimensionDepartment: 'Department',
      lineItemPrefix: 'Line item {{lineNumber}}',
      requiredTenantDimension: '{{dimension}} is required',
    },
    reimbursementLineItemMismatch:
      'The reimbursement total does not match the sum of line items. Reclassify as Full bill or By allocation to continue.',
    reimbursementBillOrCustomBanner:
      'Reimbursements must be classified as Full bill or By allocation',
    recurringLockBanner:
      'Classification is derived from the recurring bill. Unlink recurrence to edit.',
    recurringTemplateEditBanner:
      'Changes made here will be saved to the recurring bill and applied to linked bills still in pre-approval.',
    recurringTemplateEditConfirmTitle: 'Update the recurring bill classification?',
    recurringTemplateEditConfirmDescription:
      'This change will update the recurring bill and every linked bill that is still a draft, submitted, or pending approval.',
    recurringTemplateEditConfirmSave: 'Update and save',
    recurringTemplateEditConfirmSubmit: 'Update and submit',
    recurringTemplateEditMissingVendor:
      'The recurring bill could not be updated because its vendor is missing.',
    recurringLockGoToRecurringAction: 'Go to recurrence',
    recurringLockSaveBlocked: 'Classification is locked — unlink recurrence to edit.',
    recurringLockNeedsUnlink:
      'Approval failed because the derived classification is incomplete. Unlink the recurrence to fix this bill classification.',
    aiFieldTooltip: 'Classification filled by Alchemy',
    subtitleWithAmount: '{{count}} item • {{amount}}',
    subtitleWithAmount_plural: '{{count}} items • {{amount}}',
    lineItemsEmpty: 'No line items found.',
    notesLabel: 'Observations',
    notesPlaceholder: 'Internal notes',
    compactDimension: {
      distributionValueHeading: 'Value distribution',
      completeTo100: 'Complete to 100%',
      divideEqually: 'Split equally',
      removeSplitAria: 'Remove',
      totalNot100Warning: 'Total must equal 100% to save',
      splitAction: 'Split',
      splitSummary: 'Split {{amount}} across {{count}} accounts',
      addAllocation: 'Add account',
      removeNamedAllocation: 'Remove {{name}} from the split',
      roundingAbsorber: 'This row absorbs rounding',
      distributedStatus: '100% distributed',
      missingStatus: '{{amount}} · {{percent}}% missing',
      excessStatus: '{{amount}} · {{percent}}% over',
      lastRowRoundingRule: 'The last row absorbs rounding.',
    },
    workflowSuggestion: {
      bankLabel: 'Bank',
      apply: 'Apply suggestions',
      dismiss: 'Dismiss',
      badgeRule: 'Rule',
      badgeHistory: 'History',
      badgeOther: 'Suggestion',
      defaultTitle: 'AI suggestion',
      hiddenCount_one: '{{count}} more suggestion available (priority: rule > history > other)',
      hiddenCount_other: '{{count}} more suggestions available (priority: rule > history > other)',
    },
    aiInstructionUse: {
      regionLabel: 'Vendor instructions the AI used',
      summaryCostCenter: 'The AI suggested the cost center where rules left that field empty.',
      summaryCategoryAndCostCenter:
        'The AI suggested the category and cost center where rules left those fields empty.',
      usedInstruction: 'Used the instruction "{{text}}"',
      usedInstructionWithoutText: 'Used a vendor instruction',
      instructionTextHidden: 'The instruction text shows to users who can view vendors.',
      openInstruction: "Open the instruction in the vendor's Knowledge tab",
      noteLeadIn: 'AI note:',
      split: {
        title: 'AI split suggestion',
        dimension: {
          costCenters: 'Cost center',
          departments: 'Department',
          managerialAccounts: 'Managerial account',
        },
        targetWithPercent: '{{target}} {{percent}}',
        rejectedTitle: 'AI split not applied',
        rejectedRow: '{{dimension}}: {{reason}}',
        rejection: {
          AI_SPLIT_PERCENT_INVALID:
            'each percentage must be above zero, with at most two decimal places',
          AI_SPLIT_PERCENT_SUM_INVALID: 'the percentages do not add up to exactly 100%',
          AI_SPLIT_TARGET_UNKNOWN: 'an option does not exist, is inactive or cannot take postings',
          AI_SPLIT_TARGET_DUPLICATE: 'the same option appears more than once',
        },
      },
      saveRefusal: {
        title: 'AI classification not applied',
        reason: {
          AI_CLASSIFICATION_AMOUNT_TOTAL_INVALID:
            'the allocated amounts do not add up to the bill amount',
        },
      },
      skippedReference: {
        title: 'The AI followed the instruction and did not copy the history',
        pattern: {
          LINE_ITEM_LEVEL:
            "The vendor's last classified bill is classified per line item, so there is no single split to copy.",
          CUSTOM_BREAKDOWN:
            "The vendor's last classified bill uses a custom breakdown, so there is no single split to copy.",
        },
        openBill: 'Open bill {{bill}}',
      },
      creditFlags: {
        title: "PIS and COFINS credits not copied from the vendor's last bill",
        reason: {
          AI_INSTRUCTION_CREDIT_FLAGS_NOT_COPIED:
            "The instruction chose a different GL account than the vendor's last bill. Check this bill's credits.",
          AI_INSTRUCTION_CREDIT_FLAGS_COPY_FAILED:
            "The copy did not complete. Check this bill's credits.",
        },
        openBill: 'Open bill {{bill}}',
      },
    },
  },
  dashboard: {
    pendingApproval: {
      title: 'Pending approvals',
      emptyState: 'No bills pending approval',
      totalPending: '{{count}} pending bill(s)',
      totalAmount: 'Total: {{amount}}',
      tableHint: 'Review pending bills and approve or reject them directly from the list.',
      morePending: '+ {{count}} pending bills',
      columns: {
        dueDate: 'Due date',
        vendor: 'Vendor',
        classification: 'Classification',
        amount: 'Amount',
      },
      dueRelative: {
        today: 'due today',
        inDays_one: 'in {{count}} day',
        inDays_other: 'in {{count}} days',
        overdue: '{{count}}d overdue',
      },
      risksLabel: 'Risks · {{count}}',
      attentionLabel: 'Attention · {{count}}',
      risksTooltipTitle: 'Identified risks',
      attentionTooltipTitle: 'Points of attention',
      noVendor: 'Vendor not provided',
      lineItemsSummary: '{{count}} classified items',
      payeeLineBase: '{{vendor}} to be paid',
      payeeLineReimbursementPrefix: 'Reimbursement for ',
      payeeLineVia: ' via {{method}}',
      payeeLineIn: ' to {{details}}',
      approve: 'Approve',
      reject: 'Reject',
      review: 'Review',
      dueLabel: 'Due on {{date}}',
      competencyLabel: 'Competency: {{date}}',
      rejectDialog: {
        title: 'Reject bill',
        description:
          'This rejection will be permanently recorded and the bill returns to the requester for review. This action cannot be undone.',
        reasonLabel: 'Rejection reason (required)',
        placeholder: 'Rejection reason...',
        cancel: 'Cancel',
        confirm: 'Confirm rejection',
      },
      classification: {
        split: 'Split for {{dimension}}',
        glAccount: 'GL account',
        department: 'Department',
        costCenter: 'Cost center',
        managerialAccount: 'Managerial account',
        segment: 'Segment',
        lineItems: {
          sectionTitle: 'Items',
          label: '#{{lineNumber}} - {{description}}',
        },
      },
      paymentsLabel: '{{count}} payment(s)',
      toast: {
        approved: 'Bill approved successfully',
        rejected: 'Bill rejected',
        approveError: 'Failed to approve bill',
        approveSettlementGroupTotalMismatch:
          'The reconciled payments total does not match the linked transaction amount. Review this bill’s reconciliation: the allocated amount must equal the card or bank transaction amount.',
        approveSettlementPaymentSourceMismatch:
          'The payment source (card or bank account) does not match the reconciled transaction. Review this bill’s reconciliation.',
        approvePaidCardPaymentNeedsReconciliation:
          'This payment is already marked paid by card but has not been reconciled to a card-statement transaction. Reconcile the matching purchase or remove the payment if it was recorded in error, then try approval again.',
        approveSettlementGenericWithCode:
          'Failed to post the reconciled payment to the ledger ({{code}}). Review this bill’s reconciliation and try again.',
        approveNoOpenPeriod:
          'Cannot approve this bill because there is no open accounting period for the selected competency.',
        noOpenPeriodPhaseBill: 'Bill GL provision (competency)',
        noOpenPeriodPhasePayment: 'Reconciled payment clearing',
        noOpenPeriodPhaseDeferralRecognition: 'Deferral monthly recognition',
        noOpenPeriodPhaseDeferralReclassify: 'Deferral CP/LP reclassification',
        noOpenPeriodPhaseDeferralWriteoff: 'Deferral write-off',
        noOpenPeriodPhaseDeferralVendorRefund: 'Vendor refund (deferral)',
        noOpenPeriodPhaseRevenueRecognition: 'Revenue recognition',
        noOpenPeriodPhaseFixedAssetCapitalization: 'Fixed asset capitalization',
        noOpenPeriodPhaseFixedAssetDepreciation: 'Fixed asset depreciation (competency)',
        noOpenPeriodPhaseFixedAssetDisposal: 'Fixed asset disposal',
        noOpenPeriodPhaseFixedAssetTransfer:
          'Fixed asset transfer from construction in progress to final class',
        noOpenPeriodPhaseLeaseRecognition: 'Lease recognition (right of use and liability)',
        noOpenPeriodPhaseLeaseLiabilityReclassification:
          'Lease liability current/non-current reclassification',
        noOpenPeriodPhaseLeaseRemeasurement: 'Lease remeasurement',
        noOpenPeriodClosedPeriod:
          'Period {{periodId}} ({{start}}–{{end}}) exists but its status is {{status}}. Reopen that period or change the posting date.',
        noOpenPeriodMissingPeriod:
          'No GL period is defined for book {{bookId}} that covers this date.',
        noOpenPeriodPaymentRef: ' Payment: {{paymentId}}.',
        noOpenPeriodElaboration:
          '{{phase}} could not be posted. Posting reference date: {{date}}. Book: {{bookId}}. {{periodSentence}}{{paymentSuffix}}',
        noOpenPeriodGoToPeriods: 'Open accounting periods',
        approveVendorOffsetSettingsMissing:
          'To approve, configure the vendor liability root account mapping in Settings → Accounting & Tax → Account Mapping (AP tab).',
        approveVendorOffsetSettingsAction: 'Go to Settings',
        approveVendorOffsetSettingsInvalid:
          'Could not approve because the vendor liability account on this posting is invalid for the competency (it may be outside the applied chart-of-accounts version or outside the vendor liability root). Fix it in Settings → Accounting & Tax → Account Mapping (AP tab) and try approving again.',
        approveVendorOffsetRootInvalid:
          'Invalid vendor offset root account (missing or from another chart-of-accounts version). Please update tenant settings.',
        approveSupplierAdvanceSettingsMissing:
          'To approve, configure the supplier-advance root in Settings → Accounting & Tax → Account Mapping (AP tab).',
        approveSupplierAdvanceSettingsInvalid:
          'The supplier-advance account is invalid for this competency. Remap it in the current chart-of-accounts version and try again.',
        approveCreditCardSettingsMissing:
          'To approve, configure the credit-card root in Settings → Accounting & Tax → Account Mapping (AP tab).',
        approveCreditCardSettingsInvalid:
          'The credit-card account is invalid or conflicts with another account for the same institution. Review the mapping in the current version and try again.',
        approveCoaVersionNotApplied:
          'The accounting period for the bill competency is not open or has no applied chart of accounts. Open the period or apply the chart of accounts to approve.',
        approveTaxAccountNotMapped:
          'To approve, configure the tax account mappings (IRRF, PIS, COFINS, CSLL, INSS withholdings) for the competency chart of accounts.',
        approveTaxAccountNotMappedAction: 'Go to Mapping',
        approveTaxAccountSlotNotFound:
          'Tax account not configured. Check the tax account mapping in tenant settings.',
        approveSystemAccountNotConfigured:
          'To approve, configure the system accounts in tenant settings.',
        approveSystemAccountMappingMissing:
          'System account mapping missing. Check the configuration in tenant settings.',
        approveSystemAccountMappingMissingWithSlot:
          'System account "{{slotName}}" ({{slotCode}}) is not mapped. Go to Settings → Accounting & Fiscal → Account mapping (AP tab) → System accounts, configure this account, and try approving again.',
        approveSystemAccountSlotNotFound:
          'System account not found. Check the configuration in tenant settings.',
        approveSystemAccountSlotNotFoundWithSlot:
          'System account "{{slotName}}" ({{slotCode}}) was not found. Go to Settings → Accounting & Fiscal → Account mapping (AP tab) → System accounts, provision/configure this account, and try approving again.',
        clientReimbursableHasTaxCredits:
          'Client-reimbursable bills cannot carry tax credits (PIS/COFINS or IVA). Clear the credits before sending for approval.',
        leaseSettlementHasTaxCredits:
          'This bill settles a lease instalment and carries tax credits (PIS/COFINS or IVA). Clear the credits or expense the bill outside the lease.',
        documentDiscountHasDeferral:
          'The document carries a discount and this bill has an active deferral built on the gross amount. Cancel the deferral before posting the bill.',
        documentDiscountHasCapitalization:
          'The document carries a discount and this bill has a pending capitalization built on the gross amount. Discard the pending capitalization before posting the bill.',
        approveSystemAccountMappingAction: 'Go to System accounts',
        approveChainBootstrapFailed:
          'The bill was sent for approval, but the default approval chain could not be created. Review the approval settings or contact support.',
        entitySuggestionUnconfirmed: 'Confirm the suggested entity before approving',
        approveAssetCreditPolicyRequired:
          'Review the fixed-asset candidate and record the PIS/COFINS credit method and evidence before approving the bill.',
        approveAssetCreditRecoverabilityRequired:
          'The linked draft asset does not yet have an accounting recoverability conclusion. Open Assets → Registry, locate the asset created from this bill, and confirm the full-recovery expectation with its supporting forecast or accounting policy. Then try approving again.',
        approveAssetCreditSourceInvalid:
          'The fixed-asset credit is not unambiguously linked to the item and asset. Review the source bill items, classification and candidates before approving.',
        approveAssetCreditOwnerTypeMismatch:
          'The PIS/COFINS credit category does not match the linked asset. Review the purchase classification and asset linkage before approving.',
        approveAssetCreditFirstUseBeforeInService:
          'The recorded tax first-use date precedes the asset’s in-service date. Review the date and the asset’s tax decision before approving; do not silently change dates to release the credit.',
        approveAssetCreditAcquisitionAfterInService:
          'The confirmed acquisition or receipt date falls after the in-service date established by the bill posting. Review the date and the asset’s tax decision before approving; do not silently change dates to release the credit.',
        approveAssetCreditStatutoryBookRequired:
          'The fixed-asset credit requires a provision journal in the entity’s statutory book. Review the book and bill posting; the auxiliary tax book does not replace this provision.',
        approveAssetCreditRevenueUseUnsupported:
          'The revenue use of this fixed-asset credit is not fully supported in this flow. Preserve the actual classification and keep the bill pending; do not change its use merely to obtain approval.',
        approveAssetCreditScheduleInvalid:
          'Tax installments could not be reconciled with the asset rule and credit potential. Review the class tax rule and PIS/COFINS amounts; if the issue persists, request a source review before approving.',
        approveAssetCreditTypeStatusNotEditable:
          'The PIS/COFINS credit category can only be changed before bill approval. Refresh the page to review the recorded status and classification.',
        approveAssetCreditTypeCapitalizationImmutable:
          'An asset has already been created from this bill, so its credit category can no longer be changed. Review the purchase classification and linked asset.',
        approveAssetCapitalizationAmountDrift:
          'The bill changed after the capitalisation was confirmed: the classification no longer debits the amount the assets were measured at. Re-decide the candidate against the current amounts before posting.',
        approveAssetSourceReviewImmutable:
          'The confirmed asset tax decision froze this bill’s items, fiscal facts, and PIS/COFINS amounts. Informational fields remain editable. This flow cannot yet correct those frozen facts; keep the bill unapproved and send it for accounting and tax review.',
        approveAssetMethodNotImplemented:
          'The class of one of the capitalised assets uses a depreciation method that is not supported yet: only straight-line is implemented. Change the method on the class rule before approving.',
        approveClassificationTotalMismatch:
          'The accounting classification does not add up to the bill total. Open the classification, check the accounts and amounts, and save it again before approving.',
        approvePaymentScheduleAmountExceeded:
          "This bill's payments already add up to more than its amount. Check the payments in the Payments tab and cancel any duplicate before approving.",
        mxLegacyIvaRepostUnsupported:
          "This Mexican bill can't be reposted: it was provisioned with the IVA in cost and its payments stay posted, so the paid IVA would stay pending credit. Undo the bank match of its payments, repost the bill, then match them again. If the period of a payment is closed, reopen it first.",
        rejectError: 'Failed to reject bill',
      },
      customAllocationDialog: {
        badgeFallback: 'Custom allocation',
        badgeMultiple: '{{count}} custom allocations',
        tooltipHint: 'Click to see allocation details',
        title: 'Custom Allocation Breakdown',
        description: 'This bill has its amount allocated across the following dimensions:',
        splitFallbackLabel: 'Allocation {{index}}',
        glAccount: 'GL account:',
        department: 'Department:',
        costCenter: 'Cost center:',
        managerialAccount: 'Managerial account:',
        segmentFallback: 'Segment',
        close: 'Close',
      },
    },
    title: 'Dashboard',
    subtitle: 'Bills awaiting your approval',
    widgetTitle: 'Bills pending approval',
    approve: 'Approve',
    approveSuccess: 'Bill approved',
    approveError: 'Failed to approve bill',
    reject: 'Reject',
    rejecting: 'Rejecting...',
    rejectSuccess: 'Bill rejected',
    rejectError: 'Failed to reject bill',
    rejectDescription: 'Provide a reason for rejection',
    rejectPlaceholder: 'Rejection reason',
    review: 'Review',
    cancel: 'Cancel',
    countAndTotal: '{{count}} bills • {{total}} total',
    pendingCount: '{{count}} pending',
    dueLabel: 'Due {{date}}',
    empty: 'No bills pending approval.',
    recurringDashboard: {
      header: {
        title: 'Recurring bills',
        subtitle: 'Projection and monitoring by competency period',
        entityLabel: 'Entity',
        entityPlaceholder: 'Select entity',
        allEntities: 'All entities',
        costCenterFilterLabel: 'Cost center filter',
        costCenterFilterPlaceholder: 'Filter cost centers',
        departmentFilterLabel: 'Department filter',
        departmentFilterPlaceholder: 'Filter departments',
        prevMonthAria: 'Previous month',
        nextMonthAria: 'Next month',
      },
      states: {
        selectEntity: 'Select an entity to load the recurring dashboard.',
        loading: 'Loading recurring bills',
        error: 'Unable to load recurring projection.',
      },
      kpis: {
        projected: {
          label: 'Monthly projection',
          detail: '{{count}} projected recurrences',
        },
        received: {
          label: 'Received',
          detail: '{{count}} of {{total}} received',
        },
        pending: {
          label: 'Pending',
          detail: '{{count}} waiting to be received',
        },
        overdue: {
          label: 'Overdue',
          detail: '{{count}} overdue',
        },
      },
      alerts: {
        overdue: {
          title: 'Overdue recurrences',
          description: '{{count}} recurrences are missing after expected due date.',
          cta: 'View details',
        },
        variation: {
          title: 'Detected variations',
          description: '{{count}} recurrences have value variation.',
          cta: 'Investigate',
        },
      },
      filters: {
        all: 'All',
        received: 'Received',
        pending: 'Pending',
        overdue: 'Overdue',
      },
      table: {
        showing: 'Showing {{visible}} of {{total}} rows',
        columns: {
          vendor: 'Vendor / description',
          frequency: 'Frequency',
          expected: 'Expected amount',
          actual: 'Actual amount',
          variation: 'Variation',
          previousCompetencies: 'Prior periods',
          concentration: 'Concentration',
          dueDate: 'Due date',
          costCenter: 'Cost Center',
          department: 'Department',
          status: 'Status',
          actions: 'Actions',
        },
        openVendorNewTab: 'Open vendor in new tab: {{vendor}}',
        expandOccurrences: 'Expand linked bills for {{vendor}}',
        collapseOccurrences: 'Collapse linked bills for {{vendor}}',
        editRecurring: 'Edit recurring bill',
        edit: {
          loadError: 'Could not load recurring bill for editing.',
          notFound: 'Recurring bill not found for this vendor.',
        },
        merge: {
          action: 'Merge recurring templates',
          loadError: 'Could not load templates for merging.',
          tooltip: 'Deduplicate recurrences',
        },
        link: {
          action: 'Link bills',
          tooltip: 'Manage bill links',
          loadError: 'Could not load bills for linking.',
        },
        sort: {
          aria: 'Sort by {{field}}{{direction}}',
          direction: {
            asc: 'ascending',
            desc: 'descending',
          },
        },
        occurrences: {
          loading: 'Loading linked bills...',
          error: 'Could not load linked bills.',
          retry: 'Try again',
          empty: 'No linked bills found for this recurrence.',
          openBillLink: 'Open bill',
          columns: {
            monthYear: 'Competency',
            dueDate: 'Due date',
            status: 'Status',
            netAmount: 'Net amount',
            totalAmount: 'Total amount',
            openBill: 'Bill',
          },
        },
        previousCompetencies: {
          expand: 'Show competency history for this template',
          collapse: 'Hide competency history',
          loading: 'Loading timeline...',
          error: 'Could not load competency timeline.',
          retry: 'Try again',
          empty: 'No applicable months in this window.',
          issuesBadge: '{{count}} issue(s)',
          healthTooltip: {
            OK: 'Prior competency months are healthy (no missing bills or unpaid past due).',
            WARNING: 'Some prior months have a missing bill or an unpaid bill past due.',
            CRITICAL: 'Multiple months with missing or unpaid past-due bills — review urgently.',
          },
        },
        timeline: {
          columns: {
            monthYear: 'Competency',
            dueDate: 'Due date',
            status: 'Status',
            totalAmount: 'Total amount',
            openBill: 'Bill',
          },
          status: {
            RECEIVED: 'Received / paid',
            PENDING: 'Pending',
            OVERDUE: 'Overdue',
            MISSING: 'Missing',
          },
          openBillLink: 'Open bill',
        },
        timelineDrawer: {
          title: 'Prior competencies',
          description: 'Recurring history for {{vendor}}.',
          healthSummary:
            'Window: {{total}} months • Covered: {{covered}} • Missing: {{missing}} • Overdue: {{overdue}}',
        },
        variation: {
          awaiting: 'Awaiting',
          notReceived: 'Not received',
        },
        footer: {
          counts: 'Received: {{received}} • Pending: {{pending}} • Overdue: {{overdue}}',
          paginationShowingItems: 'Showing {{from}} to {{to}} of {{total}}',
          paginationPage: 'Page {{page}} of {{total}}',
          itemsPerPage: 'Items per page',
        },
      },
      status: {
        received: 'Received',
        receivedReview: 'In review',
        receivedApproved: 'Approved',
        receivedPaid: 'Paid',
        receivedPartialPrefix: 'Partially {{status}}',
        pending: 'Pending',
        overdue: 'Overdue',
      },
      linkDialog: {
        title: 'Link received bills',
        description: 'Select the bills that should stay linked to this recurrence.',
        contextTitle: 'Links for',
        vendorLabel: 'Vendor',
        templateLabel: 'Template',
        loading: 'Loading bills...',
        empty: 'No bills available for linking.',
        save: 'Save links',
        linkSummary: '{{linked}} linked, {{unlinked}} unlinked, {{reassigned}} reassigned.',
        columns: {
          selected: 'Link',
          competency: 'Competency',
          dueDate: 'Due date',
          amount: 'Amount',
          status: 'Status',
          currentTemplate: 'Current template',
          openBill: 'Bill',
        },
        currentTemplate: {
          none: 'Not linked',
        },
        toast: {
          success: 'Links updated successfully.',
          error: 'Unable to update links.',
        },
      },
      sidebar: {
        coverage: {
          title: 'Competency coverage',
          dashboardTitle: 'Recurring bills',
          received: 'Received:',
          projected: 'Projected:',
        },
        projection: {
          title: '6-month projection',
        },
        concentration: {
          title: 'Vendor concentration',
          empty: 'No recurrences for selected period.',
        },
        anomalies: {
          title: 'Variations & anomalies',
          empty: 'No variations detected for selected period.',
        },
        extraBills: {
          title: 'Bills outside recurrence',
          loading: 'Checking additional bills for the selected competency...',
          detected: '{{count}} recurrence(s) with extra bills in the selected competency.',
          clear: 'No extra bills detected in the selected competency.',
          cta: 'Open suggested links',
        },
      },
    },
    variationsWidget: {
      title: 'Variations & anomalies',
      empty: 'No significant variation on received recurring bills.',
      loadError: 'Could not load variations.',
      subtitleVsAverage: '{{pct}} vs. {{count}}-month average',
      subtitleVsTemplate: '{{pct}} vs. template amount',
    },
  },
  approvalFlow: {
    approveSuccess: 'Step approved successfully',
    approveError: 'Error approving step',
    rejectError: 'Error rejecting step',
    retryError: 'Error retrying approval',
    segregationTitle: 'Segregation of Duties',
    approveStepAria: 'Approve this step',
    approveStepSaveAndApproveAria: 'Save changes and approve this step',
  },
  payments: {
    addPaymentDisabledTooltip: "Can't add a payment: payments already total the bill amount.",
    reimbursementSwitch: 'Reimbursement',
    reimbursementToggleConfirmTitle: 'Change payment type?',
    reimbursementToggleConfirmToReimbursement:
      'Current payments will be removed and the reimbursement form will be prefilled using their amounts and due date. After the bill is approved, a new reimbursement payment will be created. Continue?',
    reimbursementToggleConfirmToRegular:
      'Disabling Reimbursement will delete the reimbursement information. Continue?',
    reimbursementToggleConfirm: 'Confirm',
    reimbursementSwitchSuccess:
      'Reimbursement enabled: previous payments removed and the request prefilled from them.',
    reimbursementSwitchDeleteError: 'Could not remove payments or save the reimbursement.',
    reimbursementSwitchMissingBill: 'Bill unavailable. Refresh the page and try again.',
    reimbursementSwitchDeleteBlockedStatus:
      'You can only switch to reimbursement and remove payments while the bill is draft, in review, or pending approval.',
    reimbursementSwitchDeleteBlockedPayments:
      'One or more payments cannot be removed (for example, reconciled). Resolve this before enabling reimbursement.',
    pendingLabel: 'Pending:',
    ofLabel: 'of',
    cancelPaymentTitle: 'Cancel payment',
    cancelPaymentDescription: 'Are you sure you want to cancel payment #{{paymentNumber}}?',
    cancelPaymentConfirm: 'Cancel payment',
    cancelPaymentSuccess: 'Payment cancelled successfully',
    cancelPaymentError: 'Failed to cancel payment',
    advanceNotice: 'Vendor has advance balance: {{amount}}',
    useAdvanceBalance: 'Use balance',
    viewAdvanceHistory: 'View history',
    advanceInDevelopmentToast: 'Feature under development',
    useAdvanceDialog: {
      title: 'Use advance balance',
      description: 'Consume the vendor advance balance to settle (fully or partially) this bill.',
      availableLabel: 'Available balance',
      billTotalLabel: 'Bill total',
      amountLabel: 'Amount to consume',
      effectiveDateLabel: 'Effective date',
      notesLabel: 'Notes',
      submit: 'Confirm',
      cancel: 'Cancel',
      success: 'Advance-based payment registered successfully',
      successReserved:
        'Balance reserved successfully. GL posting will happen when the bill is approved.',
      errorInsufficient: 'Insufficient balance',
      errorGeneric: 'Error registering advance-based payment',
      reserveNoticeTitle: 'Balance reservation',
      reserveNoticeDescription:
        'This bill is not approved yet. The vendor balance will be reserved now and the GL posting will happen when the bill is approved.',
    },
    paymentMode: {
      title: 'How would you like to pay?',
      total: 'Total',
      partial: 'Partial',
      installments: 'Installments',
      totalValue: '{{amount}}',
      defineValue: 'Define amount',
      multiple: 'Multiple',
    },
    installments: {
      count: 'Number of Installments',
      interval: 'Interval',
      preview: 'Installment Preview',
      installmentLabel: '{{number}}st installment',
      days7: '7 days',
      days15: '15 days',
      days30: '30 days (monthly)',
      days60: '60 days (bimonthly)',
    },
    hints: {
      remaining: 'Remaining: {{amount}}',
      lockedTotal: 'Amount locked (total bill amount)',
    },
    validation: {
      pixKeyRequired: 'Chave PIX é obrigatória',
      pixKeyInvalid: 'Chave PIX inválida. Informe CPF, CNPJ, email, telefone ou chave aleatória.',
      barcodeRequired: 'Código de barras é obrigatório',
      barcodeInvalid: 'Código de barras inválido. Deve ter 44 ou 47 dígitos.',
      barcodeInvalidChecksum: 'Código de barras com dígito verificador inválido',
      bankAccountRequired: 'Conta bancária é obrigatória para TED/DOC',
      noBankAccounts: 'Nenhuma conta bancária cadastrada para este fornecedor',
      creditCardRequired: 'Cartão de crédito é obrigatório',
      noCreditCards: 'Nenhum cartão de crédito cadastrado',
    },
    pixKeyTypes: {
      CPF: 'CPF',
      CNPJ: 'CNPJ',
      EMAIL: 'Email',
      PHONE: 'Telefone',
      RANDOM: 'Chave Aleatória',
    },
  },
  reimbursement: {
    policyApplied: 'Policy applied: {{name}}',
    viewPolicy: 'View policy',
    noPolicyMatched: 'No policy applied',
    policyDialogTitle: 'Reimbursement policy',
    policyDialogEmpty: 'No policy found for this reimbursement.',
    dueDate: 'Reimbursement due date',
    method: 'Payment details',
    methodHelper: 'Select the account or PIX key that will receive the reimbursement',
    originAccountHelper: 'Company account from which the reimbursement payment will be made',
    dueDateHelper: 'Set the due date for the reimbursement payment',
    addPaymentMethod: 'Add payment method',
    selectPaymentMethod: 'Select a payment method',
    noPaymentMethods: 'No reimbursement payment methods available',
    selectRequesterForPaymentMethods: 'Select the requester to see payment methods',
    selectRequesterForPaymentMethodsHint:
      'Select the reimbursement requester to show available payment methods.',
    generatedPayment: 'Generated payment',
    requestSectionLabel: 'Reimbursement request',
    reimburseFor: 'Reimburse to',
    selectBeneficiary: 'Select beneficiary',
    selectEmployeeBeneficiary: 'Select employee',
    selectVendorBeneficiary: 'Vendor',
    beneficiaryModeEmployee: 'Employee',
    beneficiaryModeVendor: 'Vendor',
    beneficiaryVendorLinked: 'Linked vendor beneficiary',
    expenseClassification: 'Expense classification',
    refundAmount: 'Reimbursement amount',
    billTotalHint: 'Total: {{total}}',
    fullAmount: 'Full amount',
    amountAboveTotalLabel: 'Amount exceeds total',
    amountAboveTotalTooltip: 'The entered amount ({amount}) exceeds the bill total.',
    policyLimit: 'Per-transaction limit',
    periodLimit: 'Period limit',
    periodUsed: 'Used in period',
    periodRemaining: 'Remaining in period',
    periodUsageLoading: 'Calculating period usage...',
    exceedsPolicyLimit: 'Amount exceeds per-transaction limit',
    exceedsPeriodLimit: 'Amount exceeds period limit',
    nearPeriodLimit: 'Period limit nearly reached',
    justification: 'Justification',
    justificationPlaceholder: 'Describe the reason for the reimbursement...',
    instructionsLabel: 'Filling instructions',
    required: 'Required',
    optional: 'Optional',
    requiredByPolicy: 'Required by the reimbursement policy',
    optionalByPolicy: 'Optional under the current reimbursement policy',
    alertsTitle: 'Alerts',
    alertsApprovalTitle: 'Approval alerts',
    flags: {
      noPolicyMatched: 'This bill is identified as reimbursement, but no policy is applied',
      goToPayments: 'Go to Payments',
      exceedsTransactionLimit: 'Amount exceeds per-transaction limit',
      exceedsPeriodLimit: 'Amount exceeds period limit',
      nearPeriodLimit: 'Period limit nearly reached',
      lateSubmission: 'Expense submitted after the policy deadline',
      autoApproved: 'Reimbursement auto-approved',
      policyOverride: 'Policy manually adjusted',
      taxIdMissing: 'Document missing entity tax ID',
    },
    validation: {
      missingInfo: 'Reimbursement details are required',
      requesterRequired: 'Select the reimbursement requester',
      expenseDateRequired: 'Expense date is required',
      dueDateRequired: 'Reimbursement due date is required',
      paymentMethodRequired: 'Select a reimbursement method',
      originAccountRequired: 'Select the reimbursement origin account',
      expenseClassificationRequired: 'Expense classification is required',
      amountRequired: 'Reimbursement amount must be greater than zero',
      justificationRequired: 'Reimbursement justification is required',
    },
  },
  countryTaxes: {
    source: 'Amounts from the tax document',
    empty: 'No taxes on the document',
    retainedToggle: 'Withholdings apply',
    payableImpact:
      'The net payable goes from {{from}} to {{to}}; the scheduled payments are adjusted to it.',
    netPayable: 'Net payable',
    notRetainedHint: 'No withholding: the vendor receives the gross amount of the bill.',
    ivaTransferred: { label: 'IVA trasladado', hint: 'Creditable; stays pending until payment' },
    ivaWithheld: {
      label: 'IVA retenido',
      hint: 'Withheld at source; the entity remits it to the SAT',
    },
    isrWithheld: {
      label: 'ISR retenido',
      hint: 'Withheld at source; the entity remits it to the SAT',
    },
    salesTax: { label: 'Sales tax', hint: 'Included in cost, no credit' },
    taxEdit: {
      save: 'Save',
      effectiveRate: 'Effective rate: {{rate}} of the pre-tax amount',
      invalid: 'The tax must be zero or more and less than the invoice total ({{total}}).',
      belowWithheld: 'IVA trasladado cannot be less than the IVA withheld ({{withheld}}).',
    },
    salesTaxInCost: '+ {{amount}} sales tax included in cost',
    unusualRate: {
      ivaTransferred:
        'IVA trasladado is {{rate}} of the pre-tax amount; the usual rates are 16%, 8% or 0%. Check the document.',
      ivaWithheld:
        'IVA withheld is {{rate}} of the pre-tax amount; the usual is 2/3 of the IVA trasladado, or 4% on freight. Check the document.',
      isrWithheld:
        'ISR withheld is {{rate}} of the pre-tax amount; the usual rates are 10% or 1.25%. Check the document.',
    },
  },
  aiPosting: {
    badge: 'AI',
    pending: 'Generating the accounting entry...',
    processingStarted: 'Sent. The accounting entry is being generated.',
    provisionFailed: 'Provision not posted. {{reason}} Close stays blocked until it is resolved.',
    provisionRefused:
      'The AI and the standard provision refused this bill. {{reason}} Fix it and send it for approval again.',
    provisionPendingBadge: 'Provision pending',
    provisionRefusedBadge: 'Provision refused',
    fixWithholdings: 'Correct withholdings',
    failureReasons: {
      AP_AI_POSTING_INPUT_INCOMPLETE:
        "The IVA and ISR withholdings don't match the total withheld, or bill data is missing (accrual date, vendor). Correct the withholdings.",
      generic: 'Review amounts, withholdings and classification.',
      AP_WITHHOLDING_SLOT_UNSUPPORTED:
        'The bill has a withholding that does not apply to this entity (e.g. a Brazilian withholding on a Mexican bill). Review the withholdings.',
      AP_WITHHOLDING_TOTAL_MISMATCH:
        "The IVA and ISR withholdings don't add up to the bill's total withheld. Correct the withholdings.",
      TAX_ACCOUNT_NOT_MAPPED:
        'Map the withholding and IVA accounts in Settings → Accounting & Tax → Account mapping (Fiscal tab).',
      OP_BANK_ACCOUNT_MISSING:
        'Map the Bank accounts account in Settings → Accounting & Tax → Account mapping (Treasury tab): the settled payment posts against it.',
      AP_BILL_FOREIGN_CURRENCY_NO_RATE:
        'The bill currency has no exchange rate on the accrual date. Add the rate.',
      AP_BILL_CURRENCY_REVIEW_REQUIRED:
        'The bill currency needs review before posting. Check the currency.',
    },
    retry: 'Retry with AI',
    retryConfirmTitle: 'Retry with AI?',
    retryConfirmDescription:
      'The AI proposes the entry again. If the proposal is valid it is posted to the ledger; otherwise the standard provision is attempted.',
    retryPosted: 'The AI posted the provision.',
    retryFallback: "The AI didn't validate; the standard provision was posted.",
    retryStillFailed: 'The provision still could not be posted. {{reason}}',
    paymentLegNotPosted:
      'Payment not posted to the ledger. {{reason}} Close stays blocked until it is resolved.',
    paymentLegReasons: {
      generic:
        'The provision posted, but the payment entry was left out. Review the payment and try again.',
    },
    paymentLegRetry: 'Post payment',
    paymentLegRetryTitle: 'Post payment again?',
    paymentLegRetryDescription:
      "This bill's payments missing from the ledger are posted again. The provision does not change.",
    paymentLegStillNotPosted: 'The payment still could not be posted. {{reason}}',
    paymentLegPosted: 'Payment posted to the ledger.',
    postedBanner: 'Accounting entry posted by AI',
    viewEntry: 'View entry',
    reviewed: 'Reviewed',
    reviewedAt: 'Reviewed on {{date}}',
    awaitingReview: 'Awaiting review at close',
    confidence: 'Confidence: {{value}}%',
    reportedConfidence: '(reported by AI: {{value}}%)',
    divergesFromClassification: 'Account differs from the classification',
    provenance: 'Model {{model}} · prompt {{version}}',
    markReviewed: 'Mark reviewed',
    correct: 'Correct',
    notCorrectableHint:
      'After payment or once the period is closed, correct it by reclassification.',
    saveCorrection: 'Reverse and repost',
    correctConfirmTitle: 'Reverse and post again?',
    correctConfirmDescription:
      'The AI entry will be reversed and a new one posted with these lines. The correction counts as the review.',
    cancelEdit: 'Cancel',
    amount: 'Amount',
    addLine: 'Add line',
    removeLine: 'Remove line',
    account: 'Account',
    memo: 'Line description',
    debit: 'Debit',
    credit: 'Credit',
    splitLine: 'Split line',
    balanced: 'Balanced',
    empty: 'No values',
    difference: 'Difference of {{amount}}',
    totalTarget: "Debits and credits must total {{amount}} (the bill's net plus withholdings)",
    lineType: 'Line type',
    lineTypePlaceholder: 'Choose the type',
    classification: 'Classification',
    classificationPlaceholder: 'Classification',
    roles: {
      EXPENSE: 'Expense',
      ASSET: 'Asset',
      TAX_RECOVERABLE: 'Recoverable tax',
      TAX_WITHHELD_IVA: 'IVA withheld',
      TAX_WITHHELD_ISR: 'ISR withheld',
      AP_VENDOR: 'Vendor',
    },
    errors: {
      AP_AI_POSTING_UNBALANCED:
        "Debits and credits don't match, or the vendor line isn't exactly the bill's net payable.",
      AP_AI_POSTING_ROLE_ACCOUNT_INVALID:
        "A line uses an account that doesn't fit its type (e.g. a liability on an expense line).",
      AP_AI_POSTING_ASSET_OUTSIDE_CLASSIFICATION:
        "The AI put cost on an asset the bill's classification didn't choose; the standard entry was used.",
      AP_AI_POSTING_ALLOCATION_MISMATCH:
        "Each of the bill's classifications must keep the same cost. When splitting an expense, keep the new line in the original line's classification.",
      AP_AI_POSTING_TAX_AMOUNT_MISMATCH:
        "The tax or withholding amounts don't match the ones on the document.",
      AP_AI_POSTING_ACCOUNT_INVALID:
        "One of the accounts doesn't exist, is inactive or isn't postable in this entity's chart.",
      AP_AI_POSTING_AMOUNT_INVALID:
        'There is an invalid amount: use positive values with up to 2 decimals.',
      AP_AI_POSTING_CLASSIFICATION_REQUIRED:
        "Expense or asset lines must come from one of the bill's classifications.",
      AP_AI_POSTING_CLASSIFICATION_INVALID: "The line points to another bill's classification.",
      AP_AI_POSTING_AP_SIDE_INVALID: 'The vendor line must be a credit.',
      AP_AI_POSTING_TAX_SIDE_INVALID: 'Withholding lines must be credits.',
      AP_AI_POSTING_NOT_POSTED: 'This bill has no AI-posted entry. Refresh the page.',
      AP_AI_POSTING_NOT_CORRECTABLE:
        "This entry can't be corrected here anymore (bill paid or period closed). Use reclassification.",
      AP_AI_POSTING_VENDOR_LOCKED: "The vendor line can't change in a correction.",
      AP_AI_POSTING_RETRY_UNAVAILABLE:
        'This bill is no longer waiting for a retry. Refresh the page.',
      AP_AI_POSTING_SCOPE_CHANGED:
        'The bill changed and no longer qualifies for AI posting (e.g. it became reimbursable or got a purchase order, recurrence or budget).',
      AP_AI_POSTING_STALE:
        'The entry changed since you opened this screen. We reloaded the current version; check it before acting again.',
    },
    toasts: {
      corrected: 'Entry reversed and posted again.',
    },
  },
  glPosting: {
    editAndPostDescription:
      'This bill has no ledger entry yet, so nothing is reversed. The bill is corrected and its provision is posted. Enter the justification.',
    editAndRepostSuccess:
      'Changes applied. When the bill is posted by AI, the new provision is generated next and shows on the bill.',
    linkLabel: 'Posted to the general ledger',
    title: 'Posted to the general ledger',
    description: 'View the journal entries posted to the account in each book.',
    comprehensiveTitle: 'General ledger postings',
    accountingEntriesTitle: 'Accounting entries',
    comprehensiveDescription: 'View accrual and payment journal entries for each book.',
    provisionSectionTitle: 'Accrual',
    paymentSectionTitle: 'Payment',
    summaryProvisionedLabel: 'Accrued',
    summaryPaidLabel: 'Paid',
    summaryPassiveLabel: 'Liability balance',
    payroll: {
      entryMonthly: 'Accrual/Payroll',
      entryVacationAdvance: 'Vacation advance',
      entryReceipt: 'Receipt',
      entryAutonomous: 'Freelancer receipt',
      entryAdvance: 'Advance',
      entryThirteenth: '13th salary',
      entryProLabore: 'Pro-labore',
      summaryGeneratedLabel: 'Obligations generated',
      summaryPaidLabel: 'Paid',
      summaryOpenLabel: 'Outstanding',
      summaryGeneratedHint:
        'Sum of credits to liability accounts in this payroll journal, excluding reversed ones.',
      summaryPaidHint:
        "Sum of this payroll's bills already paid (status Paid). Partial payments do not count yet.",
      summaryOpenHint: 'Obligations generated minus Paid.',
      summaryUnavailable: '—',
    },
    reversedShort: 'Reversed',
    entryProvision: 'Accrual',
    entryRevaluation: 'FX revaluation',
    entryRevenueTax: 'PIS/COFINS provision',
    entryRevenueTaxReversal: 'PIS/COFINS provision reversal',
    entryRevenueTaxOther: 'Revenue tax',
    revenueTaxGroup: 'Other revenue taxes provisioned',
    afterRevenueTaxGroup: 'Other entries',
    entryPayment: 'Payment',
    statusPosted: 'Posted',
    statusPending: 'Pending',
    statusReversed: 'Reversed',
    statusReversal: 'Reversal',
    periodShort: 'Period',
    lineCountLines: '{{count}} lines',
    collapsedDebitCredit: 'Dr {{d}} · Cr {{c}}',
    totalLabel: 'Total',
    generatedAt: 'Generated on {{date}}',
    export: 'Export',
    empty: 'No journal entries found.',
    loadError: 'Could not load the journal entries. Check your permissions or try again.',
    retry: 'Try again',
    journalDate: 'Date',
    period: 'Period',
    status: 'Status',
    memo: 'Memo',
    account: 'Account',
    amountColumn: 'Amount',
    debit: 'Debit',
    credit: 'Credit',
    reversed: 'Reversed',
    reversal: 'Reversal',
    reviewButton: 'Review',
    revertApproval: 'Revert approval',
    revertApprovalTitle: 'Revert approval?',
    revertApprovalDescription:
      'This approved bill has no accrual journal entry to reverse. It will return to submitted review while preserving documents, payments, and history.',
    revertApprovalConfirm: 'Revert approval',
    revertApprovalConfirming: 'Reverting...',
    revertApprovalSuccess: 'Approval reverted. The bill is back in review.',
    revertApprovalHistoryReason: 'Approval reverted for operational review without GL reversal.',
    editGlEntries: 'Edit journal entries',
    saveEditMode: 'Save changes',
    flagForReview: 'Flag for accounting review',
    flagForReviewDescription:
      'This bill will be flagged for accounting review. Provide observations on what needs to be verified.',
    observations: 'Observations',
    observationsPlaceholder: 'Describe what needs to be reviewed...',
    flagForReviewConfirm: 'Flag for review',
    flagForReviewConfirming: 'Flagging...',
    revertBill: 'Revert bill',
    revertToEditing: 'Revert to editing',
    voidBill: 'Void bill',
    voidSuccess: 'Bill voided successfully.',
    voidPostedBillDescription:
      'Journal entries will be reversed in the selected period and the bill will be voided. Select the period and provide a justification.',
    voidConfirmTitle: 'Void this bill?',
    voidConfirmDescription:
      'This permanently voids the bill. Approval flow is cancelled and document links may be removed.',
    confirmVoidInvalidation: 'Confirm void',
    selectPeriod: 'Select period',
    justification: 'Justification',
    justificationPlaceholder: 'Enter the reason for the reversal...',
    reversalDialogTitle: 'Revert bill',
    reversalActionLabel: 'Action',
    reversalDialogDescription:
      'Choose to void the bill or revert to editing. Reversals will be posted to the general ledger for the selected period.',
    reversalDialogSuccess: 'Reversal completed',
    reversalDialogRetry: 'Try again',
    reversalCompletedVoidOnly: 'Completed bills can only be voided.',
    reversalDialogConfirm: 'Confirm reversal',
    reversalDialogConfirming: 'Reverting...',
    modifyClassifications: 'Modify accounting classifications',
    modifyClassificationsBanner:
      'When modifying the accounting account, reversals of previous postings will be posted to the general ledger, followed by a new posting.',
    saveReclassification: 'Save accounting modification',
    requestClassificationChange: 'Request classification change',
    classificationChangeRequestTitle: 'Request classification change',
    classificationChangeRequestDescription:
      'Enter the desired new GL account and justification. A request will be sent for accounting review.',
    newGlAccount: 'New GL account',
    newGlAccountPlaceholder: 'Select the GL account',
    classificationChangeRequestConfirm: 'Submit request',
    classificationChangeRequestConfirming: 'Submitting...',
    reclassify: 'Modify accounting account',
    reclassifyConfirm:
      'A reversal will be posted to the general ledger for the previous entry, followed by a new posting',
    sendToReview: 'An accounting review request will be created',
    classificationChangeRequestSuccess: 'Review request sent',
    reclassifySuccess: 'Classification changed',
    applyEdits: 'Apply changes',
    rejectEdits: 'Reject changes',
    markAsReviewed: 'Mark as reviewed',
    markAsReviewedDescription:
      'Add observations about the review performed and confirm to complete.',
    markAsReviewedConfirm: 'Mark as reviewed',
    markAsReviewedConfirming: 'Saving...',
    markAsReviewedSuccess: 'Review completed',
    requestedByLine: '{{date}} · {{name}}',
    pendingReview: 'Accounting review pending',
    awaitingReview: 'Awaiting review',
    fallbackReclassification: {
      action: 'Set final account',
      title: 'Set the final GL account',
      description:
        'Move the temporary balance to the correct GL account. You can clear the full balance or post part of it now.',
      book: 'Book',
      temporaryAccount: 'Temporary account',
      remaining: 'Balance to reclassify',
      finalAccount: 'Final GL account',
      finalAccountPlaceholder: 'Select the final account',
      journalDate: 'Posting date',
      amount: 'Amount',
      submit: 'Post reclassification',
      posting: 'Posting...',
      partialSuccess: '{{amount}} reclassified. The remaining balance stays in review.',
      completedSuccess:
        'The temporary balance is zero. The review can now be completed with Apply changes.',
      reviewCompletedSuccess: 'The balance was reclassified and the accounting review is complete.',
      submitError: 'Could not post the reclassification.',
      loadErrorTitle: 'Could not load the balance',
      loadErrorDescription: 'Try again to check the current amounts.',
      noOpenPeriodTitle: 'No open period',
      noOpenPeriodDescription:
        'Open a GL period for this book before posting the reclassification.',
      clearedTitle: 'Temporary balance cleared',
      clearedDescription: 'Close this window and complete the review with Apply changes.',
      history: 'Posted corrections',
      historyAction: 'View accounting corrections',
      reverse: 'Reverse correction',
      reversed: 'Reversed',
      reverseTitle: 'Reverse this accounting correction?',
      reverseDescription:
        'The balance will return to the temporary account and the accounting review will reopen.',
      reverseConfirm: 'Confirm reversal',
      reverseSuccess: 'Correction reversed. The accounting review was reopened.',
      reverseAuditReason: 'Reversal requested from the bill accounting review.',
    },
    pendingReviewFlagDetail:
      'Bill flagged for accounting review (no classification change requested).',
    flagForReviewSuccess: 'Bill flagged for accounting review.',
    revertToSubmittedSuccess: 'Bill reverted to editing.',
    postingsToReverse: 'Postings that will be reversed',
    paymentReversalSamePeriod:
      'The payment reconciliation will be cleared. The bank transaction will return to pending and can be reconciled with another payment.',
    paymentReversalDifferentPeriod:
      'The payment and bank reconciliation will be preserved. The resulting Accounts Payable balance will need to be reclassified.',
    paymentReversalNoReconciledPayments:
      'There are no reconciled payments on this bill. Reversal follows the accounting rules for posted provisioning and payment entries.',
  },
  alertTypes: {
    BILL_ALREADY_APPROVED: 'Bill already approved',
    BILL_ALREADY_PAID: 'Bill already paid',
    DUPLICATE_DOCUMENT: 'Duplicate document',
    ENTITY_MISMATCH: 'Customer tax ID does not match any entity',
    ENTITY_MISMATCH_NO_MATCH: 'Customer tax ID does not match any entity',
    ENTITY_MISMATCH_AMBIGUOUS: 'Customer tax ID matches multiple entities',
    ENTITY_MISMATCH_TAX_ID_CONFLICT:
      'The customer tax ID points to a different entity; confirm the entity before approving',
    ENTITY_MISMATCH_DOCUMENT_DISAGREES:
      'The document names a different entity; confirm the entity before approving',
    ENTITY_MISMATCH_USER_CHOICE:
      'The customer tax ID points to a different entity, but the entity chosen for the e-mail was kept; confirm before approving',
    ENTITY_SUGGESTION_AVAILABLE: 'Entity suggestion: {{entityName}} — confirm before approving',
    EXTERNAL_FORM_TAXPAYER_MISMATCH:
      'Card taxpayer differs from the document; check the CNPJ/CPF before approving',
    AP_BILL_NET_AMOUNT_INCOHERENT:
      'Automatic update refused: the document would set gross {{grossAmount}} and net {{netPaymentAmount}}, but the expected net is {{expectedNetAmount}} (gross plus interest and fine, minus withholdings of {{retainedAmount}}). Bill amounts kept; check the document',
    AP_BILL_NET_AMOUNT_INCOHERENT_GROSS_EQUALS_NET:
      'Automatic update refused: the document would set gross {{grossAmount}} and net {{netPaymentAmount}}, but the bill has withholdings, so net and gross cannot be equal. Bill amounts kept; check the document',
    AP_BILL_NET_AMOUNT_INCOHERENT_WITH_DISCOUNT:
      'Automatic update refused: the document would set gross {{grossAmount}} and net {{netPaymentAmount}}, but the expected net is {{expectedNetAmount}} (gross plus interest and fine, minus withholdings of {{retainedAmount}}), or {{expectedDiscountedNetAmount}} with the discount of {{discountAmount}}. Bill amounts kept; check the document',
    RECONCILED_WITHOUT_APPROVAL: 'Reconciled without approval',
    AMOUNT_ANOMALY: 'Amount variation outside expected range',
    AMOUNT_ANOMALY_CURRENCY_UNRESOLVED:
      'Clara transaction currency could not be identified — confirm the currency before approving',
    AMOUNT_ANOMALY_SETTLEMENT_CURRENCY_SKIPPED:
      'Payment settlement was not posted because of a currency mismatch — check the bill and cash source currencies',
    VENDOR_CONCENTRATION: 'High spend concentration on vendor',
    EXPIRING_CONTRACT: 'Recurring contract nearing expiration',
    CORPORATE_GROUP_MIXED_CNPJ:
      'This bill contains documents from different CNPJs within the same corporate group',
  },
  corporateGroupMixedCnpjTooltip:
    "To unlink, go to vendor settings and disable 'Unify corporate group accounts', then delete the bill and reprocess the files",
  historyActions: {
    reconciled: 'Reconciled',
  },
  newBill: {
    title: 'New bill to pay',
    tabForm: 'Form',
    tabChat: 'AI Chat',
    cancel: 'Cancel',
    createDraft: 'Create as draft',
    createBill: 'Create bill',
    hintForm: 'Enter to save',
    hintChat: 'Enter to send · Shift+Enter new line',
    addBill: 'Bill',
    addBillShortcut: 'Alt+Shift+N',
    chatShortcut: 'Alt+Shift+C',
    vendor: {
      label: 'Vendor',
      placeholder: 'Select a vendor...',
      search: 'Search',
      notFound: 'Vendor not found for',
      createViaRF: 'Create via Federal Revenue',
      createManual: 'Create manually',
      consultingRF: 'Looking up CNPJ (Serpro)...',
      resolvingFromDda: 'Looking up vendor from bill CNPJ...',
      creatingFromDda: 'Creating vendor...',
      autoResolved: 'Vendor identified automatically',
      autoResolveFailed: 'Could not create vendor automatically — check the CNPJ below',
      createSuccess: 'Vendor created successfully',
      createError: 'Error creating vendor',
      duplicateCnpj: 'A vendor with this CNPJ already exists',
      duplicateCpf: 'A vendor with this CPF already exists',
      createNewSection: 'Create new vendor',
      namePlaceholder: 'Vendor name',
      cnpjCpfPlaceholder: 'Vendor CNPJ or CPF',
      ddaDiscovery: {
        knownHint: 'Vendor matched from the DDA bill CNPJ.',
        unknownTitle: 'Vendor not registered yet',
        unknownDescription:
          'This DDA bill CNPJ is not in your vendor list. Review the details below before creating the vendor — helpful for fraud checks.',
        previewSourceRegistry: 'Data from Federal registry (Serpro)',
        previewSourceDda: 'Name from DDA bill only (registry lookup unavailable)',
        createForMe: 'Create vendor for me',
        createSuccess: 'Vendor created and selected',
        previewFailedTitle: 'Could not look up CNPJ',
        previewFailedDescription: 'Check CNPJ {{cnpj}} and create the vendor manually below.',
      },
    },
    form: {
      entity: 'Entity',
      value: 'Amount',
      currency: 'Currency',
      exchangeRate: 'Exchange rate (optional)',
      exchangeRatePlaceholder: 'e.g. 5.4321',
      exchangeRateHelp:
        'If left empty, the PTAX closing rate for the competency date is used automatically.',
      competency: 'Competency',
      description: 'Description',
      descriptionPlaceholder: 'E.g.: SST services - May/2025',
      dueDate: 'Due date',
      managerialDate: 'Managerial date',
      classification: 'Classification',
      glAccount: 'GL account',
      managerialAccount: 'Managerial account',
      costCenter: 'Cost center',
      department: 'Department',
      payment: 'Payment',
      paymentMethod: 'Payment method',
      bankAccount: 'Bank account',
      bankAccountOption: 'Bank account',
      creditCardOption: 'Credit card',
      creditCardAccount: 'Credit card',
      vendorPaymentDetails: 'Vendor banking details',
      vendorBankAccount: 'Bank account',
      vendorDefaultAccount: 'Primary account',
      vendorDefaultBadge: 'Primary',
      vendorUsAccountBadge: 'US',
      vendorRoutingNumber: 'Routing number',
      vendorUsAccountNumber: 'Account',
      vendorUsAccountTitle: 'US account',
      vendorNoBankAccounts: 'This vendor has no bank accounts registered.',
      addVendorBankAccount: 'Add vendor banking details',
      recurring: 'Recurring bill',
      recurringHelp: 'Automate posting in upcoming months',
      observations: 'Observations',
      observationsPlaceholder: 'Internal notes...',
      selectPlaceholder: 'Select...',
      classificationSuggestion: 'Reuse classification from a previous bill for this vendor?',
      classificationSuggestionBadge: 'History',
      classificationSuggestionManagerial: 'Managerial',
      classificationSuggestionDept: 'Dept',
      classificationSuggestionApply: 'Apply suggestion',
      classificationSuggestionIgnore: 'Ignore',
      classificationSuggestionDismiss: 'Dismiss suggestion',
      classificationRateioHint: 'You can create apportionments after saving the bill.',
    },
    validation: {
      requiredFields: 'Please fill in all required fields',
      entityRequired: 'Select an entity',
      amountRequired: 'Enter a valid amount',
      exchangeRateInvalid: 'Enter a valid exchange rate (positive number)',
      functionalCurrency: {
        idle: 'Select an entity to look up its functional currency.',
        loading: "Looking up the entity books' functional currency...",
        error: 'Could not look up the functional currency. Please try again.',
        unresolved: 'The entity needs posting books configured with one functional currency.',
        retry: 'Try again',
        retrying: 'Trying again...',
      },
      glAccountRequired: 'Select a GL account',
      managerialAccountRequired: 'Select a managerial account',
      costCenterRequired: 'Select a cost center',
      departmentRequired: 'Select a department',
    },
    submit: {
      creating: 'Creating bill...',
      success: 'Bill created successfully',
    },
    errors: {
      entityNotFound: 'Entity not found',
      vendorNotFound: 'Vendor not found. Please search again.',
      classificationError: 'Classification error: {{message}}',
      classificationPartialFailure:
        'Bill created, but there was an error saving the classification. You can adjust it on the bill detail page.',
      paymentError:
        'Bill created, but there was an error scheduling the payment. You can add it on the bill detail page.',
      bankAccountNotFound: 'Bank account not found or inactive',
      paymentDestinationMissing:
        'Bill created without a payment: the vendor has no bank account or Pix key registered. Register one, then schedule the payment and choose its destination.',
      bankAccountWrongEntity: 'Bank account does not belong to the selected entity',
      paymentMethodIncompatible: 'Incompatible payment method: {{message}}',
      createBillError: 'Error creating bill: {{message}}',
      networkError: 'Connection error. Please check your internet and try again.',
      serverError: 'Internal server error. Please try again.',
      unexpectedError: 'Unexpected error. Please try again.',
    },
    chat: {
      welcome:
        'Describe the bill you want to create and I will take care of the rest. I can create vendors, classify and schedule payment.',
      examplesLabel: 'Examples of what you can ask:',
      placeholder: 'Describe the bill to pay...',
      example1:
        'Create a R$100 bill for 006.408.289-06, pay via Itaú. If vendor does not exist, create it. Classify as software expense.',
      example2:
        'NFS-e of R$3,500 from vendor CLIMEC, SST services, competency Feb/2026, due 25/03/2026, boleto via Santander.',
      example3:
        'Monthly AWS subscription of R$4,200, classify as service cost > infra, cost center Engineering.',
    },
  },
  import: {
    breadcrumbBills: 'Accounts payable',
    pageTitle: 'Import Bills from CSV',
    steps: {
      upload: 'Upload',
      uploadDesc: 'Select the CSV file',
      mapping: 'Mapping',
      mappingDesc: 'Map columns to fields',
      review: 'Review',
      reviewDesc: 'Validate and adjust data',
      confirm: 'Confirm',
      confirmDesc: 'Import the bills',
    },
    upload: {
      title: 'Select the CSV file',
      dropzone: 'Drag and drop a CSV file here or click to select',
      entityLabel: 'Entity',
      entityPlaceholder: 'Select entity',
      entityRequired: 'Select an entity before continuing',
      fileSelected: 'File selected',
      rows: 'rows',
      columns: 'columns',
      removeFile: 'Remove file',
      fileSizeExceeded: 'File exceeds the 5 MB limit',
      emptyFile: 'The CSV is empty or could not be read',
      invalidHeaderLine: 'Invalid header line number for this file',
      headerLineLabel: 'Header row line (optional)',
      headerLinePlaceholder: 'Automatic',
      headerLineHint:
        'Enter the 1-based line number of the table header if the file has preamble text (e.g. card statements). Leave blank for automatic detection.',
      defaultTaxIdLabel: 'Default CPF/CNPJ (optional)',
      defaultTaxIdPlaceholder: 'e.g. 00.000.000/0001-91',
      defaultTaxIdHint:
        'Use when the CSV has no CPF/CNPJ per row (e.g. corporate card). This default accepts Brazilian identifiers only; international identifiers must be supplied per row with the Type column.',
    },
    mapping: {
      title: 'Map CSV columns',
      targetField: 'Field',
      csvColumn: 'CSV Column',
      noMapping: 'Do not map',
      required: 'Required',
      optional: 'Optional',
      requiredMissing: 'Required fields not mapped: {{fields}}',
      taxIdTypeGuidanceTitle: 'How to provide the tax identifier type',
      taxIdTypeGuidance:
        'The Type column is optional. Without it, the system automatically detects CPF (11 characters) or CNPJ (14 characters). For registered foreign vendors, map Type and enter INTERNATIONAL (EIN or another foreign tax ID) or RFC (Mexico) on each row. Also map Currency and provide its ISO code (for example, USD or MXN); the row is blocked when currency is missing or unsupported. Register the vendor country and tax regime first.',
      fields: {
        vendorTaxId: 'Tax ID',
        vendorTaxIdType: 'Tax ID Type',
        vendorName: 'Vendor Name',
        totalAmount: 'Amount',
        currencyCode: 'Document Currency',
        dueDate: 'Due Date',
        vendorBillNumber: 'Supplier document number',
        billCompetencyDate: 'Competency Date',
        billManagerialDate: 'Managerial Date',
        description: 'Description',
        memo: 'Memo',
        glAccountName: 'GL Account',
        managerialAccountName: 'Managerial Account',
        costCenterName: 'Cost Center',
        departmentName: 'Department',
      },
    },
    review: {
      title: 'Review imported data',
      loading: 'Processing...',
      loadError: 'Failed to load import preview',
      retry: 'Retry',
      summaryTitle: 'Summary',
      totalRows: 'Total rows',
      totalRowsSubtitle: 'in file',
      validRows: 'Valid rows',
      validRowsSubtitle: 'ready to import',
      errorRows: 'Rows with errors',
      errorRowsSubtitle: 'need adjustment',
      newVendors: 'New vendors',
      newVendorsSubtitle: 'will be created',
      duplicates: 'Possible duplicates',
      duplicatesSubtitle: 'require attention',
      vendor: 'Vendor',
      taxId: 'Tax ID',
      taxIdPlaceholder: 'Enter CPF, CNPJ, RFC, or international tax ID',
      amount: 'Amount',
      dueDate: 'Due Date',
      competencyDate: 'Competency Date',
      managerialDate: 'Managerial Date',
      description: 'Description',
      descriptionPlaceholder: 'Bill description',
      memo: 'Memo',
      memoPlaceholder: 'Internal notes',
      glAccount: 'GL Account',
      glAccountPlaceholder: 'Select GL account',
      managerialAccount: 'Managerial Account',
      managerialAccountPlaceholder: 'Select managerial account',
      costCenter: 'Cost Center',
      costCenterPlaceholder: 'Select cost center',
      department: 'Department',
      departmentPlaceholder: 'Select department',
      suggestions: 'Suggestions',
      noSuggestions: 'No suggestions',
      status: 'Status',
      errors: 'Errors',
      warnings: 'Possible duplicate',
      valid: 'Valid',
      classificationWarning: 'Warning',
      excluded: 'Excluded',
      newVendor: 'New',
      existingVendor: 'Existing',
      invalidData: 'Invalid data',
      validationErrors: {
        taxIdRequired: 'Tax ID is required',
        invalidCnpj: 'Invalid CNPJ',
        invalidCpf: 'Invalid CPF',
        taxIdDigits: 'CPF/CNPJ must be 11 or 14 characters',
        taxIdTypeRequired:
          'The type could not be inferred. Map the Type column and use INTERNATIONAL or RFC.',
        invalidRfc: 'Invalid RFC. Use the 12- or 13-character Mexican format.',
        unsupportedTaxIdType:
          'Unsupported type. Use CPF, CNPJ, INTERNATIONAL, or RFC in the Type column.',
        foreignVendorRegistrationRequired:
          'Foreign vendor not found. Register it with its country and tax regime before importing, then upload the file again.',
        amountRequired: 'Amount is required and must be a valid number',
        dueDateRequired: 'Due date is required (DD/MM/YY)',
        competencyDateInvalid: 'Competency date must be a valid date',
        managerialDateInvalid: 'Managerial date must be a valid date',
        unknown: 'Validation error',
      },
      classificationWarnings: {
        glNotPostable: 'The selected GL account is not postable. Select a postable account.',
      },
      amountWarnings: {
        highAmount: 'Unusually high amount — please verify.',
      },
      viewDuplicateBill: 'View duplicate bill',
      scrollToRow: 'Scroll to row {{row}}',
      noValidRows: 'No valid rows to import',
      lockColumns: 'Lock columns when scrolling',
      freezeUpToColumn: 'Freeze up to this column',
      unlockColumns: 'Unlock columns',
      excludeRow: 'Exclude row',
      excludeRowConfirmTitle: 'Exclude row from import?',
      excludeRowConfirmDescription:
        'This row will not be imported. You can include it again before confirming.',
      fixInvalidRowsTooltip: 'Fix invalid rows to import',
      importConfirmTitle: 'Confirm import',
      importConfirmDescription: 'Do you want to proceed with importing the bills?',
      importConfirmDuplicatesTitle: 'Possible duplicates',
      importConfirmDuplicatesDescription:
        'There are possible duplicates in the selected rows. Do you want to continue anyway?',
      includeRow: 'Include row',
      searchDimensions: 'Search options',
      loadingDimensions: 'Loading options...',
      suggested: 'Suggested matches',
      noMatches: 'No options found',
      clearSelection: 'Clear selection',
      confidence: 'Confidence: {{value}}%',
      source: {
        rule: 'Rule',
        history: 'History',
        context: 'Context',
        csv_text: 'CSV',
      },
      importAsMigrated: 'Import as migrated',
      importAsMigratedHelp:
        'history from the previous system: no GL posting, visible only in the All tab',
    },
    confirm: {
      title: 'Importing bills...',
      success: '{{count}} bill(s) imported successfully',
      vendorsCreated: '{{count}} vendor(s) created',
      errors: '{{count}} bill(s) with errors',
      errorDetailsTitle: 'Error details',
      vendorErrorDetailsTitle: 'Vendor creation failures',
      vendorErrorTaxId: 'Tax ID {{taxId}}',
      warningDetailsTitle: 'Imported with warnings (classification not applied)',
      errorRow: 'Row {{row}}',
      viewBill: 'View bill',
      goToBills: 'Go to Bills',
      importMore: 'Import more',
      done: 'Import complete',
      downloadFailedRows: 'Download failed rows',
      knownErrors: {
        glNotPostable:
          'The selected GL account is not postable. Edit the classification on the imported bill.',
        vendorNotFound: 'Vendor not found and could not be created',
      },
    },
    actions: {
      next: 'Next',
      back: 'Back',
      import: 'Import',
      cancel: 'Cancel',
      preview: 'Preview',
    },
  },
} as const;
